import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ErrorCode, type Room } from 'colyseus.js';
import type { GameState } from '@fortress/shared/schema';
import { SPECTATOR_SEAT } from '@fortress/shared/schema';
import {
  fortressCountOf,
  ENERGY_ZONE_COUNT,
  energyZoneOf,
  ACTION_KINDS,
  ACTION_LABELS,
  ACTION_DESCRIPTIONS,
  BOARD_RADIUS,
  FORTRESS_CARDS_CONFIG,
  APP_VERSION,
  MAX_QUEUE_TURNS,
  RESOURCES,
  areAdjacent,
  buildBoard,
  canAffordWith,
  canPerformAction,
  canUseCardAction,
  capturePaymentFor,
  committedFortressNodes,
  cycleMarketRows,
  deriveSlots,
  edgeKey,
  anyCardHasPassive,
  parseStealEntry,
  fortressCardsById,
  depositsToStored,
  implementBankOrProductionOf,
  implementScalesCap,
  instanceTokenCap,
  instanceTokenTarget,
  implementPieceOptions,
  fortressCardsByName,
  fortressCostFor,
  gateReasonFor,
  grantedActionAllowance,
  matchAllFortressPatterns,
  matchFortressPattern,
  ZONE_HIGHEST,
  nodesWithinAny,
  nodesWithinInto,
  resolveBuilding,
  resolveVigorZone,
  substitutionsFor,
  tokenCapOf,
  tokenTargetOf,
  HONK_EMOJI,
  sameEmoji,
  VIGOR_ZONES_CONFIG,
  vigorZoneLevel,
  wildcardCostAs,
  type ActionNotice,
  type AimMode,
  type BuildAim,
  type BuildingConfig,
  type BoardFx,
  type BuildingType,
  type CardEffect,
  type CardEffectLookup,
  type ConstructableBuilding,
  type ImplementPiece,
  type FortressBuilding,
  type FortressCard,
  type FortressKind,
  type FortressMatch,
  type GameAction,
  type GameConfig,
  type GateQuery,
  type PaymentSubstitutions,
  type PlayerActionState,
  type ResourceAmount,
  type SkillPool,
  type SpecialActionKind,
  type VigorZone,
  TUTORIAL_STEPS,
  MAX_TURN_TIME_LIMIT_MINUTES,
  WS_CLOSE_SERVER_SUPERSEDED,
} from '@fortress/shared';
import {
  BuildingIcon,
  BUILDING_BLURBS,
  BUILDING_LABELS,
  CONSTRUCTABLE_BUILDINGS,
  PIECE_LABELS,
  PieceIcon,
  buildingCursor,
} from './buildings';
import {
  createPixiGame,
  type PixiGame,
  type RemoteCursorView,
} from './game/PixiGame';
import {
  cardTokenTray,
  FortressCardFace,
  REGULAR_ACCENT,
  WIN_ACCENT,
} from './library/FortressCardFace';
import {
  ResourceIcon,
  ResourceRow,
  RESOURCE_KINDS,
  RESOURCE_LABELS,
  type ResourceKind,
} from './resources';
import {
  DiscountedNumber,
  ObsidianCostProvider,
  PlayerCountProvider,
  ResourceCost,
  useObsidianCost,
  type ObsidianCostLookup,
} from './costDisplay';
import { PatternDiagram } from './library/fortressPattern';
import { fetchServerAppVersion, getAppState } from './appState';
import { listPools } from './auth/pools';
import { LobbyHub } from './LobbyHub';
import { FortressMark } from './FortressLogo';
import { Rulebook } from './Rulebook';
import { SwitchToggle } from './SwitchToggle';
import {
  ClientMessage,
  ServerMessage,
  joinGameById,
  reconnectGame,
  watchState,
  type ChatMessageView,
  type CursorView,
  type FortressCardView,
  type HistoryEntryView,
  type LobbyInfo,
  type LobbyPlayer,
  type SpectatorView,
  type MarketPile,
  type MarketView,
  type ResourceCounts,
  type RestorePointView,
  type TutorialView,
} from './net/colyseus';
import {
  getActiveGameTokens,
  removeActiveGameToken,
  setActiveGameToken,
} from './net/activeGame';
import { serverNow } from './net/serverClock';
import { matchRoom } from './auth/lobbies';
import { completeTutorial } from './auth/api';
import { withAuthRetry } from './auth/session';
import { matchPath, parseMatchLocation } from './matchPath';
import { audio } from './audio/AudioManager';

audio.register('turn', '/audio/turn.wav');
audio.register('turn2', '/audio/turn-2.wav');
audio.register('chat', '/audio/chat.wav');

const PLAYER_COLORS = [
  0xff5d5d, // red
  0x5db9ff, // blue
  0x6dff8f, // green
  0xffd65d, // yellow
  0xb98bff, // purple
  0xff8c38, // orange
  0x4de3d6, // teal
  0xff7ad1, // pink
];
/** Spectators hold no seat, so their live cursor renders in a neutral grey. */
const SPECTATOR_CURSOR_COLOR = 0x9a948c;

// Topology-only board graph (no pixel layout) for build-range computation. It's
// deterministic from BOARD_RADIUS, so its node ids match the board the server
// and PixiGame build — see AGENTS.md.
const BUILD_GRAPH = buildBoard(BOARD_RADIUS);

// Vigor zone per node id, derived the same way the server stamps `nodeZone`
// (every slot's zone, defaulting forest). Used to price a build's Vigor penalty.
const NODE_ZONE: VigorZone[] = (() => {
  const zones = new Array<VigorZone>(BUILD_GRAPH.nodes.length).fill('forest');
  for (const slot of deriveSlots(BUILD_GRAPH)) {
    for (const id of slot.nodeIds) zones[id] = slot.zone;
  }
  return zones;
})();

const VIGOR_IDX = RESOURCES.indexOf('vigor');

/** Total cost to build `building` on a node — mirrors the server's `buildCost`
 * (config/buildCost.ts): base cost + the node's zone Vigor penalty (if the
 * building pays one) + a Mine's permanent-resource fee. Merges the player's
 * sparse overrides with the match's pinned config. */
function buildCostFor(
  config: GameConfig,
  player: Pick<LobbyPlayer, 'buildings' | 'vigorZones'>,
  building: ConstructableBuilding,
  zone: VigorZone,
  resource: string,
  permanence: string,
  skipVigorPenalty = false,
): number[] {
  const def = resolveBuilding(config, player, building);
  const cost = [...def.cost];
  if (cost.length !== 4) return cost; // wildcard cost — no surcharges
  if (def.vigorPenalty && !skipVigorPenalty) {
    const z = resolveVigorZone(config, player, vigorZoneLevel(zone));
    cost[VIGOR_IDX] = (cost[VIGOR_IDX] ?? 0) + z.vigorPenalty;
  }
  if (
    def.permanentResourceCost &&
    permanence === 'permanent' &&
    resource !== ''
  ) {
    const idx = (RESOURCES as readonly string[]).indexOf(resource);
    if (idx >= 0) cost[idx] = (cost[idx] ?? 0) + def.permanentResourceCost;
  }
  return cost;
}

/** Whether an opponent's building blocks build-through, using that owner's
 * sparse overrides when present on the roster. */
function blocksOpponentBuild(
  config: GameConfig,
  players: readonly LobbyPlayer[],
  ownerId: string,
  building: string,
): boolean {
  const owner = players.find((p) => p.id === ownerId);
  const def = owner
    ? resolveBuilding(config, owner, building as BuildingType)
    : config.buildings[building as BuildingType];
  return !!def?.blockOpponentBuild;
}

/** The construct-building flow: choosing a building, then placing it. `null` when
 * the flow is off. In `place`, `allowed` are the legal (green) targets and
 * `tooCostly` are reachable-but-unaffordable (grey) ones. */
type BuildFlow =
  | { stage: 'pick'; penaltyCardId?: string; freeBuildCardId?: string }
  | {
      stage: 'place';
      building: ConstructableBuilding;
      allowed: ReadonlySet<number>;
      tooCostly: ReadonlySet<number>;
      /** The node tentatively picked for the building, pending Confirm. A board
       * click only selects; the construct action is sent by `confirmBuild`. */
      selected?: number;
      /** node id → Vigor it would cost to build there (only nodes that pay one). */
      vigorPenalty: ReadonlyMap<number, number>;
      /** node id → the one-time tile resource a build there would collect,
       * zone bonus included (only nodes bearing one). */
      oneTimeGain: ReadonlyMap<number, { resource: string; amount: number }>;
      /** Card id of an armed Energized Bun-Bun (double this build's one-time
       * pickup), sent as the construct action's rider. */
      doubleOneTime?: string;
      penaltyCardId?: string;
      /** Card id of a Block Us Baby placing this build for free — no
       * resources, no Vigor penalty, no action point, and the Barracks defend
       * range in place of the building's own build range. */
      freeBuildCardId?: string;
      /** Barracks-only optional build-time aim sub-flow: pick a posture, tap a
       * highlighted target, then Confirm builds the Barracks already aimed —
       * one action instead of build + aim. `target` claims board clicks for
       * the aim; `done` rides the construct action as its `aim`. Reset when
       * the selected node changes. */
      aim?:
        | { stage: 'target'; mode: AimMode; allowed: ReadonlySet<number> }
        | { stage: 'done'; mode: AimMode; targetId: number };
    }
  | null;

/** The aim-barracks flow: tap one of your unaimed Barracks, pick a posture
 * (attack / defend), then tap a highlighted target node. `defense: true` is the
 * off-turn defense-window variant — posture locked to defend, targets restricted
 * to your own attacked buildings, and no action point spent. */
type AimFlow =
  | { stage: 'pickBarracks'; sources: ReadonlySet<number>; defense: boolean }
  | {
      stage: 'mode';
      barracksId: number;
      /** False when this Barracks is absorbed into a Fortress (defend-only). */
      attackable: boolean;
      /** The posture picker never appears for defense reactions (defend-locked). */
      defense: false;
    }
  | {
      stage: 'target';
      barracksId: number;
      mode: AimMode;
      defense: boolean;
      allowed: ReadonlySet<number>;
      /** Constructed Bridges this aim may target, as canonical edge keys —
       * bridges belong to no one, so anyone may attack or defend them (tapping
       * the highlighted span sends the aim with `targetEdge`). */
      allowedBridges: ReadonlySet<string>;
    }
  | null;

/** The Card-Effects flow: the tray of your implemented cards' usable effects,
 * then a per-kind sub-flow — an inline confirm, a board pick (protect a Scout /
 * ready Barracks), a token adjust list, or a market-card pick. */
type EffectsFlow =
  | { stage: 'list' }
  | {
      stage: 'confirm';
      cardId: string;
      kind:
        | 'double_banked_resources'
        | 'force_end_round'
        | 'bank_production_reset';
    }
  | { stage: 'pickScout'; cardId: string; allowed: ReadonlySet<number> }
  | { stage: 'replicate'; cardId: string; allowed: ReadonlySet<number> }
  | {
      stage: 'untap';
      cardId: string;
      allowed: ReadonlySet<number>;
      selected: ReadonlySet<number>;
      /** How many more Barracks this card may untap this round (cap − tokens). */
      limit: number;
    }
  | { stage: 'adjustToken'; cardId: string }
  | { stage: 'copyEffect'; cardId: string }
  | {
      stage: 'markFortresses';
      cardId: string;
      markWin?: string;
      /** Set once both rows are picked and the printing lets the marker
       * choose whether to reshuffle — the flow then waits on that choice. */
      markRegular?: string;
      /** The pending per-mark reshuffle answers on the "28"+ printing, each
       * starting at the printed "shuffle back" until toggled off. */
      shuffleWin?: boolean;
      shuffleRegular?: boolean;
    }
  | {
      stage: 'market';
      cardId: string;
      kind: 'set_fortress_lock' | 'free_cycle_market' | 'market_reaction';
    }
  | {
      /** Big Boom: new token counts for the holder's fortresses, one entry per
       * held instance in `fortressCards` order. Two printings share the
       * stepper: the "50"-"52" redistribution keeps the total fixed; the
       * "53"+ adjustment (`budget`) allows up to that many unit changes from
       * each entry's `start`, minted or removed, and sends them as
       * `adjust_fortress_tokens`. */
      stage: 'redistributeTokens';
      cardId: string;
      entries: readonly {
        cardId: string;
        name: string;
        tokens: number;
        /** The count when the prompt opened — what a budgeted change is
         * measured from. */
        start: number;
        cap: number;
      }[];
      total: number;
      budget?: number;
    }
  | { stage: 'reorderMarket'; cardId: string }
  | {
      stage: 'reorderDeck';
      cardId: string;
      market: FortressKind;
      order: readonly string[];
      /** Peek printing (Curious Climb, catalogue "14"+): glanced cards routed
       * to the deck bottom or the discard pile; absent from the map = stays
       * on top in `order` order. */
      route: Readonly<Record<string, 'bottom' | 'discard'>>;
      /** Curious Climb, catalogue "23"+: face-up indices of the glanced row
       * to cycle out for free once the arrangement commits (at most the
       * card's `reorderCycleCount`). */
      cycle: readonly number[];
      submitted?: boolean;
    }
  | {
      stage: 'moveFortress';
      cardId: string;
      allowed: ReadonlySet<number>;
      selected: ReadonlySet<number>;
      /** How many destination nodes the pattern needs (Booba: 2). */
      limit: number;
      doubleOneTime?: string;
    }
  | {
      stage: 'claim';
      cardId: string;
      picked: readonly string[];
      remaining: number;
      submitted?: boolean;
    }
  | {
      stage: 'sellBuilding';
      cardId: string;
      /** Your own non-fortress buildings. */
      allowed: ReadonlySet<number>;
    }
  | {
      stage: 'designateTarget';
      cardId: string;
      /** Your own non-fortress, non-indestructible buildings at least one
       * opponent Barracks can reach with an attack aim (Stab Pole Hole). */
      allowed: ReadonlySet<number>;
    }
  | {
      stage: 'moveBuildingFrom';
      cardId: string;
      /** Your own non-fortress buildings with at least one legal destination. */
      allowed: ReadonlySet<number>;
      /** Per-source legal destinations: empty nodes within your Mine's build
       * range of that building (computed up front — the range is traced from
       * the moving building, not your network). */
      targets: ReadonlyMap<number, ReadonlySet<number>>;
    }
  | {
      stage: 'moveBuildingTo';
      cardId: string;
      fromNodeId: number;
      allowed: ReadonlySet<number>;
      /** What each destination would pay out on arrival — a move collects the
       * node's one-time pickup exactly as a build there would. */
      oneTimeGain: ReadonlyMap<number, { resource: string; amount: number }>;
      /** Card id of an armed Energized Bun-Bun doubling that pickup. */
      doubleOneTime?: string;
    }
  | { stage: 'bankTwo'; cardId: string }
  | {
      stage: 'redistribute';
      cardId: string;
      /** The split being composed, in `[minerals, food, vigor, obsidian]`
       * order. Seeded from what the player holds, so the prompt opens on a
       * no-op the player edits from. */
      amounts: readonly number[];
      /** Units held when the prompt opened — what the split must add back up
       * to (the effect moves resources between kinds, it never mints any). */
      total: number;
    }
  | {
      stage: 'pickIncome';
      cardId: string;
      remaining: number;
    }
  | {
      stage: 'dropResources';
      cardId: string;
      /** Unoccupied nodes in the eligible (highest) energy zones. */
      allowed: ReadonlySet<number>;
      /** Resource kind the next tap places. */
      resource: string;
      remaining: number;
    }
  | {
      stage: 'tutor';
      cardId: string;
      market: FortressKind;
      /** Card picked from the row's deck ('' = none yet). */
      foundCardId: string;
    }
  | {
      /** Dungeon Dice Martyr ("54"+): the discard to shuffle back into its
       * deck, then up to the card's `shuffleDiscardCycleCount` face-up
       * indices of THAT row to cycle out for free. */
      stage: 'shuffleDiscard';
      cardId: string;
      pick: { market: FortressKind; cardId: string } | null;
      cycle: readonly number[];
    }
  | null;

/** Button label per granted effect kind, shown in the effects tray. */
const EFFECT_LABELS: Partial<Record<SpecialActionKind, string>> = {
  pay_penalty_with_token: 'Pay Penalty with Token',
  make_scout_indestructible: 'Protect a Building',
  replicate_fortress: 'Replicate',
  double_banked_resources: 'Double Banked Resources',
  untap_barracks: 'Ready Barracks',
  set_fortress_lock: 'Lock / Unlock a Card',
  free_cycle_market: 'Free Cycle',
  collect_card_energy: 'Collect Energy ×2',
  reorder_fortress_deck: 'Reorder Deck',
  buy_extra_action: 'Buy Extra Action',
  force_end_round: 'End the Round',
  adjust_fortress_token: 'Adjust a Token',
  move_fortress: 'Move Fortress',
  claim_resources: 'Claim Resources',
  free_build: 'Place a Free Building',
  free_bridge: 'Ranged Bridge Build',
  adjust_fortress_tokens: 'Adjust Tokens',
  implement_from_discard: 'Implement from Discard',
  shuffle_discard_into_deck: 'Shuffle Back a Discard',
  sell_building: 'Sell a Building',
  tutor_market_card: 'Search the Deck',
  copy_fortress_effect: 'Copy an Effect',
  mark_fortresses: 'Mark Fortresses',
  move_building: 'Move a Building',
  bank_two_resources: 'Gain 2 of a Resource',
  pick_bonus_resources: 'Collect Income',
  drop_one_time_resources: 'Distribute Resources',
  designate_attack_target: 'Designate Attack Target',
  bank_production_reset: 'Bank Production ×2',
  spend_token_for_action: 'Spend a Token for an Action',
  redistribute_resources: 'Redistribute Resources',
  gain_free_action: 'Free Action',
  mineral_windfall: 'Mineral Windfall',
  market_reaction: 'Cycle / Lock a Card',
  redistribute_tokens: 'Redistribute Tokens',
};

/** Button label for a granted effect, read off the card's effect config where
 * the printing retunes it (a fixed `bankGrant` payout; a production payout
 * that no longer resets the track). */
function effectLabel(kind: SpecialActionKind, fx: CardEffect | undefined): string {
  if (kind === 'bank_two_resources' && fx?.bankGrant) {
    const { amount, resource } = fx.bankGrant;
    const kindName =
      resource !== undefined ? RESOURCE_LABELS[RESOURCE_KINDS[resource]!] : 'a Resource';
    return `Gain ${amount} ${kindName}`;
  }
  if (kind === 'free_build' && fx?.freeBuild) {
    const { building, payCost } = fx.freeBuild;
    const what = building ? BUILDING_LABELS[building] : 'Building';
    return payCost ? `Ranged ${what} Build` : `Place a Free ${what}`;
  }
  if (kind === 'adjust_fortress_tokens' && fx?.tokenAdjustments) {
    return `Add/Remove up to ${fx.tokenAdjustments} Tokens`;
  }
  if (kind === 'bank_production_reset' && fx?.bankProductionReset) {
    const { multiplier, reset } = fx.bankProductionReset;
    const m = multiplier ?? 2;
    return reset === false
      ? `Bank Production${m === 1 ? '' : ` ×${m}`}`
      : `Bank Production ×${m}`;
  }
  return EFFECT_LABELS[kind] ?? kind;
}

/** Suffix for a queued build carrying a build-time aim. */
function buildAimLabel(aim?: BuildAim): string {
  return aim
    ? ` · ${aim.mode === 'attack' ? 'attack' : 'defend'} node ${aim.targetId}`
    : '';
}

/** Short human-readable summary of an action being queued, shown in the queue
 * panel (and echoed back by the server if the move fails at auto-play). */
function queueMoveLabel(
  action: GameAction,
  cardsById: ReadonlyMap<string, FortressCard>,
): string {
  const cardName = (id: string) => cardsById.get(id)?.name ?? id;
  switch (action.type) {
    case 'construct_building':
      return `Build ${BUILDING_LABELS[action.building]} · node ${action.nodeId}${buildAimLabel(action.aim)}`;
    case 'pay_penalty_with_token':
      return `Build ${BUILDING_LABELS[action.building]} · node ${action.nodeId}${buildAimLabel(action.aim)} (token)`;
    case 'construct_bridge':
      return `Bridge · ${action.from}–${action.to}${
        action.payWith ? ` (pay ${action.payWith})` : ''
      }`;
    case 'free_bridge':
      return `Bridge · ${action.from}–${action.to}${
        action.payWith ? ` (pay ${action.payWith})` : ''
      } · ${cardName(action.cardId)}`;
    case 'implement_fortress':
      return `Implement ${cardName(action.cardId)}${
        action.fromDiscard ? ' (from discard)' : ''
      }`;
    case 'aim_barracks':
      return `${action.mode === 'attack' ? 'Attack' : 'Defend'} · ${
        action.targetEdge !== undefined
          ? `bridge ${action.targetEdge.replace('-', '–')}`
          : `node ${action.targetId}`
      }`;
    case 'cycle_market': {
      // Count across both rows (also decodes the legacy single-row shape).
      const rows = cycleMarketRows(action);
      const n = rows.regular.length + rows.win.length;
      return `Cycle ${n} card${n === 1 ? '' : 's'}`;
    }
    default:
      return EFFECT_LABELS[action.type] ?? action.type;
  }
}

/** Summary of the queued round-start cycle decision (a JSON
 * `RoundStartCycleChoice` on `Player.queuedRoundStartCycle`) for the queue
 * panel. */
function roundStartCycleLabel(
  payload: string,
  cardsById: ReadonlyMap<string, FortressCard>,
): string {
  try {
    const parsed = JSON.parse(payload) as { regular?: unknown; win?: unknown };
    // Per-row picks are id arrays; a queued choice persisted before rows
    // allowed multiple picks holds a single id (or null) instead.
    const rowIds = (v: unknown): string[] =>
      typeof v === 'string'
        ? [v]
        : Array.isArray(v)
          ? v.filter((id): id is string => typeof id === 'string')
          : [];
    const names = [...rowIds(parsed.regular), ...rowIds(parsed.win)].map(
      (id) => cardsById.get(id)?.name ?? id,
    );
    return names.length > 0 ? `Cycle out ${names.join(' + ')}` : 'Cycle nothing';
  } catch {
    return 'Round-start cycle';
  }
}

/** Effect sub-flows that legitimately outlive the player's turn: they cash (or
 * place) something a card banked, cost no action point, and the server accepts
 * them from any player at any time. Everything else is an on-turn move and
 * closes when the turn moves on.
 *
 * `dropResources` is here because the implement that banks the units can be
 * the turn's last action (or a queued one that auto-ends the turn) — without
 * an off-turn surface the distribution would be unreachable and the units
 * stranded on the player forever. */
const OFF_TURN_EFFECT_STAGES: ReadonlySet<NonNullable<EffectsFlow>['stage']> =
  new Set(['claim', 'pickIncome', 'dropResources']);

/** Effect kinds whose server handler requires an available action point
 * (`isActor`) — mirrored so the tray disables them at 0 actions left. The rest
 * only need it to be your turn (`isCurrentPlayer`). */
const EFFECT_NEEDS_ACTION_POINT: ReadonlySet<SpecialActionKind> = new Set([
  'pay_penalty_with_token',
  'double_banked_resources',
  'make_scout_indestructible',
  'collect_card_energy',
  'set_fortress_lock',
  'reorder_fortress_deck',
  'force_end_round',
  'adjust_fortress_token',
  'move_fortress',
  'sell_building',
  'tutor_market_card',
  'copy_fortress_effect',
  'mark_fortresses',
  'move_building',
  'bank_two_resources',
  'designate_attack_target',
  'bank_production_reset',
  'redistribute_resources',
  'mineral_windfall',
  'redistribute_tokens',
  'adjust_fortress_tokens',
  'implement_from_discard',
  'shuffle_discard_into_deck',
]);

/** Board/turn context the card-effect availability checks read. */
interface EffectContext {
  actionsRemaining: number;
  /** Your buildings not already protected (candidates for Godspeed) — Scouts
   * only on catalogues before "10", plus your X markers on "39"+. */
  protectableScouts: number;
  /** Your aimed Barracks (candidates for an untap effect). */
  aimedBarracks: number;
  /** Your buildings at least one opponent Barracks can reach with an attack
   * aim (Stab Pole Hole's designation targets). */
  designateTargets: number;
  /** Banked iCarlie claims awaiting cashing. */
  pendingClaims: number;
  /** Banked single-resource picks awaiting cashing (Resource Sequencer,
   * Frienemy on pinned games). */
  pendingPicks: number;
  /** Banked Pee-Pee Pinata one-time units awaiting placement. */
  pendingDrops: number;
  /** Banked Miss Mineralverse Maggot market reactions. */
  pendingMarketActions: number;
  /** Cards across both rows' discard piles (Dungeon Dice Martyr's targets). */
  discardedCards: number;
  /** True while queue-planning: resource gates don't apply (a queued yield
   * may fund the effect by the time it auto-plays). */
  planning?: boolean;
}

/** Can `kind` be used via `cardId` right now? The shared registry gate
 * (`canUseCardAction`) plus the turn/board conditions the server handlers
 * enforce, so an enabled tray button always passes server validation. */
function effectAvailability(
  player: Pick<LobbyPlayer, 'fortressCards' | 'resources'>,
  cardId: string,
  kind: SpecialActionKind,
  ctx: EffectContext,
  effectOf: CardEffectLookup,
): { allowed: boolean; reason?: string } {
  const gate = canUseCardAction(player.fortressCards, cardId, kind, effectOf);
  if (!gate.allowed) return gate;
  // Action-point cost: the action kind's default, overridable by the card's
  // effect config ("takes up an action" / "does not take up any actions").
  // Tokenizer 9000 ("49"+): token actions spend no action point for its
  // holder, whichever card grants the adjust.
  const tokenActionsFree =
    (kind === 'adjust_fortress_token' || kind === 'adjust_fortress_tokens') &&
    anyCardHasPassive(player.fortressCards, 'token_actions_free', effectOf);
  const costsAction =
    !tokenActionsFree &&
    (effectOf(cardId)?.grantCostsAction ?? EFFECT_NEEDS_ACTION_POINT.has(kind));
  if (costsAction && ctx.actionsRemaining <= 0) {
    return { allowed: false, reason: 'No actions left this turn.' };
  }
  switch (kind) {
    case 'make_scout_indestructible':
      // Planning bypasses the board gate — the board may change before the
      // queued move plays.
      return ctx.planning || ctx.protectableScouts > 0
        ? { allowed: true }
        : { allowed: false, reason: 'You have no unprotected buildings.' };
    case 'untap_barracks':
      return ctx.aimedBarracks > 0
        ? { allowed: true }
        : { allowed: false, reason: 'None of your Barracks are aimed.' };
    case 'designate_attack_target':
      // Never printed on the card, but the action only exists while an
      // opponent Barracks can actually reach one of your buildings. Planning
      // bypasses the board gate — the board may change before the move plays.
      return ctx.planning || ctx.designateTargets > 0
        ? { allowed: true }
        : {
            allowed: false,
            reason:
              'No opponent Barracks has any of your buildings in attack range.',
          };
    case 'buy_extra_action':
      // 1 Vigor, payable through substitutions (Bench: food; Treadmill:
      // obsidian). Never gates while planning — assume infinite resources.
      return ctx.planning ||
        canAffordWith(
          toAmount(player.resources),
          [0, 0, 1, 0],
          substitutionsFor(player.fortressCards, effectOf),
        )
        ? { allowed: true }
        : { allowed: false, reason: 'Need 1 Energy.' };
    case 'claim_resources':
      return ctx.pendingClaims > 0
        ? { allowed: true }
        : { allowed: false, reason: 'Nothing banked to claim.' };
    case 'pick_bonus_resources':
      return ctx.pendingPicks > 0
        ? { allowed: true }
        : { allowed: false, reason: 'No income banked.' };
    case 'drop_one_time_resources':
      return ctx.pendingDrops > 0
        ? { allowed: true }
        : { allowed: false, reason: 'Nothing left to distribute.' };
    case 'market_reaction':
      return ctx.pendingMarketActions > 0
        ? { allowed: true }
        : {
            allowed: false,
            reason: 'No reactions banked — build on a one-time resource first.',
          };
    case 'adjust_fortress_token': {
      // Supreme Bondage "54"+ spends token changes banked on the card.
      if (!effectOf(cardId)?.tokenAdjustFromStored) return { allowed: true };
      const holder = player.fortressCards.find((fc) => fc.cardId === cardId);
      return (holder?.stored ?? 0) > 0
        ? { allowed: true }
        : {
            allowed: false,
            reason: 'Nothing banked — a locked fortress being implemented banks one.',
          };
    }
    case 'shuffle_discard_into_deck':
      // A cycle may still fill a discard pile before a queued move plays.
      return ctx.planning || ctx.discardedCards > 0
        ? { allowed: true }
        : { allowed: false, reason: 'Both discard piles are empty.' };
    case 'collect_card_energy': {
      // Energy may still accrue at round end before a queued move plays. The
      // pile is `stored` on the "49"+ printing, the token count before.
      const holder = player.fortressCards.find((fc) => fc.cardId === cardId);
      const energy = holder
        ? depositsToStored(effectOf(cardId))
          ? holder.stored
          : holder.tokens
        : 0;
      return ctx.planning || energy > 0
        ? { allowed: true }
        : { allowed: false, reason: 'No energy stored on this card.' };
    }
    case 'redistribute_resources': {
      // Nothing to rearrange with an empty bank. Planning bypasses it — a
      // queued yield may have filled the bank by the time the move plays.
      const total =
        player.resources.minerals +
        player.resources.food +
        player.resources.vigor +
        player.resources.obsidian;
      return ctx.planning || total > 0
        ? { allowed: true }
        : { allowed: false, reason: 'You hold no resources to redistribute.' };
    }
    case 'spend_token_for_action': {
      // One token buys one action. Tokens can still land before a queued move
      // plays (the repeat that pays for it may be earlier in the same turn).
      const holder = player.fortressCards.find((fc) => fc.cardId === cardId);
      return ctx.planning || (holder ? holder.tokens : 0) > 0
        ? { allowed: true }
        : { allowed: false, reason: 'No tokens on this card to spend.' };
    }
    case 'rejoin_round':
      // Only a PASSED player may re-enter, and the tray renders on your turn
      // — the standalone banner above the board offers it after you pass.
      return {
        allowed: false,
        reason: 'Usable after you pass — the Re-enter banner offers it then.',
      };
    default:
      return { allowed: true };
  }
}

/** A revealed Fortress card the player could implement: its pattern is realised
 * by their free buildings somewhere on the board. `nodeIds` are the matched
 * buildings + markers (to highlight); `affordable` reflects the Obsidian cost. */
interface ImplementCandidate {
  cardId: string;
  kind: FortressKind;
  name: string;
  cost: number[];
  affordable: boolean;
  nodeIds: number[];
  /** From your face-down hand (Dungeon Dice Martyr), not the market row. */
  fromHand?: boolean;
  /** From the row's discard pile, under this held card's
   * `implement_from_discard` grant (Dungeon Dice Martyr "53"+) — the card
   * named pays a token for the use. */
  fromDiscard?: string;
  /** The card's effects declare an implement-time pick (a resource, or one
   * of `pieceOptions`). */
  needsChoice?: boolean;
  /** The pieces a "piece" pick offers (Shifty Engineer: scout / bridge).
   * Absent on a resource pick. */
  pieceOptions?: readonly ImplementPiece[];
  /** A "bank N or +1 production" pick (catalogue "49"+ passives): the banked
   * amount and, when fixed, the resource (index into RESOURCE_KINDS), or the
   * kinds the pick is limited to. */
  bankOrProduction?: { bank: number; resource?: number; resources?: readonly number[] };
  /** Big Boom's "double or halve the token capacity of another fortress you
   * own": the held fortresses the pick may name (one per card id). */
  capTargets?: readonly { cardId: string; name: string }[];
}

/** Big Boom's cap-rescale targets: the player's held fortresses, one entry
 * per card id. With nothing held the pick is moot, so the candidate needs no
 * choice at all. Spread AFTER `implementChoiceFields` so it can clear
 * `needsChoice`. */
function capTargetsFor(
  fx: CardEffect | undefined,
  held: readonly { cardId: string }[],
  cardsById: ReadonlyMap<string, FortressCard>,
): Pick<ImplementCandidate, 'needsChoice' | 'capTargets'> {
  if (!implementScalesCap(fx)) return {};
  const seen = new Set<string>();
  const capTargets: { cardId: string; name: string }[] = [];
  for (const fc of held) {
    if (seen.has(fc.cardId)) continue;
    // Big Boom reaches REGULAR-deck fortresses only.
    if (fx?.adjustRegularOnly && cardsById.get(fc.cardId)?.kind !== 'REGULAR') continue;
    seen.add(fc.cardId);
    capTargets.push({
      cardId: fc.cardId,
      name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
    });
  }
  return { capTargets, needsChoice: capTargets.length > 0 };
}

/** The buttons a bank-or-production implement pick offers: bank the printed
 * amount (as that many any-resource picks when no kind is fixed), or +1
 * production of the fixed kind / of each kind when none is fixed. A card
 * limited to some kinds offers the bank and the production of each. */
function bankOrProductionOptions(cfg: {
  bank: number;
  resource?: number;
  resources?: readonly number[];
}): { key: string; label: string; icon?: ResourceKind; choice: ImplementChoice }[] {
  if (cfg.resource === undefined && cfg.resources) {
    const kinds = cfg.resources.flatMap((i) => RESOURCE_KINDS[i] ?? []);
    return [
      ...kinds.map((rk) => ({
        key: `bank:${rk}`,
        label: `Gain ${cfg.bank} ${RESOURCE_LABELS[rk]}`,
        icon: rk,
        choice: { mode: 'bank', resource: rk },
      })),
      ...kinds.map((rk) => ({
        key: `production:${rk}`,
        label: `+1 ${RESOURCE_LABELS[rk]} production`,
        icon: rk,
        choice: { mode: 'production', resource: rk },
      })),
    ];
  }
  const fixed = cfg.resource !== undefined ? RESOURCE_KINDS[cfg.resource] : undefined;
  const bank = {
    key: 'bank',
    label: fixed
      ? `Gain ${cfg.bank} ${RESOURCE_LABELS[fixed]}`
      : `Gain ${cfg.bank} of any resources`,
    ...(fixed ? { icon: fixed } : {}),
    choice: { mode: 'bank' },
  };
  if (fixed) {
    return [
      bank,
      {
        key: 'production',
        label: `+1 ${RESOURCE_LABELS[fixed]} production`,
        icon: fixed,
        choice: { mode: 'production' },
      },
    ];
  }
  return [
    bank,
    ...RESOURCE_KINDS.map((rk) => ({
      key: `production:${rk}`,
      label: `+1 ${RESOURCE_LABELS[rk]} production`,
      icon: rk,
      choice: { mode: 'production', resource: rk },
    })),
  ];
}

/** The player's implement-time pick, as the action carries it: a resource
 * (an artifact's grant) or a piece (a choice card's denoted option). */
type ImplementChoice = {
  resource?: string;
  piece?: string;
  mode?: string;
  targetCardId?: string;
};

/** The candidate fields describing a card's implement-time pick, if any. */
function implementChoiceFields(
  fx: CardEffect | undefined,
): Pick<ImplementCandidate, 'needsChoice' | 'pieceOptions' | 'bankOrProduction'> {
  if (fx?.implementChoice === undefined) return {};
  const pieceOptions = implementPieceOptions(fx);
  if (pieceOptions) return { needsChoice: true, pieceOptions };
  const bankOrProduction = implementBankOrProductionOf(fx);
  if (bankOrProduction) return { needsChoice: true, bankOrProduction };
  return { needsChoice: true };
}

/** Display label for a recorded implement pick (`FortressCardView.choice`);
 * an unknown value (a future piece kind) shows as-is. */
function pieceLabel(choice: string): string {
  return (PIECE_LABELS as Record<string, string>)[choice] ?? choice;
}

/** All board nodes a fortress match occupies (buildings + markers + bridge
 * endpoints), deduped — what gets highlighted / hit-tested for it. */
function nodesOfMatch(m: FortressMatch): number[] {
  return [
    ...new Set([
      ...m.buildingNodeIds,
      ...m.markerNodeIds,
      ...m.bridgeNodeIdPairs.flat(),
    ]),
  ];
}

/** One "skip this piece" choice while implementing with Obsidian Ovary
 * Overload in play (null = keep the full pattern). */
type OmitPick = { building?: number; bridge?: number } | null;

/** The pattern with one building or bridge omitted. */
function omitPatternPiece(
  pattern: FortressCard['pattern'],
  omit: NonNullable<OmitPick>,
): FortressCard['pattern'] {
  return omit.building !== undefined
    ? {
        ...pattern,
        buildings: pattern.buildings.filter((_, i) => i !== omit.building),
      }
    : {
        ...pattern,
        bridges: pattern.bridges.filter((_, i) => i !== omit.bridge),
      };
}

/** Every legal one-piece omission of `pattern` (never reducing it to markers
 * alone), the full pattern first. */
function omitChoicesFor(pattern: FortressCard['pattern']): OmitPick[] {
  const out: OmitPick[] = [null];
  if (pattern.buildings.length + pattern.bridges.length <= 1) return out;
  pattern.buildings.forEach((_, i) => out.push({ building: i }));
  pattern.bridges.forEach((_, i) => out.push({ bridge: i }));
  return out;
}

/** Button label for the current omit choice ("Full pattern" / "Skip the △"). */
function omitPickLabel(
  pattern: FortressCard['pattern'],
  omit: OmitPick,
): string {
  if (!omit) return 'Full pattern';
  if (omit.building !== undefined) {
    const shape = pattern.buildings[omit.building]?.shape;
    const glyph =
      shape === 'scout'
        ? 'circle'
        : shape === 'barracks'
          ? 'triangle'
          : shape === 'mine'
            ? 'square'
            : 'star';
    return `Skip a ${glyph}${
      pattern.buildings.length > 1 ? ` (${(omit.building ?? 0) + 1})` : ''
    }`;
  }
  return `Skip a bridge${
    pattern.bridges.length > 1 ? ` (${(omit.bridge ?? 0) + 1})` : ''
  }`;
}

/** The Implement-Fortress flow.
 * `pick`: the market sidebar is the card picker — it opens with the candidate
 * tiles clickable (hovering one previews its first match).
 * `place`: a card is chosen — the sidebar closes and every node of every
 * possible placement pulses;
 * tapping one selects a concrete placement there (`selected.options` are the
 * indices into `matches` covering that node; `pos` is the one shown, cycled by
 * Rotate), and Confirm implements it. Queued planning skips `place` (the board
 * may change before the move plays) and sends straight from the picker. */
type ImplementFlow =
  | { stage: 'pick'; cards: ImplementCandidate[] }
  | {
      stage: 'place';
      /** The picker's card list, restored on Back/Esc. */
      cards: ImplementCandidate[];
      card: ImplementCandidate;
      /** Every distinct placement of the card's pattern, from the shared matcher. */
      matches: FortressMatch[];
      /** Union of all matched nodes — the pulsing "possible locations". */
      candidates: ReadonlySet<number>;
      selected: { nodeId: number; options: number[]; pos: number } | null;
      /** One-piece omissions on offer (Obsidian Ovary Overload; `[null]` when
       * the player can't omit) and which one `matches` was computed against. */
      omitChoices: OmitPick[];
      omitIndex: number;
    };

/** Stand-in bank while planning queued moves: affordability never gates a
 * queued action (a prior queued move may fund it), so pickers that disable
 * unaffordable options get this instead of the live resources. */
const PLANNING_BANK: ResourceCounts = {
  minerals: 999,
  food: 999,
  vigor: 999,
  obsidian: 999,
};

/** How long an action-notice popup stays before auto-dismissing (ms). */
const NOTICE_TTL = 5000;

/** How often the client re-reads `/app-state` to compare the server's
 * `appVersion` against its own baked-in `APP_VERSION` (a deploy detector —
 * boot also checks immediately). */
const APP_VERSION_POLL_MS = 5 * 60 * 1000;

/** How long the top-screen "It's Your Turn" toast stays up (ms). */
const TURN_TOAST_TTL = 4000;

/** How long the queued-move-halted toast stays up (ms) — longer than the turn
 * toast, since it carries the reason the auto-play stopped. */
const QUEUE_HALT_TTL = 8000;

/** How long the "match restored" toast stays up (ms) — the longest of the
 * three: the board changed under everyone at once, and the toast is the only
 * thing that says why. */
const RESTORED_TTL = 10000;

/** The browser tab's default title, captured before we ever mutate it (matches
 * index.html's <title>). Restored whenever it's not the player's turn. */
const DEFAULT_TITLE = document.title;

/** An active action notification: the server `ActionNotice` plus a monotonic
 * `key` so React remounts (and re-animates) it on each new action. */
type ActiveNotice = ActionNotice & { key: number };

/** Seat → CSS colour, wrapping the palette like the board renderer does. */
function seatColor(seat: number): string {
  const c = PLAYER_COLORS[seat % PLAYER_COLORS.length] ?? 0xffffff;
  return `#${c.toString(16).padStart(6, '0')}`;
}

/** A player's roster icon: their initial on a seat-coloured hex token (the
 * logo's hex). Reused by the expanded roster row and the collapsed rail;
 * `current` adds the gold active-turn ring (used on the rail, where there's no
 * row to highlight). `connected` drives the presence dot on the bottom-right
 * corner — filled green while their client is connected, an empty outlined
 * ring (and a dimmed token) after they drop. `size` (px) is the token width,
 * applied inline so one component serves both placements. */
function PlayerIcon({
  name,
  seat,
  connected,
  size = 20,
  current = false,
}: {
  name: string;
  seat: number;
  connected: boolean;
  size?: number;
  current?: boolean;
}) {
  return (
    <span
      className={`player-icon${current ? ' is-current' : ''}${connected ? '' : ' is-away'}`}
      style={{
        width: size,
        height: Math.round(size * 1.1),
        fontSize: Math.round(size * 0.42),
      }}
      aria-hidden
    >
      <span className="player-icon-hex" style={{ background: seatColor(seat) }}>
        {(name.trim()[0] ?? '?').toUpperCase()}
      </span>
      <span className={`presence-dot${connected ? ' is-online' : ''}`} />
    </span>
  );
}

/** One classified pointer sample — the wire shape of every presence send
 * (cursor stream, touch tap, emoji reaction). Board samples carry board-plane
 * coords in x/y; panel samples carry panel-normalised x/y plus the hovered
 * element key and the position within it (see `CursorState`). */
interface CursorSample {
  area: string;
  x: number;
  y: number;
  target: string;
  tx: number;
  ty: number;
}
const HIDDEN_CURSOR: CursorSample = {
  area: '',
  x: 0,
  y: 0,
  target: '',
  tx: 0.5,
  ty: 0.5,
};

/** Clamp a wire value to 0..1, centre when absent/bogus (tx/ty fields). */
function unitOrCenter(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.5;
}

// HONK_EMOJI (📯, the "your turn!" nudge) is shared with the server, which
// also push-notifies the honked player's devices — see shared/utils/emoji.ts.

/** Face in Clouds — dropped on a player's fortress (their roster row in the
 * left sidebar, or their home base on the board) it fogs THAT player's
 * screen: mist creeps in around the edges for `FOG_TTL`, fading in fast and
 * clearing slowly (see the CursorEmoji handler + `.fog-overlay`). */
const FOG_EMOJI = '😶‍🌫️';
const FOG_TTL = 2000;

/** Shield — dropped anywhere on the board it arms the SENDER's emoji shield
 * for `SHIELD_TTL`: while it runs, every targeted reaction effect aimed at
 * them (the horn's turn sound, the Face-in-Clouds fog) is ignored on their
 * client. Targeting is receiver-side (see the honk check), so suppressing
 * locally is full immunity — those effects only ever manifest on the
 * target's own screen. */
const SHIELD_EMOJI = '🛡️';
const SHIELD_TTL = 30_000;

// sameEmoji (VS16-insensitive equality) comes from @fortress/shared.

/** Instant-open palette: shown while the full Unicode catalogue chunk loads
 * (a handful of ms from cache) so the picker is never empty. */
const EMOJI_SET = [
  '😀', '😂', '😅', '😍', '🤔', '😮', '😭', '😱',
  '🤯', '😡', '🥳', '😴', '👍', '👎', '👏', '🙏',
  '💪', '🤝', '😶‍🌫️', '🫡', '❤️', '🔥', '💯', '✨',
  '🎉', '💀', '⚔️', '🛡️', '🏰', '💎', '⛏️', '🌉',
];

/** The full emoji catalogue, grouped Unicode-order (Smileys & Emotion →
 * Flags), lazy-loaded from `unicode-emoji-json` on first picker open and
 * cached for the session. Emojis newer than 15.1 are dropped: current
 * runtimes' `RGI_Emoji` tables (the server's validator) don't know them yet,
 * and most devices would draw tofu anyway. */
interface EmojiGroup {
  name: string;
  emojis: { emoji: string; name: string }[];
}
let emojiCataloguePromise: Promise<EmojiGroup[]> | null = null;
function loadEmojiCatalogue(): Promise<EmojiGroup[]> {
  emojiCataloguePromise ??= import('unicode-emoji-json/data-by-group.json')
    .then((mod) =>
      mod.default.map((group) => ({
        name: group.name,
        emojis: group.emojis
          .filter((e) => parseFloat(e.emoji_version) <= 15.1)
          .map((e) => ({ emoji: e.emoji, name: e.name })),
      })),
    )
    .catch((err) => {
      emojiCataloguePromise = null; // allow a retry on the next open
      throw err;
    });
  return emojiCataloguePromise;
}
/** Persisted across games deliberately (localStorage, not room state). */
const RECENT_EMOJI_KEY = 'fortress.recentEmojis';
const RECENT_EMOJI_MAX = 8;

function loadRecentEmojis(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_EMOJI_KEY) ?? '[]');
    return Array.isArray(raw)
      ? raw.filter((e): e is string => typeof e === 'string').slice(0, RECENT_EMOJI_MAX)
      : [];
  } catch {
    return [];
  }
}

function pushRecentEmoji(emoji: string): void {
  const next = [
    emoji,
    ...loadRecentEmojis().filter((e) => e !== emoji),
  ].slice(0, RECENT_EMOJI_MAX);
  try {
    localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify(next));
  } catch {
    /* storage full/blocked — recents just don't persist */
  }
}

/** Eye / eye-off glyph for the watchlist UI (Unicode has no eye-off emoji). */
function EyeGlyph({ off = false, size = 15 }: { off?: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="2.6" />
      {off && <line x1="4" y1="20" x2="20" y2="4" />}
    </svg>
  );
}

/** Line icons for the in-game chrome (24px grid, stroked in currentColor). */
const CHROME_GLYPHS = {
  'chevron-left': 'M15 6l-6 6 6 6',
  'chevron-right': 'M9 6l6 6-6 6',
  'chevron-up': 'M6 15l6-6 6 6',
  'chevron-down': 'M6 9l6 6 6-6',
  chat: 'M4 5h16v11H9l-5 4z',
  home: 'M3 11.5 12 4l9 7.5M5.5 10v10h13V10',
  clock: 'M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 7.5V12l3 2',
  players:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20c.6-3.6 3.2-5.5 6.5-5.5s5.9 1.9 6.5 5.5M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.8c2 .7 3.2 2.4 3.5 5.2',
  construct_building: 'M4 20V10l8-6 8 6v10M9 20v-5h6v5',
  construct_bridge: 'M3 16h18M6 16v-4M18 16v-4M3 12c4-5 14-5 18 0',
  implement_fortress:
    'M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9zM12 8.6l1.1 2.3 2.5.4-1.8 1.7.4 2.5-2.2-1.2-2.2 1.2.4-2.5-1.8-1.7 2.5-.4z',
  aim_barracks:
    'M12 5.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4',
  cycle_market:
    'M20 8a8.5 8.5 0 0 0-15-1.5M4 16a8.5 8.5 0 0 0 15 1.5M20 3.5V8h-4.5M4 20.5V16h4.5',
  card_effects: 'M13 2.5 5 13.5h6l-1 8 8-11h-6z',
  flag: 'M5 21V4M5 4h12l-2.5 4L17 12H5',
  undo: 'M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  send: 'M4 12 20 4l-5 16-3.5-6.5zM11.5 13.5 20 4',
} as const;

function ChromeGlyph({
  name,
  size = 20,
  strokeWidth = 2,
}: {
  name: keyof typeof CHROME_GLYPHS;
  size?: number;
  strokeWidth?: number;
}) {
  return (
    <svg
      className="chrome-glyph"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={CHROME_GLYPHS[name]} />
    </svg>
  );
}

/** The emoji reaction picker, opened at the right-click / long-press point.
 * Offers the complete Unicode emoji set (lazy-loaded, grouped by category,
 * name-searchable); recently used emojis (persisted in localStorage across
 * games) lead the grid. Picking one calls `onPick` and the parent sends +
 * echoes it. Closes on outside pointer-down or Escape. */
function EmojiPicker({
  at,
  onPick,
  onClose,
}: {
  at: { x: number; y: number };
  onPick: (emoji: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [catalogue, setCatalogue] = useState<EmojiGroup[] | null>(null);
  const [query, setQuery] = useState('');
  // Recents are read once per open (the picker remounts each time).
  const recents = useMemo(() => loadRecentEmojis(), []);

  useEffect(() => {
    let stale = false;
    loadEmojiCatalogue()
      .then((groups) => {
        if (!stale) setCatalogue(groups);
      })
      .catch(() => {
        /* chunk failed to load — the curated fallback grid stays */
      });
    return () => {
      stale = true;
    };
  }, []);

  /** What the grid shows: search results (flat), or recents + full groups
   * (curated fallback while the catalogue chunk is still loading). */
  const sections = useMemo((): EmojiGroup[] => {
    const q = query.trim().toLowerCase();
    if (q !== '' && catalogue) {
      const hits: EmojiGroup['emojis'] = [];
      for (const group of catalogue) {
        for (const e of group.emojis) {
          if (e.name.includes(q)) {
            hits.push(e);
            if (hits.length >= 120) break; // grid stays scannable
          }
        }
        if (hits.length >= 120) break;
      }
      return [{ name: hits.length > 0 ? 'Search results' : 'No matches', emojis: hits }];
    }
    const head: EmojiGroup[] =
      recents.length > 0
        ? [{ name: 'Recent', emojis: recents.map((e) => ({ emoji: e, name: e })) }]
        : [];
    if (!catalogue) {
      return [
        ...head,
        { name: 'Popular', emojis: EMOJI_SET.map((e) => ({ emoji: e, name: e })) },
      ];
    }
    return [...head, ...catalogue];
  }, [catalogue, query, recents]);

  // Clamp into the viewport once the real size is measurable. Re-clamp when
  // the content changes shape (catalogue arrives, search narrows the grid).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(at.x, window.innerWidth - r.width - 8)),
      top: Math.max(8, Math.min(at.y, window.innerHeight - r.height - 8)),
    });
  }, [at, catalogue, query]);

  // The dismiss listeners are attached exactly once per picker instance.
  // `onClose` is read through a ref: its identity changes on every parent
  // render, and depending on it would detach + re-attach the listeners each
  // time — with the deferred attach below, a keypress landing in that gap
  // would be silently missed.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target)
      ) {
        onCloseRef.current();
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    // Deferred a tick so the opening press can't instantly dismiss it.
    const id = window.setTimeout(() => {
      window.addEventListener('pointerdown', down);
      window.addEventListener('keydown', key);
    }, 0);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('keydown', key);
    };
  }, []);

  return createPortal(
    <div
      ref={ref}
      className="emoji-picker"
      style={{
        left: pos?.left ?? at.x,
        top: pos?.top ?? at.y,
        visibility: pos ? 'visible' : 'hidden',
      }}
      role="menu"
      aria-label="Send an emoji reaction"
    >
      <input
        type="search"
        className="emoji-picker-search"
        placeholder="Search emojis…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        // Autofocus pops the keyboard over the grid on touch — desktop only.
        autoFocus={!window.matchMedia('(pointer: coarse)').matches}
        aria-label="Search emojis"
      />
      <div className="emoji-picker-scroll">
        {sections.map((section) => (
          <div key={section.name} className="emoji-picker-section">
            <div className="emoji-picker-heading">{section.name}</div>
            <div className="emoji-picker-grid">
              {section.emojis.map((e, i) => (
                <button
                  // Names repeat across sections (recents), not within one.
                  key={`${e.emoji}-${i}`}
                  type="button"
                  className="emoji-picker-btn"
                  title={e.name === e.emoji ? undefined : e.name}
                  onClick={() => onPick(e.emoji)}
                >
                  {e.emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** One in-flight emoji reaction over a DOM panel (board reactions render in
 * the Pixi canvas). Transient like the tap ripples. */
interface EmojiPopView {
  key: string;
  area: string;
  x: number;
  y: number;
  target: string;
  tx: number;
  ty: number;
  emoji: string;
}

/** Remote (and locally echoed) emoji reactions over the DOM panels: pulsate
 * in, hold a beat, fade out quickly — all CSS; the parent removes each entry
 * after the animation ends. Positioned via `resolvePanelPoint`, so a reaction
 * dropped on a card lands on the same spot of that card here. */
function RemoteEmojiPops({ pops }: { pops: EmojiPopView[] }) {
  return (
    <>
      {pops.map((p) => {
        const pos = resolvePanelPoint(p.area, p.x, p.y, p.target, p.tx, p.ty);
        if (!pos) return null;
        return (
          <span
            key={p.key}
            className="remote-emoji-pop"
            style={{ left: pos.left, top: pos.top }}
            aria-hidden
          >
            {p.emoji}
          </span>
        );
      })}
    </>
  );
}

/** Resolve a synced panel cursor/tap (`area` = market / roster / lobby) to a
 * LOCAL viewport point. A `target` key (`card:<id>` / `player:<id>` /
 * `history:<id>` / `lobby:ready`) anchors to that element's local rect, with
 * `tx`/`ty` (0..1 within the element, as measured on the sender) mapped into
 * it — the exact spot on the same card or row here, whatever each client's
 * scroll, collapse state or window size. Falls back to the synced normalised
 * panel coords when the element isn't in this client's DOM. Null when the
 * panel isn't shown. */
function resolvePanelPoint(
  area: string,
  x: number,
  y: number,
  target: string,
  tx: number,
  ty: number,
): { left: number; top: number } | null {
  const panel = document.querySelector(
    area === 'market'
      ? '.sidebar-right'
      : area === 'lobby'
        ? '.lobby-card'
        : '.sidebar-left',
  );
  if (!panel) return null;
  const anchor = target
    ? panel.querySelector(`[data-cursor-target="${CSS.escape(target)}"]`)
    : null;
  if (anchor) {
    const r = anchor.getBoundingClientRect();
    return { left: r.left + tx * r.width, top: r.top + ty * r.height };
  }
  const r = panel.getBoundingClientRect();
  return { left: r.left + x * r.width, top: r.top + y * r.height };
}

/** Other clients' live pointers over the DOM panels — the two sidebars and the
 * pre-game lobby card — as floating name-tagged cursor chips (positions via
 * `resolvePanelPoint`). Chips are pure presence: fixed-positioned,
 * non-interactive, and smoothed by a CSS transform transition between the
 * ~100ms state patches. */
function RemoteSidebarCursors({
  cursors,
  players,
  spectators,
}: {
  cursors: CursorView[];
  players: LobbyPlayer[];
  spectators: SpectatorView[];
}) {
  // Anchor rects live in the DOM, so re-render on scroll/resize to keep a chip
  // glued to its card/row while the local layout moves under a held cursor.
  const [, setTick] = useState(0);
  const anyShown = cursors.length > 0;
  useEffect(() => {
    if (!anyShown) return;
    const bump = () => setTick((t) => t + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, [anyShown]);

  return (
    <>
      {cursors.map((c) => {
        const player = players.find((p) => p.id === c.id);
        const spec = player ? undefined : spectators.find((s) => s.id === c.id);
        if (!player && !spec) return null;
        const pos = resolvePanelPoint(c.area, c.x, c.y, c.target, c.tx, c.ty);
        if (!pos) return null;
        const { left, top } = pos;
        const color = player
          ? seatColor(player.seat)
          : `#${SPECTATOR_CURSOR_COLOR.toString(16).padStart(6, '0')}`;
        return (
          <div
            key={c.id}
            className="remote-cursor-chip"
            style={{
              transform: `translate(${Math.round(left)}px, ${Math.round(top)}px)`,
            }}
            aria-hidden
          >
            <svg
              className="remote-cursor-arrow"
              width="13"
              height="19"
              viewBox="-1 -1 13 19"
            >
              <path
                d="M0 0 L0 14 L3.6 10.8 L6.2 16.2 L8.6 15 L6 9.8 L10.6 9.6 Z"
                fill={color}
                stroke="#14110d"
                strokeWidth="1"
              />
            </svg>
            <span className="remote-cursor-name" style={{ background: color }}>
              {player?.name ?? spec?.name}
            </span>
          </div>
        );
      })}
    </>
  );
}

/** One live tap-ripple from a remote touch client (panel areas only; board
 * taps render inside the Pixi canvas). Transient: pushed on the `cursor_tap`
 * message and removed by its own timeout. */
interface TapRippleView {
  key: string;
  area: string;
  x: number;
  y: number;
  target: string;
  /** Position within `target` (0..1), as measured on the tapper's screen. */
  tx: number;
  ty: number;
  /** CSS colour (seat colour / spectator grey). */
  color: string;
}

/** Remote touch taps over the DOM panels, as subtle pulsating ripples — the
 * presence cue for mobile players, who have no hover cursor to stream. Same
 * anchoring rules as the cursor chips (`resolvePanelPoint`); each ripple is a
 * pure CSS animation that the parent removes shortly after it ends. */
function RemoteTapRipples({ taps }: { taps: TapRippleView[] }) {
  return (
    <>
      {taps.map((t) => {
        const pos = resolvePanelPoint(t.area, t.x, t.y, t.target, t.tx, t.ty);
        if (!pos) return null;
        return (
          <span
            key={t.key}
            className="remote-tap-ripple"
            style={{ left: pos.left, top: pos.top, color: t.color }}
            aria-hidden
          >
            <span className="remote-tap-ring" />
            <span className="remote-tap-ring is-second" />
            <span className="remote-tap-dot" />
          </span>
        );
      })}
    </>
  );
}

/** Fallback catalogue when the match config hasn't arrived yet (library /
 * pre-join). Live match UI should prefer `cardsFromConfig(gameConfig)`. */
const DEFAULT_CARDS_BY_ID = fortressCardsById(FORTRESS_CARDS_CONFIG);
const DEFAULT_CARDS_BY_NAME = fortressCardsByName(FORTRESS_CARDS_CONFIG);

function cardsFromConfig(config: GameConfig | null | undefined): {
  byId: Map<string, FortressCard>;
  byName: Map<string, FortressCard>;
} {
  if (!config?.fortressCards) {
    return { byId: DEFAULT_CARDS_BY_ID, byName: DEFAULT_CARDS_BY_NAME };
  }
  return {
    byId: fortressCardsById(config.fortressCards),
    byName: fortressCardsByName(config.fortressCards),
  };
}

const EMPTY_PILE: MarketPile = {
  cards: [],
  locked: [],
  deckCount: 0,
  discardCount: 0,
  discarded: [],
  pool: [],
};

/** Shared empty selection — a stable reference so sections that hold no cycle
 * selection don't get a fresh Set (and a needless re-render) every render. */
const EMPTY_SELECTION: ReadonlySet<number> = new Set<number>();

/** Stable empty per-row cycle selection, for when the flow holds no picks. */
const EMPTY_CYCLE_SELECTED: {
  regular: ReadonlySet<number>;
  win: ReadonlySet<number>;
} = { regular: EMPTY_SELECTION, win: EMPTY_SELECTION };

/** Stable empty map for the market's implement outlines on an ended game. */
const EMPTY_BUILDABLE: ReadonlyMap<string, 'ready' | 'poor'> = new Map();

/** Cycle-Market selection: the indices chosen in each market row. Only ONE
 * row holds picks at a time — regular and win cards never mix in a cycle
 * (the toggle clears the other row). */
interface CycleSelection {
  regular: Set<number>;
  win: Set<number>;
}

/** Everything the market sidebar needs to drive the Cycle-Market flow. */
interface CycleControls {
  /** True while the player is choosing cards to cycle. */
  active: boolean;
  /** Selected indices per market row. */
  selected: { regular: ReadonlySet<number>; win: ReadonlySet<number> };
  /** Running Food cost = each row's pick count × that row's per-card cost. */
  foodCost: number;
  /** Whether the player can pay `foodCost` from their Food on hand. */
  affordable: boolean;
  onToggleCard: (kind: FortressKind, index: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/** What an open detail popover describes — a market card, or one of a player's
 * implemented fortress cards (which also carries the board nodes it occupies,
 * so the popover can highlight / focus it). */
type SelectionTarget =
  | { kind: 'card'; card: FortressCard }
  | {
      kind: 'fortressCard';
      card: FortressCard;
      nodeIds: number[];
      tokens: number;
      /** Resources stored on the card (e.g. Bursted Flap's energy pile). */
      stored: number;
      disabled: boolean;
      /** The piece denoting the card's implement-time pick, "" when none. */
      choice: string;
      /** Stable per-instance id (a player can hold two of the same card). */
      instanceKey: string;
      /** Opened by hovering the fortress ON the board — the viewport is
       * already there, so the popover hides its "View on board" action. */
      fromBoard?: boolean;
    }
  // A card named in a Turn History entry (e.g. a Cycle's removed / added cards).
  // Distinct from `card` so it isn't pruned when the card leaves the market.
  | { kind: 'historyCard'; card: FortressCard };

/** A target's stable identity, for matching a hover-out to what's showing. */
const targetId = (t: SelectionTarget): string =>
  t.kind === 'card' || t.kind === 'historyCard' ? t.card.id : t.instanceKey;

/** The open detail popover: a target, the live tile element it anchors to (read
 * fresh each layout so the popover follows the row as the roster reflows), and
 * whether it's `pinned` (opened by a click — stays until dismissed) or a hover
 * preview (closes when the cursor leaves the tile). */
type Selection = SelectionTarget & { anchorEl: HTMLElement; pinned: boolean };

/** Tile interaction callbacks, shared by market card rows and fortress tiles.
 * The tile's own element is passed as the anchor (not a captured rect) so the
 * popover can re-measure it as live state moves it. */
interface SelectionHandlers {
  /** Cursor entered a tile → show its hover preview. */
  onPreview: (target: SelectionTarget, anchorEl: HTMLElement) => void;
  /** Cursor left a tile → drop its hover preview (a pinned target stays). */
  onPreviewEnd: (target: SelectionTarget) => void;
  /** Tile clicked → pin the detail open. */
  onPin: (target: SelectionTarget, anchorEl: HTMLElement) => void;
}

/** Grace period (ms) between the cursor leaving a hover-preview's tile and the
 * preview closing — long enough to cross the gap onto the popover and keep it
 * open. Pinned details ignore this entirely. */
const PREVIEW_CLOSE_DELAY = 140;

interface AppProps {
  authenticated?: boolean;
  /** This client's account id — our seat key in every match (seats are keyed
   * by account), and what the synced `hostUserId` / `creatorUserId` are
   * compared against. Absent when AUTH_DISABLED, where the server seats
   * guests as `guest:<sessionId>` instead. */
  selfUserId?: string;
  /** Whether the account has finished (or skipped) the tutorial — gates the
   * hub's Play tutorial card. Defaults to true (guests never see the card). */
  tutorialComplete?: boolean;
}

const NO_TUTORIAL: TutorialView = { active: false, step: 0, nodeIds: [], edge: '' };

export function App({
  authenticated = false,
  selfUserId,
  tutorialComplete = true,
}: AppProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const roomRef = useRef<Room<GameState> | null>(null);
  const gameRef = useRef<PixiGame | null>(null);
  // Mirrors of phase / current player / self id for the board click handler,
  // which is created once and would otherwise close over stale initial values.
  const phaseRef = useRef<string>('lobby');
  const currentPlayerRef = useRef<string>('');
  const selfIdRef = useRef<string>('');
  /** Mirror of this client's home-base node id (-1 until placed), read by the
   * emoji message handler's postal-horn check. */
  const selfHomeNodeRef = useRef<number>(-1);
  /** Live per-node ownership/building info, for computing legal build targets.
   * Kept in a ref (not state) — read on demand by the board click handler and
   * the build-range calc, which don't need to re-render on every node change. */
  const nodeInfoRef = useRef<
    Map<
      number,
      {
        ownerId: string;
        building: string;
        fortressOwnerId: string;
        resource: string;
        permanence: string;
        /** Barracks aim: target node id (-1 upright) and posture ("" upright). */
        aimTargetId: number;
        aimMode: string;
        /** Bridge edge key this Barracks is aimed at ("" for node aims). When
         * set, `aimTargetId` is only an anchor endpoint — never a target node. */
        aimEdge: string;
        indestructible: boolean;
        /** A placed one-time resource waiting here (Pee-Pee Pinata), and
         * how many units of it ("" / 0 when none). */
        bonusResource: string;
        bonusAmount: number;
      }
    >
  >(new Map());
  /** Severed-edge keys (chasms), mirrored for build-range line-of-sight. */
  const severedRef = useRef<Set<string>>(new Set());
  /** Constructed-bridge edge keys, mirrored to exclude already-bridged paths. */
  const bridgesRef = useRef<Set<string>>(new Set());
  /** Bumped on every node/bridge sync. Board state lives in refs (no re-render
   * per node), so memos that read those refs — the market "buildable" outlines —
   * depend on this counter to recompute when the board changes. */
  const [boardVersion, setBoardVersion] = useState(0);
  /** Mirror of `players` for the once-created board hover handler. */
  const playersRef = useRef<LobbyPlayer[]>([]);
  /** Mirror of match-pinned card catalogue for the board hover handler. */
  const cardsByIdRef = useRef(DEFAULT_CARDS_BY_ID);
  /** Mirror of `selectionHandlers` for the same reason. */
  const selectionHandlersRef = useRef<SelectionHandlers | null>(null);
  /** Invisible fixed-position element the board fortress popover anchors to —
   * moved to the hovered node's screen position (popovers anchor to DOM
   * elements; board nodes live on the canvas). */
  const boardAnchorRef = useRef<HTMLDivElement>(null);
  /** The fortress target currently previewed from a board hover, if any. */
  const boardHoverRef = useRef<SelectionTarget | null>(null);
  const [turn, setTurn] = useState<number | null>(null);
  const [round, setRound] = useState<number | null>(null);
  /** Action points left in the current turn — drives the "action n/total" readout
   * and whether the End-Turn button means "pass" (none spent) or "end turn". */
  const [actionsRemaining, setActionsRemaining] = useState<number>(0);
  /** The construction we may still take back this turn ("Build Scout"), or ''
   * when there is nothing to undo — as synced, so it clears itself the moment
   * the turn (or the chance) is gone. */
  const [undoLabel, setUndoLabel] = useState<string>('');
  /** Cumulative implemented-fortress count, as synced — Inverse Molehill's
   * growing win-deck discount reads it (`fortressDiscount.perImplementedFortress`). */
  const [fortressesImplemented, setFortressesImplemented] = useState<number>(0);
  /** Seated players pinned at go-live (0 on pre-field saves → live seat count). */
  const [startingPlayerCount, setStartingPlayerCount] = useState<number>(0);
  /** Space Cheese Curdler's last-stand state, as synced: the holder playing
   * their solo round ('' when none), how many of its turns are left, plus the
   * postponed winner and their card — drives the last-stand banners. */
  const [lastStand, setLastStand] = useState<{
    playerId: string;
    turnsRemaining: number;
    pendingWinnerId: string;
    pendingWinCardId: string;
  }>({
    playerId: '',
    turnsRemaining: 0,
    pendingWinnerId: '',
    pendingWinCardId: '',
  });
  const [currentPlayerId, setCurrentPlayerId] = useState<string>('');
  const [phase, setPhase] = useState<string>('lobby');
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);
  const [lobbyInfo, setLobbyInfo] = useState<LobbyInfo>({
    hostUserId: '',
    creatorUserId: '',
    inviteCode: '',
    visibility: 'public',
    maxPlayers: 8,
    autoStartAtMs: 0,
    ranked: false,
    poolId: '',
    poolName: '',
  });
  /** Selected match — null shows the lobby hub. Keyed by `gameId`, the
   * match's one stable identifier; the Colyseus room hosting it is resolved
   * at connect time (`POST /games/:gameId/room`), so refresh and shared
   * `/match/:gameId` links survive room deaths. `reconnectionToken` is set
   * when re-entering a live game after a refresh (reconnect, not join).
   * `spectate` joins the room watch-only (no seat; commands are ignored).
   * Admission still goes through the room's `onAuth`. */
  const [activeMatch, setActiveMatch] = useState<{
    gameId: string;
    reconnectionToken?: string;
    spectate?: boolean;
  } | null>(() => {
    const fromUrl = parseMatchLocation();
    if (!fromUrl) return null;
    return {
      gameId: fromUrl.gameId,
      ...(fromUrl.spectate ? { spectate: true } : {}),
    };
  });

  // Keep the address bar in sync with the active match so create/join lands
  // on `/match/:gameId` and leaving returns to the hub. Deep links use the
  // same path; failed joins clear `activeMatch` and replace the dead URL.
  useEffect(() => {
    if (activeMatch) {
      const next = matchPath(activeMatch.gameId, !!activeMatch.spectate);
      const cur = `${window.location.pathname}${window.location.search}`;
      if (cur !== next) {
        window.history.pushState({ matchGameId: activeMatch.gameId }, '', next);
      }
      return;
    }
    if (window.location.pathname.startsWith('/match/')) {
      window.history.replaceState({}, '', '/');
    }
  }, [activeMatch]);

  // Back/forward: URL is source of truth for which match (if any) is active.
  useEffect(() => {
    const onPopState = () => {
      const fromUrl = parseMatchLocation();
      if (!fromUrl) {
        setActiveMatch(null);
        return;
      }
      setActiveMatch((prev) => {
        if (
          prev?.gameId === fromUrl.gameId &&
          !!prev.spectate === fromUrl.spectate
        ) {
          return prev;
        }
        return {
          gameId: fromUrl.gameId,
          ...(fromUrl.spectate ? { spectate: true } : {}),
        };
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  /** Everyone currently watching this room (synced `GameState.spectators`). */
  const [spectators, setSpectators] = useState<SpectatorView[]>([]);
  const spectatorsRef = useRef<SpectatorView[]>([]);
  /** Other clients' live pointers over the sidebars (synced `GameState.cursors`,
   * areas "market"/"roster") — rendered as DOM chips anchored to the hovered
   * card/row. Board-area cursors deliberately bypass React: they update ~5Hz
   * and go straight to the Pixi layer (see `pushBoardCursors`). */
  const [sidebarCursors, setSidebarCursors] = useState<CursorView[]>([]);
  const liveCursorsRef = useRef<CursorView[]>([]);
  /** In-flight remote tap ripples over the DOM panels (board taps go straight
   * to Pixi). Each entry removes itself ~a second after it lands. */
  const [tapRipples, setTapRipples] = useState<TapRippleView[]>([]);
  const tapSeqRef = useRef(0);
  /** Open emoji picker (right-click / long-press): where to render it, and
   * the classified point the picked reaction will pin to. */
  const [emojiPicker, setEmojiPicker] = useState<{
    screen: { x: number; y: number };
    sample: CursorSample;
  } | null>(null);
  /** In-flight emoji reactions over the DOM panels (board reactions render in
   * Pixi). Each entry removes itself when its animation ends. */
  const [emojiPops, setEmojiPops] = useState<EmojiPopView[]>([]);
  /** The last reconnect attempt failed (grace lapsed / game gone) — surfaced
   * on the lobby hub once, cleared on the next room entry. */
  const [reconnectFailed, setReconnectFailed] = useState(false);
  /** The lobby we were in was closed by its creator — surfaced on the lobby
   * hub once, cleared on the next room entry. */
  const [lobbyClosed, setLobbyClosed] = useState(false);
  /** Our account joined the same game from another device, which took over
   * the seat and disconnected us — surfaced on the lobby hub once, cleared
   * on the next room entry. */
  const [sessionSuperseded, setSessionSuperseded] = useState(false);
  /** The live room's socket dropped unexpectedly (Cloud Run's request
   * timeout, a scale event, network) and the in-place reconnect ladder is
   * running — shows the "reconnecting" toast over the board. */
  const [connectionLost, setConnectionLost] = useState(false);
  const [markets, setMarkets] = useState<MarketView>({
    regular: EMPTY_PILE,
    win: EMPTY_PILE,
  });
  const [marketCollapsed, setMarketCollapsed] = useState(true);
  const [playersCollapsed, setPlayersCollapsed] = useState(true);
  /** Bottom action bar (turn / defense) collapsed to a slim status pill so the
   * whole board stays visible; lives here (not in the bar) so the choice
   * survives the bar unmounting between turns. */
  const [actionBarCollapsed, setActionBarCollapsed] = useState(false);
  /** PHONES: the turn / defense bar stays off-screen until the bottom-right
   * dock's "Your turn" / "Defend" button opens it (there is no collapsed
   * pill), so the board owns the screen between actions. Cleared whenever
   * the bar leaves the screen (turn over, defense resolved). */
  const [phoneTrayOpen, setPhoneTrayOpen] = useState(false);
  /** Phone layout (max-width 640px): sidebars widen, become exclusive, chat
   * opens as a sheet and the screen edges accept open-sidebar swipes. */
  const isSmallScreen = useMediaQuery(SMALL_SCREEN_QUERY);
  /** Phone/tablet layout (max-width 1024px): the action bar goes full-width
   * with the corner buttons docked above it. */
  const isMediumScreen = useMediaQuery(MEDIUM_SCREEN_QUERY);
  // ON SMALL SCREENS only one sidebar may be open (each is near full-width).
  // The two toggle handlers close the opposite panel synchronously, so this
  // guard only fires on programmatic market reveals (cycle flows, queued-move
  // markers, round-start window) and on a desktop→phone resize — there the
  // freshly revealed market wins and the roster yields.
  useLayoutEffect(() => {
    if (isSmallScreen && !marketCollapsed && !playersCollapsed) {
      setPlayersCollapsed(true);
    }
  }, [isSmallScreen, marketCollapsed, playersCollapsed]);
  /** Full move history (oldest first), synced from `GameState.moveHistory`. */
  const [history, setHistory] = useState<HistoryEntryView[]>([]);
  /** Full chat log (oldest first, last 1000), synced from `GameState.chatMessages`. */
  const [chat, setChat] = useState<ChatMessageView[]>([]);
  /** The chat panel/sheet is open. Lifted out of ChatPanel so the docked
   * corner cluster's chat button can open it while the action bar is up. */
  const [chatOpen, setChatOpen] = useState(false);
  /** Newest chat message id already pinged for, so repeated full-list emits
   * ring at most once per new message. Reset on (re)attach — ids restart
   * per room. */
  const chatPingSeenRef = useRef(0);
  /** Left sidebar shows the Turn History list instead of the player roster. */
  const [historyMode, setHistoryMode] = useState(false);
  /** Queue-planning mode: the upcoming-turn slot (1..MAX_QUEUE_TURNS) new
   * moves are queued into, or null when not planning. While set (off-turn),
   * the normal action bar and pickers render and route their actions into the
   * private queue instead of sending them live. */
  const [planSlot, setPlanSlot] = useState<number | null>(null);
  /** Mirror of `planSlot` for the once-created board click handler. */
  const planSlotRef = useRef<number | null>(null);
  useEffect(() => {
    planSlotRef.current = planSlot;
  }, [planSlot]);
  /** The queued move currently SELECTED in the queue panel (slot + index
   * within that slot), or null. Its preview markers — the board ghost and the
   * market "queued" badge — show only while it is selected. */
  const [queuedSel, setQueuedSel] = useState<{
    slot: number;
    index: number;
  } | null>(null);
  /** Queued-move auto-play stopped on our turn (server `queue_halted` event);
   * drives the explanatory toast. `held` marks the benign stop — the plan ran
   * clean but didn't fill the turn, which is still ours — as opposed to a plan
   * cut short by a stale move. Cleared after `QUEUE_HALT_TTL`. */
  const [queueHalt, setQueueHalt] = useState<{
    label: string;
    reason: string;
    held: boolean;
  } | null>(null);
  const queueHaltTimer = useRef<number | undefined>(undefined);
  /** The creator's restore ring, oldest first — which turn/round boundaries
   * the match can be rolled back to. Empty for everyone on a server without
   * the feature, and only ACTED on by the creator (the server refuses anyone
   * else), though everyone syncs it. */
  const [restorePoints, setRestorePoints] = useState<RestorePointView[]>([]);
  /** The match was just rolled back (server `restored` event) — drives the
   * explanatory toast, since the board changes under everyone at once.
   * Cleared after `RESTORED_TTL`. */
  const [restored, setRestored] = useState<{
    byName: string;
    round: number;
    turn: number;
    dropped: number;
  } | null>(null);
  const restoredTimer = useRef<number | undefined>(undefined);
  /** Construct-building flow (pick a building, then place it), or null when off. */
  const [buildFlow, setBuildFlow] = useState<BuildFlow>(null);
  /** Mirror of `buildFlow` for the once-created board click handler. */
  const buildFlowRef = useRef<BuildFlow>(null);
  const [aimFlow, setAimFlow] = useState<AimFlow>(null);
  /** Mirror of `aimFlow` for the once-created board click handler. */
  const aimFlowRef = useRef<AimFlow>(null);
  useEffect(() => {
    aimFlowRef.current = aimFlow;
  }, [aimFlow]);
  /** Card-Effects flow (the tray of your implemented cards' effects), or null. */
  const [effectsFlow, setEffectsFlow] = useState<EffectsFlow>(null);
  /** Mirror of `effectsFlow` for the once-created board click handler. */
  const effectsFlowRef = useRef<EffectsFlow>(null);
  useEffect(() => {
    effectsFlowRef.current = effectsFlow;
  }, [effectsFlow]);
  /** Mirror of `computeAimTargets` for the same handler (it closes over live
   * state — gameConfig, selfId — the once-created handler can't see). */
  const computeAimTargetsRef = useRef<
    (barracksId: number, mode: AimMode, defense: boolean) => ReadonlySet<number>
  >(() => new Set<number>());
  /** The defense window, as synced: whether one is open, whether it's a
   * mid-turn designation window (Stab Pole Hole), and which players (session
   * ids) still have to respond. */
  const [defense, setDefense] = useState<{
    active: boolean;
    midTurn: boolean;
    pending: string[];
  }>({ active: false, midTurn: false, pending: [] });
  /** Post-combat Barracks upkeep choices still unresolved, as synced — each
   * entry `"playerId|nodeId|foodCost"`. Non-empty freezes the ending turn
   * until every owner keeps (pays) or starves their attacking Barracks. */
  const [upkeepEntries, setUpkeepEntries] = useState<string[]>([]);
  /** Post-combat resource steals still unresolved, as synced — each entry
   * `"attackerId|victimId|count"`. Non-empty freezes the ending turn until
   * every attacker picks which of the victim's resources to take. */
  const [stealEntries, setStealEntries] = useState<string[]>([]);
  useEffect(() => {
    buildFlowRef.current = buildFlow;
  }, [buildFlow]);
  /** Construct-bridge flow: the eligible paths to bridge, or null when off.
   * `allowed` are tappable; `tooCostly` are in range but unaffordable (dim);
   * `allowedKeys` is the canonical edge-key set for the click check. With a
   * WILDCARD bridge cost (Bridgette's "any resource"), tapping a path parks it
   * in `pending` and the bar asks which resource pays before sending. */
  const [bridgeFlow, setBridgeFlow] = useState<{
    allowed: [number, number][];
    tooCostly: [number, number][];
    allowedKeys: ReadonlySet<string>;
    wildcard: boolean;
    pending: { from: number; to: number } | null;
    /** Card id of a Bridgette the Beauty placing this bridge as her granted
     * action — her range bonus on top of the Bridge's, no action point. */
    cardId?: string;
  } | null>(null);
  /** Mirror of `bridgeFlow` for the once-created board edge-click handler. */
  const bridgeFlowRef = useRef<typeof bridgeFlow>(null);
  useEffect(() => {
    bridgeFlowRef.current = bridgeFlow;
  }, [bridgeFlow]);
  /** Implement-Fortress flow (pick a card, then place it), or null when off. */
  const [implementFlow, setImplementFlow] = useState<ImplementFlow | null>(
    null,
  );
  /** Mirror of `implementFlow` for the once-created board click handler. */
  const implementFlowRef = useRef<ImplementFlow | null>(null);
  useEffect(() => {
    implementFlowRef.current = implementFlow;
  }, [implementFlow]);
  /** Mirror of `openImplement` for the effects tray (Dungeon Dice Martyr's
   * discard implement opens the ordinary Implement picker), which is created
   * before the opener in render order. */
  const openImplementRef = useRef<() => void>(() => {});
  /** Cycle-Market flow: on while picking cards, plus the current selection. */
  const [cycleMode, setCycleMode] = useState(false);
  const [cycleSel, setCycleSel] = useState<CycleSelection | null>(null);
  /** Current First-Turn marker holder — the player expected to open the next
   * round (synced `firstPlayerId`). Gates the round-start cycle queue UI. */
  const [firstPlayerId, setFirstPlayerId] = useState('');
  /** Who the open round-start cycle window waits on ('' = closed). While set,
   * the round is paused before anything else happens: the first player picks
   * cards from each market to cycle out free, or cycles nothing. */
  const [roundCyclerId, setRoundCyclerId] = useState('');
  /** Round-start cycle picking: 'live' answers the open window, 'queue'
   * pre-plans next round's choice; null = off. At most
   * `fortressCards.roundStartCycleMaxPerRow` cards per market. */
  const [rsMode, setRsMode] = useState<'live' | 'queue' | null>(null);
  const [rsSel, setRsSel] = useState<{
    regular: string[];
    win: string[];
  }>({ regular: [], win: [] });
  /** The rulebook modal — reachable from the hub, the lobby and in-game. */
  const [rulebookOpen, setRulebookOpen] = useState(false);
  /** The match result, synced from state once `phase === "ended"` (covers live
   * game-over, reconnects and late spectators alike); drives the top result
   * banner. `winnerId` null = draw; `winCardId` names the fortress card whose
   * win condition ended the game. */
  const [gameOver, setGameOver] = useState<{
    winnerId: string | null;
    winCardId: string;
  } | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [selfId, setSelfId] = useState<string>('');
  /** Tutorial match: the current step and its board targets (server-owned). */
  const [tutorial, setTutorial] = useState<TutorialView>(NO_TUTORIAL);
  /** Whether this account has finished the tutorial (flips locally the moment
   * it is marked complete, so the hub card drops without a reload). */
  const [tutorialDone, setTutorialDone] = useState(tutorialComplete);
  /** Setup phase: the node tentatively picked for the home base, pending Confirm. */
  const [selectedHomeNode, setSelectedHomeNode] = useState<number | null>(null);
  /** The match's pinned config snapshot, sent by the room on join. Drives rule
   * values the client shows (e.g. production points to distribute). */
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null);
  /** Server-advertised dev mode (non-prod). Gates the lobby's Auto-Initialize
   * button; the server independently rejects the command when this is false. */
  const [devMode, setDevMode] = useState(false);
  /** Live element of each player's roster row, keyed by id, so an action-notice
   * popup can anchor beside the player who just acted. Populated by callback refs
   * in `PlayerSidebar`. */
  const playerRowEls = useRef<Map<string, HTMLElement>>(new Map());
  const registerRow = useCallback((id: string, el: HTMLElement | null) => {
    if (el) playerRowEls.current.set(id, el);
    else playerRowEls.current.delete(id);
  }, []);
  /** Active action notifications — at most one per player (a newer action by the
   * same player replaces the older), each auto-dismissed after `NOTICE_TTL`. */
  const [notices, setNotices] = useState<ActiveNotice[]>([]);
  /** Per-player auto-dismiss timers, so a replaced/closed notice clears its timer. */
  const noticeTimers = useRef<Map<string, number>>(new Map());
  /** Shows the top-screen "It's Your Turn" banner; cleared after `TURN_TOAST_TTL`. */
  const [turnToast, setTurnToast] = useState(false);
  const turnToastTimer = useRef<number | undefined>(undefined);
  /** Fog overlay remount key: bumped when a Face-in-Clouds reaction lands on
   * this player's fortress (so back-to-back fogs restart the animation),
   * cleared to 0 after `FOG_TTL`. */
  const [fogKey, setFogKey] = useState(0);
  const fogTimer = useRef<number | undefined>(undefined);
  /** Emoji shield (armed by dropping 🛡️ on the board): epoch-ms until which
   * targeted reaction effects aimed at this player are ignored. The ref feeds
   * the socket handler (wired once); the state drives the countdown badge. */
  const shieldUntilRef = useRef(0);
  const [shieldLeft, setShieldLeft] = useState(0);
  const shieldTicker = useRef<number | undefined>(undefined);
  /** Monotonic key so React remounts (and re-animates) the popup on each action,
   * even when it replaces the same player's previous one. */
  const noticeSeq = useRef(0);

  const dismissNotice = useCallback((playerId: string) => {
    setNotices((cur) => cur.filter((n) => n.playerId !== playerId));
    const t = noticeTimers.current.get(playerId);
    if (t !== undefined) {
      window.clearTimeout(t);
      noticeTimers.current.delete(playerId);
    }
  }, []);

  const pushNotice = useCallback((notice: ActionNotice) => {
    const key = (noticeSeq.current += 1);
    setNotices((cur) => [
      ...cur.filter((n) => n.playerId !== notice.playerId),
      { ...notice, key },
    ]);
    const prev = noticeTimers.current.get(notice.playerId);
    if (prev !== undefined) window.clearTimeout(prev);
    const t = window.setTimeout(() => {
      setNotices((cur) => cur.filter((n) => n.playerId !== notice.playerId));
      noticeTimers.current.delete(notice.playerId);
    }, NOTICE_TTL);
    noticeTimers.current.set(notice.playerId, t);
  }, []);

  // Clear any in-flight dismiss timers on unmount.
  useEffect(() => {
    const timers = noticeTimers.current;
    return () => {
      for (const t of timers.values()) window.clearTimeout(t);
      timers.clear();
    };
  }, []);

  // Clear the turn-toast timer on unmount.
  useEffect(
    () => () => {
      if (turnToastTimer.current !== undefined)
        window.clearTimeout(turnToastTimer.current);
    },
    [],
  );

  /** Mist the screen edges for `FOG_TTL` — a Face-in-Clouds reaction landed
   * on this player's fortress. Re-triggering restarts both the animation
   * (fresh key remounts the overlay) and the clear timer. */
  const triggerFog = useCallback(() => {
    setFogKey((k) => k + 1);
    if (fogTimer.current !== undefined) window.clearTimeout(fogTimer.current);
    fogTimer.current = window.setTimeout(() => {
      setFogKey(0);
      fogTimer.current = undefined;
    }, FOG_TTL);
  }, []);

  /** Arm (or re-arm, resetting the clock) the emoji shield and start the
   * badge countdown ticking. */
  const armShield = useCallback(() => {
    shieldUntilRef.current = Date.now() + SHIELD_TTL;
    setShieldLeft(Math.ceil(SHIELD_TTL / 1000));
    if (shieldTicker.current !== undefined)
      window.clearInterval(shieldTicker.current);
    shieldTicker.current = window.setInterval(() => {
      const left = Math.ceil((shieldUntilRef.current - Date.now()) / 1000);
      if (left > 0) {
        setShieldLeft(left);
        return;
      }
      setShieldLeft(0);
      window.clearInterval(shieldTicker.current);
      shieldTicker.current = undefined;
    }, 250);
  }, []);

  // Clear the fog timer + shield ticker on unmount.
  useEffect(
    () => () => {
      if (fogTimer.current !== undefined) window.clearTimeout(fogTimer.current);
      if (shieldTicker.current !== undefined)
        window.clearInterval(shieldTicker.current);
    },
    [],
  );

  // Clear the queue-halt toast timer on unmount.
  useEffect(
    () => () => {
      if (queueHaltTimer.current !== undefined)
        window.clearTimeout(queueHaltTimer.current);
    },
    [],
  );

  /** Send a game action live — or, while planning, queue it for the chosen
   * upcoming turn instead. Every action-flow send routes through here so the
   * same pickers serve both modes. Reactive / off-turn sends (defense
   * reactions, iCarlie claims, prompt responses) bypass this deliberately. */
  const sendGameAction = useCallback((action: GameAction) => {
    const room = roomRef.current;
    if (!room) return;
    const slot = planSlotRef.current;
    if (slot !== null) {
      room.send(ClientMessage.QueueMove, {
        slot,
        action,
        label: queueMoveLabel(action, cardsByIdRef.current),
      });
      return;
    }
    room.send(ClientMessage.Action, action);
  }, []);
  /** Mirror for the once-created board click handler. */
  const sendGameActionRef = useRef(sendGameAction);
  useEffect(() => {
    sendGameActionRef.current = sendGameAction;
  }, [sendGameAction]);

  // Mirror my home-base node id for the postal-horn honk check (the emoji
  // message handler is wired once, so it reads refs).
  useEffect(() => {
    selfHomeNodeRef.current =
      players.find((p) => p.id === selfId)?.homeNodeId ?? -1;
  }, [players, selfId]);

  // Reflect "it's your turn" in the browser tab title, so a backgrounded tab
  // still signals it; revert to the default the moment it's no longer your turn.
  useEffect(() => {
    const myTurn = !!selfId && currentPlayerId === selfId;
    document.title = myTurn ? `Your Turn! | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [currentPlayerId, selfId]);

  // Ask for OS-notification permission once, on the first user gesture (browsers
  // reject an unprompted request). Best-effort: if it's denied or unsupported,
  // the turn notification simply never fires — the toast still does.
  useEffect(() => {
    if (
      typeof Notification === 'undefined' ||
      Notification.permission !== 'default'
    )
      return;
    const ask = () => {
      void Notification.requestPermission();
      window.removeEventListener('pointerdown', ask);
    };
    window.addEventListener('pointerdown', ask);
    return () => window.removeEventListener('pointerdown', ask);
  }, []);

  // Drop notifications for players who have since left (their anchor row is gone).
  useEffect(() => {
    const ids = new Set(players.map((p) => p.id));
    setNotices((cur) => cur.filter((n) => ids.has(n.playerId)));
  }, [players]);

  /** Feed board-area remote cursors straight into the Pixi layer, resolving
   * each session id to a seat colour + name (players) or the spectator grey.
   * Reads refs (not state) so the state watcher can call it at patch rate
   * without re-rendering the app; unresolvable ids (roster not yet synced)
   * are skipped and self-heal on the next patch. */
  const pushBoardCursors = useCallback(() => {
    const game = gameRef.current;
    if (!game) return;
    const list: RemoteCursorView[] = [];
    for (const c of liveCursorsRef.current) {
      if (c.area !== 'board' || c.id === selfIdRef.current) continue;
      const player = playersRef.current.find((p) => p.id === c.id);
      const spec = player
        ? undefined
        : spectatorsRef.current.find((s) => s.id === c.id);
      if (!player && !spec) continue;
      list.push({
        id: c.id,
        x: c.x,
        y: c.y,
        color: player
          ? (PLAYER_COLORS[player.seat % PLAYER_COLORS.length] ?? 0xffffff)
          : SPECTATOR_CURSOR_COLOR,
        name: player?.name ?? spec?.name ?? '',
      });
    }
    game.setRemoteCursors(list);
  }, []);

  // Re-resolve cursor colours/names when the roster changes (seats are
  // reassigned at game start; a held-still cursor would otherwise keep its
  // stale colour until its owner next moved).
  useEffect(() => {
    pushBoardCursors();
  }, [players, spectators, pushBoardCursors]);

  /** Play an emoji reaction locally — used for both incoming broadcasts and
   * the local echo of our own pick (the server relays to everyone else).
   * Board reactions render in the Pixi canvas at the shared board point;
   * panel reactions become a transient DOM pop anchored like the cursors. */
  const spawnEmoji = useCallback((r: Omit<EmojiPopView, 'key'>) => {
    if (r.area === 'board') {
      gameRef.current?.showEmoji({ x: r.x, y: r.y, emoji: r.emoji });
      return;
    }
    const pop: EmojiPopView = { ...r, key: `emoji:${++tapSeqRef.current}` };
    setEmojiPops((cur) => [...cur, pop]);
    window.setTimeout(
      () => setEmojiPops((cur) => cur.filter((p) => p !== pop)),
      1600,
    );
  }, []);

  useEffect(() => {
    if (!activeMatch) return;
    const { gameId, reconnectionToken, spectate } = activeMatch;

    let game: PixiGame | null = null;
    let room: Room<GameState> | null = null;
    let disposed = false;
    // Stale from a prior room whose recovery was cut short by leaving it.
    setConnectionLost(false);
    setGameOver(null);
    // Fresh join (or match switch): clear the board chrome. In-place reconnect
    // reuses the same activeMatch, so this effect does not re-run and the
    // player's open/closed preference survives the blip.
    setMarketCollapsed(true);
    setPlayersCollapsed(true);
    setChatOpen(false);
    liveCursorsRef.current = [];
    setSidebarCursors([]);
    setTapRipples([]);
    setEmojiPops([]);
    setEmojiPicker(null);

    async function boot() {
      const parent = stageRef.current;
      if (!parent) return;

      game = await createPixiGame(parent, (nodeId) => {
        const room = roomRef.current;
        if (!room) return;
        // Setup phase: a click on your turn *selects* a legal home node (pending
        // a Confirm); it isn't committed until the Confirm button is pressed.
        if (phaseRef.current === 'setup') {
          if (
            currentPlayerRef.current === selfIdRef.current &&
            gameRef.current?.isLegalHomeNode(nodeId)
          ) {
            setSelectedHomeNode(nodeId);
          }
          return;
        }
        // Playing: the aim flow claims board clicks while it's open — first the
        // Barracks pick, then the target pick (both only on highlighted nodes).
        const af = aimFlowRef.current;
        if (af) {
          if (af.stage === 'pickBarracks' && af.sources.has(nodeId)) {
            if (af.defense) {
              // Defense reactions are always DEFEND — skip the posture picker.
              setAimFlow({
                stage: 'target',
                barracksId: nodeId,
                mode: 'defend',
                defense: true,
                allowed: computeAimTargetsRef.current(nodeId, 'defend', true),
                allowedBridges: computeAimBridgesRef.current(
                  nodeId,
                  'defend',
                  true,
                ),
              });
            } else {
              setAimFlow({
                stage: 'mode',
                barracksId: nodeId,
                // Planning never gates the posture — the board may differ by
                // the time the queued aim plays.
                attackable:
                  planSlotRef.current !== null ||
                  (nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '') ===
                    '',
                defense: false,
              });
            }
          } else if (af.stage === 'target' && af.allowed.has(nodeId)) {
            const aim = {
              type: 'aim_barracks',
              nodeId: af.barracksId,
              targetId: nodeId,
              mode: af.mode,
            } as const;
            // Defense reactions are off-turn responses — never queued.
            if (af.defense) room.send(ClientMessage.Action, aim);
            else sendGameActionRef.current(aim);
            setAimFlow(null);
          }
          return;
        }
        // Card-Effects board picks: protect-a-Scout sends on the tap; the
        // untap flow toggles Barracks in/out of the selection (bounded by the
        // card's remaining per-round allowance) until its Confirm.
        const ef = effectsFlowRef.current;
        if (ef) {
          if (ef.stage === 'pickScout' && ef.allowed.has(nodeId)) {
            room.send(ClientMessage.Action, {
              type: 'make_scout_indestructible',
              cardId: ef.cardId,
              nodeId,
            });
            setEffectsFlow(null);
          } else if (ef.stage === 'replicate' && ef.allowed.has(nodeId)) {
            sendGameActionRef.current({
              type: 'replicate_fortress',
              cardId: ef.cardId,
              nodeIds: [nodeId],
            });
            setEffectsFlow(null);
          } else if (
            (ef.stage === 'untap' || ef.stage === 'moveFortress') &&
            ef.allowed.has(nodeId)
          ) {
            setEffectsFlow((cur) => {
              if (cur?.stage !== 'untap' && cur?.stage !== 'moveFortress')
                return cur;
              const selected = new Set(cur.selected);
              if (selected.has(nodeId)) selected.delete(nodeId);
              else if (selected.size < cur.limit) selected.add(nodeId);
              return { ...cur, selected };
            });
          } else if (ef.stage === 'sellBuilding' && ef.allowed.has(nodeId)) {
            sendGameActionRef.current({
              type: 'sell_building',
              cardId: ef.cardId,
              nodeId,
            });
            setEffectsFlow(null);
          } else if (ef.stage === 'designateTarget' && ef.allowed.has(nodeId)) {
            sendGameActionRef.current({
              type: 'designate_attack_target',
              cardId: ef.cardId,
              nodeId,
            });
            setEffectsFlow(null);
          } else if (ef.stage === 'moveBuildingFrom' && ef.allowed.has(nodeId)) {
            // The building to move — on to picking its destination.
            const dests = ef.targets.get(nodeId);
            if (dests && dests.size > 0) {
              const gains = new Map<
                number,
                { resource: string; amount: number }
              >();
              for (const dest of dests) {
                const gain = oneTimeGainAt(
                  dest,
                  nodeInfoRef.current.get(dest),
                );
                if (gain) gains.set(dest, gain);
              }
              setEffectsFlow({
                stage: 'moveBuildingTo',
                cardId: ef.cardId,
                fromNodeId: nodeId,
                allowed: dests,
                oneTimeGain: gains,
              });
            }
          } else if (ef.stage === 'moveBuildingTo' && ef.allowed.has(nodeId)) {
            sendGameActionRef.current({
              type: 'move_building',
              cardId: ef.cardId,
              nodeId: ef.fromNodeId,
              targetId: nodeId,
              ...(ef.doubleOneTime
                ? { riders: { doubleOneTime: ef.doubleOneTime } }
                : {}),
            });
            setEffectsFlow(null);
          } else if (ef.stage === 'dropResources' && ef.allowed.has(nodeId)) {
            // Each tap places 1 unit of the selected kind; the flow stays
            // open (the synced pending counter closes it when spent). Sent
            // LIVE, never queued: the units are banked right now and the
            // distribution is free — a plan slot would defer a placement the
            // board (and the player's own turn end) is waiting on.
            room.send(ClientMessage.Action, {
              type: 'drop_one_time_resources',
              cardId: ef.cardId,
              nodeId,
              resource: ef.resource,
              amount: 1,
            });
          }
          return;
        }
        // Implement-Fortress placement: tap a highlighted node to choose where
        // the fortress lands. The selected placement is the first match
        // covering that node; tapping the same node again cycles through the
        // other placements over it (same as the Rotate button).
        const impl = implementFlowRef.current;
        if (impl) {
          if (impl.stage === 'place' && impl.candidates.has(nodeId)) {
            setImplementFlow((cur) => {
              if (cur?.stage !== 'place') return cur;
              const options: number[] = [];
              cur.matches.forEach((m, i) => {
                if (nodesOfMatch(m).includes(nodeId)) options.push(i);
              });
              if (options.length === 0) return cur;
              const pos =
                cur.selected?.nodeId === nodeId
                  ? (cur.selected.pos + 1) % options.length
                  : 0;
              return { ...cur, selected: { nodeId, options, pos } };
            });
          }
          return;
        }
        // A click only does something while placing a building — and only on a
        // highlighted (legal) target. Otherwise it's a no-op (other actions are
        // driven from the action bar, not a bare board click). The click just
        // SELECTS the node (highlighted, pending Confirm); the construct action
        // is sent by `confirmBuild` when the Confirm button is pressed.
        const bf = buildFlowRef.current;
        if (bf?.stage === 'place') {
          // Build-time aim target pick (Barracks): the aim sub-flow claims the
          // click while it's choosing a target.
          if (bf.aim?.stage === 'target' && bf.aim.allowed.has(nodeId)) {
            setBuildFlow((cur) =>
              cur?.stage === 'place' && cur.aim?.stage === 'target'
                ? {
                    ...cur,
                    aim: { stage: 'done', mode: cur.aim.mode, targetId: nodeId },
                  }
                : cur,
            );
          } else if (bf.allowed.has(nodeId)) {
            // Moving the building resets any aim — targets depend on the spot.
            setBuildFlow((cur) =>
              cur?.stage === 'place'
                ? { ...cur, selected: nodeId, aim: undefined }
                : cur,
            );
          }
        }
      }, (a, b) => {
        const room = roomRef.current;
        if (!room) return;
        // Edge tap during an aim's target pick: tap a highlighted bridge span
        // to aim at THE BRIDGE (targetEdge) — attack or defend, same as a
        // building. Defense reactions send live; on-turn aims respect planning.
        const af = aimFlowRef.current;
        if (af) {
          const key = edgeKey(a, b);
          if (af.stage === 'target' && af.allowedBridges.has(key)) {
            const aim = {
              type: 'aim_barracks',
              nodeId: af.barracksId,
              targetId: -1,
              targetEdge: key,
              mode: af.mode,
            } as const;
            if (af.defense) room.send(ClientMessage.Action, aim);
            else sendGameActionRef.current(aim);
            setAimFlow(null);
          }
          return;
        }
        // Edge tap during the construct-bridge flow: build a bridge on that path
        // if it's one of the highlighted (eligible) paths.
        const bf = bridgeFlowRef.current;
        if (!bf || bf.pending || !bf.allowedKeys.has(edgeKey(a, b))) return;
        if (bf.wildcard) {
          // Wildcard cost: park the path; the bar asks which resource pays.
          setBridgeFlow((cur) =>
            cur ? { ...cur, pending: { from: a, to: b } } : cur,
          );
          return;
        }
        sendGameActionRef.current(
          bf.cardId
            ? { type: 'free_bridge', cardId: bf.cardId, from: a, to: b }
            : { type: 'construct_bridge', from: a, to: b },
        );
        setBridgeFlow(null);
      }, (nodeId) => {
        // Board hover: over a node of an implemented fortress (anyone's), show
        // the same fortress-card detail popover the roster tiles use, anchored
        // at the node. The popup reflects the OWNER's live card state — tokens
        // and exhausted/disabled — resolved fresh at render time.
        const handlers = selectionHandlersRef.current;
        const current = boardHoverRef.current;
        // While a card effect is picking board nodes — or the Implement flow is
        // placing a fortress — the fortress preview is suppressed: its popover
        // shares the gold-ring highlight channel with those selections, so a
        // hover would wipe the player's picks.
        const ef = effectsFlowRef.current;
        if (
          ef?.stage === 'pickScout' ||
          ef?.stage === 'replicate' ||
          ef?.stage === 'untap' ||
          ef?.stage === 'moveFortress' ||
          ef?.stage === 'sellBuilding' ||
          ef?.stage === 'designateTarget' ||
          ef?.stage === 'moveBuildingFrom' ||
          ef?.stage === 'moveBuildingTo' ||
          ef?.stage === 'dropResources' ||
          implementFlowRef.current?.stage === 'place'
        ) {
          if (current && handlers) {
            boardHoverRef.current = null;
            handlers.onPreviewEnd(current);
          }
          return;
        }
        if (nodeId !== null) {
          const fOwner = nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '';
          if (fOwner !== '') {
            const owner = playersRef.current.find((p) => p.id === fOwner);
            const idx =
              owner?.fortressCards.findIndex((fc) =>
                fc.nodeIds.includes(nodeId),
              ) ?? -1;
            const fc = idx >= 0 ? owner?.fortressCards[idx] : undefined;
            const card = fc ? cardsByIdRef.current.get(fc.cardId) : undefined;
            const anchor = boardAnchorRef.current;
            const pos = gameRef.current?.nodeScreenPosition(nodeId);
            if (owner && fc && card && anchor && pos && handlers) {
              anchor.style.left = `${pos.x}px`;
              anchor.style.top = `${pos.y}px`;
              const target: SelectionTarget = {
                kind: 'fortressCard',
                card,
                nodeIds: [...fc.nodeIds],
                tokens: fc.tokens,
                stored: fc.stored,
                disabled: fc.disabled,
                choice: fc.choice,
                instanceKey: `${owner.id}:${idx}`,
                fromBoard: true,
              };
              boardHoverRef.current = target;
              handlers.onPreview(target, anchor);
              return;
            }
          }
        }
        // Left the node (or entered a non-fortress one): drop the board-driven
        // preview, with the usual grace period to reach the popover itself.
        if (current && handlers) {
          boardHoverRef.current = null;
          handlers.onPreviewEnd(current);
        }
      });
      if (disposed) {
        game.destroy();
        game = null;
        return;
      }
      gameRef.current = game;

      /** Reconnect ladder for an unexpected socket close. First the
       * reconnection token (the seat is held under the server's grace
       * period), with backoff for transient failures — Cloud Run may be
       * cold-starting a fresh instance. A definitive rejection means the
       * token is dead; re-resolve the match to whatever room now hosts it
       * (reviving one if the old room died with the instance) and take one
       * authed join — the returning-participant gate re-seats by account —
       * before giving up to the hub. */
      const recover = async (
        lost: Room<GameState>,
        skipToken = false,
      ): Promise<void> => {
        setConnectionLost(true);
        const delays = [1000, 2000, 4000, 8000, 15000];
        for (
          let attempt = 0;
          !skipToken && attempt <= delays.length;
          attempt++
        ) {
          if (attempt > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, delays[attempt - 1]),
            );
            if (disposed) return;
          }
          try {
            const fresh = await reconnectGame(lost.reconnectionToken);
            if (disposed) {
              void fresh.leave();
              return;
            }
            attach(fresh);
            setConnectionLost(false);
            return;
          } catch (err) {
            // The matchmaker rejected the token outright (room disposed /
            // not found, token invalid or expired) — retrying it is
            // pointless; fall through to joinById. Anything else (network,
            // cold start, the ws-stage "seat reservation expired" — a plain
            // Error with no code) is transient: keep the ladder going.
            const code = (err as { code?: number }).code;
            if (
              code === ErrorCode.MATCHMAKE_INVALID_ROOM_ID ||
              code === ErrorCode.MATCHMAKE_EXPIRED
            ) {
              break;
            }
          }
        }
        try {
          const { roomId } = await matchRoom(gameId);
          const fresh = await joinGameById(roomId, { spectate });
          if (disposed) {
            void fresh.leave();
            return;
          }
          attach(fresh);
          setConnectionLost(false);
          return;
        } catch {
          // Match gone for good (or we're not allowed back in).
        }
        if (disposed) return;
        setConnectionLost(false);
        void removeActiveGameToken(gameId);
        setActiveMatch(null);
      };

      // Wire a (re)joined room: message handlers, state watchers, and the
      // unexpected-close recovery hook. Called for the initial join and again
      // for every recovered connection — each fresh Room instance needs its
      // own wiring.
      const attach = (r: Room<GameState>): void => {
        room = r;
        roomRef.current = r;
        // Our seat key. Seats are keyed by ACCOUNT, so it is our own user id
        // (a guest's is the server-issued `guest:<session>`); a spectator has
        // no seat and goes by session id, as its cursor and chat entries do.
        const self = spectate
          ? r.sessionId
          : (selfUserId ?? `guest:${r.sessionId}`);
        setSelfId(self);
        selfIdRef.current = self;
        // Clear the previous room's chat: the chat watcher only fires on
        // add/remove, so joining a new (empty) match emits nothing and would
        // otherwise leave the prior match's messages on screen. A room that
        // DOES have chat re-emits it as its state decodes (onAdd fires for
        // existing entries), so this is safe on reconnect too.
        setChat([]);
        // Chat ids restart per room, so the ping cursor must too.
        chatPingSeenRef.current = 0;
        if (import.meta.env.DEV) {
          // Dev-only handle for E2E drivers (canvas state isn't reachable from
          // the DOM). Stripped from production builds by the DEV guard.
          (
            window as unknown as { __fortressRoom?: Room<GameState> }
          ).__fortressRoom = r;
        }

        // Cloud Run force-closes every WebSocket at its request timeout, and
        // instances go away on scale-to-zero and redeploys — an unexpected
        // close is routine, not an error. Recover in place; the server holds
        // this seat for the reconnect grace period. `disposed` covers our own
        // deliberate leave (cleanup), and a finished match just wound down.
        // 4000 = colyseus CloseCode.CONSENTED (not re-exported from the
        // package root): a deliberate close — our own leave racing the effect
        // cleanup, or the server's graceful-shutdown force-close. The room is
        // going away, so skip the ladder; the hub offers Resume if the match
        // survived.
        r.onLeave((code) => {
          if (disposed || roomRef.current !== r) return;
          if (phaseRef.current === 'ended') return;
          if (code === 4000) {
            setActiveMatch(null);
            return;
          }
          // The server handed its rooms to a newer instance (a deploy): the
          // match lives on there, but our token only ever reconnects to THIS
          // server. Skip straight to re-resolving the match by gameId.
          void recover(r, code === WS_CLOSE_SERVER_SUPERSEDED);
        });

        // The room sends its pinned config snapshot once on join.
        r.onMessage(ServerMessage.Config, (config: GameConfig) =>
          setGameConfig(config),
        );

        // The lobby creator closed the lobby: the room is about to disconnect
        // us (CONSENTED close → back to the hub via onLeave). Drop the stored
        // token — the seat is gone — and surface a one-shot notice on the hub.
        r.onMessage(ServerMessage.LobbyClosed, () => {
          void removeActiveGameToken(gameId);
          setLobbyClosed(true);
        });

        // Our account joined from another device — that connection now holds
        // the seat and this one is about to close (CONSENTED → back to the
        // hub via onLeave). Drop the stored token (the seat's reservation
        // moved with it) and surface a one-shot notice on the hub.
        r.onMessage(ServerMessage.SessionSuperseded, () => {
          void removeActiveGameToken(gameId);
          setSessionSuperseded(true);
        });

        // Another player took an action: show a transient popup anchored to them.
        // Ignore our own actions — the popup is for what *others* do on their turn.
        r.onMessage(
          ServerMessage.ActionPerformed,
          (notice: ActionNotice) => {
            if (!notice || notice.playerId === selfIdRef.current) return;
            pushNotice(notice);
          },
        );

        // It just became our turn — the server targets this only at the active
        // player. Pop the top-screen toast, and fire an OS notification when the
        // tab is in the background (and the player has granted permission).
        r.onMessage(ServerMessage.YourTurn, () => {
          audio.play(Math.random() < 0.02 ? 'turn' : 'turn2');
          setTurnToast(true);
          if (turnToastTimer.current !== undefined)
            window.clearTimeout(turnToastTimer.current);
          turnToastTimer.current = window.setTimeout(
            () => setTurnToast(false),
            TURN_TOAST_TTL,
          );
          if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted' &&
            document.hidden
          ) {
            new Notification("It's Your Turn", {
              body: `${DEFAULT_TITLE} — it's your move.`,
            });
          }
        });

        // Our queued-move auto-play stopped: a queued move was stale (the
        // board changed since it was planned), a card effect needs live input,
        // or the plan simply ran out before the turn's actions did while we're
        // here to spend them. The game is paused on our turn — surface why.
        r.onMessage(
          ServerMessage.QueueHalted,
          (payload: { label?: string; reason?: string; held?: boolean }) => {
            setQueueHalt({
              label: payload?.label ?? '',
              reason: payload?.reason ?? '',
              held: payload?.held === true,
            });
            if (queueHaltTimer.current !== undefined)
              window.clearTimeout(queueHaltTimer.current);
            queueHaltTimer.current = window.setTimeout(
              () => setQueueHalt(null),
              QUEUE_HALT_TTL,
            );
          },
        );

        // The creator rolled the match back. Everyone gets this, the creator
        // included: the board just changed under every client at once, and
        // only a message explains why. (The permanent record is the
        // "Restored to …" row the synced move history carries.)
        r.onMessage(
          ServerMessage.Restored,
          (payload: {
            byName?: string;
            round?: number;
            turn?: number;
            dropped?: number;
          }) => {
            setRestored({
              byName: payload?.byName ?? '',
              round: payload?.round ?? 0,
              turn: payload?.turn ?? 0,
              dropped: payload?.dropped ?? 0,
            });
            if (restoredTimer.current !== undefined)
              window.clearTimeout(restoredTimer.current);
            restoredTimer.current = window.setTimeout(
              () => setRestored(null),
              RESTORED_TTL,
            );
          },
        );

        // A defense window just opened on us: buildings of ours fall when the
        // ending turn resolves. Pan to the first at-risk node, and raise an OS
        // notification when the tab is backgrounded (it isn't our turn, so the
        // player may well be elsewhere). The persistent banner + aim flow are
        // driven off the synced defense state, not this one-shot cue.
        // Barracks interaction cues (tap / destroyed / defended / starved) —
        // fire-once board animations; the synced state is the truth.
        r.onMessage(ServerMessage.BoardFx, (payload: { fx?: BoardFx[] }) => {
          if (payload?.fx) gameRef.current?.playBoardFx(payload.fx);
        });

        r.onMessage(
          ServerMessage.UnderAttack,
          (payload: { nodeIds?: number[] }) => {
            const first = payload?.nodeIds?.[0];
            if (first !== undefined) gameRef.current?.focusNode(first);
            if (
              typeof Notification !== 'undefined' &&
              Notification.permission === 'granted' &&
              document.hidden
            ) {
              new Notification('Under Attack!', {
                body: `${DEFAULT_TITLE} — aim defenders before the turn resolves.`,
              });
            }
          },
        );

        // The game-over result is driven off synced state (`onGameResult`
        // below), not the one-shot GameOver broadcast — the synced fields also
        // reach reconnecting players and late-joining spectators. Registered
        // as a no-op so colyseus.js doesn't warn about the unhandled type.
        r.onMessage(ServerMessage.GameOver, () => {});

        // A touch client tapped a shared surface (mobile's stand-in for the
        // hover cursor): play a one-shot ripple where it landed — in the Pixi
        // layer for board taps, as a DOM ripple over a sidebar/lobby panel.
        r.onMessage(
          ServerMessage.CursorTap,
          (payload: {
            id?: string;
            area?: string;
            x?: number;
            y?: number;
            target?: string;
            tx?: number;
            ty?: number;
          }) => {
            const id = payload?.id ?? '';
            if (!id || id === selfIdRef.current) return;
            const player = playersRef.current.find((p) => p.id === id);
            const spec = player
              ? undefined
              : spectatorsRef.current.find((s) => s.id === id);
            if (!player && !spec) return;
            const colorNum = player
              ? (PLAYER_COLORS[player.seat % PLAYER_COLORS.length] ?? 0xffffff)
              : SPECTATOR_CURSOR_COLOR;
            const x = Number(payload?.x) || 0;
            const y = Number(payload?.y) || 0;
            if (payload?.area === 'board') {
              gameRef.current?.showRemoteTap({ x, y, color: colorNum });
              return;
            }
            const ripple: TapRippleView = {
              key: `${id}:${++tapSeqRef.current}`,
              area: payload?.area ?? '',
              x,
              y,
              target: payload?.target ?? '',
              tx: unitOrCenter(payload?.tx),
              ty: unitOrCenter(payload?.ty),
              color: `#${colorNum.toString(16).padStart(6, '0')}`,
            };
            setTapRipples((cur) => [...cur, ripple]);
            window.setTimeout(
              () => setTapRipples((cur) => cur.filter((t) => t !== ripple)),
              1100,
            );
          },
        );

        // Another client dropped an emoji reaction — play it at the same
        // spot (board point, or panel element via the target key). Stale
        // payloads (timestamp older than a couple seconds) are dropped: the
        // server no longer buffers these for reconnects, but a delayed flush
        // must never dump a horn backlog on a returning player.
        r.onMessage(
          ServerMessage.CursorEmoji,
          (payload: {
            id?: string;
            area?: string;
            x?: number;
            y?: number;
            target?: string;
            tx?: number;
            ty?: number;
            emoji?: string;
            at?: number;
          }) => {
            const id = payload?.id ?? '';
            if (!id || id === selfIdRef.current) return;
            const at = typeof payload?.at === 'number' ? payload.at : 0;
            if (at > 0 && serverNow() - at > 2500) return;
            const emoji =
              typeof payload?.emoji === 'string' ? payload.emoji : '';
            if (!emoji) return;
            const area = payload?.area ?? '';
            const x = Number(payload?.x) || 0;
            const y = Number(payload?.y) || 0;
            const target = payload?.target ?? '';
            spawnEmoji({
              area,
              x,
              y,
              target,
              tx: unitOrCenter(payload?.tx),
              ty: unitOrCenter(payload?.ty),
              emoji,
            });
            // A reaction dropped on YOU — your roster row in the left
            // sidebar, or your home base on the board — can carry an effect:
            // the postal horn is a "your turn!" nudge (sound the turn
            // notification; skipped while the tab is hidden so suspended
            // audio doesn't stack into a backlog the moment the player
            // returns), Face in Clouds fogs this screen's edges. An active
            // emoji shield (🛡️ dropped on the board) mutes every such
            // effect — the plain emoji pop above still plays regardless.
            const landsOnMe =
              area === 'roster'
                ? target === `player:${selfIdRef.current}`
                : area === 'board' &&
                  selfHomeNodeRef.current >= 0 &&
                  gameRef.current?.nodeAtBoardPoint(x, y) ===
                    selfHomeNodeRef.current;
            if (landsOnMe && Date.now() >= shieldUntilRef.current) {
              if (sameEmoji(emoji, HONK_EMOJI)) {
                if (!document.hidden) {
                  audio.play(Math.random() < 0.02 ? 'turn' : 'turn2');
                }
              } else if (sameEmoji(emoji, FOG_EMOJI)) {
                triggerFog();
              }
            }
          },
        );

        watchState(r, {
          onNode: (node, seatOf) => {
            // Track ownership/building/resource for the build-range + cost calc.
            nodeInfoRef.current.set(node.id, {
              ownerId: node.ownerId,
              building: node.building,
              fortressOwnerId: node.fortressOwnerId,
              resource: node.resource,
              permanence: node.permanence,
              aimTargetId: node.aimTargetId,
              aimMode: node.aimMode,
              aimEdge: node.aimEdge,
              indestructible: node.indestructible,
              bonusResource: node.bonusResource ?? '',
              bonusAmount: node.bonusAmount ?? 0,
            });
            const colorOf = (playerId: string): number | null =>
              playerId !== ''
                ? (PLAYER_COLORS[
                    (seatOf.get(playerId) ?? 0) % PLAYER_COLORS.length
                  ] ?? 0xffffff)
                : null;
            // A captured piece (Church of Penguin) plays as its
            // captor's for every rule, but keeps its ORIGINAL owner's colour on
            // the board — capturedFromId is colour-only. Honoured only while a
            // building actually stands on the node and while that seat is still
            // known: a leftover id would otherwise paint a phantom token on an
            // empty node, or miss the seat map and fall back to the FIRST
            // seat's colour, reading as an unrelated player's piece.
            const capturedColor =
              node.building !== '' && seatOf.has(node.capturedFromId)
                ? colorOf(node.capturedFromId)
                : null;
            game?.setNodeState(node.id, {
              ownerColor: capturedColor ?? colorOf(node.ownerId),
              building: node.building,
              resource: node.resource,
              permanence: node.permanence,
              bonusResource: node.bonusResource ?? '',
              disconnected: node.disconnected,
              isHomeBase: node.isHomeBase,
              fortressColor: colorOf(node.fortressOwnerId),
              indestructible: node.indestructible,
            });
            setBoardVersion((v) => v + 1);
          },
          onTurn: setTurn,
          onRound: setRound,
          onTutorial: setTutorial,
          onActionsRemaining: setActionsRemaining,
          onUndoLabel: setUndoLabel,
          onFortressesImplemented: setFortressesImplemented,
          onStartingPlayerCount: setStartingPlayerCount,
          onLastStand: setLastStand,
          onCurrentPlayer: (id) => {
            setCurrentPlayerId(id);
            currentPlayerRef.current = id;
          },
          onFirstPlayer: setFirstPlayerId,
          onRoundStartCycle: setRoundCyclerId,
          onPhase: (p) => {
            setPhase(p);
            phaseRef.current = p;
            // Reconnect bookkeeping: while the match is live, keep this
            // room's reconnection token stored so the lobby hub can offer
            // "Reconnect" after a refresh (which rooms still qualify is the
            // server's call, via /games/mine); a finished match drops its
            // token. Runs again after a reconnect, re-storing the freshly
            // minted token.
            const r = roomRef.current;
            if (!r) return;
            // A spectator holds no seat, so there is nothing to reconnect to —
            // never advertise their room on the hub's guest reconnect list.
            if (spectate) return;
            if (p === 'setup' || p === 'playing') {
              void setActiveGameToken(gameId, r.reconnectionToken);
            } else if (p === 'ended') {
              void removeActiveGameToken(gameId);
            }
          },
          onLobbyInfo: (info) => {
            setLobbyInfo(info);
            // A lobby holds this device's seat while away (once readied) —
            // keep its reconnection token stored from the moment we're
            // seated, so a closed tab can find its way back (guests
            // included; the setup/playing store in `onPhase` only starts at
            // match start).
            const r = roomRef.current;
            if (r && !spectate && r.state.phase === 'lobby') {
              void setActiveGameToken(gameId, r.reconnectionToken);
            }
          },
          onPlayers: (roster) => {
            setPlayers(roster);
            playersRef.current = roster;
          },
          onMarkets: setMarkets,
          onPlacements: (placements) => game?.applyPlacements(placements),
          onSevered: (severedEdges) => {
            severedRef.current = new Set(severedEdges);
            game?.setSeveredEdges(severedEdges);
          },
          onBridges: (bridges) => {
            bridgesRef.current = new Set(bridges);
            game?.setBridges(bridges);
            setBoardVersion((v) => v + 1);
          },
          onHistory: setHistory,
          onRestorePoints: setRestorePoints,
          onChat: (list) => {
            setChat(list);
            // Ping once per batch when someone ELSE's message arrives. The
            // id cursor keeps repeated full-list emits from re-ringing; the
            // recency check keeps a (re)join's backlog re-decode silent —
            // stored messages are old, only just-sent ones ring. Skipped
            // while the tab is hidden (matching the horn: suspended audio
            // must not stack into a backlog), but the cursor still advances,
            // so returning to the tab stays silent.
            const seen = chatPingSeenRef.current;
            const now = serverNow();
            const hasNew = list.some(
              (m) =>
                m.id > seen &&
                m.senderId !== selfIdRef.current &&
                now - m.at < 10_000,
            );
            chatPingSeenRef.current = Math.max(seen, list.at(-1)?.id ?? 0);
            if (hasNew && !document.hidden) audio.play('chat');
          },
          onDefense: setDefense,
          onUpkeep: setUpkeepEntries,
          onSteals: setStealEntries,
          onSpectators: (list) => {
            spectatorsRef.current = list;
            setSpectators(list);
          },
          onCursors: (list) => {
            liveCursorsRef.current = list;
            // Board cursors go straight to Pixi (no React render at 5Hz)…
            pushBoardCursors();
            // …while sidebar cursors re-render only when they actually change.
            const sidebar = list.filter(
              (c) => c.area !== 'board' && c.id !== selfIdRef.current,
            );
            setSidebarCursors((prev) =>
              prev.length === sidebar.length &&
              prev.every((p, i) => {
                const n = sidebar[i]!;
                return (
                  p.id === n.id &&
                  p.area === n.area &&
                  p.target === n.target &&
                  p.x === n.x &&
                  p.y === n.y &&
                  p.tx === n.tx &&
                  p.ty === n.ty
                );
              })
                ? prev
                : sidebar,
            );
          },
          onGameResult: ({ winnerId, winCardId }) =>
            setGameOver({ winnerId: winnerId || null, winCardId }),
        });
      };

      try {
        let joined: Room<GameState> | null = null;
        if (reconnectionToken) {
          joined = await reconnectGame(reconnectionToken);
        } else {
          // This device may hold a seat in this match from a previous visit
          // (a lobby seat kept ready, or a live match) — prefer its stored
          // reconnection token so reopening the URL reclaims that seat
          // instead of taking a second one (guests have no account for the
          // server-side returning gate). A dead token falls back to a fresh
          // join via the match's current room.
          const stored = spectate
            ? undefined
            : (await getActiveGameTokens())[gameId];
          if (stored) {
            try {
              joined = await reconnectGame(stored);
            } catch {
              void removeActiveGameToken(gameId);
            }
          }
          if (!joined) {
            // The match identifier is all the URL carries — resolve it to
            // the live Colyseus room hosting it (reviving one when needed).
            const { roomId } = await matchRoom(gameId);
            joined = await joinGameById(roomId, { spectate });
          }
        }
        if (disposed) {
          void joined.leave();
          return;
        }
        attach(joined);
      } catch (err) {
        console.error('Failed to join game:', err);
        // A failed reconnect means the seat is gone (grace lapsed / room
        // disposed) — drop this match's token so the hub stops offering it.
        if (reconnectionToken) {
          void removeActiveGameToken(gameId);
          setReconnectFailed(true);
        }
        setActiveMatch(null);
      }
    }

    void boot();

    return () => {
      disposed = true;
      void room?.leave();
      game?.destroy();
      roomRef.current = null;
      gameRef.current = null;
      room = null;
      game = null;
    };
  }, [activeMatch]);

  /** Classify a pointer position into a `CursorSample` — which shared surface
   * it's over (board canvas / market sidebar / roster sidebar / lobby card)
   * and where, in that surface's shared coordinate scheme. Board points are
   * camera-independent board-plane coords (same tile spot for everyone);
   * panels get normalised coords plus the hovered element's
   * `data-cursor-target` key and the position within it. Shared by the cursor
   * stream, touch taps and the emoji picker. */
  const samplePointer = useCallback(
    (clientX: number, clientY: number, el: Element | null): CursorSample => {
      const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
      const norm = (rect: DOMRect) => ({
        x: clamp01(rect.width > 0 ? (clientX - rect.left) / rect.width : 0),
        y: clamp01(rect.height > 0 ? (clientY - rect.top) / rect.height : 0),
      });
      // Keyed element under the pointer + the pointer's position WITHIN it,
      // so receivers can put the cursor on the exact spot of their own copy
      // of that card/row (their element sizes differ from ours).
      const keyedEl = el?.closest('[data-cursor-target]') ?? null;
      const target = keyedEl?.getAttribute('data-cursor-target') ?? '';
      const within = keyedEl
        ? norm(keyedEl.getBoundingClientRect())
        : { x: 0.5, y: 0.5 };

      const sidebar = el?.closest('.sidebar-right, .sidebar-left');
      if (sidebar) {
        const p = norm(sidebar.getBoundingClientRect());
        return {
          area: sidebar.classList.contains('sidebar-right')
            ? 'market'
            : 'roster',
          x: p.x,
          y: p.y,
          target,
          tx: within.x,
          ty: within.y,
        };
      }
      // Pre-game ready-up screen: normalise against the lobby card itself
      // (the panel every client sees), not the full-screen wrapper, so the
      // fallback position maps sensibly across window sizes.
      const lobby = el?.closest('.lobby');
      if (lobby) {
        const card = lobby.querySelector('.lobby-card') ?? lobby;
        const p = norm(card.getBoundingClientRect());
        return {
          area: 'lobby',
          x: p.x,
          y: p.y,
          target,
          tx: within.x,
          ty: within.y,
        };
      }
      if (el?.closest('.stage')) {
        const p = gameRef.current?.boardPointFromClient(clientX, clientY);
        if (p) {
          return { area: 'board', x: p.x, y: p.y, target: '', tx: 0.5, ty: 0.5 };
        }
      }
      // Over neither shared surface (popovers, overlays, banners) — hidden.
      return HIDDEN_CURSOR;
    },
    [],
  );

  // Broadcast this client's own pointer to the room (see `GameState.cursors`).
  // One delegated window listener classifies where the pointer is (see
  // `samplePointer`) and sends trailing-throttled updates. The pointer hides
  // (one `area: ""` message) on window blur / tab hide / leaving the window /
  // 20s idle.
  useEffect(() => {
    if (!activeMatch) return;
    const SEND_MS = 100; // self-throttle; the room patches every 200ms
    const IDLE_HIDE_MS = 20_000;
    const TAP_MIN_MS = 150; // local guard for touch-tap broadcasts

    let sentRoom: Room<GameState> | null = null;
    let lastSent: CursorSample | null = null;
    let pending: { x: number; y: number; el: Element | null } | null = null;
    let throttleTimer: number | undefined;
    let idleTimer: number | undefined;

    const send = (c: CursorSample) => {
      const room = roomRef.current;
      if (!room) return;
      if (room !== sentRoom) {
        // Fresh connection (initial join or in-place reconnect): resend even
        // an unchanged sample — the new session starts with no cursor entry.
        sentRoom = room;
        lastSent = null;
      }
      if (
        lastSent &&
        lastSent.area === c.area &&
        lastSent.target === c.target &&
        Math.abs(lastSent.x - c.x) < 1e-3 &&
        Math.abs(lastSent.y - c.y) < 1e-3
      ) {
        return;
      }
      // Nothing shown yet and nothing to show — skip the no-op hide.
      if (lastSent === null && c.area === '') return;
      try {
        room.send(ClientMessage.Cursor, c);
        lastSent = c;
      } catch {
        // Socket mid-close during a reconnect — the next move retries.
      }
    };

    const flushPending = () => {
      throttleTimer = undefined;
      if (!pending) return;
      const { x, y, el } = pending;
      pending = null;
      send(samplePointer(x, y, el));
    };

    const hide = () => {
      pending = null;
      if (throttleTimer !== undefined) {
        window.clearTimeout(throttleTimer);
        throttleTimer = undefined;
      }
      if (idleTimer !== undefined) {
        window.clearTimeout(idleTimer);
        idleTimer = undefined;
      }
      send(HIDDEN_CURSOR);
    };

    const onPointerMove = (e: PointerEvent) => {
      // A touch "move" is a pan/pinch gesture, not pointing — touch presence
      // is broadcast as one-shot taps (below) instead of a streamed cursor.
      if (e.pointerType === 'touch') return;
      pending = {
        x: e.clientX,
        y: e.clientY,
        el: e.target instanceof Element ? e.target : null,
      };
      if (throttleTimer === undefined) {
        throttleTimer = window.setTimeout(flushPending, SEND_MS);
      }
      if (idleTimer !== undefined) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(hide, IDLE_HIDE_MS);
    };
    // Touch tap → a one-shot ripple on everyone else's screen (mobile has no
    // hover cursor to stream). Primary contact only, so a two-finger
    // pinch/rotate doesn't fire twice.
    let lastTapAt = 0;
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !e.isPrimary) return;
      const now = performance.now();
      if (now - lastTapAt < TAP_MIN_MS) return;
      const s = samplePointer(
        e.clientX,
        e.clientY,
        e.target instanceof Element ? e.target : null,
      );
      if (s.area === '') return;
      lastTapAt = now;
      try {
        roomRef.current?.send(ClientMessage.CursorTap, s);
      } catch {
        /* socket mid-close — drop the tap */
      }
    };
    // Pointer left the window entirely (no relatedTarget to land on).
    const onPointerOut = (e: PointerEvent) => {
      if (e.relatedTarget === null) hide();
    };
    const onVisibility = () => {
      if (document.hidden) hide();
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerout', onPointerOut);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerout', onPointerOut);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibility);
      hide(); // best-effort: clear our cursor before leaving the room
    };
  }, [activeMatch, samplePointer]);

  // Emoji reaction trigger: right-click (desktop) or long-press (touch) on
  // any shared surface opens the picker at that point. The browser context
  // menu is suppressed only when the picker actually opens, so right-clicking
  // the page chrome still behaves normally. (On Android a long-press also
  // fires `contextmenu` — both paths open the same picker, harmlessly.)
  useEffect(() => {
    if (!activeMatch) return;
    const LONG_PRESS_MS = 500;
    const MOVE_TOL_PX = 10;
    let press: { id: number; x: number; y: number; timer: number } | null =
      null;

    const openAt = (
      clientX: number,
      clientY: number,
      el: Element | null,
    ): boolean => {
      const sample = samplePointer(clientX, clientY, el);
      if (sample.area === '') return false;
      setEmojiPicker({ screen: { x: clientX, y: clientY }, sample });
      return true;
    };

    const onContextMenu = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (openAt(e.clientX, e.clientY, el)) e.preventDefault();
    };
    const cancelPress = () => {
      if (press) {
        window.clearTimeout(press.timer);
        press = null;
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !e.isPrimary) return;
      cancelPress();
      const el = e.target instanceof Element ? e.target : null;
      const { clientX, clientY, pointerId } = e;
      press = {
        id: pointerId,
        x: clientX,
        y: clientY,
        timer: window.setTimeout(() => {
          press = null;
          openAt(clientX, clientY, el);
        }, LONG_PRESS_MS),
      };
    };
    // A drag/pan is not a long-press — cancel once the finger wanders.
    const onPointerMove = (e: PointerEvent) => {
      if (
        press &&
        e.pointerId === press.id &&
        Math.hypot(e.clientX - press.x, e.clientY - press.y) > MOVE_TOL_PX
      ) {
        cancelPress();
      }
    };
    const onPointerEnd = (e: PointerEvent) => {
      if (press && e.pointerId === press.id) cancelPress();
    };

    window.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', onPointerEnd, { passive: true });
    window.addEventListener('pointercancel', onPointerEnd, { passive: true });
    return () => {
      cancelPress();
      window.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
      setEmojiPicker(null);
    };
  }, [activeMatch, samplePointer]);

  const self = players.find((p) => p.id === selfId);
  /** This client's unresolved Barracks-upkeep choices, parsed from the synced
   * `"playerId|nodeId|foodCost"` entries. */
  const upkeepSelf = upkeepEntries
    .map((entry) => {
      const [playerId, nodeId, cost] = entry.split('|');
      return {
        playerId: playerId ?? '',
        nodeId: Number(nodeId),
        cost: Number(cost) || 0,
      };
    })
    .filter((e) => e.playerId === selfId);
  /** Watch-only session: this client joined via a Watch entry point. `self` is
   * undefined for spectators, so act-gated UI already stays hidden; this flag
   * drives the few explicit "you're spectating" touches. */
  const isSpectator = !!activeMatch?.spectate;
  /** Match-pinned fortress catalogue (falls back to code defaults pre-config). */
  const { byId: cardsById, byName: cardsByName } = useMemo(
    () => cardsFromConfig(gameConfig),
    [gameConfig],
  );
  /** Card behavior is catalogue DATA — resolve a card id to its effect config
   * within this match's catalogue (never keyed off the id in code). */
  const effectOf = useCallback<CardEffectLookup>(
    (id) => cardsById.get(id)?.effects,
    [cardsById],
  );
  /** What THIS player pays in Obsidian for a fortress card — the printed cost
   * less the discounts their own implemented cards grant (Inverse Molehill).
   * Feeds every cost display through `ObsidianCostProvider`, so a discounted
   * price always shows up beside the struck-through printed one. */
  const obsidianCostOf = useCallback<ObsidianCostLookup>(
    (card) =>
      self
        ? (fortressCostFor(
            self.fortressCards,
            card,
            effectOf,
            fortressesImplemented,
            startingPlayerCount || players.length,
          )[3] ?? 0)
        : (card.cost[3] ?? 0),
    [self, effectOf, fortressesImplemented, startingPlayerCount, players.length],
  );

  /** Mirror of the server's `blockedNodesFor`: opponent pieces a trace may
   * enter but not pass through. `bypass` is the flavor's
   * `*_through_obstructions` passive — note it exempts only BARRACKS from
   * `blockOpponentBuild` (matching `FortressRoom.blockedNodesFor`), while
   * opponent fortress nodes open up wholesale. */
  const blockedNodesForClient = useCallback(
    (playerId: string, bypass: boolean): Set<number> => {
      const blocked = new Set<number>();
      if (!gameConfig) return blocked;
      for (const [id, info] of nodeInfoRef.current) {
        const hostile = info.ownerId !== '' && info.ownerId !== playerId;
        if (
          hostile &&
          info.building !== '' &&
          blocksOpponentBuild(
            gameConfig,
            playersRef.current,
            info.ownerId,
            info.building,
          ) &&
          !(bypass && info.building === 'barracks')
        ) {
          blocked.add(id);
        }
        // Mirrors the server: a piece of this player's own that a Corrupt
        // Hermit conscripted into someone else's fortress is still theirs to
        // build from and trace through — only the fortress changed hands.
        if (
          !bypass &&
          info.fortressOwnerId !== '' &&
          info.fortressOwnerId !== playerId &&
          !(info.building !== '' && info.ownerId === playerId)
        ) {
          blocked.add(id);
        }
      }
      return blocked;
    },
    [gameConfig],
  );

  /** Mirror of the server's `freeEdgesFor`: the zero-cost edges a player's
   * range traces ride for free, or `undefined` when they have none. Bridgette
   * the Beauty's `free_range_over_bridges` makes every BUILT bridge on the
   * board — anyone's, over a chasm or not — cost nothing from a range budget,
   * so each one on a route is worth +1 effective range. */
  const freeEdgesForClient = useCallback(
    (player: LobbyPlayer | null): ReadonlySet<string> | undefined => {
      if (!player || bridgesRef.current.size === 0) return undefined;
      return anyCardHasPassive(
        player.fortressCards,
        'free_range_over_bridges',
        (id) => cardsByIdRef.current.get(id)?.effects,
      )
        ? bridgesRef.current
        : undefined;
    },
    [],
  );

  /** Mirror of the server's `aimProfile` — THE client's resolved geometry for
   * one player's Barracks in `mode`. Range comes from their config (widened by
   * `attack_range` / `defend_range`), and both tracing relaxations are already
   * folded into the returned sets. Every client-side attack/defend range trace
   * must start here so a highlight can never disagree with what the server
   * will accept. */
  const aimProfileFor = useCallback(
    (
      player: LobbyPlayer,
      mode: AimMode,
    ): {
      range: number;
      blocked: Set<number>;
      severed: ReadonlySet<string>;
      freeEdges: ReadonlySet<string> | undefined;
    } => {
      if (!gameConfig) {
        return {
          range: 0,
          blocked: new Set(),
          severed: new Set(),
          freeEdges: undefined,
        };
      }
      const cfg = resolveBuilding(gameConfig, player, 'barracks');
      const lookup = (id: string) => cardsByIdRef.current.get(id)?.effects;
      const bypass = anyCardHasPassive(
        player.fortressCards,
        mode === 'attack'
          ? 'attack_through_obstructions'
          : 'defend_through_obstructions',
        lookup,
      );
      // Telescopic Arm / Porkypine Neck: range traces across bridge gaps.
      const overGaps = anyCardHasPassive(
        player.fortressCards,
        mode === 'attack' ? 'attack_across_gaps' : 'defend_across_gaps',
        lookup,
      );
      return {
        range: (mode === 'attack' ? cfg.range.attack : cfg.range.defend) ?? 0,
        blocked: blockedNodesForClient(player.id, bypass),
        severed: overGaps ? new Set<string>() : severedRef.current,
        freeEdges: freeEdgesForClient(player),
      };
    },
    [gameConfig, blockedNodesForClient, freeEdgesForClient],
  );

  /** This client's unresolved steal choices, parsed from the synced
   * `pendingSteals` entries. The attacker picks what to take — unless the
   * victim holds a `victim_picks_steal` fortress (Porkypine Neck, catalogue
   * "14"+) or the entry itself pins the pick to them (Space Cheese Assassin's
   * decoy theft), in which case the VICTIM picks what to give. */
  const stealSelf = stealEntries
    .map((raw) => {
      const entry = parseStealEntry(raw);
      const victim = players.find((p) => p.id === entry.victimId);
      const victimPicks =
        entry.victimPicks ||
        (victim
          ? anyCardHasPassive(victim.fortressCards, 'victim_picks_steal', effectOf)
          : false);
      return { ...entry, victimPicks };
    })
    .filter((e) => (e.victimPicks ? e.victimId : e.attackerId) === selfId);
  const toggleReady = () => {
    roomRef.current?.send(ClientMessage.Ready, { ready: !self?.ready });
  };
  /** Lobby creator: close the lobby for everyone (server re-checks who asks). */
  const sendKillLobby = () => {
    roomRef.current?.send(ClientMessage.KillLobby);
  };
  /** Dev-only force-start (server enforces `devMode`); no payload. */
  const sendAutoInit = () => {
    roomRef.current?.send(ClientMessage.AutoInit);
  };
  /** Whether this client hosts the lobby. `hostUserId` is an account id, and
   * so is our seat key `selfId` — for a guest under AUTH_DISABLED that is the
   * server-issued `guest:<sessionId>`, which `selfId` already carries. */
  const isHost =
    lobbyInfo.hostUserId !== '' &&
    (lobbyInfo.hostUserId === selfUserId || lobbyInfo.hostUserId === selfId);
  /** Whether this client CREATED the match — the rollback authority. Unlike
   * `isHost` this survives the lobby (the server re-seeds it on resume), and
   * it never moves to a promoted host: the creator alone may restore. Guest
   * creators are identified by session, so their authority lapses when they
   * reconnect under a new one — the server is the judge either way. */
  const isCreator =
    lobbyInfo.creatorUserId !== '' &&
    (lobbyInfo.creatorUserId === selfUserId ||
      lobbyInfo.creatorUserId === selfId);
  /** Lobby host: make the match ranked in `pool`, or unranked with `null`.
   * The server re-checks host + phase. */
  const sendRankedSettings = (pool: SkillPool | null) => {
    roomRef.current?.send(ClientMessage.RankedSettings, {
      ranked: pool !== null,
      poolId: pool?.poolId ?? '',
      poolName: pool?.name ?? '',
    });
  };
  /** Lobby host: set the max minutes per move (0 = none). The server
   * re-checks host + phase and re-sends the config with the new limit. */
  const sendTurnTimeLimit = (minutes: number) => {
    roomRef.current?.send(ClientMessage.TurnTimeLimit, { minutes });
  };

  // Read the server's dev-mode flag once (memoised fetch) to decide whether to
  // surface the lobby Auto-Initialize button. The server still gates the command.
  useEffect(() => {
    getAppState()
      .then((s) => setDevMode(s.devMode))
      .catch(() => {});
  }, []);

  // During setup, highlight the legal home-base nodes only while it's my turn to
  // place and I haven't placed yet; drop any pending pick once that's no longer so.
  const myHomePlaced = !!self?.homePlaced;
  useEffect(() => {
    const placingHome =
      phase === 'setup' && currentPlayerId === selfId && !myHomePlaced;
    gameRef.current?.setHomePlacementActive(placingHome);
    if (!placingHome) setSelectedHomeNode(null);
  }, [phase, currentPlayerId, selfId, myHomePlaced]);

  // Reflect the tentatively-picked home node on the board.
  useEffect(() => {
    gameRef.current?.setHomeSelection(selectedHomeNode);
  }, [selectedHomeNode]);

  const confirmHomeBase = useCallback((nodeId: number) => {
    roomRef.current?.send(ClientMessage.PlaceHome, { nodeId });
    setSelectedHomeNode(null);
  }, []);

  const closeSelection = useCallback(() => setSelection(null), []);

  // --- Queued-move selection markers ----------------------------------------
  // Resolve the panel selection to its live queue entry's payload. Null when
  // nothing is selected or the entry is gone (cancelled / slots shifted when a
  // turn played) — the markers below key off this, so they clear too. The
  // payload STRING is the memo boundary: `self`'s identity churns on every
  // roster sync, and keying the decoded action off the stable string keeps the
  // ghost effect (with its focus pan) from re-firing on unrelated updates.
  const selectedQueuedPayload = useMemo(() => {
    if (!queuedSel || !self) return null;
    const move = self.queuedMoves.filter((m) => m.slot === queuedSel.slot)[
      queuedSel.index
    ];
    return move?.payload ?? null;
  }, [queuedSel, self]);
  const selectedQueued = useMemo(() => {
    if (selectedQueuedPayload === null) return null;
    try {
      return { action: JSON.parse(selectedQueuedPayload) as GameAction };
    } catch {
      return null;
    }
  }, [selectedQueuedPayload]);

  // Drop a selection whose entry no longer exists, so a stale slot/index can't
  // re-attach to a different move after the queue shifts.
  useEffect(() => {
    if (queuedSel && !selectedQueued) setQueuedSel(null);
  }, [queuedSel, selectedQueued]);

  // Board ghost for a selected queued construct (building or bridge): shown
  // only while that queue entry is selected, panned into view on selection.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    const a = selectedQueued?.action;
    if (
      a &&
      (a.type === 'construct_building' || a.type === 'pay_penalty_with_token')
    ) {
      game.setQueuedGhost({
        kind: 'building',
        nodeId: a.nodeId,
        building: a.building,
        color:
          PLAYER_COLORS[(self?.seat ?? 0) % PLAYER_COLORS.length] ?? 0xffffff,
      });
      game.focusNode(a.nodeId);
    } else if (a && a.type === 'construct_bridge') {
      game.setQueuedGhost({ kind: 'bridge', from: a.from, to: a.to });
      game.focusNode(a.from);
    } else {
      game.setQueuedGhost(null);
    }
    return () => gameRef.current?.setQueuedGhost(null);
  }, [selectedQueued, self?.seat]);

  // Market badges for a selected queued Implement / Cycle: per row, the card
  // (by id) or row slots (by index) to mark. A queued Cycle can span both rows,
  // so this is a list (at most one mark per row).
  const queuedMarketMarks = useMemo((): QueuedMarketMark[] => {
    const a = selectedQueued?.action;
    if (!a) return [];
    if (a.type === 'implement_fortress') {
      return [{ row: a.kind, mode: 'implement', cardId: a.cardId }];
    }
    if (a.type === 'cycle_market') {
      const rows = cycleMarketRows(a);
      return (['REGULAR', 'win'] as const)
        .map((row): QueuedMarketMark => ({
          row,
          mode: 'cycle',
          indices: new Set<number>(row === 'win' ? rows.win : rows.regular),
        }))
        .filter((m) => (m.indices?.size ?? 0) > 0);
    }
    return [];
  }, [selectedQueued]);

  // A market-marked selection is invisible while the market rail is collapsed —
  // reveal the sidebar so selecting the move always shows its marker.
  useEffect(() => {
    if (queuedMarketMarks.length > 0) setMarketCollapsed(false);
  }, [queuedMarketMarks]);

  // A hover preview shouldn't vanish the instant the cursor leaves its tile —
  // otherwise you can't reach the popover to read it. The close is deferred a
  // beat: moving onto the popover cancels it (`cancelPreviewClose`), and the
  // popover's own mouseleave reschedules it (`closePreviewSoon`). Pinned
  // (click-opened) details never take this path — their lifecycle is unchanged.
  const previewCloseTimer = useRef<number | null>(null);
  const cancelPreviewClose = useCallback(() => {
    if (previewCloseTimer.current !== null) {
      window.clearTimeout(previewCloseTimer.current);
      previewCloseTimer.current = null;
    }
  }, []);
  const closePreviewSoon = useCallback(() => {
    cancelPreviewClose();
    previewCloseTimer.current = window.setTimeout(() => {
      previewCloseTimer.current = null;
      setSelection((cur) => (cur && !cur.pinned ? null : cur));
    }, PREVIEW_CLOSE_DELAY);
  }, [cancelPreviewClose]);

  // --- Cycle Market flow ---------------------------------------------------
  // Per-card Food cost for a row, read from the match's pinned config.
  const cycleUnitFood = useCallback(
    (kind: FortressKind): number => {
      const fc = gameConfig?.fortressCards;
      if (!fc) return 0;
      const cost =
        kind === 'win' ? fc.cycleCostPerWinCard : fc.cycleCostPerRegularCard;
      // Costs are [minerals, food, vigor, obsidian]; cycling is paid in Food.
      return cost[1] ?? 0;
    },
    [gameConfig],
  );
  const cycleFoodCost = cycleSel
    ? cycleSel.regular.size * cycleUnitFood('REGULAR') +
      cycleSel.win.size * cycleUnitFood('win')
    : 0;
  // Sub-aware: the Bench pays Food costs with Vigor, the Treadmill with
  // Obsidian — mirrors the server's canAffordFor. While planning, cost never
  // gates (a queued yield may fund it; auto-play re-validates).
  const cycleAffordable =
    planSlot !== null
      ? true
      : self
        ? canAffordWith(
            toAmount(self.resources),
            [0, cycleFoodCost, 0, 0],
            substitutionsFor(self.fortressCards, effectOf),
          )
        : false;

  // The "Cycle Market" action toggles the flow: a second click leaves cycle
  // mode. Opening it reveals the market sidebar (if collapsed) and arms the
  // selection; the detail popover stays available for inspecting cards.
  const startCycleMarket = useCallback(() => {
    if (cycleMode) {
      setCycleMode(false);
      setCycleSel(null);
      return;
    }
    setMarketCollapsed(false);
    setCycleMode(true);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
  }, [cycleMode]);

  // Toggle a card in/out of the selection. A cycle is one row at a time —
  // picking in the other row drops the picks held there. Emptying the
  // selection clears it.
  const toggleCycleCard = useCallback((kind: FortressKind, index: number) => {
    setCycleSel((prev) => {
      const next: CycleSelection = {
        regular: kind === 'win' ? new Set() : new Set(prev?.regular),
        win: kind === 'win' ? new Set(prev?.win) : new Set(),
      };
      const row = kind === 'win' ? next.win : next.regular;
      if (row.has(index)) row.delete(index);
      else row.add(index);
      return next.regular.size === 0 && next.win.size === 0 ? null : next;
    });
  }, []);

  const cancelCycle = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
  }, []);

  const confirmCycle = useCallback(() => {
    if (!cycleSel || (cycleSel.regular.size === 0 && cycleSel.win.size === 0))
      return;
    sendGameAction({
      type: 'cycle_market',
      regular: [...cycleSel.regular],
      win: [...cycleSel.win],
    });
    setCycleMode(false);
    setCycleSel(null);
  }, [cycleSel, sendGameAction]);

  // Leave the cycle flow if the turn moves on (auto-end, pass, disconnect) so it
  // can't linger into another player's turn — unless the player is planning
  // queued moves, which happens precisely while it is NOT their turn.
  useEffect(() => {
    if (currentPlayerId !== selfId && planSlot === null) {
      setCycleMode(false);
      setCycleSel(null);
    }
  }, [currentPlayerId, selfId, planSlot]);

  // --- Round-start cycle (the first player's free step) ---------------------

  /** The open round-start window waits on ME — the round is paused until I
   * cycle up to `roundStartCycleMaxPerRow` cards per market (free) or cycle
   * nothing. */
  const rsWindowMine = roundCyclerId !== '' && roundCyclerId === selfId;

  // Setup phase: reveal the market rail so the starting Fortress deals can
  // inform home-base placement. Desktop only — on phones an open rail covers
  // the board that placement itself needs (the edge handle stays available).
  useEffect(() => {
    if (phase === 'setup' && !isSmallScreen) setMarketCollapsed(false);
  }, [phase, isSmallScreen]);

  // The window opening on us force-enters live picking (and reveals the
  // market); it closing force-leaves. The paid Cycle-Market flow can't be
  // live at the same time — no turn is running while the window is open.
  useEffect(() => {
    if (rsWindowMine) {
      setMarketCollapsed(false);
      setRsMode('live');
      setRsSel({ regular: [], win: [] });
      setCycleMode(false);
      setCycleSel(null);
    } else {
      setRsMode((m) => (m === 'live' ? null : m));
    }
  }, [rsWindowMine]);

  // Queue-planning the choice is only offered while we hold the First-Turn
  // marker AND have passed (i.e. we took the marker by passing first this
  // round); losing it (someone else passed first) or the round rolling over
  // (passed resets) closes the picker.
  useEffect(() => {
    if (firstPlayerId !== selfId || !self?.passed) {
      setRsMode((m) => (m === 'queue' ? null : m));
    }
  }, [firstPlayerId, selfId, self?.passed]);

  /** How many cards the round-start cycle allows out of each market row. */
  const rsMaxPerRow =
    gameConfig?.fortressCards?.roundStartCycleMaxPerRow ?? 1;

  /** Toggle a card in/out of its row's round-start picks. A pick on a full
   * row replaces the oldest one (mirrors the old single-pick swap). */
  const toggleRsCard = useCallback(
    (kind: FortressKind, cardId: string | null) => {
      if (!cardId) return;
      setRsSel((prev) => {
        const key = kind === 'win' ? 'win' : 'regular';
        const cur = prev[key];
        const next = cur.includes(cardId)
          ? cur.filter((id) => id !== cardId)
          : [...cur, cardId].slice(-rsMaxPerRow);
        return { ...prev, [key]: next };
      });
    },
    [rsMaxPerRow],
  );

  /** Send the round-start choice — live answer or queued plan, per `rsMode`.
   * `{ regular: [], win: [] }` is the explicit "cycle nothing". */
  const confirmRoundStartCycle = useCallback(
    (choice: { regular: string[]; win: string[] }) => {
      const room = roomRef.current;
      if (!room || !rsMode) return;
      room.send(
        rsMode === 'queue'
          ? ClientMessage.QueueRoundStartCycle
          : ClientMessage.RoundStartCycle,
        choice,
      );
      setRsMode(null);
      setRsSel({ regular: [], win: [] });
    },
    [rsMode],
  );

  /** Enter/leave queue-planning for the round-start cycle. */
  const toggleRsQueueMode = useCallback(() => {
    setRsSel({ regular: [], win: [] });
    if (rsMode === 'queue') {
      setRsMode(null);
      return;
    }
    setMarketCollapsed(false);
    setRsMode('queue');
  }, [rsMode]);

  const clearQueuedRoundStartCycle = useCallback(() => {
    roomRef.current?.send(ClientMessage.QueueRoundStartCycle, { clear: true });
  }, []);

  // --- Construct Building flow ---------------------------------------------
  // Targets for `building`, split three ways (the caller renders the first two):
  //  - `allowed`  : empty + in build range + affordable  → green, clickable
  //  - `tooCostly`: empty + in range but unaffordable     → grey indicator
  //  - everything else (out of range) is left untouched.
  // Range mirrors the server's `inRange` (won't trace through an opponent's
  // blocking Barracks / Fortress marker or across a severed path) and cost
  // mirrors `buildCost`, so a green node always passes server validation.
  /** What a building ARRIVING on `id` would collect — the node's printed
   * one-time resource plus any placed units (Pee-Pee Pinata), at that node's
   * zone bonus. Mirrors the server's `collectOneTimeResource`. Shared by the
   * build flow and Nomadic Pufferfish's move flow: a move collects exactly
   * what a build there would. One bubble per node — when a placed stack is a
   * different kind from the printed pickup, the printed one wins the slot. */
  const oneTimeGainAt = useCallback(
    (
      id: number,
      info: { resource: string; permanence: string; bonusResource: string; bonusAmount: number } | undefined,
    ): { resource: string; amount: number } | null => {
      if (!gameConfig || !self) return null;
      let gain: { resource: string; amount: number } | null = null;
      if (info?.permanence === 'oneTime' && info.resource !== '') {
        const idx = (RESOURCES as readonly string[]).indexOf(info.resource);
        if (idx >= 0) {
          const zone = resolveVigorZone(
            gameConfig,
            self,
            vigorZoneLevel(NODE_ZONE[id] ?? 'forest'),
          );
          gain = {
            resource: info.resource,
            amount: 1 + (zone.oneTimeResourceBonus[idx] ?? 0),
          };
        }
      }
      if (info && info.bonusAmount > 0 && info.bonusResource !== '') {
        const bIdx = (RESOURCES as readonly string[]).indexOf(info.bonusResource);
        const bZone = resolveVigorZone(
          gameConfig,
          self,
          vigorZoneLevel(NODE_ZONE[id] ?? 'forest'),
        );
        const placed =
          info.bonusAmount +
          (bIdx >= 0 ? (bZone.oneTimeResourceBonus[bIdx] ?? 0) : 0);
        if (gain?.resource === info.bonusResource) gain.amount += placed;
        else if (!gain) gain = { resource: info.bonusResource, amount: placed };
      }
      return gain;
    },
    [gameConfig, self],
  );

  const computeBuildTargets = useCallback(
    (
      building: ConstructableBuilding,
      skipVigorPenalty = false,
      /** Block Us Baby's free placement: nothing is charged, so no node is
       * ever "too costly", and the reach comes from the card's catalogue
       * `freeBuild` range rather than the building's own build range. */
      freeBuildCardId?: string,
    ): {
      allowed: Set<number>;
      tooCostly: Set<number>;
      vigorPenalty: Map<number, number>;
      oneTimeGain: Map<number, { resource: string; amount: number }>;
    } => {
      const allowed = new Set<number>();
      const tooCostly = new Set<number>();
      const vigorPenalty = new Map<number, number>();
      const oneTimeGain = new Map<
        number,
        { resource: string; amount: number }
      >();
      if (!gameConfig || !selfId || !self) {
        return { allowed, tooCostly, vigorPenalty, oneTimeGain };
      }
      const cfg = resolveBuilding(gameConfig, self, building);
      // The Vigor penalty a build here pays = the node's zone penalty (when the
      // building is subject to one). Shown beside each highlighted node.
      const zonePenalty = (id: number): number => {
        if (!cfg.vigorPenalty) return 0;
        const zone = NODE_ZONE[id] ?? 'forest';
        return resolveVigorZone(gameConfig, self, vigorZoneLevel(zone))
          .vigorPenalty;
      };
      const bank = toAmount(self.resources);
      const subs = substitutionsFor(self.fortressCards, effectOf);
      const freeCfg = freeBuildCardId
        ? effectOf(freeBuildCardId)?.freeBuild
        : undefined;
      const useDefend = freeCfg?.range === 'barracks_defend';
      // What a card placement charges ("49"+ printings): the building's cost
      // with `payCost`, the zone penalty with `payVigorPenalty`, else nothing.
      const cardPaysCost = !!freeBuildCardId && freeCfg?.payCost === true;
      const cardPaysPenalty =
        !!freeBuildCardId && freeCfg?.payVigorPenalty === true;
      const waivesPenalty = !!freeBuildCardId && !cardPaysPenalty;
      const bypass =
        building === 'scout' &&
        anyCardHasPassive(
          self.fortressCards,
          'build_through_obstructions',
          effectOf,
        );
      const sources: number[] = [];
      const blocked = blockedNodesForClient(selfId ?? '', bypass);
      for (const [id, info] of nodeInfoRef.current) {
        // Build sources: the player's own buildings AND every node their
        // Fortress occupies (empty X markers included) — mirrors inRange.
        if (
          (info.ownerId === selfId && info.building !== '') ||
          info.fortressOwnerId === selfId
        )
          sources.push(id);
      }
      if (sources.length === 0) {
        return { allowed, tooCostly, vigorPenalty, oneTimeGain };
      }
      // The free placement takes the owner's REAL defend geometry — range,
      // obstruction bypass AND gap crossing — exactly as the server does.
      const defend = useDefend ? aimProfileFor(self, 'defend') : null;
      const reach = nodesWithinAny(
        BUILD_GRAPH,
        sources,
        defend ? defend.range : cfg.range.build + (freeCfg?.rangeBonus ?? 0),
        defend ? defend.blocked : blocked,
        defend ? defend.severed : severedRef.current,
        // Bridgette rides built bridges for free in either geometry.
        defend ? defend.freeEdges : freeEdgesForClient(self),
      );
      for (const id of reach.keys()) {
        const info = nodeInfoRef.current.get(id);
        // Only empty nodes are buildable (the source buildings are occupied).
        if ((info?.building ?? '') !== '') continue;
        // An empty X marker is buildable by the fortress's OWN owner and by
        // nobody else — mirrors the server. A card's free placement takes no
        // fortress node at all, its own included.
        const fOwner = info?.fortressOwnerId ?? '';
        if (fOwner !== '' && (freeBuildCardId || fOwner !== selfId)) continue;
        const pen = zonePenalty(id);
        const cost = cardPaysCost
          ? buildCostFor(
              gameConfig,
              self,
              building,
              NODE_ZONE[id] ?? 'forest',
              info?.resource ?? '',
              info?.permanence ?? '',
              waivesPenalty,
            )
          : freeBuildCardId
            ? [0, 0, cardPaysPenalty ? pen : 0, 0]
            : buildCostFor(
                gameConfig,
                self,
                building,
                NODE_ZONE[id] ?? 'forest',
                info?.resource ?? '',
                info?.permanence ?? '',
                skipVigorPenalty,
              );
        if (skipVigorPenalty && pen <= 0) continue;
        // A placement that pays nothing is always affordable and shows no
        // Vigor bubble; one that pays the penalty shows it.
        const affordable =
          (!!freeBuildCardId && !cardPaysCost && !cardPaysPenalty) ||
          canAffordWith(bank, cost, subs);
        (affordable ? allowed : tooCostly).add(id);
        if (pen > 0 && !waivesPenalty) vigorPenalty.set(id, pen);
        // What the build would collect on landing — same preview the move
        // flow shows, since a move collects the same thing.
        const gain = oneTimeGainAt(id, info);
        if (gain) oneTimeGain.set(id, gain);
      }
      return { allowed, tooCostly, vigorPenalty, oneTimeGain };
    },
    [
      gameConfig,
      selfId,
      self,
      effectOf,
      blockedNodesForClient,
      aimProfileFor,
      freeEdgesForClient,
      oneTimeGainAt,
    ],
  );

  // The Construct-Building action button toggles the picker: open it from
  // scratch, or close whatever build flow is currently open (pick or place).
  const openBuildPicker = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    setBuildFlow((prev) => (prev ? null : { stage: 'pick' }));
  }, [closeSelection]);

  const selectBuilding = useCallback(
    (building: ConstructableBuilding) => {
      const penaltyCardId = buildFlowRef.current?.penaltyCardId;
      const freeBuildCardId = buildFlowRef.current?.freeBuildCardId;
      // Planning queued moves: no range / cost / occupancy gating — a prior
      // queued move may supply the resources, range, or clear the node, so
      // EVERY node is offered. The server re-validates when the move actually
      // plays; an invalid one halts auto-play for manual intervention.
      if (planSlotRef.current !== null) {
        setBuildFlow({
          stage: 'place',
          building,
          allowed: new Set(nodeInfoRef.current.keys()),
          tooCostly: new Set<number>(),
          vigorPenalty: new Map<number, number>(),
          oneTimeGain: new Map<number, { resource: string; amount: number }>(),
          ...(penaltyCardId ? { penaltyCardId } : {}),
          ...(freeBuildCardId ? { freeBuildCardId } : {}),
        });
        return;
      }
      const { allowed, tooCostly, vigorPenalty, oneTimeGain } =
        computeBuildTargets(building, !!penaltyCardId, freeBuildCardId);
      setBuildFlow({
        stage: 'place',
        building,
        allowed,
        tooCostly,
        vigorPenalty,
        oneTimeGain,
        ...(penaltyCardId ? { penaltyCardId } : {}),
        ...(freeBuildCardId ? { freeBuildCardId } : {}),
      });
    },
    [computeBuildTargets],
  );

  const cancelBuild = useCallback(() => setBuildFlow(null), []);

  /** Start the optional build-time aim on the pending Barracks placement:
   * posture picked here, target picked on the board (same targets the
   * aim-barracks flow would offer from that node once built). Reads
   * `computeAimTargets` through its ref — it's declared further down. */
  const selectBuildAim = useCallback((mode: AimMode) => {
    setBuildFlow((cur) => {
      if (
        cur?.stage !== 'place' ||
        cur.building !== 'barracks' ||
        cur.selected === undefined
      ) {
        return cur;
      }
      return {
        ...cur,
        aim: {
          stage: 'target',
          mode,
          allowed: computeAimTargetsRef.current(cur.selected, mode, false),
        },
      };
    });
  }, []);

  /** Drop the build-time aim (back to a plain unaimed build). */
  const clearBuildAim = useCallback(() => {
    setBuildFlow((cur) =>
      cur?.stage === 'place' ? { ...cur, aim: undefined } : cur,
    );
  }, []);

  /** Confirm the tentatively-selected node: send the construct (or token-build)
   * action for it. The board click only selects; this commits. */
  const confirmBuild = useCallback(() => {
    const bf = buildFlowRef.current;
    if (bf?.stage !== 'place' || bf.selected === undefined) return;
    const aim =
      bf.building === 'barracks' && bf.aim?.stage === 'done'
        ? { targetId: bf.aim.targetId, mode: bf.aim.mode }
        : undefined;
    sendGameAction(
      bf.freeBuildCardId
        ? {
            // Block Us Baby: free of resources, Vigor and action points, so it
            // carries no riders — an Energized Bun-Bun doubling would be a
            // pickup on a build that paid nothing.
            type: 'free_build',
            cardId: bf.freeBuildCardId,
            nodeId: bf.selected,
            building: bf.building,
            ...(aim ? { aim } : {}),
          }
        : bf.penaltyCardId
        ? {
            type: 'pay_penalty_with_token',
            cardId: bf.penaltyCardId,
            nodeId: bf.selected,
            building: bf.building,
            ...(aim ? { aim } : {}),
            ...(bf.doubleOneTime
              ? { riders: { doubleOneTime: bf.doubleOneTime } }
              : {}),
          }
        : {
            type: 'construct_building',
            nodeId: bf.selected,
            building: bf.building,
            ...(aim ? { aim } : {}),
            ...(bf.doubleOneTime
              ? { riders: { doubleOneTime: bf.doubleOneTime } }
              : {}),
          },
    );
    setBuildFlow(null);
  }, [sendGameAction]);

  /** The permanent resource printed on the tentatively-selected build node, if
   * any — drives the "this building won't collect it" warning for non-Mine
   * builds (only a Mine yields a permanent resource). */
  const selectedPermanentResource = useMemo(() => {
    void boardVersion; // node state lives in a ref; recompute on board syncs
    if (buildFlow?.stage !== 'place' || buildFlow.selected === undefined) {
      return null;
    }
    const info = nodeInfoRef.current.get(buildFlow.selected);
    return info && info.permanence === 'permanent' && info.resource !== ''
      ? (info.resource as ResourceKind)
      : null;
  }, [buildFlow, boardVersion]);

  /** The usable Energized Bun-Bun for the build rider, if held. */
  const bunBun = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectOf(fc.cardId);
      if (fx?.rider !== 'double_one_time' || fc.disabled) continue;
      const cap = fx.tokenCap ?? Number.POSITIVE_INFINITY;
      if (fc.tokens >= cap) continue;
      return { cardId: fc.cardId, tokens: fc.tokens, cap };
    }
    return null;
  }, [self, effectOf]);

  /** Arm/disarm the Bun-Bun rider on the current placement. */
  const toggleDoubleOneTime = useCallback(() => {
    setBuildFlow((cur) => {
      if (cur?.stage !== 'place') return cur;
      return {
        ...cur,
        doubleOneTime: cur.doubleOneTime ? undefined : (bunBun?.cardId ?? undefined),
      };
    });
  }, [bunBun]);

  /** The usable Zapped Bapped Statue (pay windows / auto-pay), if held. All
   * charging flows through the reactive pay banner now — including the
   * owner's own actions — so there is no build-time arming toggle. */
  const statue = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectOf(fc.cardId);
      if (fc.disabled) continue;
      if (!fx?.hooks?.some((h) => h.kind === 'opponent_penalty_charge_offer'))
        continue;
      return {
        cardId: fc.cardId,
        name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
        stored: fc.stored,
        target: fx.winAtStored ?? Number.POSITIVE_INFINITY,
      };
    }
    return null;
  }, [self, effectOf, cardsById]);

  /** The held card with the round-end energy deposit (Swole Turtle), if any.
   * A flipped card means the ×2 collect was used this round, which forfeits
   * the round-end deposit (catalogue v7) — kept in the list so the panel can
   * say so instead of silently dropping the deposit control. */
  const turtle = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectOf(fc.cardId);
      if (!fx?.hooks?.some((h) => h.kind === 'round_end_vigor_deposit'))
        continue;
      return {
        cardId: fc.cardId,
        name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
        tokens: fc.tokens,
        collected: fc.disabled,
      };
    }
    return null;
  }, [self, effectOf, cardsById]);

  /** The held BAM Exploder (round-end ranged fortress destroy), if any —
   * drives the sidebar's auto-destroy toggle. */
  const exploder = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectOf(fc.cardId);
      if (!fx?.hooks?.some((h) => h.kind === 'round_end_ranged_fortress_destroy'))
        continue;
      return {
        cardId: fc.cardId,
        name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
        tokens: fc.tokens,
        target: fx.winAtTokens ?? Number.POSITIVE_INFINITY,
      };
    }
    return null;
  }, [self, effectOf, cardsById]);

  /** The held Telescopic Arm (capture-on-destroy passive), if any —
   * drives the sidebar's standing capture toggle + payment pick. */
  const captureBot = useMemo(() => {
    if (!self) return null;
    for (const fc of self.fortressCards) {
      const fx = effectOf(fc.cardId);
      if (!fx?.passives?.some((p) => p.kind === 'capture_on_destroy')) continue;
      return {
        cardId: fc.cardId,
        name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
        // What each capture hands the victim (Telescopic Arm "54"+: 2).
        payment: capturePaymentFor(self.fortressCards, effectOf),
      };
    }
    return null;
  }, [self, effectOf, cardsById]);

  // --- Construct Bridge flow -----------------------------------------------
  // Eligible bridge paths, excluding paths already bridged: a CONNECTED path
  // needs BOTH endpoints within the bridge's build range (so a bridge never
  // reaches further than the range), while a SEVERED path (chasm) needs only ONE
  // — you anchor on the reachable bank and span the gap. Mirrors the server.
  // (Tiny gap: the server seeds its BFS at the candidate endpoint, so it also
  // allows a path whose only in-range end is a blocked opponent node; we omit
  // that rare case — harmless, the server re-validates.) Bridge cost is uniform,
  // so the split is all-or-nothing: affordable → `allowed`, else `tooCostly`.
  // With `freeBridgeCardId` (Bridgette the Beauty's granted bridge) her
  // `freeBridge.rangeBonus` widens the Bridge's build range, and the flow is
  // tagged with the card so a tap sends `free_bridge`.
  const computeBridgeTargets = useCallback((freeBridgeCardId?: string): {
    allowed: [number, number][];
    tooCostly: [number, number][];
    allowedKeys: Set<string>;
    wildcard: boolean;
    pending: { from: number; to: number } | null;
    cardId?: string;
  } => {
    const allowed: [number, number][] = [];
    const tooCostly: [number, number][] = [];
    const allowedKeys = new Set<string>();
    const tag = freeBridgeCardId ? { cardId: freeBridgeCardId } : {};
    if (!gameConfig || !selfId || !self) {
      return { allowed, tooCostly, allowedKeys, wildcard: false, pending: null, ...tag };
    }
    const cfg = resolveBuilding(gameConfig, self, 'bridge');
    const range =
      cfg.range.build +
      (freeBridgeCardId ? (effectOf(freeBridgeCardId)?.freeBridge?.rangeBonus ?? 0) : 0);
    // Wildcard cost ("n of any resource") → the tap asks which resource pays.
    const wildcard = (cfg.cost as readonly number[]).length === 1;
    const bypass = anyCardHasPassive(
      self.fortressCards,
      'build_through_obstructions',
      effectOf,
    );
    // Sub-aware, and — unlike a componentwise compare — correct for the
    // wildcard bridge cost Bridgette grants ("1 of any resource").
    const affordable = canAffordWith(
      toAmount(self.resources),
      cfg.cost,
      substitutionsFor(self.fortressCards, effectOf),
    );
    const sources: number[] = [];
    const blocked = blockedNodesForClient(selfId ?? '', bypass);
    for (const [id, info] of nodeInfoRef.current) {
      // Build sources include the player's Fortress nodes (X markers), the
      // same as inRange — a fortress is a valid point to build a Bridge from.
      if (
        (info.ownerId === selfId && info.building !== '') ||
        info.fortressOwnerId === selfId
      )
        sources.push(id);
    }
    if (sources.length === 0) {
      return { allowed, tooCostly, allowedKeys, wildcard, pending: null, ...tag };
    }
    const reach = nodesWithinAny(
      BUILD_GRAPH,
      sources,
      range,
      blocked,
      severedRef.current,
      freeEdgesForClient(self),
    );
    for (const [a, b] of BUILD_GRAPH.edges) {
      const key = edgeKey(a, b);
      if (bridgesRef.current.has(key)) continue; // already bridged
      const inA = reach.has(a);
      const inB = reach.has(b);
      // Connected path → both ends in range; severed path → one end is enough.
      const inRange = severedRef.current.has(key) ? inA || inB : inA && inB;
      if (!inRange) continue;
      if (affordable) {
        allowed.push([a, b]);
        allowedKeys.add(key);
      } else {
        tooCostly.push([a, b]);
      }
    }
    return { allowed, tooCostly, allowedKeys, wildcard, pending: null, ...tag };
  }, [gameConfig, selfId, self, effectOf, blockedNodesForClient, freeEdgesForClient]);

  /** The edge-selection flow for a bridge: the core action's, or — with
   * `cardId` — Bridgette the Beauty's granted one (her range bonus, no action
   * point). Planning: every unbridged path is offered — range and cost are
   * the auto-play validator's problem (a prior queued move may change both). */
  const bridgeFlowFor = useCallback(
    (cardId?: string): NonNullable<typeof bridgeFlow> => {
      if (planSlotRef.current !== null) {
        const allowed: [number, number][] = [];
        const allowedKeys = new Set<string>();
        for (const [a, b] of BUILD_GRAPH.edges) {
          const key = edgeKey(a, b);
          if (bridgesRef.current.has(key)) continue; // already bridged
          allowed.push([a, b]);
          allowedKeys.add(key);
        }
        // The resource pick still rides on a CURRENTLY wildcard cost, so the
        // queued move carries the choice if Bridgette is still in play then.
        const wildcard =
          !!gameConfig &&
          !!self &&
          (resolveBuilding(gameConfig, self, 'bridge').cost as readonly number[])
            .length === 1;
        return {
          allowed,
          tooCostly: [],
          allowedKeys,
          wildcard,
          pending: null,
          ...(cardId ? { cardId } : {}),
        };
      }
      return computeBridgeTargets(cardId);
    },
    [computeBridgeTargets, gameConfig, self],
  );

  // The Construct-Bridge button toggles the edge-selection flow (and takes
  // over from an open card-granted one).
  const startBridge = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    setBridgeFlow((prev) => (prev && !prev.cardId ? null : bridgeFlowFor()));
  }, [closeSelection, bridgeFlowFor]);

  const cancelBridge = useCallback(() => setBridgeFlow(null), []);

  // --- Aim Barracks flow -----------------------------------------------------
  /** Node ids currently under attack: targets of standing attack aims (absorbed
   * attackers, and aims that have come to point at their OWN owner's building,
   * excluded — mirroring the server's tallies). */
  const attackedTargets = useCallback((): Set<number> => {
    const attacked = new Set<number>();
    for (const info of nodeInfoRef.current.values()) {
      if (info.building !== 'barracks' || info.aimMode !== 'attack') continue;
      if (info.aimTargetId < 0 || info.fortressOwnerId !== '') continue;
      if (info.aimEdge !== '') continue; // bridge aim — anchor id, not a target
      const target = nodeInfoRef.current.get(info.aimTargetId);
      const targetOwner = target?.building === '' ? null : (target?.ownerId ?? null);
      if (targetOwner === info.ownerId) continue; // committed but inert
      attacked.add(info.aimTargetId);
    }
    return attacked;
  }, []);

  /** Bridge edge keys currently under attack: targets of standing bridge-attack
   * aims (absorbed attackers excluded, mirroring the server's tallies). */
  const attackedBridges = useCallback((): Set<string> => {
    const attacked = new Set<string>();
    for (const info of nodeInfoRef.current.values()) {
      if (info.building !== 'barracks' || info.aimMode !== 'attack') continue;
      if (info.aimEdge === '' || info.fortressOwnerId !== '') continue;
      attacked.add(info.aimEdge);
    }
    return attacked;
  }, []);

  /** Your unaimed Barracks — the legal sources to start an aim from. */
  const computeAimSources = useCallback((): Set<number> => {
    const sources = new Set<number>();
    if (!selfId) return sources;
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId !== selfId || info.building !== 'barracks') continue;
      if (info.aimTargetId >= 0) continue; // committed until the round ends
      sources.add(id);
    }
    return sources;
  }, [selfId]);

  // Legal targets for one Barracks + posture — mirrors the server's `validateAim`
  // (attack: an opponent's non-fortress, non-indestructible building in attack
  // range; defend: any building in defend range), tracing range with the same
  // enter-but-not-through blocker semantics (`nodesWithinInto`). The defense-
  // window variant additionally restricts to YOUR buildings under attack.
  /** Is `nodeId` covered by a DESTRUCTIBLE fortress (attackable despite the
   * fortress stamp)? Client mirror of the server's destructibleFortressAt. */
  const destructibleFortressNode = useCallback((nodeId: number): boolean => {
    const ownerId = nodeInfoRef.current.get(nodeId)?.fortressOwnerId ?? '';
    if (ownerId === '') return false;
    const owner = playersRef.current.find((p) => p.id === ownerId);
    return !!owner?.fortressCards.some(
      (fc) =>
        fc.nodeIds.includes(nodeId) &&
        cardsByIdRef.current.get(fc.cardId)?.effects?.destructible,
    );
  }, []);

  /** BFS reach of one Barracks + posture: the node ids within the posture's
   * range, traced with the enter-but-not-through blocker semantics the server
   * uses (`aimInRange`). Shared by node AND bridge target computation. Null
   * when the Barracks may not take the posture at all (absorbed attack). */
  const computeAimReach = useCallback(
    (barracksId: number, mode: AimMode): ReadonlySet<number> | null => {
      if (!gameConfig || !selfId || !self) return new Set<number>();
      const source = nodeInfoRef.current.get(barracksId);
      if (mode === 'attack' && (source?.fortressOwnerId ?? '') !== '') {
        return null; // absorbed into a Fortress — may only defend
      }
      const { range, blocked, severed, freeEdges } = aimProfileFor(self, mode);
      return new Set(
        nodesWithinInto(
          BUILD_GRAPH,
          barracksId,
          range,
          blocked,
          severed,
          freeEdges,
        ).keys(),
      );
    },
    [gameConfig, selfId, self, aimProfileFor],
  );

  const computeAimTargets = useCallback(
    (barracksId: number, mode: AimMode, defenseOnly: boolean): Set<number> => {
      const out = new Set<number>();
      // Planning (never defense reactions — those are live): any node may be
      // the target; a prior queued move may create the building or the range.
      if (planSlotRef.current !== null && !defenseOnly) {
        for (const id of nodeInfoRef.current.keys()) {
          if (id !== barracksId) out.add(id);
        }
        return out;
      }
      if (!gameConfig || !selfId || !self) return out;
      const effectLookup = (id: string) => cardsByIdRef.current.get(id)?.effects;
      // BAM Exploder: our attacks may target opponent fortress nodes.
      const razeFortresses =
        mode === 'attack' &&
        anyCardHasPassive(self.fortressCards, 'attack_fortress_nodes', effectLookup);
      const reach = computeAimReach(barracksId, mode);
      if (reach === null) return out; // absorbed — may only defend
      const attacked = defenseOnly ? attackedTargets() : null;
      for (const id of reach.keys()) {
        if (id === barracksId) continue;
        const info = nodeInfoRef.current.get(id);
        if (!info) continue;
        if (info.building === '') continue;
        const targetOwner = info.ownerId;
        if (mode === 'attack') {
          if (targetOwner === selfId) continue;
          if (info.indestructible) continue;
          // Fortress nodes are unattackable unless the covering card is
          // destructible (Blimp-port, Traveling Booba) or we hold BAM
          // Exploder's raze passive (opponent fortresses only) — mirrors the
          // server.
          if (
            info.fortressOwnerId !== '' &&
            !destructibleFortressNode(id) &&
            !(razeFortresses && info.fortressOwnerId !== selfId)
          )
            continue;
        } else if (attacked && (targetOwner !== selfId || !attacked.has(id))) {
          continue; // reactions defend only your own attacked buildings
        }
        out.add(id);
      }
      return out;
    },
    [
      gameConfig,
      selfId,
      self,
      attackedTargets,
      computeAimReach,
      destructibleFortressNode,
    ],
  );
  // Mirrored for the once-created board click handler (it can't see live state).
  useEffect(() => {
    computeAimTargetsRef.current = computeAimTargets;
  }, [computeAimTargets]);

  // Legal BRIDGE targets for one Barracks + posture — mirrors the server's
  // `validateAimBridge`: a constructed Bridge, not claimed by an implemented
  // Fortress pattern (hardened like fortress nodes), reachable through EITHER
  // endpoint. Bridges belong to no one, so ownership never gates a bridge aim;
  // the defense-window variant restricts to bridges under attack.
  const computeAimBridges = useCallback(
    (barracksId: number, mode: AimMode, defenseOnly: boolean): Set<string> => {
      const out = new Set<string>();
      const claimed = (key: string) =>
        playersRef.current.some((p) =>
          p.fortressCards.some((fc) => fc.bridgeKeys.includes(key)),
        );
      // Planning: any current bridge may be the target — the board may differ
      // by the time the queued aim plays.
      if (planSlotRef.current !== null && !defenseOnly) {
        for (const key of bridgesRef.current) {
          if (!claimed(key)) out.add(key);
        }
        return out;
      }
      const reach = computeAimReach(barracksId, mode);
      if (reach === null) return out; // absorbed — may only defend
      const attacked = defenseOnly ? attackedBridges() : null;
      for (const key of bridgesRef.current) {
        if (attacked && !attacked.has(key)) continue;
        if (claimed(key)) continue;
        const dash = key.indexOf('-');
        const a = Number(key.slice(0, dash));
        const b = Number(key.slice(dash + 1));
        // An endpoint the Barracks stands on reaches trivially.
        if (a === barracksId || b === barracksId || reach.has(a) || reach.has(b)) {
          out.add(key);
        }
      }
      return out;
    },
    [attackedBridges, computeAimReach],
  );
  // Mirrored for the once-created board click/edge handlers.
  const computeAimBridgesRef = useRef(computeAimBridges);
  useEffect(() => {
    computeAimBridgesRef.current = computeAimBridges;
  }, [computeAimBridges]);

  // The Aim-Barracks action button toggles the flow (normal, on-turn variant).
  const openAimFlow = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setImplementFlow(null);
    setEffectsFlow(null);
    closeSelection();
    setAimFlow((prev) =>
      prev
        ? null
        : {
            stage: 'pickBarracks',
            // Planning: any node may be the aiming Barracks — a queued build
            // may have just placed it. Live: your unaimed Barracks only.
            sources:
              planSlotRef.current !== null
                ? new Set(nodeInfoRef.current.keys())
                : computeAimSources(),
            defense: false,
          },
    );
  }, [closeSelection, computeAimSources]);

  /** The defense banner's Aim Defender button — the defend-locked variant. */
  const openDefenseAim = useCallback(() => {
    setAimFlow((prev) =>
      prev
        ? null
        : { stage: 'pickBarracks', sources: computeAimSources(), defense: true },
    );
  }, [computeAimSources]);

  /** Posture picked for the selected Barracks — advance to target selection. */
  const selectAimMode = useCallback(
    (mode: AimMode) => {
      setAimFlow((prev) => {
        if (prev?.stage !== 'mode') return prev;
        return {
          stage: 'target',
          barracksId: prev.barracksId,
          mode,
          defense: false,
          allowed: computeAimTargets(prev.barracksId, mode, false),
          allowedBridges: computeAimBridges(prev.barracksId, mode, false),
        };
      });
    },
    [computeAimTargets, computeAimBridges],
  );

  const cancelAim = useCallback(() => setAimFlow(null), []);

  const sendDefenseDone = useCallback(() => {
    setAimFlow(null);
    roomRef.current?.send(ClientMessage.DefenseDone);
  }, []);

  // Escape cancels the aim flow.
  useEffect(() => {
    if (!aimFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAimFlow(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [aimFlow]);

  // A defense window opening freezes the (ending) turn — drop any on-turn flow
  // still open. A closing window (or our confirm) drops the defense aim flow.
  useEffect(() => {
    if (defense.active) {
      setBuildFlow(null);
      setBridgeFlow(null);
      setImplementFlow(null);
      setCycleMode(false);
      setCycleSel(null);
      setEffectsFlow((flow) => (flow?.stage === 'claim' ? flow : null));
      setAimFlow((cur) => (cur && !cur.defense ? null : cur));
    } else {
      setAimFlow((cur) => (cur?.defense ? null : cur));
    }
  }, [defense.active]);
  const iAmDefending = defense.active && !!selfId && defense.pending.includes(selfId);

  /** A bottom action bar (your turn / queueing / defense window) is on screen —
   * on phones it goes full-width and the corner buttons dock above it. */
  const bottomBarUp =
    phase === 'playing' &&
    (((currentPlayerId === selfId || planSlot !== null) &&
      !!self &&
      !defense.active) ||
      iAmDefending);
  useEffect(() => {
    if (!bottomBarUp) setPhoneTrayOpen(false);
  }, [bottomBarUp]);
  /** The Queue turn button (phone dock / desktop corner), on someone else's
   * turn: plan the next open upcoming turn. With none open — a Pass queued,
   * or every turn already holding moves — plan the last turn that holds
   * moves, so the bar still opens on the queue to review or cancel it. */
  const openQueuePlanning = () => {
    if (!self) return;
    setPlanSlot(
      offeredQueueSlot(self.queuedMoves) ??
        Math.max(...self.queuedMoves.map((m) => m.slot)),
    );
    if (isSmallScreen) setPhoneTrayOpen(true);
  };
  /** The bar's collapsed state: on phones "collapsed" means hidden behind
   * the dock button; elsewhere it is the slim status pill. */
  const barCollapsed = isSmallScreen ? !phoneTrayOpen : actionBarCollapsed;
  const toggleBarCollapsed = () => {
    if (isSmallScreen) setPhoneTrayOpen((o) => !o);
    else setActionBarCollapsed((c) => !c);
  };
  const inGamePhase =
    phase === 'setup' || phase === 'playing' || phase === 'ended';
  /** PHONES: open the Players sheet (the market sheet yields). */
  const openPlayersSheet = () => {
    setMarketCollapsed(true);
    setPlayersCollapsed(false);
    closeSelection();
  };

  // ON SMALL SCREENS: edge flicks open the sidebars (the sidebars' own swipe
  // handlers close them). Opening one closes the other, like the toggles.
  useEdgeSwipe(
    isSmallScreen &&
      (phase === 'setup' || phase === 'playing' || phase === 'ended'),
    () => {
      if (!playersCollapsed) return;
      setPlayersCollapsed(false);
      setMarketCollapsed(true);
      closeSelection();
    },
    () => {
      if (!marketCollapsed) return;
      setMarketCollapsed(false);
      setPlayersCollapsed(true);
      closeSelection();
    },
  );

  // --- Tutorial ------------------------------------------------------------
  const tutorialActive = tutorial.active && !!activeMatch && !activeMatch.spectate;
  const tutorialStepDef = tutorialActive ? TUTORIAL_STEPS[tutorial.step] : undefined;
  /** The action-bar control the step spotlights (others dim) — only while the
   * bar is actually the learner's to use. */
  const tutorialSpotlight =
    tutorialActive && phase === 'playing' && currentPlayerId === selfId
      ? tutorialStepDef?.spotlight
      : undefined;
  const [tutorialCardHeight, setTutorialCardHeight] = useState(0);
  const tutorialMarkedRef = useRef(false);
  /** Record completion once (finish, exit, or simply reaching the last step). */
  const markTutorialComplete = useCallback(() => {
    if (tutorialMarkedRef.current) return;
    tutorialMarkedRef.current = true;
    setTutorialDone(true);
    if (authenticated) {
      withAuthRetry((token) => completeTutorial(token)).catch(() => {
        // Fail-soft: the hub card merely shows again next launch.
      });
    }
  }, [authenticated]);
  useEffect(() => {
    if (tutorialActive && tutorialStepDef?.id === 'done') markTutorialComplete();
  }, [tutorialActive, tutorialStepDef, markTutorialComplete]);

  /** Glow the step's targets and pan to them when the step changes. The
   * action flows own the board highlights while they are open; the targets
   * come back when they close. */
  const tutorialFocusedStepRef = useRef(-1);
  useEffect(() => {
    const game = gameRef.current;
    if (!game || !tutorialActive) return;
    const flowOpen = !!buildFlow || !!aimFlow || !!implementFlow || !!effectsFlow;
    if (!flowOpen) game.highlightNodes(tutorial.nodeIds.length ? tutorial.nodeIds : null);
    if (!bridgeFlow) {
      if (tutorial.edge) {
        const [a = -1, b = -1] = tutorial.edge.split('-').map(Number);
        game.setBridgeHighlight([[a, b]]);
      } else {
        game.setBridgeHighlight(null);
      }
    }
    if (tutorialFocusedStepRef.current !== tutorial.step && tutorial.nodeIds.length > 0) {
      tutorialFocusedStepRef.current = tutorial.step;
      game.focusNodes(tutorial.nodeIds);
    }
  }, [tutorialActive, tutorial, buildFlow, aimFlow, implementFlow, effectsFlow, bridgeFlow]);

  /** Leave the room and return to the lobby list (the durable lobby keeps the
   * seat, so a live game can be resumed from there). */
  const backToLobbies = useCallback(() => {
    void roomRef.current?.leave();
    setActiveMatch(null);
    setPhase('lobby');
    setPlayers([]);
    setSpectators([]);
    setSelfId('');
    setGameOver(null);
  }, []);
  /** Finish or exit the tutorial: record it, then back to the hub. */
  const finishTutorial = useCallback(() => {
    markTutorialComplete();
    backToLobbies();
  }, [markTutorialComplete, backToLobbies]);

  /** The corner Back-to-Lobby button — mid-game it double-checks first. */
  const confirmBackToLobbies = useCallback(() => {
    if (
      phase === 'playing' &&
      !window.confirm(
        'Return to the lobby? Your seat is saved — you can resume the game from the lobby list.',
      )
    ) {
      return;
    }
    backToLobbies();
  }, [phase, backToLobbies]);
  useEffect(() => {
    if (!iAmDefending) setAimFlow((cur) => (cur?.defense ? null : cur));
  }, [iAmDefending]);

  // Mirror every synced aim onto the board as arrows (attack red / defend blue).
  // A bridge aim's arrow points at the span's midpoint, not its anchor node.
  useEffect(() => {
    void boardVersion; // re-runs when node state changes
    const aims: {
      from: number;
      to: number;
      mode: string;
      edge?: readonly [number, number];
    }[] = [];
    for (const [id, info] of nodeInfoRef.current) {
      if (info.building !== 'barracks' || info.aimTargetId < 0) continue;
      if (info.aimMode !== 'attack' && info.aimMode !== 'defend') continue;
      if (info.aimEdge !== '') {
        const dash = info.aimEdge.indexOf('-');
        aims.push({
          from: id,
          to: info.aimTargetId,
          mode: info.aimMode,
          edge: [
            Number(info.aimEdge.slice(0, dash)),
            Number(info.aimEdge.slice(dash + 1)),
          ],
        });
      } else {
        aims.push({ from: id, to: info.aimTargetId, mode: info.aimMode });
      }
    }
    gameRef.current?.setAims(aims);
  }, [boardVersion]);

  // --- Card Effects flow -----------------------------------------------------
  // The tray of your implemented Fortress cards' effects. Availability comes
  // from the shared registry gate (`canUseCardAction` — ownership, grant,
  // exhaustion, token cap) plus the same turn/action-point rules the server
  // handlers enforce, so an enabled button always passes validation.

  /** Your buildings not already protected — targets for Godspeed's protect
   * action. Scouts only unless the card's effects carry `protectAnyBuilding`
   * (catalogue "10"+), which widens it to every building you own, fortress
   * pieces included (a destructible fortress can lose one and unravel). With
   * `protectMarkers` on top (catalogue "39"+) every X marker of your own
   * fortresses joins them — mirrors the server's gate. */
  const computeProtectableScouts = useCallback(
    (anyBuilding: boolean, markers = false): Set<number> => {
      const out = new Set<number>();
      if (!selfId) return out;
      for (const [id, info] of nodeInfoRef.current) {
        if (info.indestructible) continue;
        if (!anyBuilding) {
          if (info.ownerId !== selfId || info.building !== 'scout') continue;
          out.add(id);
          continue;
        }
        if (info.building === '') {
          // An X marker: any of your fortress-stamped empty nodes, on "39"+
          // only — earlier printings protect buildings alone.
          if (!markers || info.fortressOwnerId !== selfId) continue;
        } else if (info.ownerId !== selfId) {
          continue;
        }
        out.add(id);
      }
      return out;
    },
    [selfId],
  );

  /** True if any Godspeed you hold may cover ANY building (catalogue "10"+). */
  const protectAnyBuilding = useMemo(
    () =>
      !!self?.fortressCards.some(
        (fc) =>
          effectOf(fc.cardId)?.grants?.includes('make_scout_indestructible') &&
          effectOf(fc.cardId)?.protectAnyBuilding === true,
      ),
    [self, effectOf],
  );

  /** True if any Godspeed you hold may cover your X markers (catalogue "39"+). */
  const protectMarkers = useMemo(
    () =>
      !!self?.fortressCards.some(
        (fc) =>
          effectOf(fc.cardId)?.grants?.includes('make_scout_indestructible') &&
          effectOf(fc.cardId)?.protectMarkers === true,
      ),
    [self, effectOf],
  );

  /** Your aimed (committed) Barracks — candidates for "Ready Barracks". */
  const computeAimedBarracks = useCallback((): Set<number> => {
    const out = new Set<number>();
    if (!selfId) return out;
    for (const [id, info] of nodeInfoRef.current) {
      if (info.ownerId !== selfId || info.building !== 'barracks') continue;
      if (info.aimTargetId < 0) continue; // upright — nothing to ready
      out.add(id);
    }
    return out;
  }, [selfId]);

  /** Stab Pole Hole's legal designation targets: your own non-fortress,
   * non-indestructible buildings that at least one opponent Barracks can
   * reach with an attack aim — the server's forced-attacker sweep, mirrored
   * per opponent (each traces with THEIR attack range, obstruction and gap
   * passives). Also gates the tray button: no reachable building, no action. */
  const computeDesignateTargets = useCallback((): Set<number> => {
    const allowed = new Set<number>();
    if (!gameConfig || !selfId) return allowed;
    for (const opponent of playersRef.current) {
      if (opponent.id === selfId) continue;
      // That opponent's OWN attack geometry — their range and their passives.
      const { range, blocked, severed, freeEdges } = aimProfileFor(
        opponent,
        'attack',
      );
      if (range <= 0) continue;
      for (const [raxId, raxInfo] of nodeInfoRef.current) {
        if (raxInfo.ownerId !== opponent.id) continue;
        if (raxInfo.building !== 'barracks') continue;
        if (raxInfo.fortressOwnerId !== '') continue; // absorbed — never attacks
        const reach = nodesWithinInto(
          BUILD_GRAPH,
          raxId,
          range,
          blocked,
          severed,
          freeEdges,
        );
        for (const id of reach.keys()) {
          if (allowed.has(id)) continue;
          const info = nodeInfoRef.current.get(id);
          if (!info || info.ownerId !== selfId || info.building === '') continue;
          if (info.fortressOwnerId !== '' || info.indestructible) continue;
          allowed.add(id);
        }
      }
    }
    return allowed;
  }, [gameConfig, selfId, aimProfileFor]);

  /** Live board counts feeding effect availability (recomputed with the board). */
  const effectContext = useMemo((): EffectContext => {
    void boardVersion; // node state lives in refs — recompute when it changes
    return {
      // While planning queued moves it isn't our turn, so the live count
      // belongs to someone else — gate against a fresh turn's worth instead
      // (the server re-validates when the queued move actually plays).
      actionsRemaining:
        planSlot !== null ? Number.MAX_SAFE_INTEGER : actionsRemaining,
      protectableScouts: computeProtectableScouts(
        protectAnyBuilding,
        protectMarkers,
      ).size,
      aimedBarracks: computeAimedBarracks().size,
      designateTargets: computeDesignateTargets().size,
      pendingClaims: self?.pendingResourceClaims ?? 0,
      pendingPicks: self?.pendingResourcePicks ?? 0,
      pendingDrops: self?.pendingResourceDrops ?? 0,
      pendingMarketActions: self?.pendingMarketActions ?? 0,
      discardedCards:
        markets.regular.discarded.length + markets.win.discarded.length,
      // Planning bypasses resource gates (see effectAvailability) — a queued
      // yield may fund the effect by the time it plays.
      planning: planSlot !== null,
    };
  }, [
    boardVersion,
    actionsRemaining,
    planSlot,
    computeProtectableScouts,
    protectAnyBuilding,
    protectMarkers,
    computeAimedBarracks,
    computeDesignateTargets,
    self,
    markets,
  ]);

  /** How many (card, effect) pairs are usable right now — the bar button badge. */
  const usableEffectCount = useMemo(() => {
    if (!self) return 0;
    let count = 0;
    for (const fc of self.fortressCards) {
      for (const kind of effectOf(fc.cardId)?.grants ?? []) {
        if (
          effectAvailability(self, fc.cardId, kind, effectContext, effectOf)
            .allowed
        ) {
          count += 1;
        }
      }
    }
    return count;
  }, [self, effectContext, effectOf]);

  /** Whether the tray has anything to show at all (any card with an effect). */
  const hasEffectCards = useMemo(
    () =>
      !!self?.fortressCards.some((fc) => {
        const fx = effectOf(fc.cardId);
        return (fx?.grants?.length ?? 0) > 0 || (fx?.passives?.length ?? 0) > 0;
      }),
    [self, effectOf],
  );

  /** Cards marked by ANY player's Dog Den, pinned in the market sidebar so
   * everyone knows them. A mark whose deck-half is already satisfied stays
   * visible but flips to a ✓ "condition met" state; when several dens mark
   * one card, still-gating beats satisfied. */
  const markedTiles = useMemo(() => {
    const map = new Map<string, boolean>(); // card id -> half satisfied
    for (const p of players) {
      for (const fc of p.fortressCards) {
        for (const id of fc.markedCardIds) {
          const isWin = cardsById.get(id)?.kind === 'win';
          const done = isWin
            ? fc.markedWinImplemented
            : fc.markedRegularImplemented;
          const cur = map.get(id);
          map.set(id, cur === undefined ? done : cur && done);
        }
      }
    }
    return [...map.entries()].map(([id, done]) => ({ id, done }));
  }, [players, cardsById]);

  /** The Card-Effects bar button toggles the tray. */
  const openEffects = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setImplementFlow(null);
    closeSelection();
    setEffectsFlow((prev) => (prev ? null : { stage: 'list' }));
  }, [closeSelection]);

  const cancelEffects = useCallback(() => setEffectsFlow(null), []);
  /** Sub-flow back button — return to the tray list. */
  const backToEffectsList = useCallback(
    () => setEffectsFlow({ stage: 'list' }),
    [],
  );

  /** Pee-Pee Pinata: unoccupied nodes at or above the granting card's zone
   * floor ("the highest two energy zones" on the current printing — the floor
   * is catalogue hook data, mirroring the server). */
  const computeDropTargets = useCallback(
    (cardId: string): Set<number> => {
      const dropHook = effectOf(cardId)?.hooks?.find(
        (h) => h.kind === 'implement_grant_resource_drops',
      );
      const minZone =
        dropHook && 'minZoneLevel' in dropHook ? dropHook.minZoneLevel : 3;
      const out = new Set<number>();
      for (const [id, info] of nodeInfoRef.current) {
        if (info.building !== '' || info.fortressOwnerId !== '') continue;
        if (vigorZoneLevel(NODE_ZONE[id] ?? 'forest') < minZone) continue;
        out.add(id);
      }
      return out;
    },
    [effectOf],
  );

  /** A tray Use button — route the effect kind to its sub-flow (or send it
   * outright when it needs no further input). */
  const startEffect = useCallback(
    (cardId: string, kind: SpecialActionKind) => {
      switch (kind) {
        case 'pay_penalty_with_token':
          setEffectsFlow(null);
          setBuildFlow({ stage: 'pick', penaltyCardId: cardId });
          return;
        case 'free_build':
          // Block Us Baby reuses the ordinary build picker, like the penalty
          // build does — pick a building, then a highlighted node.
          setEffectsFlow(null);
          setBuildFlow({ stage: 'pick', freeBuildCardId: cardId });
          return;
        case 'free_bridge':
          // Bridgette the Beauty reuses the ordinary bridge flow — tap a
          // highlighted path — at her wider range, without an action point.
          setEffectsFlow(null);
          setBridgeFlow(bridgeFlowFor(cardId));
          return;
        case 'replicate_fortress': {
          const owned = self?.fortressCards.find((card) => card.cardId === cardId);
          const definition = cardsById.get(cardId);
          if (!owned || !definition || owned.zoneLevel < 0) return;
          const allowed = new Set<number>();
          for (const anchor of BUILD_GRAPH.nodes) {
            const match = matchFortressPattern(
              BUILD_GRAPH,
              () => null,
              definition.pattern,
              {
                anchorNodeId: anchor.id,
                // Mirror the server ("50"): the replica's bridges may reach
                // across a zone boundary — adjacency and a free edge are all
                // that's asked of them.
                bridgeAt: (a, b) =>
                  areAdjacent(BUILD_GRAPH, a, b) && !bridgesRef.current.has(edgeKey(a, b)),
                // The replica's X needs a wholly empty node in the original's
                // ENERGY zone (forest + foothill are one) — no demolition, no
                // sharing.
                markerBlockedAt: (id) => {
                  if (
                    energyZoneOf(vigorZoneLevel(NODE_ZONE[id] ?? 'forest')) !==
                    energyZoneOf(owned.zoneLevel)
                  ) {
                    return true;
                  }
                  const info = nodeInfoRef.current.get(id);
                  return (
                    !!info && (info.building !== '' || info.fortressOwnerId !== '')
                  );
                },
              },
            );
            if (match) allowed.add(anchor.id);
          }
          setEffectsFlow({ stage: 'replicate', cardId, allowed });
          return;
        }
        case 'double_banked_resources':
        case 'force_end_round':
        case 'bank_production_reset':
          setEffectsFlow({ stage: 'confirm', cardId, kind });
          return;
        case 'buy_extra_action':
        case 'collect_card_energy':
        case 'spend_token_for_action':
        case 'gain_free_action':
        case 'mineral_windfall':
          sendGameAction({ type: kind, cardId });
          return;
        case 'market_reaction':
          setEffectsFlow({ stage: 'market', cardId, kind });
          return;
        case 'redistribute_tokens': {
          // Big Boom (`adjustRegularOnly`) moves tokens between REGULAR-deck
          // fortresses only; the win deck's stay where they are.
          const regularOnly = effectOf(cardId)?.adjustRegularOnly === true;
          const entries = (self?.fortressCards ?? [])
            .filter((fc) => !regularOnly || cardsById.get(fc.cardId)?.kind === 'REGULAR')
            .map((fc) => ({
              cardId: fc.cardId,
              name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
              tokens: fc.tokens,
              start: fc.tokens,
              cap: instanceTokenCap(effectOf(fc.cardId), fc),
            }));
          setEffectsFlow({
            stage: 'redistributeTokens',
            cardId,
            entries,
            total: entries.reduce((a, e) => a + e.tokens, 0),
          });
          return;
        }
        case 'adjust_fortress_tokens': {
          // Big Boom ("53"+): up to N unit changes across the holder's OTHER
          // regular fortresses, composed on the same stepper under a change
          // budget instead of a fixed total. One instance of the granting
          // card is hidden from its own list (its token is the use counter).
          const fx = effectOf(cardId);
          const regularOnly = fx?.adjustRegularOnly === true;
          let hidSource = false;
          const entries = (self?.fortressCards ?? [])
            .filter((fc) => {
              if (!hidSource && fc.cardId === cardId) {
                hidSource = true;
                return false;
              }
              return !regularOnly || cardsById.get(fc.cardId)?.kind === 'REGULAR';
            })
            .map((fc) => ({
              cardId: fc.cardId,
              name: cardsById.get(fc.cardId)?.name ?? fc.cardId,
              tokens: fc.tokens,
              start: fc.tokens,
              cap: instanceTokenCap(effectOf(fc.cardId), fc),
            }));
          setEffectsFlow({
            stage: 'redistributeTokens',
            cardId,
            entries,
            total: entries.reduce((a, e) => a + e.tokens, 0),
            budget: fx?.tokenAdjustments ?? 1,
          });
          return;
        }
        case 'implement_from_discard':
          // Dungeon Dice Martyr ("53"+): the discard piles' cards already sit
          // in the Implement picker while the grant has uses — open it.
          setEffectsFlow(null);
          openImplementRef.current();
          return;
        case 'make_scout_indestructible':
          setEffectsFlow({
            stage: 'pickScout',
            cardId,
            allowed: computeProtectableScouts(
              effectOf(cardId)?.protectAnyBuilding === true,
              effectOf(cardId)?.protectMarkers === true,
            ),
          });
          return;
        case 'untap_barracks': {
          const card = self?.fortressCards.find((fc) => fc.cardId === cardId);
          const cap = instanceTokenCap(
            card ? effectOf(card.cardId) : effectOf(cardId),
            card,
          );
          const limit = Number.isFinite(cap)
            ? Math.max(0, cap - (card?.tokens ?? 0))
            : Number.MAX_SAFE_INTEGER;
          setEffectsFlow({
            stage: 'untap',
            cardId,
            allowed: computeAimedBarracks(),
            selected: new Set<number>(),
            limit,
          });
          return;
        }
        case 'adjust_fortress_token':
          setEffectsFlow({ stage: 'adjustToken', cardId });
          return;
        case 'copy_fortress_effect':
          setEffectsFlow({ stage: 'copyEffect', cardId });
          return;
        case 'mark_fortresses':
          setEffectsFlow({ stage: 'markFortresses', cardId });
          return;
        case 'set_fortress_lock':
        case 'free_cycle_market':
          setMarketCollapsed(false);
          setEffectsFlow({ stage: 'market', cardId, kind });
          return;
        case 'reorder_fortress_deck':
          setEffectsFlow({ stage: 'reorderMarket', cardId });
          return;
        case 'move_fortress': {
          // Empty, un-hardened nodes within your Barracks ATTACK range of the
          // fortress's current nodes — mirrors the server's move validation.
          const fc = self?.fortressCards.find((c) => c.cardId === cardId);
          const card = cardsById.get(cardId);
          if (!fc || !card || !gameConfig || !self) return;
          // The move traces with your real ATTACK geometry — Telescopic Arm's
          // gap-crossing included, matching `moveFortress.ts`'s `canAimAt`.
          const { range, blocked, severed, freeEdges } = aimProfileFor(
            self,
            'attack',
          );
          const allowed = new Set<number>();
          for (const from of fc.nodeIds) {
            const reach = nodesWithinInto(
              BUILD_GRAPH,
              from,
              range,
              blocked,
              severed,
              freeEdges,
            );
            for (const id of reach.keys()) {
              const info = nodeInfoRef.current.get(id);
              if (!info || info.building !== '' || info.fortressOwnerId !== '')
                continue;
              allowed.add(id);
            }
          }
          setEffectsFlow({
            stage: 'moveFortress',
            cardId,
            allowed,
            selected: new Set<number>(),
            limit: card.pattern.buildings.length,
          });
          return;
        }
        case 'claim_resources':
          setEffectsFlow({
            stage: 'claim',
            cardId,
            picked: [],
            remaining: self?.pendingResourceClaims ?? 0,
          });
          return;
        case 'sell_building': {
          // Your own buildings not hardened into a fortress.
          const allowed = new Set<number>();
          for (const [id, info] of nodeInfoRef.current) {
            if (info.ownerId !== selfId || info.building === '') continue;
            if (info.fortressOwnerId !== '') continue;
            allowed.add(id);
          }
          setEffectsFlow({ stage: 'sellBuilding', cardId, allowed });
          return;
        }
        case 'designate_attack_target':
          setEffectsFlow({
            stage: 'designateTarget',
            cardId,
            allowed: computeDesignateTargets(),
          });
          return;
        case 'tutor_market_card':
          setEffectsFlow({
            stage: 'tutor',
            cardId,
            // A `tutorMarkets` printing ("54"+ Poopy: regular only) opens on
            // its first searchable row.
            market: effectOf(cardId)?.tutorMarkets?.[0] ?? 'REGULAR',
            foundCardId: '',
          });
          return;
        case 'shuffle_discard_into_deck':
          setEffectsFlow({ stage: 'shuffleDiscard', cardId, pick: null, cycle: [] });
          return;
        case 'move_building': {
          // Nomadic Pufferfish: each of your non-fortress buildings, with its
          // legal destinations — empty nodes within your Mine's build range
          // OF THAT BUILDING (traced from it, not your network; mirrors the
          // server's aimInRange with build-flavor obstructions).
          if (!gameConfig || !self) return;
          const range = resolveBuilding(gameConfig, self, 'mine').range.build;
          const bypass = anyCardHasPassive(
            self.fortressCards,
            'build_through_obstructions',
            effectOf,
          );
          const blocked = blockedNodesForClient(selfId ?? '', bypass);
          const allowed = new Set<number>();
          const targets = new Map<number, ReadonlySet<number>>();
          for (const [id, info] of nodeInfoRef.current) {
            if (info.ownerId !== selfId || info.building === '') continue;
            if (info.fortressOwnerId !== '') continue;
            const reach = nodesWithinInto(
              BUILD_GRAPH,
              id,
              range,
              blocked,
              severedRef.current,
              freeEdgesForClient(self),
            );
            const dests = new Set<number>();
            for (const dest of reach.keys()) {
              const dinfo = nodeInfoRef.current.get(dest);
              if (!dinfo || dinfo.building !== '' || dinfo.fortressOwnerId !== '')
                continue;
              dests.add(dest);
            }
            if (dests.size > 0) {
              allowed.add(id);
              targets.set(id, dests);
            }
          }
          setEffectsFlow({ stage: 'moveBuildingFrom', cardId, allowed, targets });
          return;
        }
        case 'bank_two_resources': {
          // A fixed-kind payout (Plasmamagnetic Waves "49"+: "+1 energy")
          // needs no pick.
          const fixed = effectOf(cardId)?.bankGrant?.resource;
          if (fixed !== undefined) {
            sendGameAction({
              type: kind,
              cardId,
              resource: RESOURCE_KINDS[fixed] ?? 'vigor',
            });
            return;
          }
          setEffectsFlow({ stage: 'bankTwo', cardId });
          return;
        }
        case 'redistribute_resources': {
          const bag = self ? toAmount(self.resources) : [0, 0, 0, 0];
          setEffectsFlow({
            stage: 'redistribute',
            cardId,
            amounts: bag,
            total: bag.reduce((a, b) => a + b, 0),
          });
          return;
        }
        case 'pick_bonus_resources':
          setEffectsFlow({
            stage: 'pickIncome',
            cardId,
            remaining: self?.pendingResourcePicks ?? 0,
          });
          return;
        case 'drop_one_time_resources':
          setEffectsFlow({
            stage: 'dropResources',
            cardId,
            allowed: computeDropTargets(cardId),
            resource: 'minerals',
            remaining: self?.pendingResourceDrops ?? 0,
          });
          return;
        default:
          return; // no client flow (stubs / build-rider kinds) — button is disabled
      }
    },
    [
      self,
      selfId,
      gameConfig,
      cardsById,
      effectOf,
      computeProtectableScouts,
      computeAimedBarracks,
      computeDropTargets,
      computeDesignateTargets,
      sendGameAction,
      aimProfileFor,
      blockedNodesForClient,
      freeEdgesForClient,
      bridgeFlowFor,
    ],
  );

  /** Move-Fortress selection confirmed — relocate to the chosen nodes. */
  const confirmMoveFortress = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'moveFortress' || flow.selected.size !== flow.limit)
      return;
    sendGameAction({
      type: 'move_fortress',
      cardId: flow.cardId,
      nodeIds: [...flow.selected],
      ...(flow.doubleOneTime
        ? { riders: { doubleOneTime: flow.doubleOneTime } }
        : {}),
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  const toggleMoveDoubleOneTime = useCallback(() => {
    setEffectsFlow((flow) => {
      // Both moves that collect: Traveling Booba's fortress move and Nomadic
      // Pufferfish's single-building move.
      if (flow?.stage !== 'moveFortress' && flow?.stage !== 'moveBuildingTo') {
        return flow;
      }
      return {
        ...flow,
        doubleOneTime: flow.doubleOneTime
          ? undefined
          : (bunBun?.cardId ?? undefined),
      };
    });
  }, [bunBun]);

  /** Poopy Investigator: switch which row's pool is being searched. */
  const setTutorMarket = useCallback((market: FortressKind) => {
    setEffectsFlow((flow) =>
      flow?.stage === 'tutor' ? { ...flow, market, foundCardId: '' } : flow,
    );
  }, []);

  /** Poopy Investigator: a deck card was picked as the find. */
  const setTutorFound = useCallback((foundCardId: string) => {
    setEffectsFlow((flow) =>
      flow?.stage === 'tutor' ? { ...flow, foundCardId } : flow,
    );
  }, []);

  /** Poopy Investigator: search confirmed — the found card goes on top of the
   * reshuffled deck. */
  const confirmTutor = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'tutor' || flow.foundCardId === '') return;
    sendGameAction({
      type: 'tutor_market_card',
      cardId: flow.cardId,
      market: flow.market,
      foundCardId: flow.foundCardId,
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Dungeon Dice Martyr: pick (or un-pick) the discard to shuffle back. The
   * free cycles belong to the picked card's row, so a pick from the other
   * row (or none) clears them. */
  const pickShuffleDiscard = useCallback(
    (market: FortressKind, cardId: string) => {
      setEffectsFlow((flow) => {
        if (flow?.stage !== 'shuffleDiscard') return flow;
        const same = flow.pick?.market === market && flow.pick.cardId === cardId;
        const pick = same ? null : { market, cardId };
        const keepCycle = pick !== null && flow.pick?.market === market;
        return { ...flow, pick, cycle: keepCycle ? flow.cycle : [] };
      });
    },
    [],
  );

  /** Dungeon Dice Martyr: toggle a face-up card of the picked row on or off
   * the free cycle, keeping at most `shuffleDiscardCycleCount` selected. */
  const toggleShuffleCycle = useCallback(
    (index: number) => {
      setEffectsFlow((flow) => {
        if (flow?.stage !== 'shuffleDiscard' || !flow.pick) return flow;
        if (flow.cycle.includes(index)) {
          return { ...flow, cycle: flow.cycle.filter((i) => i !== index) };
        }
        const cap = effectOf(flow.cardId)?.shuffleDiscardCycleCount ?? 0;
        if (flow.cycle.length >= cap) return flow;
        return { ...flow, cycle: [...flow.cycle, index] };
      });
    },
    [effectOf],
  );

  /** Dungeon Dice Martyr: shuffle the pick back, then cycle the selection. */
  const confirmShuffleDiscard = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'shuffleDiscard' || !flow.pick) return;
    sendGameAction({
      type: 'shuffle_discard_into_deck',
      cardId: flow.cardId,
      market: flow.pick.market,
      discardCardId: flow.pick.cardId,
      ...(flow.cycle.length ? { cycle: [...flow.cycle] } : {}),
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Curious Clit: start the server-side shuffle for one market row. */
  const startReorderMarket = useCallback(
    (market: FortressKind) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'reorderMarket') return;
      sendGameAction({
        type: 'reorder_fortress_deck',
        cardId: flow.cardId,
        market,
        order: [],
      });
    },
    [sendGameAction],
  );

  /** The private top ten arrive through the owning player's StateView. */
  useEffect(() => {
    if (!self) return;
    setEffectsFlow((current) => {
      if (self.pendingReorderCards.length === 0) {
        return current?.stage === 'reorderDeck' ? null : current;
      }
      if (current?.stage === 'reorderDeck' && current.submitted) return current;
      const cardId =
        self.fortressCards.find((card) =>
          effectOf(card.cardId)?.grants?.includes('reorder_fortress_deck'),
        )?.cardId ?? '';
      return {
        stage: 'reorderDeck',
        cardId,
        market: self.pendingReorderMarket === 'win' ? 'win' : 'REGULAR',
        order: [...self.pendingReorderCards],
        route: {},
        cycle: [],
      };
    });
  }, [self, effectOf]);

  const moveReorderCard = useCallback((index: number, delta: -1 | 1) => {
    setEffectsFlow((flow) => {
      if (flow?.stage !== 'reorderDeck') return flow;
      const to = index + delta;
      if (to < 0 || to >= flow.order.length) return flow;
      const order = [...flow.order];
      [order[index], order[to]] = [order[to]!, order[index]!];
      return { ...flow, order };
    });
  }, []);

  /** Curious Climb's peek printing: toggle a glanced card's destination —
   * clicking its current destination sends it back to the top. */
  const setReorderRoute = useCallback(
    (cardId: string, dest: 'bottom' | 'discard') => {
      setEffectsFlow((flow) => {
        if (flow?.stage !== 'reorderDeck' || flow.submitted) return flow;
        const route: Record<string, 'bottom' | 'discard'> = { ...flow.route };
        if (route[cardId] === dest) delete route[cardId];
        else route[cardId] = dest;
        return { ...flow, route };
      });
    },
    [],
  );

  /** Curious Climb's free cycle: toggle a face-up card of the glanced row on
   * or off the cycle pick, keeping at most `reorderCycleCount` selected. */
  const toggleReorderCycle = useCallback(
    (index: number) => {
      setEffectsFlow((flow) => {
        if (flow?.stage !== 'reorderDeck' || flow.submitted) return flow;
        if (flow.cycle.includes(index)) {
          return { ...flow, cycle: flow.cycle.filter((i) => i !== index) };
        }
        const cap = effectOf(flow.cardId)?.reorderCycleCount ?? 0;
        if (flow.cycle.length >= cap) return flow;
        return { ...flow, cycle: [...flow.cycle, index] };
      });
    },
    [effectOf],
  );

  const confirmReorder = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'reorderDeck') return;
    const peek = !!effectOf(flow.cardId)?.reorderPeek;
    roomRef.current?.send(ClientMessage.Action, {
      type: 'reorder_fortress_deck',
      cardId: flow.cardId,
      market: flow.market,
      order: peek
        ? flow.order.filter((id) => flow.route[id] === undefined)
        : flow.order,
      ...(peek
        ? {
            bottom: flow.order.filter((id) => flow.route[id] === 'bottom'),
            discard: flow.order.filter((id) => flow.route[id] === 'discard'),
          }
        : {}),
      ...(flow.cycle.length ? { cycle: [...flow.cycle] } : {}),
    });
    setEffectsFlow({ ...flow, submitted: true });
  }, [effectOf]);

  /** Claim stage: add one resource pick. Every banked award is cashed in ONE
   * prompt — two picks per award — so the send waits until the last pick. */
  const pickClaimResource = useCallback((kind: string) => {
    setEffectsFlow((cur) => {
      if (cur?.stage !== 'claim') return cur;
      if (cur.submitted) return cur;
      const picked = [...cur.picked, kind];
      if (picked.length < cur.remaining * 2) return { ...cur, picked };
      roomRef.current?.send(ClientMessage.Action, {
        type: 'claim_resources',
        cardId: cur.cardId,
        resources: picked,
      });
      return { ...cur, picked, submitted: true };
    });
  }, []);

  /** iCarlie awards are chosen as soon as they are triggered, including during
   * another player's turn. */
  useEffect(() => {
    if (!self || self.pendingResourceClaims <= 0 || effectsFlow) return;
    const card = self.fortressCards.find((fc) =>
      effectOf(fc.cardId)?.grants?.includes('claim_resources'),
    );
    if (card) {
      setEffectsFlow({
        stage: 'claim',
        cardId: card.cardId,
        picked: [],
        remaining: self.pendingResourceClaims,
      });
    }
  }, [self, effectsFlow, effectOf]);

  useEffect(() => {
    if (effectsFlow?.stage !== 'claim' || !self) return;
    const claims = self.pendingResourceClaims;
    setEffectsFlow((cur) => {
      if (cur?.stage !== 'claim') return cur;
      // Awaiting the server: the batch is cashed once the bank drops below
      // what we sent. Anything still banked (a trigger that landed after the
      // send) opens a fresh prompt.
      if (cur.submitted) {
        if (claims >= cur.remaining) return cur;
        return claims > 0
          ? { ...cur, picked: [], remaining: claims, submitted: false }
          : null;
      }
      // Still picking: a fresh trigger RAISES this prompt's target
      // (2/2 -> 4/4) instead of queueing another prompt behind it.
      if (claims === cur.remaining) return cur;
      return claims > 0
        ? { ...cur, remaining: claims, picked: cur.picked.slice(0, claims * 2) }
        : null;
    });
  }, [self, effectsFlow]);

  /** Morphing Midget: one chosen kind, +2 banked. */
  const sendBankTwo = useCallback(
    (resource: string) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'bankTwo') return;
      sendGameAction({
        type: 'bank_two_resources',
        cardId: flow.cardId,
        resource,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Demented Rapist: nudge one kind of the split being composed. Units come
   * out of (and go back into) the unassigned pool — `total` is fixed, so the
   * confirm only unlocks once the pool is empty again. */
  const nudgeRedistribution = useCallback((index: number, delta: number) => {
    setEffectsFlow((flow) => {
      if (flow?.stage !== 'redistribute') return flow;
      const next = (flow.amounts[index] ?? 0) + delta;
      if (next < 0) return flow;
      const assigned = flow.amounts.reduce((a, b) => a + b, 0);
      if (delta > 0 && assigned >= flow.total) return flow;
      return {
        ...flow,
        amounts: flow.amounts.map((n, i) => (i === index ? next : n)),
      };
    });
  }, []);

  /** Demented Rapist: commit the composed split (one action, once per game). */
  const sendRedistribute = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'redistribute') return;
    sendGameAction({
      type: 'redistribute_resources',
      cardId: flow.cardId,
      distribution: [...flow.amounts],
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Big Boom: move one token on or off a held fortress of the composed
   * redistribution. Tokens come out of (and go back into) the unassigned
   * pool — `total` is fixed, so the confirm only unlocks once it's empty. */
  const nudgeTokenRedistribution = useCallback((index: number, delta: number) => {
    setEffectsFlow((flow) => {
      if (flow?.stage !== 'redistributeTokens') return flow;
      const entry = flow.entries[index];
      if (!entry) return flow;
      const next = entry.tokens + delta;
      if (next < 0 || next > entry.cap) return flow;
      if (flow.budget !== undefined) {
        // Budgeted (Big Boom "53"+): the unit changes from the opening counts
        // may not exceed the budget — stepping back toward `start` frees one.
        const used = flow.entries.reduce(
          (a, e, i) => a + Math.abs((i === index ? next : e.tokens) - e.start),
          0,
        );
        if (used > flow.budget) return flow;
      } else {
        const assigned = flow.entries.reduce((a, e) => a + e.tokens, 0);
        if (delta > 0 && assigned >= flow.total) return flow;
      }
      return {
        ...flow,
        entries: flow.entries.map((e, i) =>
          i === index ? { ...e, tokens: next } : e,
        ),
      };
    });
  }, []);

  const sendTokenRedistribution = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'redistributeTokens') return;
    if (flow.budget !== undefined) {
      // Big Boom ("53"+): each entry's net change, as unit adjustments.
      const adjustments = flow.entries.flatMap((e) => {
        const d = e.tokens - e.start;
        return Array.from({ length: Math.abs(d) }, () => ({
          targetCardId: e.cardId,
          delta: d > 0 ? 1 : -1,
        }));
      });
      if (adjustments.length === 0) return;
      sendGameAction({
        type: 'adjust_fortress_tokens',
        cardId: flow.cardId,
        adjustments,
      });
      setEffectsFlow(null);
      return;
    }
    sendGameAction({
      type: 'redistribute_tokens',
      cardId: flow.cardId,
      distribution: flow.entries.map((e) => ({ cardId: e.cardId, tokens: e.tokens })),
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Cash one banked single-resource pick (free, off-turn OK). */
  const sendIncomePick = useCallback((kind: string) => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'pickIncome') return;
    roomRef.current?.send(ClientMessage.Action, {
      type: 'pick_bonus_resources',
      cardId: flow.cardId,
      resources: [kind],
    });
  }, []);

  /** Pee-Pee Pinata: switch which kind the next board tap places. */
  const setDropResource = useCallback((resource: string) => {
    setEffectsFlow((flow) =>
      flow?.stage === 'dropResources' ? { ...flow, resource } : flow,
    );
  }, []);

  /** Pick income banks at round start (possibly off-turn) — prompt like an
   * iCarlie claim as soon as picks are waiting. */
  useEffect(() => {
    if (!self || self.pendingResourcePicks <= 0 || effectsFlow) return;
    const card = self.fortressCards.find((fc) =>
      effectOf(fc.cardId)?.grants?.includes('pick_bonus_resources'),
    );
    if (card) {
      setEffectsFlow({
        stage: 'pickIncome',
        cardId: card.cardId,
        remaining: self.pendingResourcePicks,
      });
    }
  }, [self, effectsFlow, effectOf]);

  /** Keep the income prompt's counter live; close it once everything is
   * collected. */
  useEffect(() => {
    if (effectsFlow?.stage !== 'pickIncome' || !self) return;
    if (self.pendingResourcePicks === effectsFlow.remaining) return;
    setEffectsFlow(
      self.pendingResourcePicks > 0
        ? { ...effectsFlow, remaining: self.pendingResourcePicks }
        : null,
    );
  }, [self, effectsFlow]);

  /** Pee-Pee Pinata: the distribution opens as soon as the implement banks
   * units ("as soon as this fortress is implemented, distribute..."). */
  useEffect(() => {
    if (!self || self.pendingResourceDrops <= 0 || effectsFlow) return;
    const card = self.fortressCards.find((fc) =>
      effectOf(fc.cardId)?.grants?.includes('drop_one_time_resources'),
    );
    if (card) {
      setEffectsFlow({
        stage: 'dropResources',
        cardId: card.cardId,
        allowed: computeDropTargets(card.cardId),
        resource: 'minerals',
        remaining: self.pendingResourceDrops,
      });
    }
  }, [self, effectsFlow, effectOf, computeDropTargets]);

  /** Keep the distribute prompt's counter (and target set) live; close it
   * once the banked units are spent. */
  useEffect(() => {
    if (effectsFlow?.stage !== 'dropResources' || !self) return;
    if (self.pendingResourceDrops === effectsFlow.remaining) return;
    setEffectsFlow(
      self.pendingResourceDrops > 0
        ? {
            ...effectsFlow,
            remaining: self.pendingResourceDrops,
            allowed: computeDropTargets(effectsFlow.cardId),
          }
        : null,
    );
  }, [self, effectsFlow, computeDropTargets]);

  /** Confirm stage accepted — send the pending one-shot effect. */
  const confirmEffect = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'confirm') return;
    sendGameAction({
      type: flow.kind,
      cardId: flow.cardId,
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Untap selection confirmed — ready the chosen Barracks. */
  const confirmUntap = useCallback(() => {
    const flow = effectsFlowRef.current;
    if (flow?.stage !== 'untap' || flow.selected.size === 0) return;
    sendGameAction({
      type: 'untap_barracks',
      cardId: flow.cardId,
      nodeIds: [...flow.selected],
    });
    setEffectsFlow(null);
  }, [sendGameAction]);

  /** Tokenizer: adjust a token on an implemented fortress — the holder's own,
   * or any player's under an `adjustAnyFortress` printing (catalogue
   * "14"-"37"; "38"+ is own-only again). */
  const sendAdjustToken = useCallback(
    (targetPlayerId: string, targetCardId: string, delta: 1 | -1) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'adjustToken') return;
      sendGameAction({
        type: 'adjust_fortress_token',
        cardId: flow.cardId,
        targetCardId,
        targetPlayerId,
        delta,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** CurdleCat: copy the effect of another implemented fortress. */
  const sendCopyEffect = useCallback(
    (targetPlayerId: string, targetCardId: string) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'copyEffect') return;
      sendGameAction({
        type: 'copy_fortress_effect',
        cardId: flow.cardId,
        targetPlayerId,
        targetCardId,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Dog Den: send the two marks once both rows have a pick, with the
   * marker's reshuffle answers — per mark on the "28"+ printing, one answer
   * for both on "24"–"27" (pre-"24" printings always reshuffle, so the server
   * ignores both fields). */
  const sendMarkFortresses = useCallback(
    (
      markWin: string,
      markRegular: string,
      shuffleWin: boolean,
      shuffleRegular: boolean,
    ) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'markFortresses') return;
      sendGameAction({
        type: 'mark_fortresses',
        cardId: flow.cardId,
        markWin,
        markRegular,
        shuffleWin,
        shuffleRegular,
        // The one-answer field the "24"–"27" printings read; on those the two
        // per-mark answers are always set together anyway.
        shuffleBack: shuffleWin,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Dog Den pick: first the win-row mark, then the regular-row mark. On a
   * printing that leaves the reshuffle to the marker the second pick parks
   * the flow on the shuffle/keep question; otherwise it sends straight
   * away. */
  const pickMarkFortress = useCallback(
    (pickedId: string) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'markFortresses') return;
      if (flow.markWin === undefined) {
        setEffectsFlow({ ...flow, markWin: pickedId });
      } else if (
        cardsByIdRef.current.get(flow.cardId)?.effects?.markShuffleOptional
      ) {
        setEffectsFlow({
          ...flow,
          markRegular: pickedId,
          // Both marks start on the printed reshuffle; the per-card printing
          // lets either be toggled off before confirming.
          shuffleWin: true,
          shuffleRegular: true,
        });
      } else {
        sendMarkFortresses(flow.markWin, pickedId, true, true);
      }
    },
    [sendMarkFortresses],
  );

  /** Dog Den, "28"+ printing: flip one mark between shuffling back and
   * staying face up, before the answers are confirmed. */
  const toggleMarkShuffle = useCallback((which: 'win' | 'regular') => {
    setEffectsFlow((cur) => {
      if (cur?.stage !== 'markFortresses') return cur;
      return which === 'win'
        ? { ...cur, shuffleWin: !cur.shuffleWin }
        : { ...cur, shuffleRegular: !cur.shuffleRegular };
    });
  }, []);

  /** Dog Den: answer the reshuffle question — on "24"–"27" one answer covers
   * both marks, on "28"+ the per-mark answers as toggled are confirmed. */
  const answerMarkShuffle = useCallback(
    (shuffleBack?: boolean) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'markFortresses') return;
      if (flow.markWin === undefined || flow.markRegular === undefined) return;
      sendMarkFortresses(
        flow.markWin,
        flow.markRegular,
        shuffleBack ?? flow.shuffleWin !== false,
        shuffleBack ?? flow.shuffleRegular !== false,
      );
    },
    [sendMarkFortresses],
  );

  /** Supreme Bondage: lock or unlock a face-up market card. */
  const sendFortressLock = useCallback(
    (market: FortressKind, marketCardId: string, locked: boolean) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'market' || flow.kind === 'free_cycle_market') return;
      if (flow.kind === 'market_reaction') {
        // Miss Mineralverse Maggot: one banked reaction spent on a lock.
        sendGameAction({
          type: 'market_reaction',
          cardId: flow.cardId,
          lock: { market, marketCardId, locked },
        });
        setEffectsFlow(null);
        return;
      }
      sendGameAction({
        type: 'set_fortress_lock',
        cardId: flow.cardId,
        market,
        marketCardId,
        locked,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  /** Cyclopz: cycle any number of one market row's face-up cards — up to the
   * whole row — free of cost and action, as a single effect use. */
  const sendFreeCycle = useCallback(
    (market: FortressKind, indices: readonly number[]) => {
      const flow = effectsFlowRef.current;
      if (flow?.stage !== 'market' || flow.kind === 'set_fortress_lock') return;
      if (indices.length === 0) return;
      if (flow.kind === 'market_reaction') {
        // Miss Mineralverse Maggot: one banked reaction cycles ONE card.
        sendGameAction({
          type: 'market_reaction',
          cardId: flow.cardId,
          cycle: { market, index: indices[0]! },
        });
        setEffectsFlow(null);
        return;
      }
      sendGameAction({
        type: 'free_cycle_market',
        cardId: flow.cardId,
        market,
        indices,
      });
      setEffectsFlow(null);
    },
    [sendGameAction],
  );

  // Escape closes the effects flow (sub-flows fall back to the tray list).
  useEffect(() => {
    if (!effectsFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setEffectsFlow((cur) =>
        cur?.stage === 'reorderDeck' || cur?.stage === 'claim'
          ? cur
          : cur && cur.stage !== 'list'
            ? { stage: 'list' }
            : null,
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [effectsFlow]);

  // Reflect the untap selection as persistent gold rings (the pickable set
  // pulses via the shared highlight effect below). Any other stage — including
  // backing out to the tray list — clears them, so rings can't outlive the
  // selection they represent.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (effectsFlow?.stage === 'untap' || effectsFlow?.stage === 'moveFortress') {
      game.highlightNodes(
        effectsFlow.selected.size ? [...effectsFlow.selected] : null,
      );
    } else {
      game.highlightNodes(null);
    }
  }, [effectsFlow]);

  // --- Implement Fortress flow ---------------------------------------------
  // The revealed (face-up) cards whose pattern your FREE buildings (those not
  // already in one of your fortresses) realise somewhere on the board, in any
  // orientation. Mirrors the server's `implementFortress`, so a card shown here
  // (and affordable) will pass server validation. `implementable` (kept live —
  // recomputed via `boardVersion` when the board changes) feeds the implement
  // picker and the always-on "buildable" outlines on the market tiles; picking
  // a card then enumerates EVERY placement so the player chooses the location
  // (and orientation) on the board before confirming.
  // Per-card matcher inputs shared by the implement picker, the placement
  // stage, and the always-on market outlines: the cell / bridge / marker
  // constraints, mirroring the server's `implementFortress` exactly.
  const implementMatcherArgs = useCallback(
    (cardId: string) => {
      if (!self || !selfId) return null;
      const card = cardsById.get(cardId);
      if (!card) return null;
      const fx = card.effects;
      const committed = committedFortressNodes(self.fortressCards);
      // Zone placement constraint, folded into the matcher like the server.
      const zone = fx?.placement?.zone;
      const zoneOk = (id: number): boolean => {
        if (!zone) return true;
        const level = vigorZoneLevel(NODE_ZONE[id] ?? 'forest');
        if (level < (zone.min ?? 0) || level > (zone.max ?? ZONE_HIGHEST)) {
          return false;
        }
        // Mirror the server's vigor gate (Travelling Booba): the penalty cap
        // reads the STATIC default zone config, not the match/player's
        // effective vigor. Without this the client offered Booba in every zone
        // while the server rejected any zone with a non-zero default penalty.
        if (
          zone.maxVigorPenalty !== undefined &&
          (VIGOR_ZONES_CONFIG[level]?.vigorPenalty ?? 0) > zone.maxVigorPenalty
        ) {
          return false;
        }
        return true;
      };
      // Exodial: ANY player's free (non-fortress) building fills a cell —
      // and Corrupt Hermit's holder gets the same relaxation on EVERY
      // implement (mirrors the server's `implementUseOpponentBuildings`).
      const holderMixes = self.fortressCards.some(
        (fc) => effectOf(fc.cardId)?.implementUseOpponentBuildings === true,
      );
      const anyOwner =
        fx?.placement?.useOpponentBuildings === true || holderMixes;
      // Mirrors the server: the Hermit's relaxation is a COMBINATION — a
      // realised set made purely of other players' buildings is not a legal
      // placement. Exodial's own per-card relaxation stays unrestricted.
      const requiresOwnBuilding =
        holderMixes && fx?.placement?.useOpponentBuildings !== true;
      const cellAt = (id: number): FortressBuilding | null => {
        if (!zoneOk(id)) return null;
        if (committed.has(id)) return null; // already part of a fortress
        const info = nodeInfoRef.current.get(id);
        if (!info) return null;
        // Hardened into ANY fortress (own or an opponent's) → spoken for.
        if (info.fortressOwnerId !== '') return null;
        if (anyOwner) {
          if (info.ownerId === '') return null;
          // An opponent's protected building (Godspeed Machine) can't be
          // conscripted — mirrors the server.
          if (info.indestructible && info.ownerId !== selfId) return null;
        } else if (info.ownerId !== selfId) {
          return null;
        }
        const b = info.building;
        return b === 'scout' || b === 'barracks' || b === 'mine' ? b : null;
      };
      // A bridge-only pattern (Hole Worm, Artifact of Atrocity) realises no
      // building cells, so nothing ties it to the player's network. Mirror the
      // server's gate: its X's must sit within Scout build range of the
      // player's own buildings / fortress nodes, so only reachable placements
      // light up as candidates.
      let scoutReach: ReadonlySet<number> | null = null;
      if (card.pattern.buildings.length === 0 && gameConfig) {
        const bypass = anyCardHasPassive(
          self.fortressCards,
          'build_through_obstructions',
          effectOf,
        );
        const sources: number[] = [];
        const blocked = blockedNodesForClient(selfId ?? '', bypass);
        for (const [id, info] of nodeInfoRef.current) {
          if (
            (info.ownerId === selfId && info.building !== '') ||
            info.fortressOwnerId === selfId
          )
            sources.push(id);
        }
        scoutReach = new Set(
          nodesWithinAny(
            BUILD_GRAPH,
            sources,
            resolveBuilding(gameConfig, self, 'scout').range.build,
            blocked,
            severedRef.current,
            freeEdgesForClient(self),
          ).keys(),
        );
      }
      const matchOpts = {
        bridgeAt: (a: number, b: number) =>
          zoneOk(a) && zoneOk(b) && bridgesRef.current.has(edgeKey(a, b)),
        // Mirrors the server: an X can't take a fortress-hardened node — own
        // included (shared nodes corrupt on unravel/move) — EXCEPT an
        // opponent's DESTRUCTIBLE fortress, which the rising fortress razes.
        // Zone constraints bind the X nodes too.
        markerBlockedAt: (id: number) => {
          if (!zoneOk(id)) return true;
          if (scoutReach && !scoutReach.has(id)) return true;
          const info = nodeInfoRef.current.get(id);
          if (!info) return false;
          // An opponent's protected building is indestructible: no X may land
          // on it and demolish it (mirrors the server).
          if (info.indestructible && info.ownerId !== '' && info.ownerId !== selfId) {
            return true;
          }
          if (info.fortressOwnerId === '') return false;
          if (info.fortressOwnerId === selfId) return true;
          const holder = players.find((p) => p.id === info.fortressOwnerId);
          const destructible = holder?.fortressCards.some(
            (fc) => fc.nodeIds.includes(id) && !!effectOf(fc.cardId)?.destructible,
          );
          return !destructible;
        },
        acceptMatch: (candidate: FortressMatch) => {
          // At least one own building in the realised set (bridge-only
          // patterns realise none, so they're exempt) — mirrors the server.
          if (
            requiresOwnBuilding &&
            candidate.buildingNodeIds.length > 0 &&
            !candidate.buildingNodeIds.some(
              (id) => nodeInfoRef.current.get(id)?.ownerId === selfId,
            )
          ) {
            return false;
          }
          const required = fx?.placement?.requireBuildingInZone;
          if (
            required !== undefined &&
            !candidate.buildingNodeIds.some(
              (id) => vigorZoneLevel(NODE_ZONE[id] ?? 'forest') === required,
            )
          ) {
            return false;
          }
          if (fx?.placement?.sameZone) {
            // Occupied nodes only (buildings + X markers), in ENERGY zones
            // (forest + foothill are one), as the server.
            const ids = [...candidate.buildingNodeIds, ...candidate.markerNodeIds];
            if (
              new Set(
                ids.map((id) => energyZoneOf(vigorZoneLevel(NODE_ZONE[id] ?? 'forest'))),
              ).size !== 1
            ) {
              return false;
            }
          }
          return true;
        },
      };
      return { card, cellAt, matchOpts };
    },
    [
      self,
      selfId,
      players,
      cardsById,
      gameConfig,
      effectOf,
      blockedNodesForClient,
      freeEdgesForClient,
    ],
  );

  const implementable = useMemo((): ImplementCandidate[] => {
    void boardVersion; // reruns this memo when node/bridge refs change
    const out: ImplementCandidate[] = [];
    if (!self || !selfId) return out;
    const bank = toAmount(self.resources);
    const subs = substitutionsFor(self.fortressCards, effectOf);
    // Client mirror of the server's GateQuery — registry build gates ("mine in
    // every zone", "6+ fortresses") hide a card here exactly when the server
    // would reject it.
    const gateQuery: GateQuery = {
      fortressCount: fortressCountOf(self.fortressCards),
      hasMineInEveryZone: () => {
        // The four energy zones the rules count (forest + foothill are one).
        const zones = new Set<number>();
        for (const [id, info] of nodeInfoRef.current) {
          if (info.ownerId === selfId && info.building === 'mine') {
            zones.add(energyZoneOf(vigorZoneLevel(NODE_ZONE[id] ?? 'forest')));
          }
        }
        return zones.size === ENERGY_ZONE_COUNT;
      },
      allOwnBuildingsInZone: (level) => {
        for (const [id, info] of nodeInfoRef.current) {
          if (info.ownerId === selfId && info.building !== '') {
            if (vigorZoneLevel(NODE_ZONE[id] ?? 'forest') !== level) return false;
          }
        }
        return true;
      },
    };
    // Obsidian Ovary Overload: a card whose full pattern doesn't fit may
    // still be implementable with one piece omitted.
    const canOmit = self.fortressCards.some(
      (fc) => effectOf(fc.cardId)?.implementOmitPiece === true,
    );
    const consider = (
      cardIds: readonly string[],
      marketKind: FortressKind | null,
      fromHand = false,
      fromDiscard?: string,
    ) => {
      for (const cardId of cardIds) {
        const args = implementMatcherArgs(cardId);
        if (!args) continue;
        const kind = marketKind ?? args.card.kind;
        const fx = args.card.effects;
        if (gateReasonFor(fx?.gate, gateQuery)) continue; // gate unmet — server rejects
        let match = matchFortressPattern(
          BUILD_GRAPH,
          args.cellAt,
          args.card.pattern,
          args.matchOpts,
        );
        if (!match && canOmit) {
          for (const omit of omitChoicesFor(args.card.pattern)) {
            if (!omit) continue;
            match = matchFortressPattern(
              BUILD_GRAPH,
              args.cellAt,
              omitPatternPiece(args.card.pattern, omit),
              args.matchOpts,
            );
            if (match) break;
          }
        }
        if (!match) continue;
        // The price THIS player pays (fortress discounts applied), payable
        // through their substitutions. Hand cards are 1 obsidian cheaper.
        const cost = fortressCostFor(
          self.fortressCards,
          args.card,
          effectOf,
          fortressesImplemented,
          startingPlayerCount || players.length,
        );
        if (fromHand) cost[3] = Math.max(0, (cost[3] ?? 0) - 1);
        out.push({
          cardId,
          kind,
          name: args.card.name,
          cost: [...cost],
          affordable: canAffordWith(bank, cost, subs),
          nodeIds: nodesOfMatch(match),
          ...(fromHand ? { fromHand: true } : {}),
          ...(fromDiscard ? { fromDiscard } : {}),
          ...implementChoiceFields(fx),
          ...capTargetsFor(fx, self.fortressCards, cardsById),
        });
      }
    };
    consider(markets.regular.cards, 'REGULAR');
    consider(markets.win.cards, 'win');
    // Dungeon Dice Martyr's face-down hand — only its holder ever has these.
    consider(self.hiddenCards, null, true);
    // Dungeon Dice Martyr ("53"+): either row's discard pile, while a held
    // card's `implement_from_discard` grant has uses left.
    const discardGrant = self.fortressCards.find(
      (fc) =>
        canUseCardAction(
          self.fortressCards,
          fc.cardId,
          'implement_from_discard',
          effectOf,
        ).allowed,
    );
    if (discardGrant) {
      consider(markets.regular.discarded, 'REGULAR', false, discardGrant.cardId);
      consider(markets.win.discarded, 'win', false, discardGrant.cardId);
    }
    // BAM Exploder: cards a held fortress lets us implement any number of
    // times, from outside the market (`alwaysBuildable`). A copy sitting in
    // a market row is skipped here — that one is a normal market buy.
    const granted = new Set<string>();
    for (const fc of self.fortressCards) {
      for (const id of effectOf(fc.cardId)?.alwaysBuildable ?? []) {
        granted.add(id);
      }
    }
    for (const id of [...markets.regular.cards, ...markets.win.cards]) {
      granted.delete(id);
    }
    consider([...granted], null);
    return out;
  }, [
    self,
    selfId,
    markets,
    boardVersion,
    implementMatcherArgs,
    effectOf,
    fortressesImplemented,
  ]);

  /** cardId → market-tile outline: 'ready' when the pattern is on the board AND
   * the Obsidian is in hand; 'poor' when only the pattern is. */
  const buildableById = useMemo(() => {
    const m = new Map<string, 'ready' | 'poor'>();
    for (const c of implementable) {
      m.set(c.cardId, c.affordable ? 'ready' : 'poor');
    }
    return m;
  }, [implementable]);

  // The Implement-Fortress button toggles the pick mode. Opening it reveals
  // the market sidebar (like Cycle Market) — the sidebar IS the card picker;
  // the small-screen guard collapses the roster for it.
  const openImplement = useCallback(() => {
    setCycleMode(false);
    setCycleSel(null);
    setBuildFlow(null);
    setBridgeFlow(null);
    setAimFlow(null);
    setEffectsFlow(null);
    closeSelection();
    if (!implementFlowRef.current) setMarketCollapsed(false);
    // Planning: EVERY face-up card (and hand card) is offered, no pattern
    // match or Obsidian required — a queued build may complete the pattern
    // and a queued yield may fund it. Real costs still shown; auto-play
    // re-validates and halts if the pattern/funds never materialise.
    if (planSlotRef.current !== null && self) {
      setImplementFlow((prev) => {
        if (prev) return null;
        const out: ImplementCandidate[] = [];
        const seen = new Set<string>();
        const consider = (
          ids: readonly string[],
          kind: FortressKind | null,
          fromHand = false,
          fromDiscard?: string,
        ) => {
          for (const cardId of ids) {
            const card = cardsById.get(cardId);
            if (!card || seen.has(`${cardId}:${fromHand}`)) continue;
            seen.add(`${cardId}:${fromHand}`);
            const cost = fortressCostFor(
              self.fortressCards,
              card,
              effectOf,
              fortressesImplemented,
              startingPlayerCount || players.length,
            );
            if (fromHand) cost[3] = Math.max(0, (cost[3] ?? 0) - 1);
            out.push({
              cardId,
              kind: kind ?? card.kind,
              name: card.name,
              cost: [...cost],
              affordable: true,
              nodeIds: [],
              ...implementChoiceFields(card.effects),
              ...capTargetsFor(card.effects, self.fortressCards, cardsById),
              ...(fromHand ? { fromHand: true } : {}),
              ...(fromDiscard ? { fromDiscard } : {}),
            });
          }
        };
        consider(markets.regular.cards, 'REGULAR');
        consider(markets.win.cards, 'win');
        consider(self.hiddenCards, null, true);
        const discardGrant = self.fortressCards.find(
          (fc) =>
            canUseCardAction(
              self.fortressCards,
              fc.cardId,
              'implement_from_discard',
              effectOf,
            ).allowed,
        );
        if (discardGrant) {
          consider(markets.regular.discarded, 'REGULAR', false, discardGrant.cardId);
          consider(markets.win.discarded, 'win', false, discardGrant.cardId);
        }
        // BAM Exploder's `alwaysBuildable` grants (the `seen` set already
        // drops ids a market row offers).
        const granted = new Set<string>();
        for (const fc of self.fortressCards) {
          for (const id of effectOf(fc.cardId)?.alwaysBuildable ?? []) {
            granted.add(id);
          }
        }
        consider([...granted], null);
        return { stage: 'pick', cards: out };
      });
      return;
    }
    setImplementFlow((prev) =>
      prev ? null : { stage: 'pick', cards: implementable },
    );
  }, [
    closeSelection,
    implementable,
    self,
    markets,
    cardsById,
    effectOf,
    fortressesImplemented,
  ]);

  const cancelImplement = useCallback(() => setImplementFlow(null), []);
  useEffect(() => {
    openImplementRef.current = openImplement;
  }, [openImplement]);

  const sendImplement = useCallback(
    (
      cardId: string,
      kind: FortressKind,
      choice?: ImplementChoice,
      placement?: { anchorNodeId: number; orientation: number },
      omit?: NonNullable<OmitPick>,
      fromDiscard?: string,
    ) => {
      sendGameAction({
        type: 'implement_fortress',
        kind,
        cardId,
        ...(choice ? { choice } : {}),
        ...(placement ? { placement } : {}),
        ...(omit ? { omit } : {}),
        ...(fromDiscard ? { fromDiscard } : {}),
      });
      setImplementFlow(null);
    },
    [sendGameAction],
  );

  /** A card picked in the sidebar's Implement pick mode. Live play closes the
   * sidebar and moves to the placement stage — every possible location pulses
   * and the player picks one on the board. Planning sends the queued action
   * straight away (no placement: the board may change before the move plays,
   * so the server auto-finds one). */
  const pickImplementCard = useCallback(
    (cardId: string, kind: FortressKind, choice?: ImplementChoice) => {
      if (planSlotRef.current !== null) {
        const planned = implementFlowRef.current;
        const fromDiscard =
          planned?.stage === 'pick'
            ? planned.cards.find((c) => c.cardId === cardId && c.kind === kind)
                ?.fromDiscard
            : undefined;
        sendImplement(cardId, kind, choice, undefined, undefined, fromDiscard);
        return;
      }
      const flow = implementFlowRef.current;
      if (flow?.stage !== 'pick') return;
      // Prefer the LIVE candidate (fresh cost/affordability for the placement
      // bar) — the flow's list is a snapshot from when the picker opened.
      const candidate =
        implementable.find((c) => c.cardId === cardId && c.kind === kind) ??
        flow.cards.find((c) => c.cardId === cardId && c.kind === kind);
      const args = candidate ? implementMatcherArgs(cardId) : null;
      if (!candidate || !args) return;
      // Obsidian Ovary Overload: the placement bar offers one-piece omissions;
      // when the full pattern fits nowhere, fall through to the first omission
      // that does.
      const canOmit =
        self?.fortressCards.some(
          (fc) => effectOf(fc.cardId)?.implementOmitPiece === true,
        ) ?? false;
      const omitChoices = canOmit
        ? omitChoicesFor(args.card.pattern)
        : [null as OmitPick];
      let omitIndex = 0;
      let matches = matchAllFortressPatterns(
        BUILD_GRAPH,
        args.cellAt,
        args.card.pattern,
        args.matchOpts,
      );
      for (let i = 1; matches.length === 0 && i < omitChoices.length; i += 1) {
        const omit = omitChoices[i];
        if (!omit) continue;
        matches = matchAllFortressPatterns(
          BUILD_GRAPH,
          args.cellAt,
          omitPatternPiece(args.card.pattern, omit),
          args.matchOpts,
        );
        if (matches.length > 0) omitIndex = i;
      }
      if (matches.length === 0) {
        // Stale pick (the board changed since the picker opened and the
        // pattern is no longer realisable): refresh the list so the dead
        // candidate visibly drops its ring instead of swallowing the click.
        setImplementFlow({ stage: 'pick', cards: implementable });
        return;
      }
      const candidates: ReadonlySet<number> = new Set(
        matches.flatMap(nodesOfMatch),
      );
      // A single possible placement is preselected — Confirm is one tap away.
      const selected =
        matches.length === 1
          ? { nodeId: nodesOfMatch(matches[0]!)[0]!, options: [0], pos: 0 }
          : null;
      // The sidebar's job is done — clear the stage for the board.
      setMarketCollapsed(true);
      setImplementFlow({
        stage: 'place',
        cards: flow.cards,
        card: candidate,
        matches,
        candidates,
        selected,
        omitChoices,
        omitIndex,
      });
    },
    [sendImplement, implementMatcherArgs, implementable, self, effectOf],
  );

  /** Rotate: cycle to the next possible placement over the picked node. */
  const rotateImplement = useCallback(() => {
    setImplementFlow((cur) => {
      if (cur?.stage !== 'place' || !cur.selected) return cur;
      const { options, pos } = cur.selected;
      if (options.length < 2) return cur;
      return {
        ...cur,
        selected: { ...cur.selected, pos: (pos + 1) % options.length },
      };
    });
  }, []);

  /** Obsidian Ovary Overload: cycle which pattern piece is omitted (full
   * pattern → each building → each bridge), skipping choices with no fit. */
  const cycleOmit = useCallback(() => {
    setImplementFlow((cur) => {
      if (cur?.stage !== 'place' || cur.omitChoices.length < 2) return cur;
      const args = implementMatcherArgs(cur.card.cardId);
      if (!args) return cur;
      for (let step = 1; step <= cur.omitChoices.length; step += 1) {
        const idx = (cur.omitIndex + step) % cur.omitChoices.length;
        const omit = cur.omitChoices[idx] ?? null;
        const pattern = omit
          ? omitPatternPiece(args.card.pattern, omit)
          : args.card.pattern;
        const matches = matchAllFortressPatterns(
          BUILD_GRAPH,
          args.cellAt,
          pattern,
          args.matchOpts,
        );
        if (matches.length === 0) continue;
        if (idx === cur.omitIndex) return cur; // nothing else fits
        return {
          ...cur,
          matches,
          candidates: new Set(matches.flatMap(nodesOfMatch)),
          selected:
            matches.length === 1
              ? { nodeId: nodesOfMatch(matches[0]!)[0]!, options: [0], pos: 0 }
              : null,
          omitIndex: idx,
        };
      }
      return cur;
    });
  }, [implementMatcherArgs]);

  /** Confirm the selected placement — send the implement pinned to it. */
  const confirmImplement = useCallback(
    (choice?: ImplementChoice) => {
      const cur = implementFlowRef.current;
      if (cur?.stage !== 'place' || !cur.selected) return;
      const match = cur.matches[cur.selected.options[cur.selected.pos]!];
      if (!match) return;
      const omit = cur.omitChoices[cur.omitIndex] ?? null;
      sendImplement(
        cur.card.cardId,
        cur.card.kind,
        choice,
        {
          anchorNodeId: match.anchorNodeId,
          orientation: match.orientation,
        },
        omit ?? undefined,
        cur.card.fromDiscard,
      );
    },
    [sendImplement],
  );

  /** Back out of the placement stage to the sidebar card picker. */
  const backToImplementPick = useCallback(() => {
    if (implementFlowRef.current?.stage !== 'place') return;
    setMarketCollapsed(false);
    setImplementFlow((cur) =>
      cur?.stage === 'place' ? { stage: 'pick', cards: cur.cards } : cur,
    );
  }, []);

  /** Sidebar Implement pick mode: while the flow's pick stage is open the
   * market sidebar is the card picker — candidate market tiles become click
   * targets and face-down hand candidates get their own banner rows. */
  const implementPick = useMemo((): ImplementPickControls | null => {
    if (implementFlow?.stage !== 'pick') return null;
    const candidates = new Map<string, ImplementCandidate>();
    const handCards: ImplementCandidate[] = [];
    const discardCards: ImplementCandidate[] = [];
    // Live play tracks the LIVE candidate set (the flow's list is a snapshot
    // from open time — the board may have changed under it); planning keeps
    // the snapshot, which deliberately offers every card.
    const source = planSlot === null ? implementable : implementFlow.cards;
    for (const c of source) {
      if (c.fromHand) handCards.push(c);
      else if (c.fromDiscard) discardCards.push(c);
      else candidates.set(`${c.kind}:${c.cardId}`, c);
    }
    return {
      candidates,
      handCards,
      discardCards,
      deferChoice: planSlot === null,
      onPick: pickImplementCard,
      onHover: (nodeIds) => gameRef.current?.highlightNodes(nodeIds),
      onCancel: cancelImplement,
    };
  }, [implementFlow, planSlot, implementable, pickImplementCard, cancelImplement]);

  // The implement flow's gold-ring channel: the placement stage rings the
  // currently selected placement (the pick stage's card hover drives the same
  // channel directly); anything else clears it.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (implementFlow?.stage === 'place' && implementFlow.selected) {
      const { options, pos } = implementFlow.selected;
      const match = implementFlow.matches[options[pos]!];
      game.highlightNodes(match ? nodesOfMatch(match) : null);
      return;
    }
    game.highlightNodes(null);
  }, [implementFlow]);

  // Escape steps the implement flow back: placement → sidebar picker → closed.
  useEffect(() => {
    if (!implementFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const cur = implementFlowRef.current;
      if (cur?.stage === 'place') {
        setMarketCollapsed(false);
        setImplementFlow({ stage: 'pick', cards: cur.cards });
      } else {
        setImplementFlow(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [implementFlow]);

  // Reflect the bridge flow on the board: highlight the eligible paths. The
  // aim flow shares the channel: while it's picking a target, targetable
  // bridge spans highlight (and tap) in the posture's colour — only one of
  // the two flows is ever open at a time.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (aimFlow?.stage === 'target' && aimFlow.allowedBridges.size > 0) {
      const pairs: [number, number][] = [];
      for (const key of aimFlow.allowedBridges) {
        const dash = key.indexOf('-');
        pairs.push([Number(key.slice(0, dash)), Number(key.slice(dash + 1))]);
      }
      game.setBridgeHighlight(
        pairs,
        null,
        // The board's aim-arrow colours: attack red / defend blue.
        aimFlow.mode === 'attack' ? 0xd7263d : 0x2e86ab,
      );
      return;
    }
    game.setBridgeHighlight(
      bridgeFlow?.allowed ?? null,
      bridgeFlow?.tooCostly ?? null,
    );
  }, [bridgeFlow, aimFlow]);

  // Escape cancels the bridge flow.
  useEffect(() => {
    if (!bridgeFlow) return;
    const onKey = (e: KeyboardEvent) => {
      // Esc steps back from the wildcard resource pick; otherwise it cancels.
      if (e.key === 'Escape') {
        setBridgeFlow((cur) => (cur?.pending ? { ...cur, pending: null } : null));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bridgeFlow]);

  // Reflect the build/aim/effects flows on the board (they're mutually
  // exclusive; one effect owns the node highlight so none can clobber the
  // others'): pulse the build flow's legal targets and swap the cursor while
  // placing, pulse the aim flow's pickable Barracks / legal targets (the chosen
  // Barracks stays lit while picking a posture), or pulse a card effect's
  // pickable nodes (Scouts to protect / Barracks to ready); clear otherwise.
  useEffect(() => {
    const game = gameRef.current;
    if (!game) return;
    if (buildFlow?.stage === 'place') {
      if (buildFlow.aim?.stage === 'target') {
        // Build-time aim: pulse the aim's legal targets (the pending build
        // node stays lit via the selection ring); drop the building cursor so
        // the pick reads as targeting, not placing.
        game.setBuildHighlight([...buildFlow.aim.allowed]);
        game.setBuildSelection(buildFlow.selected ?? null);
        game.setBuildCursor(null);
        return;
      }
      if (buildFlow.aim?.stage === 'done') {
        // Aim chosen: light the target alongside the selected build spot.
        game.setBuildHighlight([buildFlow.aim.targetId]);
        game.setBuildSelection(buildFlow.selected ?? null);
        game.setBuildCursor(buildingCursor(buildFlow.building));
        return;
      }
      game.setBuildHighlight(
        [...buildFlow.allowed],
        [...buildFlow.tooCostly],
        buildFlow.vigorPenalty,
        buildFlow.oneTimeGain,
      );
      game.setBuildSelection(buildFlow.selected ?? null);
      game.setBuildCursor(buildingCursor(buildFlow.building));
      return;
    }
    game.setBuildCursor(null);
    game.setBuildSelection(null);
    if (aimFlow) {
      game.setBuildHighlight(
        aimFlow.stage === 'pickBarracks'
          ? [...aimFlow.sources]
          : aimFlow.stage === 'mode'
            ? [aimFlow.barracksId]
            : [...aimFlow.allowed],
      );
      return;
    }
    if (
      effectsFlow?.stage === 'pickScout' ||
      effectsFlow?.stage === 'replicate' ||
      effectsFlow?.stage === 'untap' ||
      effectsFlow?.stage === 'moveFortress' ||
      effectsFlow?.stage === 'sellBuilding' ||
      effectsFlow?.stage === 'designateTarget' ||
      effectsFlow?.stage === 'moveBuildingFrom' ||
      effectsFlow?.stage === 'dropResources'
    ) {
      game.setBuildHighlight([...effectsFlow.allowed]);
      return;
    }
    // Move destinations show the same "+X" pickup bubble a build would, so the
    // collection is visible BEFORE the tap that commits it.
    if (effectsFlow?.stage === 'moveBuildingTo') {
      game.setBuildHighlight(
        [...effectsFlow.allowed],
        [],
        undefined,
        effectsFlow.oneTimeGain,
      );
      return;
    }
    // Implement-Fortress placement: pulse every node the card's pattern can
    // land on (the selected placement rides the gold-ring channel instead).
    if (implementFlow?.stage === 'place') {
      game.setBuildHighlight([...implementFlow.candidates]);
      return;
    }
    game.setBuildHighlight(null);
  }, [buildFlow, aimFlow, effectsFlow, implementFlow]);

  // Escape cancels the build flow; and leaving your turn ends it.
  useEffect(() => {
    if (!buildFlow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBuildFlow(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [buildFlow]);

  // While picking a building, a tap/click on other UI chrome dismisses the
  // picker. Only during the "pick" stage: in "place" the board itself is the
  // target surface (a click there places the building), so we mustn't
  // intercept it. The board stage is excluded too — panning/zooming around to
  // scout a spot (or an idle board tap) must not throw the picker away. The
  // Construct-Building button is excluded so its own onClick handles the toggle.
  useEffect(() => {
    if (buildFlow?.stage !== 'pick') return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('.build-picker')) return;
      if (t?.closest('.stage')) return;
      if (t?.closest('[data-action="construct_building"]')) return;
      setBuildFlow(null);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [buildFlow]);

  useEffect(() => {
    // Planning queued moves keeps the flows alive off-turn — they close when
    // planning ends (planSlot returns to null re-runs this effect).
    if (currentPlayerId !== selfId && planSlot === null) {
      setBuildFlow(null);
      setBridgeFlow(null);
      setImplementFlow(null);
      setEffectsFlow((flow) =>
        flow && OFF_TURN_EFFECT_STAGES.has(flow.stage) ? flow : null,
      );
      // The defense-window variant legitimately runs off-turn; only the normal
      // on-turn aim flow ends with the turn.
      setAimFlow((cur) => (cur && !cur.defense ? null : cur));
    }
  }, [currentPlayerId, selfId, planSlot]);

  // Entering your own turn ends planning — the same pickers now act live (and
  // this turn's queued moves have just auto-played or halted server-side).
  useEffect(() => {
    if (currentPlayerId === selfId) setPlanSlot(null);
  }, [currentPlayerId, selfId]);

  // Market rows and the player roster both mutate live (deal / cycle; join /
  // leave / play). When the tile under an open popover unmounts, React does NOT
  // synthesise an onMouseLeave, so a hover preview (or a pinned detail) would be
  // left orphaned at a stale anchor. Drop any open detail whose target is gone.
  useEffect(() => {
    const cardIds = new Set([...markets.regular.cards, ...markets.win.cards]);
    const playerIds = new Set(players.map((p) => p.id));
    setSelection((cur) => {
      if (!cur) return cur;
      const present =
        cur.kind === 'card'
          ? cardIds.has(cur.card.id)
          : cur.kind === 'fortressCard'
            ? // keep while its owning player still exists (instanceKey is
              // `${playerId}:${index}`).
              playerIds.has(
                cur.instanceKey.slice(0, cur.instanceKey.lastIndexOf(':')),
              )
            : // historyCard: not tied to the live market — its anchor (a
              // history row) persists, and leaving History mode closes it.
              true;
      return present ? cur : null;
    });
  }, [markets, players]);

  // Hover previews and click-to-pin, shared by market cards and fortress tiles.
  // Functional updates keep these independent of the current selection, so the
  // handler identities can stay stable across renders.
  const selectionHandlers = useMemo<SelectionHandlers>(
    () => ({
      onPreview: (target, anchorEl) => {
        // Entering any tile cancels a pending close (e.g. sliding tile→tile, or
        // back onto the tile from its popover).
        cancelPreviewClose();
        setSelection((cur) =>
          // A pinned target wins over hover, so passing over other tiles doesn't
          // yank an explicitly-opened detail away.
          cur?.pinned ? cur : { ...target, anchorEl, pinned: false },
        );
      },
      onPreviewEnd: (target) => {
        // Defer the close so the cursor can travel onto the popover and keep it
        // alive; only drop it if this same hover target is still showing.
        cancelPreviewClose();
        previewCloseTimer.current = window.setTimeout(() => {
          previewCloseTimer.current = null;
          setSelection((cur) =>
            cur &&
            !cur.pinned &&
            cur.kind === target.kind &&
            targetId(cur) === targetId(target)
              ? null
              : cur,
          );
        }, PREVIEW_CLOSE_DELAY);
      },
      onPin: (target, anchorEl) => {
        cancelPreviewClose();
        setSelection({ ...target, anchorEl, pinned: true });
      },
    }),
    [cancelPreviewClose],
  );

  // Ref mirrors for the once-created board hover handler.
  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    cardsByIdRef.current = cardsById;
  }, [cardsById]);
  useEffect(() => {
    selectionHandlersRef.current = selectionHandlers;
  }, [selectionHandlers]);

  // While a fortress-card detail is open (hover or pinned), ring its nodes on
  // the board; clear the highlight whenever it's not a fortress selection.
  const fortressKey =
    selection?.kind === 'fortressCard' ? selection.instanceKey : null;
  const fortressNodes =
    selection?.kind === 'fortressCard' ? selection.nodeIds : null;
  useEffect(() => {
    gameRef.current?.highlightNodes(fortressNodes);
    // `fortressKey` keys the effect to the fortress identity (not the array
    // ref, which changes every hover) so the pulse isn't restarted needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fortressKey]);

  /** "View on board": pan + zoom the board to the open fortress's nodes. */
  const focusFortress = useCallback((nodeIds: number[]) => {
    gameRef.current?.focusNodes(nodeIds);
  }, []);

  // Deploy detector: this bundle bakes in the APP_VERSION it was built from;
  // the server reports its own (live) one via /app-state. Server higher → a
  // new build shipped since this tab loaded → raise the sticky refresh banner.
  // Checked at boot and re-polled; once raised it never clears (the only way
  // forward is the reload it asks for).
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  useEffect(() => {
    let stopped = false;
    const check = async () => {
      const server = await fetchServerAppVersion();
      if (!stopped && server !== null && server > APP_VERSION) {
        setNewVersionAvailable(true);
      }
    };
    void check();
    const id = window.setInterval(() => void check(), APP_VERSION_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <ObsidianCostProvider value={obsidianCostOf}>
    <PlayerCountProvider value={startingPlayerCount || undefined}>
    <div
      className="app"
      data-tutorial={tutorialActive ? '' : undefined}
      data-tutorial-spotlight={tutorialSpotlight}
      style={tutorialActive ? { ['--tutorial-card-h' as string]: `${tutorialCardHeight}px` } : undefined}
    >
      {newVersionAvailable && (
        <div className="version-banner" role="alert">
          <span className="version-banner-text">
            A new version of Fortress is available.
          </span>
          <button
            type="button"
            className="version-banner-refresh"
            onClick={() => window.location.reload()}
          >
            ⟳ REFRESH
          </button>
        </div>
      )}
      {!activeMatch ? (
        <>
          <LobbyHub
            authenticated={authenticated}
            onEnterMatch={(gameId, opts) => {
              setReconnectFailed(false);
              setLobbyClosed(false);
              setSessionSuperseded(false);
              setActiveMatch({ gameId, spectate: opts?.spectate });
            }}
            onReconnect={({ gameId, reconnectionToken }) => {
              setReconnectFailed(false);
              setLobbyClosed(false);
              setSessionSuperseded(false);
              setActiveMatch({ gameId, reconnectionToken });
            }}
            reconnectFailed={reconnectFailed}
            lobbyClosed={lobbyClosed}
            sessionSuperseded={sessionSuperseded}
            tutorialComplete={tutorialDone}
          />
        </>
      ) : (
        <>
      <div className="stage" ref={stageRef} />
      {/* Anchor for board-hover fortress popovers: an invisible fixed point
          moved to the hovered node's screen position. */}
      <div
        ref={boardAnchorRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
      {/* Turn cues are moot once the game has ended (and would overlap the
          result banner, which shares the top-center slot). */}
      {turnToast && phase !== 'ended' && (
        <div className="turn-toast" role="status">
          It&apos;s Your Turn
        </div>
      )}
      {queueHalt && phase !== 'ended' && (
        <div
          className={`turn-toast queue-halt-toast${queueHalt.held ? ' is-held' : ''}`}
          role="status"
        >
          <strong>
            {queueHalt.held ? 'Your turn continues' : 'Queued moves stopped'}
          </strong>
          <span>
            {queueHalt.label
              ? `"${queueHalt.label}" — ${queueHalt.reason || 'no longer valid.'}`
              : queueHalt.reason || 'Take your turn.'}
          </span>
        </div>
      )}
      {restored && phase !== 'ended' && (
        <div className="turn-toast restored-toast" role="status">
          <strong>Match restored</strong>
          <span>
            {`${restored.byName || 'The creator'} rolled the match back to Round ${restored.round} · Turn ${restored.turn}`}
            {restored.dropped > 0
              ? ` — ${restored.dropped} action${restored.dropped === 1 ? '' : 's'} undone.`
              : '.'}
          </span>
        </div>
      )}
      {connectionLost && (
        <div className="conn-toast" role="status">
          Connection lost — reconnecting…
        </div>
      )}
      {/* Face in Clouds landed on your fortress: mist creeps in around the
          screen edges, then slowly clears (~2s). Cosmetic only — clicks pass
          through. Keyed so back-to-back fogs restart the animation. */}
      {fogKey > 0 && <div key={fogKey} className="fog-overlay" aria-hidden />}
      {/* Emoji-shield countdown (armed by dropping 🛡️ on the board):
          targeted reaction effects aimed at you are muted while it runs. */}
      {shieldLeft > 0 && (
        <div className="shield-badge" role="status">
          🛡️ Shielded · {shieldLeft}s
        </div>
      )}
      {/* Game-over result banner — a top strip, NOT a modal: the match is
          over (no more actions or turns), but everyone stays in the room to
          study the final board, the win condition and each player's cards.
          The winning card is a hover/click chip that opens the same fortress
          detail popover the roster and history use. */}
      {(gameOver !== null || phase === 'ended') && (
        <div className="gameover-banner" role="status">
          {(() => {
            const winner = gameOver?.winnerId
              ? players.find((p) => p.id === gameOver.winnerId)
              : undefined;
            const title = winner
              ? winner.id === selfId
                ? '🏆 Victory!'
                : `🏆 ${winner.name} wins`
              : gameOver?.winnerId === null && gameOver
                ? 'Game over — a draw'
                : 'Game over';
            const winCard = gameOver?.winCardId
              ? cardsById.get(gameOver.winCardId)
              : undefined;
            // Prefer the winner's LIVE fortress instance (so the popover can
            // highlight it and pan the board to it); fall back to the plain
            // catalogue card if the instance isn't on their board (e.g. a
            // hand-count win) or on old rooms without the synced card id.
            const idx =
              winner && winCard
                ? winner.fortressCards.findIndex(
                    (fc) => fc.cardId === winCard.id,
                  )
                : -1;
            const fc = idx >= 0 ? winner?.fortressCards[idx] : undefined;
            const target: SelectionTarget | null = winCard
              ? winner && fc
                ? {
                    kind: 'fortressCard',
                    card: winCard,
                    nodeIds: [...fc.nodeIds],
                    tokens: fc.tokens,
                    stored: fc.stored,
                    disabled: fc.disabled,
                    choice: fc.choice,
                    instanceKey: `${winner.id}:${idx}`,
                  }
                : { kind: 'historyCard', card: winCard }
              : null;
            return (
              <>
                <strong className="gameover-title">{title}</strong>
                <span className="gameover-sub">
                  {winCard && target ? (
                    <>
                      won with{' '}
                      <button
                        type="button"
                        className="gameover-card-link"
                        onMouseEnter={(e) =>
                          selectionHandlers.onPreview(target, e.currentTarget)
                        }
                        onMouseLeave={() =>
                          selectionHandlers.onPreviewEnd(target)
                        }
                        onClick={(e) =>
                          selectionHandlers.onPin(target, e.currentTarget)
                        }
                      >
                        {winCard.name}
                      </button>
                    </>
                  ) : (
                    'A fortress win condition was met.'
                  )}
                </span>
                <span className="gameover-hint">
                  The board stays open for review — no more turns.
                </span>
              </>
            );
          })()}
          <button
            type="button"
            className="turn-action turn-action-end"
            onClick={backToLobbies}
          >
            Back to lobbies
          </button>
        </div>
      )}
      {(phase === 'setup' || phase === 'playing' || phase === 'ended') && (
        <PlayerSidebar
          players={players}
          round={round}
          turn={turn}
          phase={phase}
          currentPlayerId={currentPlayerId}
          timePerTurnMs={gameConfig?.timePerTurnMs}
          selfId={selfId}
          spectators={spectators}
          handlers={selectionHandlers}
          registerRow={registerRow}
          history={history}
          historyMode={historyMode}
          restorePoints={restorePoints}
          canRestore={isCreator && phase === 'playing'}
          onRestore={(restoreId) =>
            roomRef.current?.send(ClientMessage.Restore, { restoreId })
          }
          cardsById={cardsById}
          cardsByName={cardsByName}
          onToggleHistory={() => {
            setHistoryMode((h) => !h);
            closeSelection();
          }}
          onFocusNode={(nodeId) => gameRef.current?.focusNode(nodeId)}
          collapsed={playersCollapsed}
          sheet={isSmallScreen}
          onToggle={() => {
            // Opening on a phone claims the screen — the market panel yields.
            if (playersCollapsed && isSmallScreen) setMarketCollapsed(true);
            setPlayersCollapsed((c) => !c);
            closeSelection();
          }}
        />
      )}
      {/* Bottom-left chat. Pushes out to clear the left sidebar when it's
          expanded (mirrors the zoom controls' behaviour on the right).
          Keyed by room so a fresh join remounts it collapsed. */}
      {(phase === 'setup' || phase === 'playing' || phase === 'ended') && (
        <ChatPanel
          key={activeMatch?.gameId ?? 'chat'}
          messages={chat}
          selfId={selfId}
          sidebarExpanded={!playersCollapsed}
          // PHONES: the bar only shows while the dock opened it, and CSS hides
          // the trigger under a live bar — no need to hide it on bottomBarUp.
          barUp={bottomBarUp && !isSmallScreen}
          open={chatOpen}
          onToggleOpen={() => setChatOpen((o) => !o)}
          onSend={(text) =>
            roomRef.current?.send(ClientMessage.Chat, { text })
          }
        />
      )}
      {/* PHONES: the sidebars open as bottom sheets over a dimmer (tap it to
          close), and the bottom-right dock opens them — plus the action tray
          while a turn / queueing / defense bar is live. The dock and the chat
          trigger hide under an open tray or flow panel (CSS :has). */}
      {isSmallScreen &&
        inGamePhase &&
        (!playersCollapsed || !marketCollapsed) && (
          <div
            className="sheet-backdrop"
            onClick={() => {
              setPlayersCollapsed(true);
              setMarketCollapsed(true);
              closeSelection();
            }}
            aria-hidden
          />
        )}
      {isSmallScreen && inGamePhase && (
        <PhoneDock
          playerCount={players.length}
          turnName={
            phase === 'ended'
              ? null
              : (players.find((p) => p.id === currentPlayerId)?.name ?? null)
          }
          onPlayers={openPlayersSheet}
          onMarket={() => {
            setPlayersCollapsed(true);
            setMarketCollapsed(false);
            closeSelection();
          }}
          turnLabel={
            phase === 'playing' && self
              ? iAmDefending
                ? 'Defend'
                : planSlot !== null
                  ? 'Queueing'
                  : currentPlayerId === selfId
                    ? 'Your turn'
                    : 'Queue turn'
              : null
          }
          turnLive={bottomBarUp}
          turnDisabled={defense.active && !iAmDefending}
          onTurn={() => {
            if (bottomBarUp) {
              setPhoneTrayOpen(true);
              return;
            }
            openQueuePlanning();
          }}
        />
      )}
      {/* Action notifications — a transient popup beside the roster row of each
          player who just acted. Anchored to the live row element, so it follows
          the sidebar as the roster reflows; shown in both setup and playing. */}
      {(phase === 'setup' || phase === 'playing') &&
        notices.map((n) => {
          const anchorEl = playerRowEls.current.get(n.playerId);
          if (!anchorEl) return null;
          return (
            <ActionNoticePopover
              key={n.key}
              notice={n}
              anchorEl={anchorEl}
              onDismiss={() => dismissNotice(n.playerId)}
            />
          );
        })}
      {/* Other players' live pointers over the sidebars ("Alice is looking at
          this fortress card"). Board-area cursors render inside the Pixi
          canvas instead (see pushBoardCursors). */}
      <RemoteSidebarCursors
        cursors={sidebarCursors}
        players={players}
        spectators={spectators}
      />
      {/* Remote touch taps over the panels (board taps render in Pixi). */}
      <RemoteTapRipples taps={tapRipples} />
      {/* Emoji reactions over the panels (board reactions render in Pixi). */}
      <RemoteEmojiPops pops={emojiPops} />
      {/* Right-click / long-press emoji picker: picking sends the reaction to
          everyone, pinned to the pressed point, and echoes it locally. */}
      {emojiPicker && (
        <EmojiPicker
          at={emojiPicker.screen}
          onClose={() => setEmojiPicker(null)}
          onPick={(emoji) => {
            pushRecentEmoji(emoji);
            try {
              roomRef.current?.send(ClientMessage.CursorEmoji, {
                ...emojiPicker.sample,
                emoji,
              });
            } catch {
              /* socket mid-close — still echo locally */
            }
            spawnEmoji({ ...emojiPicker.sample, emoji });
            // A shield dropped anywhere on the board arms YOUR emoji
            // shield: targeted reaction effects aimed at you (horn, fog)
            // are muted for the next `SHIELD_TTL`.
            if (
              emojiPicker.sample.area === 'board' &&
              sameEmoji(emoji, SHIELD_EMOJI)
            ) {
              armShield();
            }
            setEmojiPicker(null);
          }}
        />
      )}
      {phase === 'setup' && (
        <StartupOverlay
          self={self}
          players={players}
          currentPlayerId={currentPlayerId}
          selfId={selfId}
          startingProduction={gameConfig?.startingProduction ?? 0}
          selectedNode={selectedHomeNode}
          onConfirmHome={confirmHomeBase}
          onCancelHome={() => setSelectedHomeNode(null)}
          onConfirmProduction={(amounts) =>
            roomRef.current?.send(ClientMessage.SetProduction, amounts)
          }
        />
      )}
      {/* The board view (markets, card popovers, zoom, library) is up from
          setup — the starting Fortress deals inform home-base placement — and
          stays up on an ended game for the post-match review; only the action
          UI below is gated to the live `playing` phase. */}
      {(phase === 'setup' || phase === 'playing' || phase === 'ended') && (
        <>
          <MarketSidebar
            markets={markets}
            marked={markedTiles}
            watch={
              self
                ? {
                    ids: self.watchedFortresses,
                    onToggle: (cardId) =>
                      roomRef.current?.send(ClientMessage.ToggleWatch, {
                        cardId,
                      }),
                  }
                : null
            }
            queuedMarks={queuedMarketMarks}
            collapsed={marketCollapsed}
            sheet={isSmallScreen}
            onToggle={() => {
              // Opening on a phone claims the screen — the roster yields.
              if (marketCollapsed && isSmallScreen) setPlayersCollapsed(true);
              setMarketCollapsed((c) => !c);
              closeSelection();
            }}
            handlers={selectionHandlers}
            cardsById={cardsById}
            // No implement outlines on an ended game — nothing is buildable.
            buildable={phase === 'playing' ? buildableById : EMPTY_BUILDABLE}
            implementPick={phase === 'playing' ? implementPick : null}
            cycle={{
              active: cycleMode,
              selected: cycleSel ?? EMPTY_CYCLE_SELECTED,
              foodCost: cycleFoodCost,
              affordable: cycleAffordable,
              onToggleCard: toggleCycleCard,
              onConfirm: confirmCycle,
              onCancel: cancelCycle,
            }}
            roundStart={
              rsMode
                ? {
                    mode: rsMode,
                    sel: rsSel,
                    maxPerRow: rsMaxPerRow,
                    onToggle: toggleRsCard,
                    onConfirm: () => confirmRoundStartCycle(rsSel),
                    onSkip: () =>
                      confirmRoundStartCycle({ regular: [], win: [] }),
                    onCancel:
                      rsMode === 'queue'
                        ? () => setRsMode(null)
                        : undefined,
                  }
                : null
            }
          />
          {phase === 'playing' && self?.pendingMartyrDiscards[0] && (
            <MartyrDiscardPrompt
              key={`${self.pendingMartyrDiscards[0]}:${self.pendingMartyrDiscards.length}`}
              pending={self.pendingMartyrDiscards[0]}
              startedMs={self.martyrPromptStartedMs}
              limitMs={gameConfig?.timePerTurnMs}
              victimName={
                players.find(
                  (player) =>
                    player.id === self.pendingMartyrDiscards[0]!.split('|')[0],
                )?.name ?? 'Opponent'
              }
              onChoose={(victimId, index) =>
                roomRef.current?.send(ClientMessage.MartyrDiscard, {
                  victimId,
                  index,
                })
              }
            />
          )}
          {phase === 'playing' &&
            self &&
            statue &&
            self.pendingVigorOffer > 0 && (
              <VigorOfferBanner
                key={self.pendingVigorOffer}
                amount={self.pendingVigorOffer}
                statueName={statue.name}
                stored={statue.stored}
                target={statue.target}
                affordable={canAffordWith(
                  toAmount(self.resources),
                  [0, 0, self.pendingVigorOffer, 0],
                  substitutionsFor(self.fortressCards, effectOf),
                )}
                onAnswer={(pay) =>
                  roomRef.current?.send(ClientMessage.VigorOffer, { pay })
                }
              />
            )}
          {phase === 'playing' && self && upkeepSelf.length > 0 && (
            <BarracksUpkeepPrompt
              key={upkeepSelf.map((e) => e.nodeId).join(',')}
              entries={upkeepSelf.map(({ nodeId, cost }) => ({
                nodeId,
                cost,
                // The zone's printed upkeep, for the strikeout — the server's
                // figure already carries this player's Food discount
                // (Hippoman).
                baseCost:
                  gameConfig?.vigorZones[
                    vigorZoneLevel(NODE_ZONE[nodeId] ?? 'forest')
                  ]?.barracksAttackUpkeep ?? cost,
                zone: NODE_ZONE[nodeId] ?? 'forest',
                affordable: canAffordWith(
                  toAmount(self.resources),
                  [0, cost, 0, 0],
                  substitutionsFor(self.fortressCards, effectOf),
                ),
              }))}
              onFocus={(nodeId) => gameRef.current?.focusNode(nodeId)}
              onResolve={(nodeId, keep) =>
                roomRef.current?.send(ClientMessage.BarracksUpkeep, {
                  nodeId,
                  keep,
                })
              }
            />
          )}
          {phase === 'playing' && self && stealSelf.length > 0 && (
            <StealResourcesPrompt
              key={stealSelf
                .map((e) => `${e.attackerId}|${e.victimId}`)
                .join(',')}
              entries={stealSelf.map(({ attackerId, victimId, count, victimPicks }) => {
                const victim = players.find((p) => p.id === victimId);
                const attacker = players.find((p) => p.id === attackerId);
                return {
                  attackerId,
                  victimId,
                  attackerName: attacker?.name ?? 'Opponent',
                  victimName: victim?.name ?? 'Opponent',
                  holdings: victim
                    ? toAmount(victim.resources)
                    : ([0, 0, 0, 0] as ResourceAmount),
                  count,
                  /** True = this client is the VICTIM choosing what to give. */
                  give: victimPicks,
                };
              })}
              onResolve={(attackerId, victimId, resources) =>
                roomRef.current?.send(ClientMessage.StealResources, {
                  victimId,
                  attackerId,
                  resources,
                })
              }
            />
          )}
          {(selection?.kind === 'card' ||
            selection?.kind === 'historyCard') && (
            <CardDetailPopover
              card={selection.card}
              companions={companionCardsOf(selection.card, cardsById)}
              anchorEl={selection.anchorEl}
              pinned={selection.pinned}
              onClose={closeSelection}
              onHoverEnter={cancelPreviewClose}
              onHoverLeave={closePreviewSoon}
            />
          )}
          {selection?.kind === 'fortressCard' &&
            (() => {
              // Resolve the owner's LIVE card state each render (instanceKey is
              // `${playerId}:${index}`), so tokens / exhausted update while the
              // popover is open instead of freezing the hover-time snapshot.
              // Falls back to the snapshot if the instance moved (e.g. the
              // owner's list changed under an open popover).
              const sep = selection.instanceKey.lastIndexOf(':');
              const ownerId = selection.instanceKey.slice(0, sep);
              const index = Number(selection.instanceKey.slice(sep + 1));
              const live = players.find((p) => p.id === ownerId)
                ?.fortressCards[index];
              const fresh =
                live && live.cardId === selection.card.id ? live : undefined;
              return (
                <FortressCardDetailPopover
                  card={selection.card}
                  companions={[
                    // The copied card's face (CurdleCat), live from state.
                    ...(fresh?.copiedCardId
                      ? [cardsById.get(fresh.copiedCardId)].filter(
                          (c): c is FortressCard => c !== undefined,
                        )
                      : []),
                    ...companionCardsOf(selection.card, cardsById),
                  ]}
                  // The pool the card's EFFECT plays with — the copied
                  // effect's own on a copying CurdleCat.
                  tokens={fresh?.tokens ?? selection.tokens}
                  // Stored piles are public like tokens — Swole Turtle's
                  // banked energy and Singularity Ball's race to its
                  // threshold are open information for every player.
                  stored={fresh?.stored ?? selection.stored}
                  disabled={fresh?.disabled ?? selection.disabled}
                  choice={fresh?.choice ?? selection.choice}
                  isCopy={fresh?.copied ?? false}
                  hasNodes={selection.nodeIds.length > 0}
                  // Hovering the fortress on the board already has the
                  // viewport on it — the pan-to action would be a no-op.
                  onViewOnBoard={
                    selection.fromBoard
                      ? undefined
                      : () => focusFortress(selection.nodeIds)
                  }
                  anchorEl={selection.anchorEl}
                  pinned={selection.pinned}
                  onClose={closeSelection}
                  onHoverEnter={cancelPreviewClose}
                  onHoverLeave={closePreviewSoon}
                />
              );
            })()}
          {/* Corner cluster (rulebook / zoom / Back to Lobby). ON PHONES AND
              TABLETS while an action bar is up, the corner copy hides and the
              same cluster docks above the full-width bar instead (passed to
              TurnActionBar / DefenseBar as `cornerControls`). */}
          {isSmallScreen ? (
            // PHONES: Home top-left, −/+/? top-right; the bottom-right
            // corner belongs to the dock (PhoneDock, rendered after the chat).
            <PhoneChrome
              onRulebook={() => setRulebookOpen(true)}
              onZoomIn={() => gameRef.current?.zoomByFactor(1.25)}
              onZoomOut={() => gameRef.current?.zoomByFactor(0.8)}
              onBackToLobby={confirmBackToLobbies}
            />
          ) : (
            !(isMediumScreen && bottomBarUp) && (
              <CornerControls
                marketExpanded={!marketCollapsed}
                onQueueTurn={
                  phase === 'playing' && self && !bottomBarUp
                    ? openQueuePlanning
                    : undefined
                }
                queueTurnDisabled={defense.active && !iAmDefending}
                onRulebook={() => setRulebookOpen(true)}
                onZoomIn={() => gameRef.current?.zoomByFactor(1.25)}
                onZoomOut={() => gameRef.current?.zoomByFactor(0.8)}
                onBackToLobby={confirmBackToLobbies}
              />
            )
          )}
          {phase === 'playing' && iAmDefending && (
            <DefenseBar
              flowPanel={
                aimFlow ? (
                  <AimPicker
                    flow={aimFlow}
                    range={
                      gameConfig && self
                        ? resolveBuilding(gameConfig, self, 'barracks').range
                        : gameConfig?.buildings.barracks?.range
                    }
                    onMode={selectAimMode}
                    onCancel={cancelAim}
                  />
                ) : null
              }
              cornerControls={
                isMediumScreen && !isSmallScreen ? (
                  <CornerControls
                    docked
                    onChat={() => setChatOpen((o) => !o)}
                    onRulebook={() => setRulebookOpen(true)}
                    onZoomIn={() => gameRef.current?.zoomByFactor(1.25)}
                    onZoomOut={() => gameRef.current?.zoomByFactor(0.8)}
                    onBackToLobby={confirmBackToLobbies}
                  />
                ) : undefined
              }
              aiming={!!aimFlow}
              collapsed={barCollapsed}
              hideCollapsed={isSmallScreen}
              onToggleCollapsed={toggleBarCollapsed}
              onAim={openDefenseAim}
              onDone={sendDefenseDone}
            />
          )}
          {currentPlayerId === selfId && self && defense.active && (
            <StatusBanner
              title={defense.midTurn ? 'Designated target' : 'Turn ending'}
              sub={
                defense.midTurn
                  ? 'Aim your defending Barracks for free, then press Done — your turn continues.'
                  : iAmDefending
                    ? // Combat resolves at every turn end, so buildings of ours
                      // can come under threat during our OWN turn (building onto
                      // a node an opponent already aims at). We get the same free
                      // reactive defense as anyone else.
                      'Buildings of yours would fall — aim defenders for free, then press Done.'
                    : "Attacked players may aim defenders — combat resolves when they're done."
              }
            />
          )}
          {/* Round-start cycle window: the round is paused on the first
              player's free "cycle up to `rsMaxPerRow` cards per market" step
              — or, during
              a Space Cheese Curdler last stand, on the HOLDER's cycle step
              before the turns of their solo round. */}
          {rsWindowMine && (
            <StatusBanner
              title={
                lastStand.playerId === selfId
                  ? 'Last stand — your call'
                  : 'Round start — your call'
              }
              sub={
                lastStand.playerId === selfId
                  ? `Cycle the markets for free if you like — then your round begins, ${
                      lastStand.turnsRemaining
                    } turn${lastStand.turnsRemaining === 1 ? '' : 's'} of it, yours alone. ${
                      players.find((p) => p.id === lastStand.pendingWinnerId)
                        ?.name ?? 'The winner'
                    }'s victory executes when the round ends.`
                  : `Cycle out up to ${rsMaxPerRow} card${
                      rsMaxPerRow === 1 ? '' : 's'
                    } from each Fortress market for free, or cycle nothing — choose in the market panel.`
              }
            />
          )}
          {roundCyclerId !== '' && !rsWindowMine && (
            <StatusBanner
              title={lastStand.playerId !== '' ? 'Last stand' : 'Round starting'}
              sub={
                lastStand.playerId !== ''
                  ? `${
                      players.find((p) => p.id === lastStand.playerId)?.name ??
                      'The holder'
                    } may cycle the markets, then plays a round of ${
                      lastStand.turnsRemaining
                    } turn${lastStand.turnsRemaining === 1 ? '' : 's'} alone — ${
                      players.find((p) => p.id === lastStand.pendingWinnerId)
                        ?.name ?? 'the winner'
                    }'s victory executes when that round ends.`
                  : `${
                      players.find((p) => p.id === roundCyclerId)?.name ??
                      'The first player'
                    } may cycle a card out of each market before the round begins.`
              }
            />
          )}
          {/* The last-stand grace turn itself (Space Cheese Curdler): keep the
              postponed victory on screen for the whole table. */}
          {phase === 'playing' &&
            lastStand.playerId !== '' &&
            roundCyclerId === '' &&
            currentPlayerId === lastStand.playerId && (
              <StatusBanner
                title={
                  lastStand.playerId === selfId
                    ? 'Your last stand'
                    : `${
                        players.find((p) => p.id === lastStand.playerId)?.name ??
                        'A player'
                      }'s last stand`
                }
                sub={`${
                  players.find((p) => p.id === lastStand.pendingWinnerId)
                    ?.name ?? 'The winner'
                }'s victory (${
                  cardsById.get(lastStand.pendingWinCardId)?.name ??
                  lastStand.pendingWinCardId
                }) executes when this round ends — ${
                  lastStand.turnsRemaining
                } turn${lastStand.turnsRemaining === 1 ? '' : 's'} left${
                  lastStand.playerId === selfId ? ', win before it does' : ''
                }.`}
              />
            )}
          {/* Grand Entrance: "Re-enter the round if you already passed" — an
              off-turn, action-free use, so it lives outside the turn bar (a
              passed player never sees one). Sent live, never queued. */}
          {phase === 'playing' &&
            planSlot === null &&
            self?.passed &&
            (() => {
              const card = self.fortressCards.find(
                (fc) =>
                  canUseCardAction(
                    self.fortressCards,
                    fc.cardId,
                    'rejoin_round',
                    effectOf,
                  ).allowed,
              );
              if (!card) return null;
              const bonus = effectOf(card.cardId)?.rejoinBonusActions ?? 0;
              return (
                <div className="setup-banner" role="status">
                  <strong className="setup-banner-title">
                    {cardsById.get(card.cardId)?.name ?? card.cardId}
                  </strong>
                  <span className="setup-banner-sub">
                    You have passed — re-enter the round
                    {bonus > 0 ? ` with +${bonus} actions on your next turn` : ''}
                    . Free of actions; one token.
                  </span>
                  <div className="setup-banner-actions">
                    <button
                      type="button"
                      className="setup-confirm"
                      onClick={() =>
                        roomRef.current?.send(ClientMessage.Action, {
                          type: 'rejoin_round',
                          cardId: card.cardId,
                        })
                      }
                    >
                      Re-enter the Round
                    </button>
                  </div>
                </div>
              );
            })()}
          {phase === 'playing' &&
            (currentPlayerId === selfId || planSlot !== null) &&
            self &&
            !defense.active && (
              <TurnActionBar
                effectOf={effectOf}
                flowPanel={
                  buildFlow ? (
                    <BuildPicker
                      flow={buildFlow}
                      freeBuildCfg={
                        buildFlow?.freeBuildCardId
                          ? effectOf(buildFlow.freeBuildCardId)?.freeBuild
                          : undefined
                      }
                      // Planning assumes infinite resources — every building is
                      // pickable; the auto-play validator settles affordability.
                      resources={
                        planSlot !== null ? PLANNING_BANK : self.resources
                      }
                      subs={substitutionsFor(self.fortressCards, effectOf)}
                      bunBun={bunBun}
                      onToggleRider={toggleDoubleOneTime}
                      selectedPermanentResource={selectedPermanentResource}
                      buildings={
                        gameConfig
                          ? {
                              scout: resolveBuilding(gameConfig, self, 'scout'),
                              barracks: resolveBuilding(
                                gameConfig,
                                self,
                                'barracks',
                              ),
                              mine: resolveBuilding(gameConfig, self, 'mine'),
                              bridge: resolveBuilding(gameConfig, self, 'bridge'),
                            }
                          : undefined
                      }
                      baseBuildings={gameConfig?.buildings}
                      onSelect={selectBuilding}
                      onAimMode={selectBuildAim}
                      onClearAim={clearBuildAim}
                      onConfirm={confirmBuild}
                      onCancel={cancelBuild}
                    />
                  ) : bridgeFlow ? (
                    <div
                      className="build-picker"
                      role="dialog"
                      aria-label="Construct a bridge"
                    >
                      <div className="build-picker-head">
                        <span className="build-picker-title">
                          {bridgeFlow.pending
                            ? 'Build bridge — pay with which resource?'
                            : bridgeFlow.allowed.length > 0
                              ? bridgeFlow.cardId
                                ? 'Tap a path to build a bridge — no action point'
                                : 'Tap a path to build a bridge'
                              : 'No bridgeable paths in range'}
                        </span>
                        <button
                          type="button"
                          className="build-picker-cancel"
                          onClick={
                            bridgeFlow.pending
                              ? () =>
                                  setBridgeFlow((cur) =>
                                    cur ? { ...cur, pending: null } : cur,
                                  )
                              : cancelBridge
                          }
                          title={bridgeFlow.pending ? 'Back (Esc)' : 'Cancel (Esc)'}
                          aria-label={bridgeFlow.pending ? 'Back' : 'Cancel'}
                        >
                          ✕
                        </button>
                      </div>
                      {bridgeFlow.pending && gameConfig && self && (
                        <div className="build-picker-options">
                          {RESOURCE_KINDS.map((kind) => {
                            const cost = wildcardCostAs(
                              resolveBuilding(gameConfig, self, 'bridge').cost,
                              kind,
                            );
                            // Planning queues anything; live play gates on the
                            // same sub-aware answer the server validates with.
                            const affordable =
                              planSlot !== null ||
                              canAffordWith(
                                toAmount(self.resources),
                                cost,
                                substitutionsFor(self.fortressCards, effectOf),
                              );
                            return (
                              <button
                                key={kind}
                                type="button"
                                className="build-option"
                                disabled={!affordable}
                                onClick={() => {
                                  const p = bridgeFlow.pending;
                                  if (!p) return;
                                  sendGameAction(
                                    bridgeFlow.cardId
                                      ? {
                                          type: 'free_bridge',
                                          cardId: bridgeFlow.cardId,
                                          from: p.from,
                                          to: p.to,
                                          payWith: kind,
                                        }
                                      : {
                                          type: 'construct_bridge',
                                          from: p.from,
                                          to: p.to,
                                          payWith: kind,
                                        },
                                  );
                                  setBridgeFlow(null);
                                }}
                                title={
                                  affordable
                                    ? `Pay with ${RESOURCE_LABELS[kind]}`
                                    : `Not enough ${RESOURCE_LABELS[kind]}`
                                }
                              >
                                <ResourceIcon kind={kind} size={36} />
                                <span className="build-option-name">
                                  {RESOURCE_LABELS[kind]}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : implementFlow?.stage === 'place' ? (
                    <ImplementPlacementBar
                      card={implementFlow.card}
                      selected={implementFlow.selected}
                      omitLabel={
                        implementFlow.omitChoices.length > 1
                          ? omitPickLabel(
                              cardsById.get(implementFlow.card.cardId)
                                ?.pattern ?? {
                                buildings: [],
                                markers: [],
                                bridges: [],
                              },
                              implementFlow.omitChoices[
                                implementFlow.omitIndex
                              ] ?? null,
                            )
                          : null
                      }
                      onCycleOmit={cycleOmit}
                      onRotate={rotateImplement}
                      onConfirm={confirmImplement}
                      onBack={backToImplementPick}
                    />
                  ) : effectsFlow ? (
                    <EffectsPicker
                      flow={effectsFlow}
                      player={self}
                      allPlayers={players}
                      cardsById={cardsById}
                      markets={markets}
                      context={effectContext}
                      bunBun={bunBun}
                      onStart={startEffect}
                      onConfirm={confirmEffect}
                      onConfirmUntap={confirmUntap}
                      onConfirmMove={confirmMoveFortress}
                      onToggleMoveRider={toggleMoveDoubleOneTime}
                      onStartReorder={startReorderMarket}
                      onMoveReorder={moveReorderCard}
                      onSetReorderRoute={setReorderRoute}
                      onToggleReorderCycle={toggleReorderCycle}
                      onConfirmReorder={confirmReorder}
                      onPickClaim={pickClaimResource}
                      onBankTwo={sendBankTwo}
                      onNudgeRedistribute={nudgeRedistribution}
                      onConfirmRedistribute={sendRedistribute}
                      onNudgeTokens={nudgeTokenRedistribution}
                      onConfirmTokens={sendTokenRedistribution}
                      onIncomePick={sendIncomePick}
                      onSetDropResource={setDropResource}
                      onAdjustToken={sendAdjustToken}
                      onCopyEffect={sendCopyEffect}
                      onMarkPick={pickMarkFortress}
                      onMarkShuffle={answerMarkShuffle}
                      onToggleMarkShuffle={toggleMarkShuffle}
                      onLock={sendFortressLock}
                      onFreeCycle={sendFreeCycle}
                      onTutorMarket={setTutorMarket}
                      onTutorPick={setTutorFound}
                      onTutorConfirm={confirmTutor}
                      onShuffleDiscardPick={pickShuffleDiscard}
                      onShuffleCycle={toggleShuffleCycle}
                      onShuffleConfirm={confirmShuffleDiscard}
                      onBack={backToEffectsList}
                      onCancel={cancelEffects}
                    />
                  ) : aimFlow ? (
                    <AimPicker
                      flow={aimFlow}
                      range={
                        gameConfig && self
                          ? resolveBuilding(gameConfig, self, 'barracks').range
                          : gameConfig?.buildings.barracks?.range
                      }
                      onMode={selectAimMode}
                      onCancel={cancelAim}
                    />
                  ) : null
                }
                cornerControls={
                  isMediumScreen && !isSmallScreen ? (
                    <CornerControls
                      docked
                      onChat={() => setChatOpen((o) => !o)}
                      onRulebook={() => setRulebookOpen(true)}
                      onZoomIn={() => gameRef.current?.zoomByFactor(1.25)}
                      onZoomOut={() => gameRef.current?.zoomByFactor(0.8)}
                      onBackToLobby={confirmBackToLobbies}
                    />
                  ) : undefined
                }
                player={self}
                actionsRemaining={actionsRemaining}
                actionsPerTurn={Math.max(
                  gameConfig?.actionsPerTurn ?? 0,
                  grantedActionAllowance(
                    self.fortressCards,
                    players.flatMap((player) => [...player.fortressCards]),
                    effectOf,
                  ),
                )}
                anyPassed={players.some((p) => p.passed)}
                cycleActive={cycleMode}
                buildActive={!!buildFlow}
                bridgeActive={!!bridgeFlow && !bridgeFlow.cardId}
                implementActive={!!implementFlow}
                aimActive={!!aimFlow}
                effectsActive={!!effectsFlow}
                hasEffectCards={hasEffectCards}
                usableEffectCount={usableEffectCount}
                collapsed={barCollapsed}
                hideCollapsed={isSmallScreen}
                onToggleCollapsed={toggleBarCollapsed}
                onConstructBuilding={openBuildPicker}
                onConstructBridge={startBridge}
                onImplementFortress={openImplement}
                onCycleMarket={startCycleMarket}
                onAimBarracks={openAimFlow}
                onEffects={openEffects}
                onEndTurn={() => roomRef.current?.send(ClientMessage.EndTurn)}
                onPass={() => roomRef.current?.send(ClientMessage.Pass)}
                undoLabel={currentPlayerId === selfId ? undoLabel : ''}
                onUndo={() => roomRef.current?.send(ClientMessage.Undo)}
                planningSlot={planSlot}
                onEndPlanning={() => setPlanSlot(null)}
                // A queued Pass must be alone in its turn slot, but other turns
                // may already hold actions (e.g. act next turn, pass the one
                // after). Offer it when this planning slot is empty and no Pass
                // is already queued. Hidden once we've already passed this round.
                canQueuePass={
                  !self.passed &&
                  planSlot !== null &&
                  !self.queuedMoves.some((m) => m.slot === planSlot) &&
                  !self.queuedMoves.some((m) => {
                    try {
                      return (
                        (JSON.parse(m.payload) as { type?: unknown }).type ===
                        'pass'
                      );
                    } catch {
                      return false;
                    }
                  })
                }
                onQueuePass={() => {
                  if (planSlot === null) return;
                  roomRef.current?.send(ClientMessage.QueueMove, {
                    slot: planSlot,
                    action: { type: 'pass' },
                    label: 'Pass',
                  });
                  setPlanSlot(null);
                }}
                queuePanel={
                  <QueuePanel
                    self={self}
                    planSlot={planSlot}
                    onUnqueueMove={(slot, index) =>
                      roomRef.current?.send(ClientMessage.UnqueueMove, {
                        slot,
                        index,
                      })
                    }
                    queuedSel={queuedSel}
                    onSelectQueued={setQueuedSel}
                    cardsById={cardsById}
                    expectedFirst={
                      firstPlayerId === selfId &&
                      self.passed &&
                      roundCyclerId === ''
                    }
                    rsQueueActive={rsMode === 'queue'}
                    rsMaxPerRow={rsMaxPerRow}
                    onToggleRsQueue={toggleRsQueueMode}
                    onClearQueuedRoundStart={clearQueuedRoundStartCycle}
                    statue={statue}
                    onVigorAutoPay={(enabled, floor) =>
                      roomRef.current?.send(ClientMessage.VigorAutoPay, {
                        enabled,
                        floor,
                      })
                    }
                    turtle={turtle}
                    onVigorDepositFloor={(floor) =>
                      roomRef.current?.send(ClientMessage.VigorDepositFloor, {
                        floor,
                      })
                    }
                    exploder={exploder}
                    onArtifactAutoDestroy={(enabled) =>
                      roomRef.current?.send(ClientMessage.ArtifactAutoDestroy, {
                        enabled,
                      })
                    }
                    captureBot={captureBot}
                    onCaptureSettings={(enabled, resource) =>
                      roomRef.current?.send(ClientMessage.CaptureSettings, {
                        enabled,
                        resource,
                      })
                    }
                  />
                }
              />
          )}
          {phase === 'playing' &&
            currentPlayerId !== selfId &&
            planSlot === null &&
            self &&
            effectsFlow &&
            OFF_TURN_EFFECT_STAGES.has(effectsFlow.stage) && (
              <EffectsPicker
                flow={effectsFlow}
                player={self}
                allPlayers={players}
                cardsById={cardsById}
                markets={markets}
                context={effectContext}
                bunBun={bunBun}
                onStart={startEffect}
                onConfirm={confirmEffect}
                onConfirmUntap={confirmUntap}
                onConfirmMove={confirmMoveFortress}
                onToggleMoveRider={toggleMoveDoubleOneTime}
                onStartReorder={startReorderMarket}
                onMoveReorder={moveReorderCard}
                onSetReorderRoute={setReorderRoute}
                onToggleReorderCycle={toggleReorderCycle}
                onConfirmReorder={confirmReorder}
                onPickClaim={pickClaimResource}
                onBankTwo={sendBankTwo}
                onNudgeRedistribute={nudgeRedistribution}
                onConfirmRedistribute={sendRedistribute}
                onNudgeTokens={nudgeTokenRedistribution}
                onConfirmTokens={sendTokenRedistribution}
                onIncomePick={sendIncomePick}
                onSetDropResource={setDropResource}
                onAdjustToken={sendAdjustToken}
                onCopyEffect={sendCopyEffect}
                onMarkPick={pickMarkFortress}
                onMarkShuffle={answerMarkShuffle}
                onToggleMarkShuffle={toggleMarkShuffle}
                onLock={sendFortressLock}
                onFreeCycle={sendFreeCycle}
                onTutorMarket={setTutorMarket}
                onTutorPick={setTutorFound}
                onTutorConfirm={confirmTutor}
                onShuffleDiscardPick={pickShuffleDiscard}
                onShuffleCycle={toggleShuffleCycle}
                onShuffleConfirm={confirmShuffleDiscard}
                onBack={backToEffectsList}
                onCancel={cancelEffects}
              />
            )}
          {tutorialActive && tutorialStepDef && (
            <TutorialCard
              step={tutorial.step}
              def={tutorialStepDef}
              marketSwapPending={roundCyclerId === selfId}
              onSkip={() => roomRef.current?.send(ClientMessage.TutorialSkip)}
              onFinish={finishTutorial}
              onRulebook={() => setRulebookOpen(true)}
              onHeight={setTutorialCardHeight}
            />
          )}
        </>
      )}
      {phase === 'lobby' && (
        <Lobby
          players={players}
          self={self}
          lobbyInfo={lobbyInfo}
          isHost={isHost}
          onRankedChange={sendRankedSettings}
          timePerTurnMs={gameConfig?.timePerTurnMs}
          onTurnTimeLimitChange={sendTurnTimeLimit}
          spectators={spectators}
          isSpectator={isSpectator}
          onToggleReady={toggleReady}
          onKillLobby={sendKillLobby}
          disabled={!self}
          devMode={devMode}
          onAutoInit={sendAutoInit}
          onLeave={() => {
            // An unready exit frees the seat server-side — drop the stored
            // token so the hub doesn't offer a dead reconnect. A ready exit
            // keeps it: the seat stays held for this device. The creator's
            // seat is held ready or not, so their token always survives.
            if (activeMatch && !self?.ready && !isHost) {
              void removeActiveGameToken(activeMatch.gameId);
            }
            void roomRef.current?.leave();
            setActiveMatch(null);
            setPhase('lobby');
            setPlayers([]);
            setSpectators([]);
            setSelfId('');
            setLobbyInfo({
              hostUserId: '',
              creatorUserId: '',
              inviteCode: '',
              visibility: 'public',
              maxPlayers: 8,
              autoStartAtMs: 0,
              ranked: false,
              poolId: '',
              poolName: '',
            });
          }}
        />
      )}
        </>
      )}
      {/* On the hub and in the lobby the rulebook gets its own floating button;
          in-game it lives in the zoom stack instead (above the +/− buttons). */}
      {(!activeMatch || phase === 'lobby') && (
        <button
          type="button"
          className={`zoom-btn rulebook-fab${!activeMatch ? ' is-on-hub' : ''}`}
          onClick={() => setRulebookOpen(true)}
          title="How to play"
          aria-label="Open the rulebook"
        >
          ?
        </button>
      )}
      {rulebookOpen && (
        <Rulebook
          onClose={() => setRulebookOpen(false)}
          // In a room, show the match's pinned rules; on the hub the rulebook
          // fetches the server's default config itself.
          config={activeMatch ? gameConfig : null}
        />
      )}
    </div>
    </PlayerCountProvider>
    </ObsidianCostProvider>
  );
}

/** A player's four resource counts → the canonical `[m, f, v, o]` tuple. */
function toAmount(c: ResourceCounts): ResourceAmount {
  return [c.minerals, c.food, c.vigor, c.obsidian];
}

/** A simple centred status banner for the setup phase (e.g. "waiting on X"). */
/** The tutorial's step card (top centre, above the setup banners and toasts,
 * which shift down by its height — `--tutorial-card-h` on `.app`). Every
 * step can be skipped: the server plays its move for the learner. */
function TutorialCard({
  step,
  def,
  marketSwapPending,
  onSkip,
  onFinish,
  onRulebook,
  onHeight,
}: {
  step: number;
  def: (typeof TUTORIAL_STEPS)[number];
  /** The round-start market swap is waiting on the learner — say so, since
   * no turn action works until it is answered. */
  marketSwapPending: boolean;
  onSkip: () => void;
  onFinish: () => void;
  onRulebook: () => void;
  onHeight: (px: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const report = () => onHeight(el.getBoundingClientRect().height);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => {
      ro.disconnect();
      onHeight(0);
    };
  }, [onHeight]);
  const last = def.id === 'done';
  return (
    <div className="tutorial-card" ref={ref} role="region" aria-label="Tutorial">
      <div className="tutorial-card-head">
        <span className="tutorial-card-kicker">
          Tutorial · {step + 1} / {TUTORIAL_STEPS.length}
        </span>
        {!last && (
          <button type="button" className="tutorial-card-exit" onClick={onFinish}>
            Exit tutorial
          </button>
        )}
      </div>
      <strong className="tutorial-card-title">{def.title}</strong>
      {def.body.map((line, i) => (
        <p key={i} className="tutorial-card-body">
          {line}
        </p>
      ))}
      {marketSwapPending && def.id !== 'round_start_cycle' && (
        <p className="tutorial-card-note">
          Answer the market swap first.
        </p>
      )}
      <div className="tutorial-card-actions">
        {last ? (
          <>
            <button type="button" className="setup-confirm" onClick={onFinish}>
              Finish
            </button>
            <button type="button" className="setup-cancel" onClick={onRulebook}>
              Open rulebook
            </button>
          </>
        ) : (
          <button
            type="button"
            className={def.readOnly ? 'setup-confirm' : 'setup-cancel'}
            onClick={onSkip}
          >
            {def.readOnly ? 'Next' : 'Skip step'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusBanner({ title, sub }: { title?: string; sub: string }) {
  return (
    <div className="setup-banner" role="status">
      {title && <strong className="setup-banner-title">{title}</strong>}
      <span className="setup-banner-sub">{sub}</span>
    </div>
  );
}

/** Home-base placement prompt for the player whose turn it is to place. */
function HomePlacementBanner({
  selectedNode,
  onConfirm,
  onCancel,
}: {
  selectedNode: number | null;
  onConfirm: (nodeId: number) => void;
  onCancel: () => void;
}) {
  return (
    <div className="setup-banner" role="status">
      <strong className="setup-banner-title">Place your home base</strong>
      <span className="setup-banner-sub">
        {selectedNode === null
          ? 'Click a highlighted node on an outer tile.'
          : 'Confirm your pick, or click another node to change it.'}
      </span>
      {selectedNode !== null && (
        <div className="setup-banner-actions">
          <button
            type="button"
            className="setup-confirm"
            onClick={() => onConfirm(selectedNode)}
          >
            Confirm home base
          </button>
          <button type="button" className="setup-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** The whole starting phase, from this player's point of view. Home-base
 * placement is turn-based (only the current placer picks); production opens only
 * once *every* home base is down, then everyone assigns in parallel and the game
 * starts when all have confirmed. So the overlay shows: your placement prompt, a
 * "waiting to place" status, a "waiting for the last home bases" status, your
 * production panel, or a "ready" status. */
function StartupOverlay({
  self,
  players,
  currentPlayerId,
  selfId,
  startingProduction,
  selectedNode,
  onConfirmHome,
  onCancelHome,
  onConfirmProduction,
}: {
  self: LobbyPlayer | undefined;
  players: LobbyPlayer[];
  currentPlayerId: string;
  selfId: string;
  startingProduction: number;
  selectedNode: number | null;
  onConfirmHome: (nodeId: number) => void;
  onCancelHome: () => void;
  onConfirmProduction: (amounts: number[]) => void;
}) {
  if (!self?.homePlaced) {
    if (currentPlayerId === selfId) {
      return (
        <HomePlacementBanner
          selectedNode={selectedNode}
          onConfirm={onConfirmHome}
          onCancel={onCancelHome}
        />
      );
    }
    const current = players.find((p) => p.id === currentPlayerId);
    return (
      <StatusBanner
        title="Get ready"
        sub={`Waiting for ${current?.name ?? 'another player'} to place their home base…`}
      />
    );
  }
  if (players.some((p) => !p.homePlaced)) {
    return (
      <StatusBanner
        title="Home base placed"
        sub="Waiting for the other players to place their home bases…"
      />
    );
  }
  if (!self.productionSet) {
    return (
      <ProductionPanel
        startingProduction={startingProduction}
        onConfirm={onConfirmProduction}
      />
    );
  }
  return (
    <StatusBanner
      title="Ready"
      sub="Waiting for the other players to finish setting up…"
    />
  );
}

/** Bottom stack shown on your turn (or while queue-planning): your Bank and
 * Production above the action bar, then the bar itself — or, while an action
 * flow is open, that flow's picker in the bar's place (`flowPanel`), so picker
 * and bar never show at once. The action list comes
 * straight from the shared `ACTION_KINDS` (with labels/descriptions from
 * `config/actions.ts`), and each button's availability from the shared
 * `canPerformAction` — so e.g. Cycle Market disables when you can't afford it
 * (no Food). A disabled button shows the reason on hover.
 *
 * The bar's border glows (CSS) whenever it's shown — i.e. the moment it becomes
 * your turn — to draw the eye. The header spans the full width and shows how
 * many of your turn's actions you've spent ("action n/total"), or the queueing
 * status while planning.
 *
 * The End-Turn control's meaning depends on whether you've acted yet:
 *  - You've spent ≥1 action → "End turn": yield your turn but stay in the round.
 *  - You've spent none → there's nothing to end, so it's a Pass (out for the
 *    round). If you'd be the round's first passer, you take the First-Turn marker
 *    (lead next round), so the button says so.
 * While planning, Queue Pass and Done Queuing sit side by side instead.
 *
 * Each core action button opens its own flow. */
/**
 * Bottom-right button cluster: rulebook, zoom in/out and Back to Lobby.
 * Normally a vertical column absolutely anchored in the corner (shifting left
 * when the market sidebar is expanded); `docked` renders the same buttons as a
 * right-aligned row inside `.turn-action-stack`, just above the phone
 * full-width action bar.
 */
/** PHONE chrome (max-width 640px): Home top-left; zoom −/+ and the rulebook
 * "?" in a row top-right. Chat keeps its own bottom-left trigger and the
 * bottom-right corner is the dock (PhoneDock), so the small chrome buttons
 * never crowd the big dock buttons. */
function PhoneChrome({
  onRulebook,
  onZoomIn,
  onZoomOut,
  onBackToLobby,
}: {
  onRulebook: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onBackToLobby: () => void;
}) {
  return (
    <>
      <button
        type="button"
        className="zoom-btn zoom-btn-leave phone-home"
        onClick={onBackToLobby}
        title="Back to lobby"
        aria-label="Back to lobby"
      >
        <ChromeGlyph name="home" />
      </button>
      <div className="zoom-controls phone-top-right">
        <button
          type="button"
          className="zoom-btn"
          onClick={onZoomOut}
          title="Zoom out"
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className="zoom-btn"
          onClick={onZoomIn}
          title="Zoom in"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className="zoom-btn"
          onClick={onRulebook}
          title="How to play"
          aria-label="Open the rulebook"
        >
          ?
        </button>
      </div>
    </>
  );
}

/** PHONE bottom-right dock: large light buttons (visually apart from the dark
 * chrome buttons) that open the Players sheet (roster + turn history — the
 * left sidebar as a bottom sheet), the Fortress market sheet, and — while
 * playing — the action tray: live on your turn / while defending, or
 * queue-planning for an upcoming turn on anyone else's. */
function PhoneDock({
  playerCount,
  turnName,
  onPlayers,
  onMarket,
  turnLabel,
  turnLive,
  turnDisabled,
  onTurn,
}: {
  /** Seated players in the game, shown on the Players button. */
  playerCount: number;
  /** Whose turn it is (under the Players label), or null when nobody's. */
  turnName: string | null;
  onPlayers: () => void;
  onMarket: () => void;
  /** "Your turn" / "Queueing" / "Defend" while a bar is live, "Queue turn" on
   * anyone else's turn; null outside the playing phase (and for spectators). */
  turnLabel: string | null;
  /** Gold glow: a bar is live (your turn, queueing, or defending). */
  turnLive: boolean;
  /** A defense window you're not part of pauses the bar — nothing to open. */
  turnDisabled: boolean;
  onTurn: () => void;
}) {
  return (
    <div className="phone-dock">
      <button
        type="button"
        className="phone-dock-btn"
        onClick={onPlayers}
        aria-label="Players and turn history"
      >
        <ChromeGlyph name="players" size={22} />
        <span className="phone-dock-text">
          <span>Players ({playerCount})</span>
          {turnName && (
            <span className="phone-dock-sub">{turnName}’s turn</span>
          )}
        </span>
      </button>
      <button
        type="button"
        className="phone-dock-btn"
        onClick={onMarket}
        aria-label="Fortress markets"
      >
        <svg
          className="chrome-glyph"
          width={22}
          height={22}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="7" y="3" width="13" height="17" rx="2" />
          <path d="M4 7v12a2 2 0 0 0 2 2h11" />
        </svg>
        <span>Market</span>
      </button>
      {turnLabel && (
        <button
          type="button"
          className={`phone-dock-btn${turnLive ? ' is-turn' : ''}`}
          onClick={onTurn}
          disabled={turnDisabled}
          aria-label={`${turnLabel} — open the action tray`}
        >
          <span>{turnLabel}</span>
          <ChromeGlyph name="chevron-up" size={18} strokeWidth={2.4} />
        </button>
      )}
    </div>
  );
}

function CornerControls({
  docked = false,
  marketExpanded = false,
  onQueueTurn,
  queueTurnDisabled = false,
  onChat,
  onRulebook,
  onZoomIn,
  onZoomOut,
  onBackToLobby,
}: {
  docked?: boolean;
  marketExpanded?: boolean;
  /** Corner copy, on someone else's turn: the Queue turn button (left of the
   * stack, bottom-aligned) that opens queue-planning — the desktop twin of
   * the phone dock's. */
  onQueueTurn?: () => void;
  /** A defense window you're not part of pauses play — nothing to plan. */
  queueTurnDisabled?: boolean;
  /** Docked only: opens the chat (whose own bottom-left trigger is hidden
   * under the full-width action bar). Sits immediately left of the rulebook. */
  onChat?: () => void;
  onRulebook: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onBackToLobby: () => void;
}) {
  return (
    <div
      className={`zoom-controls${
        docked ? ' is-docked' : marketExpanded ? ' is-market-expanded' : ''
      }`}
    >
      {onQueueTurn && (
        <button
          type="button"
          className="queue-turn-btn"
          onClick={onQueueTurn}
          disabled={queueTurnDisabled}
          title="Queue moves for an upcoming turn — they auto-play when it starts. Your queue and card settings are listed in the bar."
        >
          Queue turn
          <ChromeGlyph name="chevron-up" size={18} strokeWidth={2.4} />
        </button>
      )}
      {onChat && (
        <button
          type="button"
          className="zoom-btn zoom-btn-chat"
          onClick={onChat}
          title="Chat"
          aria-label="Open chat"
        >
          <ChromeGlyph name="chat" />
        </button>
      )}
      <button
        type="button"
        className="zoom-btn"
        onClick={onRulebook}
        title="How to play"
        aria-label="Open the rulebook"
      >
        ?
      </button>
      <button
        type="button"
        className="zoom-btn"
        onClick={onZoomIn}
        title="Zoom in"
        aria-label="Zoom in"
      >
        +
      </button>
      <button
        type="button"
        className="zoom-btn"
        onClick={onZoomOut}
        title="Zoom out"
        aria-label="Zoom out"
      >
        −
      </button>
      <button
        type="button"
        className="zoom-btn zoom-btn-leave"
        onClick={onBackToLobby}
        title="Back to lobby"
        aria-label="Back to lobby"
      >
        <ChromeGlyph name="home" />
      </button>
    </div>
  );
}

function TurnActionBar({
  cornerControls,
  flowPanel,
  player,
  actionsRemaining,
  actionsPerTurn,
  anyPassed,
  cycleActive,
  buildActive,
  bridgeActive,
  implementActive,
  aimActive,
  effectsActive,
  hasEffectCards,
  usableEffectCount,
  collapsed,
  hideCollapsed,
  onToggleCollapsed,
  onConstructBuilding,
  onConstructBridge,
  onImplementFortress,
  onCycleMarket,
  onAimBarracks,
  onEffects,
  onEndTurn,
  onPass,
  undoLabel,
  onUndo,
  planningSlot,
  onEndPlanning,
  canQueuePass,
  onQueuePass,
  queuePanel,
  effectOf,
}: {
  /** Phone/tablet-only: the corner button cluster, docked above the bar (the
   * absolute corner copy is hidden while the full-width bar is up). */
  cornerControls?: ReactNode;
  /** The open action flow's picker (Build / Bridge / Implement / Aim /
   * Effects), if any. It renders IN PLACE of the action bar — the bar returns
   * when the flow confirms or cancels — so the two never stack (on phones the
   * full-width bar used to cover the floating picker). */
  flowPanel?: ReactNode;
  player: LobbyPlayer;
  actionsRemaining: number;
  actionsPerTurn: number;
  /** True if any player has already passed this round (so you wouldn't be first). */
  anyPassed: boolean;
  /** True while the Cycle-Market selection flow is open (highlights the button). */
  cycleActive: boolean;
  /** True while the Construct-Building flow is open (highlights the button). */
  buildActive: boolean;
  /** True while the Construct-Bridge flow is open (highlights the button). */
  bridgeActive: boolean;
  /** True while the Implement-Fortress picker is open (highlights the button). */
  implementActive: boolean;
  /** True while the Aim-Barracks flow is open (highlights the button). */
  aimActive: boolean;
  /** True while the Card-Effects tray is open (highlights the button). */
  effectsActive: boolean;
  /** Whether any implemented card carries an effect (shows the tray button). */
  hasEffectCards: boolean;
  /** How many effects are usable right now (the tray button's badge). */
  usableEffectCount: number;
  /** Bar collapsed to a slim status pill so the whole board is visible. The
   * pill keeps the pulsing "your turn" glow, so a live bar stays evident. */
  collapsed?: boolean;
  /** PHONES: render nothing while collapsed (the bottom-right dock's "Your
   * turn" / "Defend" button reopens the bar) instead of the status pill. */
  hideCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Open the building picker to construct a Scout / Barracks / Mine. */
  onConstructBuilding: () => void;
  /** Start the bridge flow (pick a path to bridge). */
  onConstructBridge: () => void;
  /** Open the Implement-Fortress picker (list of realisable revealed cards). */
  onImplementFortress: () => void;
  /** Open the market sidebar and start choosing cards to cycle. */
  onCycleMarket: () => void;
  /** Start the aim flow (pick a Barracks, a posture, then a target). */
  onAimBarracks: () => void;
  /** Open the Card-Effects tray (your implemented cards' granted actions). */
  onEffects: () => void;
  onEndTurn: () => void;
  onPass: () => void;
  /** The construction still takeable back this turn ("Build Scout"), or '' —
   * only Construct Building / Construct Bridge can be undone, only as the
   * most recent action, and only while the turn is still ours. */
  undoLabel?: string;
  /** Take back the action `undoLabel` names (the board rewinds to before it
   * and the action point comes back). */
  onUndo?: () => void;
  /** Non-null while queue-planning (off-turn): the upcoming-turn slot picked
   * actions are queued into. The bar relabels and swaps End Turn/Pass for a
   * Done button — actions themselves route to the queue upstream. */
  planningSlot?: number | null;
  /** Leave queue-planning mode (the Done button). */
  onEndPlanning?: () => void;
  /** Whether a Pass may be queued for the current planning slot — that slot
   * must be empty, and no Pass may already be queued on another turn. */
  canQueuePass?: boolean;
  /** Queue the Pass pseudo-move as the sole move for this planning turn: when
   * that turn comes you pass for the round. Other turns may still hold actions. */
  onQueuePass?: () => void;
  /** Everything queued (QueuePanel), listed under the End-Turn / Done row —
   * the one place the queue is shown and edited. */
  queuePanel?: ReactNode;
  /** Resolves card ids to effect config in the match's catalogue. */
  effectOf: CardEffectLookup;
}) {
  const state: PlayerActionState = {
    bank: toAmount(player.resources),
    production: toAmount(player.production),
    fortressCards: player.fortressCards,
  };
  // Nothing done yet → the only exit is to pass (out for the round). Otherwise
  // "End turn" yields the turn but keeps you in the round.
  const isPass = actionsRemaining === actionsPerTurn;
  // First to pass this round → take the First-Turn marker (lead next round).
  const takesMarker = isPass && !anyPassed;
  const endLabel = takesMarker
    ? 'Pass and take first turn marker'
    : isPass
      ? 'Pass until next round'
      : 'End turn';
  const endTitle = takesMarker
    ? 'Pass for the rest of this round and take the First-Turn marker — you lead next round.'
    : isPass
      ? 'Pass for the rest of this round.'
      : 'End your turn; you keep your place in this round.';
  const planning = planningSlot != null;
  // Planning a turn that holds the queued Pass (the Queue turn button opens
  // on it when no turn is free): a Pass stands alone in its turn, so nothing
  // more can be queued there until it's cancelled in the list below.
  const passQueuedHere =
    planning &&
    player.queuedMoves.some(
      (m) => m.slot === planningSlot && isQueuedPass(m.payload),
    );
  const dockRef = useDockHeight();
  const resources = (
    <div className="turn-action-resources" aria-label="Your resources">
      <ResourceRow
        label="Bank"
        resources={player.resources}
        oneTime
        iconSize={24}
        highlight
      />
      <ResourceRow
        label="Production"
        resources={player.production}
        iconSize={24}
      />
    </div>
  );
  // An action flow is open — its picker docks where the bar would be (the
  // resources strip and corner cluster stay put above it).
  if (flowPanel) {
    return (
      <div className="turn-action-stack" ref={dockRef}>
        {cornerControls}
        {resources}
        {flowPanel}
      </div>
    );
  }
  // Collapsed: the whole stack (resources included) shrinks to one glowing pill
  // so the board is fully visible. Flows can't open while collapsed — their
  // buttons live on the expanded bar — so this never hides an active picker.
  if (collapsed) {
    if (hideCollapsed) return null;
    return (
      <div className="turn-action-stack" ref={dockRef}>
        {cornerControls}
        <button
          type="button"
          className={`turn-action-bar is-collapsed${planning ? ' is-planning' : ''}`}
          onClick={onToggleCollapsed}
          aria-expanded={false}
          title="Show the action bar"
        >
          <span className="turn-action-bar-label">
            {planning ? 'Queueing' : 'Your turn'}
          </span>
          <span className="turn-action-bar-count">
            {planning
              ? planningSlot === 1
                ? 'next turn'
                : `in ${planningSlot} turns`
              : `${actionsRemaining} action${actionsRemaining === 1 ? '' : 's'} left`}
          </span>
          <span className="turn-action-collapse-chevron" aria-hidden>
            <ChromeGlyph name="chevron-up" size={16} strokeWidth={2.4} />
          </span>
        </button>
      </div>
    );
  }
  return (
    <div className="turn-action-stack" ref={dockRef}>
      {cornerControls}
      <div
        className={`turn-action-bar${planning ? ' is-planning' : ''}`}
        role="toolbar"
        aria-label={
          planning
            ? 'Planning queued moves — available actions'
            : 'Your turn — available actions'
        }
      >
        <div className="turn-action-bar-head">
          <span className="turn-action-bar-label">
            {planning
              ? `Queueing · ${planningSlot === 1 ? 'next turn' : `in ${planningSlot} turns`}`
              : 'Your turn'}
          </span>
          <span className="turn-action-bar-count">
            {planning
              ? passQueuedHere
                ? 'pass queued — cancel it to add moves'
                : 'picked actions are queued'
              : `${actionsRemaining} action${actionsRemaining === 1 ? '' : 's'} left`}
          </span>
          {onToggleCollapsed && (
            <button
              type="button"
              className="turn-action-collapse"
              onClick={onToggleCollapsed}
              aria-expanded
              aria-label="Collapse the action bar"
              title="Collapse to a pill so the whole board is visible"
            >
              <ChromeGlyph name="chevron-down" size={16} strokeWidth={2.4} />
            </button>
          )}
        </div>
        {resources}
        <div className="turn-action-main">
          <div className="turn-action-list">
            {ACTION_KINDS.map((kind) => {
              // Planning ignores every availability gate (cost, board state):
              // queue anything; the auto-play validator is the arbiter.
              const { allowed, reason } = planning
                ? passQueuedHere
                  ? { allowed: false, reason: PASS_QUEUED_HERE }
                  : { allowed: true, reason: undefined }
                : canPerformAction(kind, state, { effectOf });
              // Every core action opens its own flow.
              const isCycle = kind === 'cycle_market';
              const isBuild = kind === 'construct_building';
              const isBridge = kind === 'construct_bridge';
              const isImplement = kind === 'implement_fortress';
              const isAim = kind === 'aim_barracks';
              const onClick = isBuild
                ? onConstructBuilding
                : isBridge
                  ? onConstructBridge
                  : isImplement
                    ? onImplementFortress
                    : isCycle
                      ? onCycleMarket
                      : isAim
                        ? onAimBarracks
                        : undefined;
              const active =
                (isCycle && cycleActive) ||
                (isBuild && buildActive) ||
                (isBridge && bridgeActive) ||
                (isImplement && implementActive) ||
                (isAim && aimActive);
              return (
                <button
                  key={kind}
                  type="button"
                  data-action={kind}
                  className={`turn-action${active ? ' is-active' : ''}`}
                  disabled={!allowed}
                  onClick={onClick}
                  title={
                    allowed
                      ? ACTION_DESCRIPTIONS[kind]
                      : (reason ?? ACTION_DESCRIPTIONS[kind])
                  }
                >
                  <ChromeGlyph name={kind} />
                  {ACTION_LABELS[kind]}
                </button>
              );
            })}
            {hasEffectCards && (
              <button
                type="button"
                data-action="card_effects"
                className={`turn-action${effectsActive ? ' is-active' : ''}`}
                onClick={onEffects}
                disabled={passQueuedHere}
                title={
                  passQueuedHere
                    ? PASS_QUEUED_HERE
                    : 'Use an effect granted by one of your implemented Fortresses.'
                }
              >
                <ChromeGlyph name="card_effects" />
                Card Effects
                {usableEffectCount > 0 && (
                  <span className="turn-action-badge" aria-hidden>
                    {usableEffectCount}
                  </span>
                )}
              </button>
            )}
          </div>
          <div className="turn-action-ends">
            {!planning && undoLabel && onUndo && (
              <button
                type="button"
                className="turn-action turn-action-undo"
                onClick={onUndo}
                title={`Take back your ${undoLabel} — the board goes back to before it and you get the action back. Only your most recent construction, and only while it's still your turn.`}
              >
                <ChromeGlyph name="undo" size={18} />
                Undo {undoLabel}
              </button>
            )}
            {planning ? (
              <>
                {canQueuePass && (
                  <button
                    type="button"
                    className="turn-action turn-action-end is-pass"
                    onClick={onQueuePass}
                    title="Queue a Pass as the only move for this upcoming turn: when that turn comes you pass for the round (taking the First-Turn marker if you're first). Other turns may still have queued actions — a Pass just can't share its own turn with them."
                  >
                    <ChromeGlyph name="flag" size={18} strokeWidth={2.2} />
                    Queue Pass
                  </button>
                )}
                <button
                  type="button"
                  className="turn-action turn-action-end"
                  onClick={onEndPlanning}
                  title="Stop queueing moves. They play automatically when the turn comes, then the turn ends."
                >
                  <ChromeGlyph name="check" size={18} strokeWidth={2.4} />
                  Done queueing
                </button>
              </>
            ) : (
              <button
                type="button"
                className={`turn-action turn-action-end${isPass ? ' is-pass' : ''}`}
                onClick={isPass ? onPass : onEndTurn}
                title={endTitle}
              >
                <ChromeGlyph name="flag" size={18} strokeWidth={2.2} />
                {endLabel}
              </button>
            )}
          </div>
          {queuePanel}
        </div>
      </div>
    </div>
  );
}

/** The Construct-Building picker, docked in place of the turn action bar. Shows
 * Scout / Barracks / Mine with their base cost; a building unaffordable even at
 * base cost is disabled. Picking one enters placement mode (the parent highlights
 * legal nodes and swaps the cursor); the selected building stays highlighted so
 * it's clear what you're placing, and you can switch or cancel.
 *
 * A Block Us Baby free placement (`flow.freeBuildCardId`) reuses the same
 * picker: nothing is charged, so every building is pickable and the cost /
 * permanent-surcharge hints give way to "Free", and the range shown is the
 * player's resolved Barracks DEFEND range rather than each building's own. */
function BuildPicker({
  flow,
  resources,
  buildings,
  baseBuildings,
  subs,
  bunBun,
  onToggleRider,
  selectedPermanentResource,
  onSelect,
  onAimMode,
  onClearAim,
  onConfirm,
  onCancel,
  freeBuildCfg,
}: {
  flow: BuildFlow;
  resources: ResourceCounts;
  buildings: Record<BuildingType, BuildingConfig> | undefined;
  /** The match's UNMODIFIED building configs. `buildings` already carries the
   * player's own discounts (Resource Sequencer's cheaper mines, Hippoman's
   * cheaper Food), so the two together let a cost show the printed
   * price struck through beside what this player actually pays. */
  baseBuildings: Record<BuildingType, BuildingConfig> | undefined;
  /** The player's payment substitutions (Treadmill / Bench). */
  subs: PaymentSubstitutions;
  /** A usable Energized Bun-Bun (double one-time pickups), if held. */
  bunBun: { cardId: string; tokens: number; cap: number } | null;
  /** Arm/disarm Bun-Bun's double on the pending placement. */
  onToggleRider: () => void;
  /** The permanent resource printed on the tentatively-selected node, if any —
   * shows a warning that a non-Mine build won't collect it. */
  selectedPermanentResource: ResourceKind | null;
  onSelect: (building: ConstructableBuilding) => void;
  /** Start the optional build-time aim on a pending Barracks placement. */
  onAimMode: (mode: AimMode) => void;
  /** Drop the build-time aim (back to a plain unaimed build). */
  onClearAim: () => void;
  /** Commit the tentatively-selected node (only shown once one is picked). */
  onConfirm: () => void;
  onCancel: () => void;
  /** The card placement's catalogue `freeBuild` config, when the picker is
   * driving one (`flow.freeBuildCardId`): which building it allows, how far
   * it reaches, and what it still charges. */
  freeBuildCfg?: CardEffect['freeBuild'];
}) {
  if (!flow) return null;
  const placing = flow.stage === 'place' ? flow.building : null;
  const riderArmed = flow.stage === 'place' && !!flow.doubleOneTime;
  const hasSelection = flow.stage === 'place' && flow.selected !== undefined;
  const aim = flow.stage === 'place' ? flow.aim : undefined;
  const barracksRange = buildings?.barracks?.range;
  const bank = toAmount(resources);
  return (
    <div
      className="build-picker"
      role="dialog"
      aria-label="Construct a building"
    >
      <div className="build-picker-head">
        <span className="build-picker-title">
          {placing
            ? `${flow.freeBuildCardId ? 'Free' : flow.penaltyCardId ? 'Token-build' : 'Placing'} ${BUILDING_LABELS[placing]}`
            : flow.freeBuildCardId
              ? 'Place a Free Building'
              : flow.penaltyCardId
                ? 'Pay Penalty with Token'
                : 'Construct Building'}
        </span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Cancel (Esc)"
          aria-label="Cancel"
        >
          ✕
        </button>
      </div>
      <div className="build-picker-options">
        {CONSTRUCTABLE_BUILDINGS.filter(
          (b) => !flow.freeBuildCardId || !freeBuildCfg?.building || freeBuildCfg.building === b,
        ).map((b) => {
          // A card placement waives what its config leaves out: Block Us
          // Baby waives resources, the Vigor penalty and the Mine's
          // permanent-resource surcharge alike, reaching by the Barracks
          // defend stat; a `payCost` printing (Rabid Shoes, Hurtful Shrine)
          // pays the building's price and shows it.
          const free = !!flow.freeBuildCardId && !freeBuildCfg?.payCost;
          const cost = free ? [] : buildings?.[b]?.cost ?? [];
          // The printed price, for the strikeout — only meaningful against a
          // concrete four-slot cost (a wildcard cost has no per-resource
          // counterpart to compare with).
          const printedCost =
            !free && cost.length === 4
              ? (baseBuildings?.[b]?.cost ?? cost)
              : cost;
          // Conditional surcharge: a Mine built on a permanent resource node
          // pays this much extra, charged in that node's resource (see
          // buildCostFor / server buildCost). Shown as an "if" hint here since
          // the target node isn't known until placement.
          const permCost = free ? 0 : buildings?.[b]?.permanentResourceCost ?? 0;
          const useDefend =
            !!flow.freeBuildCardId && freeBuildCfg?.range === 'barracks_defend';
          const range = useDefend
            ? { build: buildings?.barracks?.range?.defend ?? 0 }
            : flow.freeBuildCardId && buildings?.[b]?.range
              ? {
                  ...buildings[b].range,
                  build:
                    buildings[b].range.build + (freeBuildCfg?.rangeBonus ?? 0),
                }
              : buildings?.[b]?.range;
          // Base affordability only (the per-node Vigor penalty is enforced
          // server-side); a base-unaffordable building can never be built.
          const affordable = free || canAffordWith(bank, cost, subs);
          return (
            <button
              key={b}
              type="button"
              className={`build-option${placing === b ? ' is-selected' : ''}`}
              disabled={!affordable}
              onClick={() => onSelect(b)}
              title={affordable ? BUILDING_BLURBS[b] : 'Not enough resources'}
            >
              <BuildingIcon kind={b} />
              <span className="build-option-name">{BUILDING_LABELS[b]}</span>
              <span className="build-option-cost">
                {free
                  ? <span className="build-cost-num">Free</span>
                  : RESOURCE_KINDS.map((k, i) =>
                      // A discount that zeroes a component still shows, so the
                      // saving is visible rather than the row just going quiet.
                      (cost[i] ?? 0) > 0 || (printedCost[i] ?? 0) > 0 ? (
                        <span key={k} className="build-cost-item">
                          <ResourceCost
                            kind={k}
                            printed={printedCost[i] ?? 0}
                            paid={cost[i] ?? 0}
                            size={36}
                          />
                        </span>
                      ) : null,
                    )}
              </span>
              {range ? (
                <span
                  className="build-option-range"
                  title={
                    useDefend
                      ? 'Barracks defend range, in nodes along edges'
                      : 'Build range, in nodes along edges'
                  }
                >
                  Range {range.build}
                </span>
              ) : null}
              {permCost > 0 ? (
                <span
                  className="build-option-extra"
                  title="Extra cost when built on a permanent resource node — charged in that node's own resource (shown as ?)"
                >
                  +{permCost}? on permanent resource
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {/* A free placement pays nothing, so Bun-Bun's double has nothing to
          ride on — the toggle is hidden rather than silently ignored. */}
      {placing && bunBun && !flow.freeBuildCardId && (
        <SwitchToggle
          className="build-picker-hint effect-rider-toggle"
          checked={riderArmed}
          onChange={onToggleRider}
          title="Energized Bun-Bun: double the one-time resource this build collects — spends 1 of its 5 uses (only if something is actually collected)."
        >
          Double one-time pickup (Bun-Bun {bunBun.tokens}/{bunBun.cap} used)
        </SwitchToggle>
      )}
      {placing && placing !== 'mine' && hasSelection && selectedPermanentResource && (
        <div className="build-picker-warning" role="alert">
          ⚠️ A {BUILDING_LABELS[placing]} won&apos;t collect this{' '}
          {RESOURCE_LABELS[selectedPermanentResource]} — only a Mine can.
        </div>
      )}
      {placing === 'barracks' && hasSelection && (
        <div className="build-picker-options">
          <button
            type="button"
            className={`build-option${aim?.mode === 'attack' ? ' is-selected' : ''}`}
            onClick={() =>
              aim?.mode === 'attack' ? onClearAim() : onAimMode('attack')
            }
            title="Optional — aim the new Barracks at an enemy building as part of this build, saving the separate Aim action. Click again to build it unaimed."
          >
            <span className="build-option-name">⚔️ Build aimed: Attack</span>
            <span className="build-option-range">
              Range {barracksRange?.attack ?? '—'}
            </span>
          </button>
          <button
            type="button"
            className={`build-option${aim?.mode === 'defend' ? ' is-selected' : ''}`}
            onClick={() =>
              aim?.mode === 'defend' ? onClearAim() : onAimMode('defend')
            }
            title="Optional — aim the new Barracks to defend a building as part of this build, saving the separate Aim action. Click again to build it unaimed."
          >
            <span className="build-option-name">🛡️ Build aimed: Defend</span>
            <span className="build-option-range">
              Range {barracksRange?.defend ?? '—'}
            </span>
          </button>
        </div>
      )}
      {placing && hasSelection && aim?.stage !== 'target' && (
        <button type="button" className="setup-confirm" onClick={onConfirm}>
          Confirm {BUILDING_LABELS[placing]}
          {aim?.stage === 'done'
            ? ` — ${aim.mode === 'attack' ? 'attacking' : 'defending'} node ${aim.targetId}`
            : ''}
        </button>
      )}
      <div className="build-picker-hint">
        {placing
          ? aim?.stage === 'target'
            ? aim.allowed.size === 0
              ? 'No legal targets in range — switch posture or click it again to build unaimed'
              : aim.mode === 'attack'
                ? 'Tap a highlighted enemy building to aim the new Barracks at'
                : 'Tap a highlighted building for the new Barracks to defend'
            : hasSelection
              ? placing === 'barracks' && aim?.stage !== 'done'
                ? 'Confirm to build, or aim it now — same action, no extra cost'
                : 'Confirm to build, or click another highlighted node · Esc to cancel'
              : 'Click a highlighted node to select the spot · Esc to cancel'
          : 'Pick a building, then click a highlighted node'}
      </div>
    </div>
  );
}

/** The Aim-Barracks flow's panel, docked in place of the turn (or defense)
 * action bar. Stage 1: tap one of your (pulsing)
 * unaimed Barracks. Stage 2: choose the posture — Attack (destroy the target at
 * end of turn if attackers outnumber defenders) or Defend (add a defender to a
 * building, yours or another player's). Stage 3: tap a highlighted target. The
 * defense-window variant skips stage 2 (defend-locked). */
function AimPicker({
  flow,
  range,
  onMode,
  onCancel,
}: {
  flow: AimFlow;
  /** The Barracks' ranges from the match config (for the posture hints). */
  range: { attack?: number; defend?: number } | undefined;
  onMode: (mode: AimMode) => void;
  onCancel: () => void;
}) {
  if (!flow) return null;
  const title =
    flow.stage === 'pickBarracks'
      ? flow.sources.size === 0
        ? 'No unaimed Barracks available'
        : flow.defense
          ? 'Defend: tap a Barracks to aim'
          : 'Tap one of your unaimed Barracks'
      : flow.stage === 'mode'
        ? 'Attack or defend?'
        : flow.allowed.size === 0
          ? 'No legal targets in range'
          : flow.mode === 'attack'
            ? 'Tap an enemy building to attack'
            : 'Tap a building to defend';
  return (
    <div className="build-picker" role="dialog" aria-label="Aim Barracks">
      <div className="build-picker-head">
        <span className="build-picker-title">{title}</span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Cancel (Esc)"
          aria-label="Cancel"
        >
          ✕
        </button>
      </div>
      {flow.stage === 'mode' && (
        <div className="build-picker-options">
          <button
            type="button"
            className="build-option"
            disabled={!flow.attackable}
            onClick={() => onMode('attack')}
            title={
              flow.attackable
                ? 'Destroyed at end of turn if attackers outnumber its defenders'
                : 'A Barracks inside a Fortress can only defend'
            }
          >
            <span className="build-option-name">⚔️ Attack</span>
            <span className="build-option-range">
              Range {range?.attack ?? '—'}
            </span>
          </button>
          <button
            type="button"
            className="build-option"
            onClick={() => onMode('defend')}
            title="Adds a defender to a building — yours or another player's"
          >
            <span className="build-option-name">🛡️ Defend</span>
            <span className="build-option-range">
              Range {range?.defend ?? '—'}
            </span>
          </button>
        </div>
      )}
      <div className="build-picker-hint">
        {flow.stage === 'mode'
          ? 'An aimed Barracks is committed until the round ends'
          : 'Click a highlighted node · Esc to cancel'}
      </div>
    </div>
  );
}

/** Zapped Bapped Statue pay window — the top-of-screen banner, NOT a modal:
 * an opponent's action just paid an energy penalty, and the Statue's owner
 * may match it onto the card's stored pile. The window lives until the next
 * action finalizes (the server closes it, replacing this banner with that
 * action's own if it also paid energy), so play never pauses on it. Queued
 * volleys skip the banner entirely — the auto-pay setting in the queue panel
 * covers those. */
function VigorOfferBanner({
  amount,
  statueName,
  stored,
  target,
  affordable,
  onAnswer,
}: {
  /** Energy the opponent's action paid — the price to match it. */
  amount: number;
  statueName: string;
  stored: number;
  target: number;
  affordable: boolean;
  onAnswer: (pay: boolean) => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const answer = (pay: boolean) => {
    setSubmitted(true);
    onAnswer(pay);
  };
  return (
    <div className="vigor-offer-banner" role="status">
      <span className="vigor-offer-label">
        Vigor spent <ResourceIcon kind="vigor" size={16} /> {amount}
      </span>
      <span className="vigor-offer-sub">
        {statueName} · {stored}/{Number.isFinite(target) ? target : '—'} stored
      </span>
      <button
        type="button"
        className="vigor-offer-pay"
        disabled={submitted || !affordable}
        title={
          affordable
            ? 'Match the penalty onto the Statue — the window closes when the next action is taken'
            : 'Not enough Energy'
        }
        onClick={() => answer(true)}
      >
        {submitted ? 'Paying…' : (
          <>
            Pay <ResourceIcon kind="vigor" size={14} /> {amount}
          </>
        )}
      </button>
      <button
        type="button"
        className="vigor-offer-dismiss"
        disabled={submitted}
        title="Close this window"
        aria-label="Dismiss"
        onClick={() => answer(false)}
      >
        ✕
      </button>
    </div>
  );
}

/** Blind attacker choice required by Dungeon Dice Martyr. Card identities stay
 * hidden; the attacker chooses only a face-down position. */
function MartyrDiscardPrompt({
  pending,
  startedMs,
  limitMs,
  victimName,
  onChoose,
}: {
  pending: string;
  /** Timed matches: when this choice was put to us; out of time, the server
   * picks at random. */
  startedMs: number;
  limitMs?: number;
  victimName: string;
  onChoose: (victimId: string, index: number) => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [victimId, rawCount] = pending.split('|');
  const count = Math.max(1, Number(rawCount) || 1);
  const timed = !!limitMs && startedMs > 0;
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!timed) return;
    const id = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(id);
  }, [timed]);
  return (
    <div className="build-picker effects-picker" role="dialog" aria-modal="true">
      <div className="build-picker-head">
        <span className="build-picker-title">Dungeon Dice Martyr</span>
        {timed && (
          <TurnTimer
            remainingMs={Math.max(0, limitMs! - (now - startedMs))}
          />
        )}
      </div>
      <span className="build-picker-hint">
        Choose one of {victimName}&apos;s face-down fortress cards to discard.
      </span>
      <div className="build-picker-options">
        {Array.from({ length: count }, (_, index) => (
          <button
            key={index}
            type="button"
            className="build-option effect-use"
            disabled={submitted}
            onClick={() => {
              setSubmitted(true);
              onChoose(victimId ?? '', index);
            }}
          >
            {submitted ? 'Discarding...' : `Face-down card ${index + 1}`}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Post-combat Barracks upkeep (pay-to-keep): each of your Barracks that just
 * attacked must be fed its zone's Food upkeep or it starves and is destroyed.
 * One keep-or-starve decision per row; the ending turn stays frozen until every
 * owner has answered (the turn clock backstops a no-show). */
function BarracksUpkeepPrompt({
  entries,
  onFocus,
  onResolve,
}: {
  entries: {
    nodeId: number;
    cost: number;
    /** The zone's printed upkeep — struck through when this player pays less. */
    baseCost: number;
    zone: string;
    affordable: boolean;
  }[];
  /** Pan the board to the Barracks in question. */
  onFocus: (nodeId: number) => void;
  onResolve: (nodeId: number, keep: boolean) => void;
}) {
  const [sent, setSent] = useState<ReadonlySet<number>>(new Set());
  const answer = (nodeId: number, keep: boolean) => {
    setSent((prev) => new Set(prev).add(nodeId));
    onResolve(nodeId, keep);
  };
  return (
    <div className="build-picker effects-picker" role="dialog" aria-modal="true">
      <div className="build-picker-head">
        <span className="build-picker-title">Barracks Upkeep</span>
      </div>
      <span className="build-picker-hint">
        Your Barracks attacked — pay each one&apos;s Food upkeep to keep it, or
        let it starve.
      </span>
      <div className="effects-list">
        {entries.map(({ nodeId, cost, baseCost, zone, affordable }) => (
          <div className="effect-row" key={nodeId}>
            <div className="effect-row-head">
              <button
                type="button"
                className="effect-row-name"
                onClick={() => onFocus(nodeId)}
                title="Show this Barracks on the board"
              >
                Barracks — {zone}
              </button>
            </div>
            <div className="effect-row-actions">
              <button
                type="button"
                className="build-option effect-use"
                disabled={sent.has(nodeId) || !affordable}
                title={
                  affordable
                    ? 'Feed the troops that made the assault'
                    : 'Not enough Food'
                }
                onClick={() => answer(nodeId, true)}
              >
                Pay <DiscountedNumber printed={baseCost} paid={cost} /> Food
              </button>
              <button
                type="button"
                className="build-option effect-use"
                disabled={sent.has(nodeId)}
                title="The unfed Barracks is destroyed"
                onClick={() => answer(nodeId, false)}
              >
                Let it starve
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Post-combat plunder: your Barracks destroyed another player's building —
 * pick which of the victim's resources to steal (up to the entry's count).
 * One pick per victim; the ending turn stays frozen until every attacker has
 * answered (the turn clock backstops a no-show with the greedy default). */
function StealResourcesPrompt({
  entries,
  onResolve,
}: {
  entries: {
    attackerId: string;
    victimId: string;
    attackerName: string;
    victimName: string;
    /** The victim's live bank — the per-resource ceiling on the pick. */
    holdings: ResourceAmount;
    count: number;
    /** True = this client is the VICTIM choosing what to hand over
     * (Porkypine Neck's `victim_picks_steal`); false = the attacker picks. */
    give: boolean;
  }[];
  onResolve: (
    attackerId: string,
    victimId: string,
    resources: ResourceAmount,
  ) => void;
}) {
  const [picks, setPicks] = useState<Record<string, ResourceAmount>>({});
  const [sent, setSent] = useState<ReadonlySet<string>>(new Set());
  const keyOf = (e: { attackerId: string; victimId: string }): string =>
    `${e.attackerId}|${e.victimId}`;
  const pickFor = (key: string): ResourceAmount =>
    picks[key] ?? [0, 0, 0, 0];
  const adjust = (key: string, index: number, delta: number) => {
    setPicks((prev) => {
      const next = [...(prev[key] ?? [0, 0, 0, 0])] as ResourceAmount;
      next[index] = Math.max(0, (next[index] ?? 0) + delta);
      return { ...prev, [key]: next };
    });
  };
  const giving = entries.every((e) => e.give);
  return (
    <div className="build-picker effects-picker" role="dialog" aria-modal="true">
      <div className="build-picker-head">
        <span className="build-picker-title">
          {giving ? 'Hand Over Resources' : 'Steal Resources'}
        </span>
      </div>
      <span className="build-picker-hint">
        {giving
          ? 'A building of yours was plundered — your Porkypine Neck lets ' +
            'YOU choose which resources to give the attacker.'
          : 'Your Barracks destroyed a building — choose what to plunder ' +
            'from its owner.'}
      </span>
      <div className="effects-list">
        {entries.map((entry) => {
          const { attackerId, victimId, attackerName, victimName } = entry;
          const { holdings, count, give } = entry;
          const key = keyOf(entry);
          const pick = pickFor(key);
          const total = pick.reduce((s, n) => s + n, 0);
          const available = holdings.reduce((s, n) => s + n, 0);
          const target = Math.min(count, available);
          const verb = give ? 'Give' : 'Steal';
          return (
            <div className="effect-row" key={key}>
              <div className="effect-row-head">
                <span className="effect-row-name">
                  {give
                    ? `Give ${attackerName} ${target}`
                    : `${victimName} — take ${target}`}
                </span>
              </div>
              <div className="production-rows">
                {RESOURCE_KINDS.map((kind, i) => (
                  <div className="production-row" key={kind}>
                    <span className="production-res">
                      <ResourceIcon kind={kind} size={16} />{' '}
                      {RESOURCE_LABELS[kind]} ({holdings[i] ?? 0})
                    </span>
                    <div className="production-stepper">
                      <button
                        type="button"
                        disabled={sent.has(key) || (pick[i] ?? 0) <= 0}
                        aria-label={`${verb} less ${RESOURCE_LABELS[kind]}`}
                        onClick={() => adjust(key, i, -1)}
                      >
                        −
                      </button>
                      <span className="production-count">{pick[i] ?? 0}</span>
                      <button
                        type="button"
                        disabled={
                          sent.has(key) ||
                          total >= target ||
                          (pick[i] ?? 0) >= (holdings[i] ?? 0)
                        }
                        aria-label={`${verb} more ${RESOURCE_LABELS[kind]}`}
                        onClick={() => adjust(key, i, 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="effect-row-actions">
                <button
                  type="button"
                  className="build-option effect-use"
                  disabled={sent.has(key) || total !== target}
                  title={
                    total === target
                      ? give
                        ? `Give these resources to ${attackerName}`
                        : `Take these resources from ${victimName}`
                      : `Pick ${target} resource${target === 1 ? '' : 's'} to ${verb.toLowerCase()}`
                  }
                  onClick={() => {
                    setSent((prev) => new Set(prev).add(key));
                    onResolve(attackerId, victimId, pick);
                  }}
                >
                  {verb}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Off-turn banner while YOUR defense window is open: buildings of yours fall
 * when the ending turn resolves. Aim reactive defenders (free of action points),
 * then confirm Done so combat can resolve and the turn pass on. */
function DefenseBar({
  cornerControls,
  flowPanel,
  aiming,
  collapsed,
  hideCollapsed,
  onToggleCollapsed,
  onAim,
  onDone,
}: {
  /** Phone/tablet-only: the corner button cluster, docked above the bar. */
  cornerControls?: ReactNode;
  /** The defense Aim picker, if aiming — docks in place of the bar until the
   * aim lands or cancels (same swap as TurnActionBar's flowPanel). */
  flowPanel?: ReactNode;
  aiming: boolean;
  /** Bar collapsed to a slim glowing pill (same toggle as TurnActionBar). */
  collapsed?: boolean;
  /** PHONES: render nothing while collapsed (the bottom-right dock's "Your
   * turn" / "Defend" button reopens the bar) instead of the status pill. */
  hideCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  onAim: () => void;
  onDone: () => void;
}) {
  const dockRef = useDockHeight();
  if (flowPanel) {
    return (
      <div className="turn-action-stack" ref={dockRef}>
        {cornerControls}
        {flowPanel}
      </div>
    );
  }
  if (collapsed) {
    if (hideCollapsed) return null;
    return (
      <div className="turn-action-stack" ref={dockRef}>
        {cornerControls}
        <button
          type="button"
          className="turn-action-bar is-collapsed"
          onClick={onToggleCollapsed}
          aria-expanded={false}
          title="Show the defense bar"
        >
          <span className="turn-action-bar-label">Under attack!</span>
          <span className="turn-action-collapse-chevron" aria-hidden>
            <ChromeGlyph name="chevron-up" size={16} strokeWidth={2.4} />
          </span>
        </button>
      </div>
    );
  }
  return (
    <div className="turn-action-stack" ref={dockRef}>
      {cornerControls}
      <div className="turn-action-bar" role="toolbar" aria-label="Defense window">
        <div className="turn-action-bar-head">
          <span className="turn-action-bar-label">Under attack!</span>
          <span className="turn-action-bar-count">
            aim defenders before the turn resolves
          </span>
          {onToggleCollapsed && (
            <button
              type="button"
              className="turn-action-collapse"
              onClick={onToggleCollapsed}
              aria-expanded
              aria-label="Collapse the defense bar"
              title="Collapse to a pill so the whole board is visible"
            >
              <ChromeGlyph name="chevron-down" size={16} strokeWidth={2.4} />
            </button>
          )}
        </div>
        <div className="turn-action-main">
          <div className="turn-action-list">
            <button
              type="button"
              className={`turn-action${aiming ? ' is-active' : ''}`}
              onClick={onAim}
              title="Aim an unaimed Barracks to defend an attacked building — free during a defense window"
            >
              <ChromeGlyph name="aim_barracks" />
              Aim Defender
            </button>
          </div>
          <div className="turn-action-ends">
            <button
              type="button"
              className="turn-action turn-action-end"
              onClick={onDone}
              title="Finish defending — combat resolves once every attacked player is done"
            >
              <ChromeGlyph name="check" size={18} strokeWidth={2.4} />
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Production allocation panel: distribute `startingProduction` points across the
 * four resources (total must equal the budget) then Confirm. Shown to a player
 * once they've placed their home base; players allocate in parallel. */
function ProductionPanel({
  startingProduction,
  onConfirm,
}: {
  startingProduction: number;
  onConfirm: (amounts: number[]) => void;
}) {
  // Allocation as a [minerals, food, vigor, obsidian] tuple (RESOURCE_KINDS order).
  const [alloc, setAlloc] = useState<number[]>([0, 0, 0, 0]);
  const used = alloc.reduce((sum, n) => sum + n, 0);
  const remaining = startingProduction - used;
  const bump = (i: number, d: number) =>
    setAlloc((a) => a.map((n, j) => (j === i ? n + d : n)));

  return (
    <div
      className="production-panel"
      role="dialog"
      aria-label="Assign production"
    >
      <strong className="setup-banner-title">Assign your production</strong>
      <span className="setup-banner-sub">
        Distribute {startingProduction} production · {remaining} left
      </span>
      <div className="production-rows">
        {RESOURCE_KINDS.map((kind, i) => (
          <div className="production-row" key={kind}>
            <span className="production-res">
              <ResourceIcon kind={kind} size={16} /> {RESOURCE_LABELS[kind]}
            </span>
            <div className="production-stepper">
              <button
                type="button"
                aria-label={`Less ${RESOURCE_LABELS[kind]}`}
                disabled={alloc[i] === 0}
                onClick={() => bump(i, -1)}
              >
                −
              </button>
              <span className="production-count">{alloc[i]}</span>
              <button
                type="button"
                aria-label={`More ${RESOURCE_LABELS[kind]}`}
                disabled={remaining === 0}
                onClick={() => bump(i, 1)}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="setup-confirm"
        disabled={remaining !== 0 || startingProduction === 0}
        onClick={() => onConfirm(alloc)}
      >
        Confirm production
      </button>
    </div>
  );
}

/** The Implement-Fortress placement bar (live play, after a card is picked):
 * every node the pattern can land on pulses on the board; tapping one selects
 * a concrete placement there (gold rings — tapping it again, or Rotate, cycles
 * the other placements over that node) and Confirm implements it. A card whose
 * implement effect consumes a resource choice asks for it on Confirm. Docks
 * in place of the turn action bar, like the Build picker. */
function ImplementPlacementBar({
  card,
  selected,
  omitLabel,
  onCycleOmit,
  onRotate,
  onConfirm,
  onBack,
}: {
  card: ImplementCandidate;
  selected: { nodeId: number; options: number[]; pos: number } | null;
  /** Current omit-choice label (Obsidian Ovary Overload), or null to hide. */
  omitLabel: string | null;
  onCycleOmit: () => void;
  onRotate: () => void;
  onConfirm: (choice?: ImplementChoice) => void;
  onBack: () => void;
}) {
  /** Waiting on the card's resource / piece pick (after Confirm)? */
  const [choosing, setChoosing] = useState(false);
  const needsChoice = card.needsChoice === true;
  return (
    <div
      className="build-picker"
      role="dialog"
      aria-label={`Place ${card.name}`}
    >
      <div className="build-picker-head">
        <span className="build-picker-title">
          {choosing
            ? card.pieceOptions
              ? `${card.name} — place which piece on the card?`
              : card.bankOrProduction
                ? `${card.name} — bank it, or grow production?`
                : card.capTargets
                  ? `${card.name} — double or halve which fortress's token cap?`
                  : `${card.name} — gain which resource?`
            : selected
              ? `Place ${card.name}: location ${selected.pos + 1} of ${selected.options.length} at this spot`
              : `Place ${card.name}: tap a highlighted spot`}
        </span>
        <button
          type="button"
          className="build-picker-cancel"
          onClick={choosing ? () => setChoosing(false) : onBack}
          title="Back (Esc)"
          aria-label="Back"
        >
          ✕
        </button>
      </div>
      {choosing ? (
        <div className="build-picker-options">
          {card.pieceOptions
            ? // A choice card: the piece placed on the card denotes which of
              // its effects is live for the rest of the game.
              card.pieceOptions.map((piece) => (
                <button
                  key={piece}
                  type="button"
                  className="build-option"
                  onClick={() => onConfirm({ piece })}
                  title={`Choose ${PIECE_LABELS[piece]}`}
                >
                  <PieceIcon kind={piece} size={36} />
                  <span className="build-option-name">{PIECE_LABELS[piece]}</span>
                </button>
              ))
            : card.bankOrProduction
              ? bankOrProductionOptions(card.bankOrProduction).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className="build-option"
                    onClick={() => onConfirm(opt.choice)}
                    title={opt.label}
                  >
                    {opt.icon && <ResourceIcon kind={opt.icon} size={36} />}
                    <span className="build-option-name">{opt.label}</span>
                  </button>
                ))
            : card.capTargets
              ? card.capTargets.flatMap((t) =>
                  (['double', 'halve'] as const).map((mode) => (
                    <button
                      key={`${t.cardId}:${mode}`}
                      type="button"
                      className="build-option"
                      onClick={() =>
                        onConfirm({ mode, targetCardId: t.cardId })
                      }
                      title={`${mode === 'double' ? 'Double' : 'Halve'} ${t.name}'s token cap`}
                    >
                      <span className="build-option-name">
                        {mode === 'double' ? '×2' : '÷2'} {t.name}
                      </span>
                    </button>
                  )),
                )
            : RESOURCE_KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className="build-option"
                  onClick={() => onConfirm({ resource: kind })}
                  title={`Gain 1 ${RESOURCE_LABELS[kind]}`}
                >
                  <ResourceIcon kind={kind} size={36} />
                  <span className="build-option-name">{RESOURCE_LABELS[kind]}</span>
                </button>
              ))}
        </div>
      ) : (
        <div className="build-picker-options">
          {omitLabel !== null && (
            <button
              type="button"
              className="build-option"
              onClick={onCycleOmit}
              title="Ovary Overload: build with one less piece — cycle which piece is left out"
            >
              <span className="build-option-name">⌦ {omitLabel}</span>
            </button>
          )}
          <button
            type="button"
            className="build-option"
            disabled={!selected || selected.options.length < 2}
            onClick={onRotate}
            title={
              !selected
                ? 'Pick a spot first'
                : selected.options.length > 1
                  ? 'Show the next placement over this spot'
                  : 'Only one placement over this spot'
            }
          >
            <span className="build-option-name">⟳ Rotate</span>
          </button>
          <button
            type="button"
            className="build-option"
            disabled={!selected}
            onClick={() => (needsChoice ? setChoosing(true) : onConfirm())}
            title={
              selected ? `Implement ${card.name} here` : 'Pick a spot first'
            }
          >
            <span className="build-option-name">✓ Confirm</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** One row of the effects tray: the card's name + live token/exhausted state,
 * with a Use button per effect the card grants. Passive-only cards render
 * their rule chips instead of a button. */
function EffectCardRow({
  card,
  view,
  player,
  copiedName,
  context,
  onStart,
  effectOf,
}: {
  card: FortressCard | undefined;
  view: FortressCardView;
  player: LobbyPlayer;
  context: EffectContext;
  onStart: (cardId: string, kind: SpecialActionKind) => void;
  effectOf: CardEffectLookup;
  /** Name of the card this instance is copying (CurdleCat), if any. */
  copiedName?: string | null;
}) {
  // A CurdleCat-awarded copy is a plain instance of the copied card — its
  // row renders like any other, marked with a ⧉ Copy chip; the CurdleCat's
  // own row wears a chip naming its current copy.
  const fx = effectOf(view.cardId);
  const target = instanceTokenTarget(fx, view);
  const name = card?.name ?? view.cardId;
  return (
    <div className="effect-row">
      <div className="effect-row-head">
        <span className="effect-row-name" title={card?.description}>
          {name}
        </span>
        {view.copied && (
          <span
            className="effect-chip"
            title="A CurdleCat copy — the effect without the board fortress"
          >
            ⧉ Copy
          </span>
        )}
        {copiedName && (
          <span className="effect-chip" title="Currently copied effect">
            ⧉ {copiedName}
          </span>
        )}
        {(view.tokens > 0 || Number.isFinite(target)) && (
          <span className="effect-chip" title="Tokens on this fortress">
            ● {view.tokens}
            {Number.isFinite(target) ? `/${target}` : ''}
          </span>
        )}
        {(view.stored > 0 || fx?.winAtStored !== undefined) &&
          (fx?.tokenAdjustFromStored ? (
            // Supreme Bondage "54"+: the pile is banked token changes.
            <span
              className="effect-chip"
              title="Token changes banked by locked-fortress implements — spend each on a regular fortress you own"
            >
              ±● {view.stored}
            </span>
          ) : (
            <span
              className="effect-chip"
              title={
                depositsToStored(fx)
                  ? 'Energy deposited on this fortress — the collect pays it out ×2'
                  : fx?.winAtStored !== undefined
                    ? `Resources stored on this fortress — wins at ${fx.winAtStored}`
                    : 'Resources stored on this fortress'
              }
            >
              <ResourceIcon kind="vigor" size={12} /> {view.stored}
              {fx?.winAtStored !== undefined ? `/${fx.winAtStored}` : ''}
            </span>
          ))}
        {view.choice !== '' && (
          <span
            className="effect-chip effect-chip-choice"
            title="The piece placed on this card denotes the option chosen on implement"
          >
            <PieceIcon kind={view.choice as ImplementPiece} size={14} />{' '}
            {pieceLabel(view.choice)}
          </span>
        )}
        {view.disabled && (
          <span className="effect-chip is-off" title="Effect already used">
            Exhausted
          </span>
        )}
      </div>
      <div className="effect-row-actions">
        {(fx?.grants ?? []).map((kind) => {
          const { allowed, reason } = effectAvailability(
            player,
            view.cardId,
            kind,
            context,
            effectOf,
          );
          return (
            <button
              key={kind}
              type="button"
              className="build-option effect-use"
              disabled={!allowed}
              onClick={() => onStart(view.cardId, kind)}
              title={reason ?? card?.description}
            >
              {effectLabel(kind, fx)}
            </button>
          );
        })}
        {(fx?.grants?.length ?? 0) === 0 && (
          <span className="effect-passive" title={card?.description}>
            Passive — always on
          </span>
        )}
      </div>
    </div>
  );
}

/** The Card-Effects tray, floating above the turn action bar like the Build
 * picker. The list stage shows every implemented card that carries an effect;
 * sub-stages walk one effect's inputs (confirm / board pick / token adjust /
 * market pick). Board-pick stages keep the tray up as a hint banner — the
 * actual picking happens on the board. */
function EffectsPicker({
  flow,
  player,
  allPlayers,
  cardsById,
  markets,
  context,
  bunBun,
  onStart,
  onConfirm,
  onConfirmUntap,
  onConfirmMove,
  onToggleMoveRider,
  onStartReorder,
  onMoveReorder,
  onSetReorderRoute,
  onToggleReorderCycle,
  onConfirmReorder,
  onPickClaim,
  onBankTwo,
  onNudgeRedistribute,
  onConfirmRedistribute,
  onNudgeTokens,
  onConfirmTokens,
  onIncomePick,
  onSetDropResource,
  onAdjustToken,
  onCopyEffect,
  onMarkPick,
  onMarkShuffle,
  onToggleMarkShuffle,
  onLock,
  onFreeCycle,
  onTutorMarket,
  onTutorPick,
  onTutorConfirm,
  onShuffleDiscardPick,
  onShuffleCycle,
  onShuffleConfirm,
  onBack,
  onCancel,
}: {
  flow: NonNullable<EffectsFlow>;
  player: LobbyPlayer;
  /** Every seat (the player included) — CurdleCat's copy targets. */
  allPlayers: readonly LobbyPlayer[];
  cardsById: ReadonlyMap<string, FortressCard>;
  markets: MarketView;
  context: EffectContext;
  bunBun: { cardId: string; tokens: number; cap: number } | null;
  onStart: (cardId: string, kind: SpecialActionKind) => void;
  onConfirm: () => void;
  onConfirmUntap: () => void;
  onConfirmMove: () => void;
  onToggleMoveRider: () => void;
  onStartReorder: (market: FortressKind) => void;
  onMoveReorder: (index: number, delta: -1 | 1) => void;
  /** Peek printing: toggle a glanced card between top / bottom / discard. */
  onSetReorderRoute: (cardId: string, dest: 'bottom' | 'discard') => void;
  /** Free-cycle printing: toggle a face-up card of the glanced row on or off
   * the cycle pick made alongside the arrangement. */
  onToggleReorderCycle: (index: number) => void;
  onConfirmReorder: () => void;
  onPickClaim: (kind: string) => void;
  /** Morphing Midget: gain 2 of the chosen kind. */
  onBankTwo: (kind: string) => void;
  /** Demented Rapist: move one unit on or off a kind of the composed split. */
  onNudgeRedistribute: (index: number, delta: 1 | -1) => void;
  /** Demented Rapist: commit the split (only once it totals what's held). */
  onConfirmRedistribute: () => void;
  /** Big Boom: move one token on or off a held fortress of the composed
   * redistribution. */
  onNudgeTokens: (index: number, delta: 1 | -1) => void;
  /** Big Boom: commit the redistribution (only once every token is placed). */
  onConfirmTokens: () => void;
  /** Resource Sequencer / Frienemy: cash one banked single-resource pick. */
  onIncomePick: (kind: string) => void;
  /** Pee-Pee Pinata: switch which kind the next board tap places. */
  onSetDropResource: (kind: string) => void;
  onAdjustToken: (
    targetPlayerId: string,
    targetCardId: string,
    delta: 1 | -1,
  ) => void;
  onCopyEffect: (targetPlayerId: string, targetCardId: string) => void;
  /** Dog Den: one mark pick (win row first, then regular). */
  onMarkPick: (cardId: string) => void;
  /** Dog Den: confirm the reshuffle answers once both rows are marked. The
   * "24"–"27" printings pass one answer covering both marks; the "28"+
   * printing passes nothing and confirms the per-mark toggles. */
  onMarkShuffle: (shuffleBack?: boolean) => void;
  /** Dog Den, "28"+ printing: flip one mark between shuffle-back and
   * stay-face-up before confirming. */
  onToggleMarkShuffle: (which: 'win' | 'regular') => void;
  onLock: (market: FortressKind, marketCardId: string, locked: boolean) => void;
  onFreeCycle: (market: FortressKind, indices: readonly number[]) => void;
  onTutorMarket: (market: FortressKind) => void;
  onTutorPick: (foundCardId: string) => void;
  onTutorConfirm: () => void;
  /** Dungeon Dice Martyr: pick / un-pick the discard to shuffle back. */
  onShuffleDiscardPick: (market: FortressKind, cardId: string) => void;
  /** Dungeon Dice Martyr: toggle a face-up card of the picked row on or off
   * the free cycle. */
  onShuffleCycle: (index: number) => void;
  onShuffleConfirm: () => void;
  onBack: () => void;
  onCancel: () => void;
}) {
  const title =
    flow.stage === 'list'
      ? 'Card Effects'
      : (cardsById.get(flow.cardId)?.name ?? 'Card Effect');
  const withEffects = player.fortressCards.filter((fc) => {
    const fx = cardsById.get(fc.cardId)?.effects;
    return (fx?.grants?.length ?? 0) > 0 || (fx?.passives?.length ?? 0) > 0;
  });
  const lockedTotal = markets.regular.locked.length + markets.win.locked.length;
  const marketRows: { kind: FortressKind; label: string; pile: MarketPile }[] = [
    { kind: 'REGULAR', label: 'Regular', pile: markets.regular },
    { kind: 'win', label: 'Win', pile: markets.win },
  ];
  // Cyclopz multi-select: any number of picks up to the whole row, all from
  // the same market row (the first pick fixes the row, like the paid cycle
  // flow). Local UI state — cleared
  // whenever the flow leaves the free-cycle market stage.
  const [freeSel, setFreeSel] = useState<{
    kind: FortressKind;
    indices: ReadonlySet<number>;
  } | null>(null);
  const freeCycleActive =
    flow.stage === 'market' && flow.kind === 'free_cycle_market';
  useEffect(() => {
    if (!freeCycleActive) setFreeSel(null);
  }, [freeCycleActive]);

  // Reorder rows name a card but don't show it, so you'd be arranging cards you
  // can't read. Hovering a row's name previews its full face beside the tray;
  // clicking (tapping, on touch — the only path there, since there's no hover)
  // pins it. Same hover/pin semantics as the market and roster tiles: a pinned
  // detail wins over hover, and the popover closes on Escape or an outside
  // click.
  const [rowDetail, setRowDetail] = useState<{
    card: FortressCard;
    anchorEl: HTMLElement;
    pinned: boolean;
  } | null>(null);
  // One tray serves every stage; drop an open detail when the stage (and so the
  // row list under it) changes.
  useEffect(() => {
    setRowDetail(null);
  }, [flow.stage]);
  const previewRowCard = useCallback(
    (card: FortressCard | undefined, anchorEl: HTMLElement) => {
      if (!card) return;
      setRowDetail((cur) =>
        cur?.pinned ? cur : { card, anchorEl, pinned: false },
      );
    },
    [],
  );
  const endRowPreview = useCallback((cardId: string) => {
    setRowDetail((cur) =>
      cur && !cur.pinned && cur.card.id === cardId ? null : cur,
    );
  }, []);
  const pinRowCard = useCallback(
    (card: FortressCard | undefined, anchorEl: HTMLElement) => {
      if (!card) return;
      setRowDetail({ card, anchorEl, pinned: true });
    },
    [],
  );
  return (
    <div className="build-picker effects-picker" role="dialog" aria-label={title}>
      <div className="build-picker-head">
        {flow.stage !== 'list' &&
          flow.stage !== 'reorderDeck' &&
          flow.stage !== 'claim' && (
          <button
            type="button"
            className="build-picker-cancel"
            onClick={onBack}
            title="Back (Esc)"
            aria-label="Back"
          >
            ‹
          </button>
        )}
        <span className="build-picker-title">{title}</span>
        {flow.stage !== 'reorderDeck' && flow.stage !== 'claim' && <button
          type="button"
          className="build-picker-cancel"
          onClick={onCancel}
          title="Close"
          aria-label="Close"
        >
          ✕
        </button>}
      </div>

      {flow.stage === 'list' && (
        <div className="effects-list">
          {withEffects.length === 0 ? (
            <span className="build-picker-hint">
              None of your fortresses carries an effect.
            </span>
          ) : (
            withEffects.map((fc, i) => (
              <EffectCardRow
                key={`${fc.cardId}-${i}`}
                card={cardsById.get(fc.cardId)}
                view={fc}
                player={player}
                copiedName={
                  fc.copiedCardId !== ''
                    ? `${cardsById.get(fc.copiedCardId)?.name ?? fc.copiedCardId}` +
                      (Number.isFinite(
                        tokenCapOf(cardsById.get(fc.cardId)?.effects),
                      )
                        ? ` · ${fc.tokens}/${tokenCapOf(cardsById.get(fc.cardId)?.effects)} copies`
                        : '')
                    : null
                }
                context={context}
                onStart={onStart}
                effectOf={(id) => cardsById.get(id)?.effects}
              />
            ))
          )}
        </div>
      )}

      {flow.stage === 'confirm' && (
        <div className="effects-confirm">
          <p className="effects-blurb">
            {flow.kind === 'double_banked_resources'
              ? 'Double every resource in your bank. Once per game — the card flips afterward. Costs 1 action.'
              : flow.kind === 'bank_production_reset'
                ? 'Bank 2× your current production, then destroy your production track (reset to 0/0/0/0) — your mines keep standing but stop yielding. Once per game — the card flips afterward. Costs 1 action.'
                : 'End the round immediately for everyone and take the First-Turn marker. Adds a token to the card.'}
          </p>
          <div className="effects-confirm-actions">
            <button type="button" className="build-option" onClick={onConfirm}>
              Confirm
            </button>
            <button type="button" className="build-option" onClick={onBack}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'pickScout' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? 'Tap one of your highlighted buildings or X markers to make it indestructible for the rest of the game.'
            : 'Nothing protectable — nothing to pick.'}
        </span>
      )}

      {flow.stage === 'sellBuilding' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? 'Tap one of your highlighted buildings to sell it for 100% of its build cost. Energy is not refunded, and spent one-time resources stay spent.'
            : 'No sellable buildings — fortress pieces cannot be sold.'}
        </span>
      )}

      {flow.stage === 'designateTarget' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? 'Tap one of your highlighted buildings to designate it: every opponent Barracks in attack range must tap and attack it, and you get a free window to aim defenders.'
            : 'No opponent Barracks has any of your buildings in attack range.'}
        </span>
      )}

      {flow.stage === 'tutor' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Search a row's deck for any card. The deck is shuffled and your
            pick goes on top — only you know which card it is.
          </span>
          <div className="effects-confirm-actions">
            {marketRows
              .filter(({ kind }) => {
                // "54"+ Poopy Investigator searches the regular deck only.
                const rows = cardsById.get(flow.cardId)?.effects?.tutorMarkets;
                return !rows || rows.includes(kind);
              })
              .map(({ kind, label, pile }) => (
                <button
                  key={kind}
                  type="button"
                  className={`build-option${flow.market === kind ? ' is-selected' : ''}`}
                  onClick={() => onTutorMarket(kind)}
                >
                  {label} ({pile.pool.length})
                </button>
              ))}
          </div>
          {(() => {
            const pile = flow.market === 'win' ? markets.win : markets.regular;
            return (
              <>
                <div className="effects-list">
                  {pile.pool.length === 0 ? (
                    <span className="build-picker-hint">
                      Nothing in that row's deck.
                    </span>
                  ) : (
                    pile.pool.map((id) => (
                      <button
                        key={id}
                        type="button"
                        className={`build-option effect-use${
                          flow.foundCardId === id ? ' is-selected' : ''
                        }`}
                        onClick={() => onTutorPick(id)}
                      >
                        {cardsById.get(id)?.name ?? id}
                      </button>
                    ))
                  )}
                </div>
                {flow.foundCardId !== '' && (
                  <div className="effects-confirm-actions">
                    <button
                      type="button"
                      className="build-option"
                      onClick={onTutorConfirm}
                    >
                      Put {cardsById.get(flow.foundCardId)?.name ?? 'it'} on
                      top of the deck
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {flow.stage === 'shuffleDiscard' &&
        (() => {
          const cap =
            cardsById.get(flow.cardId)?.effects?.shuffleDiscardCycleCount ?? 0;
          const discards = marketRows.flatMap(({ kind, label, pile }) =>
            // Newest first, like the market's own discard list; a card can sit
            // in a pile twice, so the row index keys it.
            [...pile.discarded].reverse().map((id, i) => ({ kind, label, id, i })),
          );
          // The free cycles come out of the picked discard's own row.
          const cyclePile = flow.pick?.market === 'win' ? markets.win : markets.regular;
          return (
            <div className="effects-list">
              <span className="build-picker-hint">
                {`Pick a discarded fortress to shuffle back into its deck` +
                  (cap > 0
                    ? `, then up to ${cap} face-up card${cap === 1 ? '' : 's'} ` +
                      `of that same row to cycle out for free.`
                    : '.') +
                  ' Costs 1 action.'}
              </span>
              {discards.length === 0 ? (
                <span className="build-picker-hint">
                  Both discard piles are empty.
                </span>
              ) : (
                discards.map(({ kind, label, id, i }) => {
                  const picked =
                    flow.pick?.market === kind && flow.pick.cardId === id;
                  return (
                    <div className="effect-row" key={`discard:${kind}:${id}:${i}`}>
                      <div className="effect-row-head">
                        <span
                          className="effect-row-name"
                          title={cardsById.get(id)?.description}
                        >
                          {cardsById.get(id)?.name ?? id}
                        </span>
                        <span className="effect-chip">{label}</span>
                      </div>
                      <div className="effect-row-actions">
                        <button
                          type="button"
                          className={`build-option effect-use${
                            picked ? ' is-selected' : ''
                          }`}
                          onClick={() => onShuffleDiscardPick(kind, id)}
                        >
                          {picked ? 'Shuffling Back' : 'Shuffle Back'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
              {cap > 0 &&
                flow.pick &&
                cyclePile.cards.map(
                  (id, index) => {
                    const isLocked = cyclePile.locked.includes(id);
                    const selected = flow.cycle.includes(index);
                    const full = !selected && flow.cycle.length >= cap;
                    return (
                      <div className="effect-row" key={`cycle:${id}`}>
                        <div className="effect-row-head">
                          <span
                            className="effect-row-name"
                            title={cardsById.get(id)?.description}
                          >
                            {cardsById.get(id)?.name ?? id}
                          </span>
                          {isLocked && (
                            <span className="effect-chip is-off">Locked</span>
                          )}
                        </div>
                        <div className="effect-row-actions">
                          <button
                            type="button"
                            className={`build-option effect-use${
                              selected ? ' is-selected' : ''
                            }`}
                            disabled={isLocked || full}
                            onClick={() => onShuffleCycle(index)}
                            title={
                              isLocked
                                ? 'Locked cards can’t be cycled.'
                                : full
                                  ? `At most ${cap} card${cap === 1 ? '' : 's'} per use.`
                                  : undefined
                            }
                          >
                            {selected ? 'Cycling' : 'Cycle'}
                          </button>
                        </div>
                      </div>
                    );
                  },
                )}
              <button
                type="button"
                className="build-option"
                disabled={!flow.pick}
                onClick={onShuffleConfirm}
              >
                {!flow.pick
                  ? 'Pick a discard to shuffle back'
                  : flow.cycle.length > 0
                    ? `Shuffle back + cycle ${flow.cycle.length}`
                    : 'Shuffle back'}
              </button>
            </div>
          );
        })()}

      {flow.stage === 'replicate' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? `Tap a highlighted anchor to place the same bridge fortress in its original energy level. This costs no resources${
                cardsById.get(flow.cardId)?.effects?.grantCostsAction
                  ? ' but takes up an action'
                  : ' or action point'
              }.`
            : 'No empty placement fits in the original energy level.'}
        </span>
      )}

      {flow.stage === 'untap' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Tap up to {flow.limit} aimed Barracks to stand back upright (
            {flow.selected.size}/{flow.limit} selected).
          </span>
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              disabled={flow.selected.size === 0}
              onClick={onConfirmUntap}
            >
              Ready {flow.selected.size || ''} Barracks
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'moveFortress' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Tap {flow.limit} adjacent destination nodes within your attack range
            ({flow.selected.size}/{flow.limit} picked). Moving costs the energy
            penalty of the highest zone entered.
          </span>
          {bunBun && (
            <SwitchToggle
              className="effect-rider-toggle"
              checked={!!flow.doubleOneTime}
              onChange={onToggleMoveRider}
              title="Energized Bun-Bun: double the one-time resource this move collects — spends 1 of its 5 uses (only if something is actually collected)."
            >
              Double one-time pickup (Bun-Bun {bunBun.tokens}/{bunBun.cap} used)
            </SwitchToggle>
          )}
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              disabled={flow.selected.size !== flow.limit}
              onClick={onConfirmMove}
            >
              Move here
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'claim' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Pick {flow.remaining * 2} resources to gain ({flow.picked.length}/
            {flow.remaining * 2}
            {flow.picked.length > 0 ? ` — ${flow.picked.join(', ')}` : ''}).
          </span>
          <div className="build-picker-options">
            {RESOURCE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="build-option effect-use"
                disabled={flow.submitted}
                onClick={() => onPickClaim(kind)}
              >
                <ResourceIcon kind={kind} size={36} />
                {RESOURCE_LABELS[kind]}
              </button>
            ))}
          </div>
        </div>
      )}

      {flow.stage === 'moveBuildingFrom' && (
        <span className="build-picker-hint">
          {flow.allowed.size > 0
            ? "Tap the highlighted building to move — you'll pick its destination next. Fortress pieces can't be moved."
            : 'No moveable buildings with a legal destination.'}
        </span>
      )}

      {flow.stage === 'moveBuildingTo' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Tap the destination node — empty, within your Mine's build range of
            the building. No energy penalties. Arriving collects the node's
            one-time pickup, just like building there would.
          </span>
          {bunBun && (
            <SwitchToggle
              className="effect-rider-toggle"
              checked={!!flow.doubleOneTime}
              onChange={onToggleMoveRider}
              title="Energized Bun-Bun: double the one-time resource this move collects — spends 1 of its 5 uses (only if something is actually collected)."
            >
              Double one-time pickup (Bun-Bun {bunBun.tokens}/{bunBun.cap} used)
            </SwitchToggle>
          )}
        </div>
      )}

      {flow.stage === 'bankTwo' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Pick one resource to gain 2 of. Costs 1 action; the card flips
            until next round.
          </span>
          <div className="build-picker-options">
            {RESOURCE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="build-option effect-use"
                onClick={() => onBankTwo(kind)}
              >
                <ResourceIcon kind={kind} size={36} />
                {RESOURCE_LABELS[kind]}
              </button>
            ))}
          </div>
        </div>
      )}

      {flow.stage === 'redistribute' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Split your {flow.total} resource{flow.total === 1 ? '' : 's'} any way
            you like ({flow.amounts.reduce((a, b) => a + b, 0)}/{flow.total}{' '}
            assigned). Costs 1 action, and the card takes its only token — once
            per game.
          </span>
          <div className="production-rows">
            {RESOURCE_KINDS.map((kind, i) => (
              <div className="production-row" key={kind}>
                <span className="production-res">
                  <ResourceIcon kind={kind} size={16} /> {RESOURCE_LABELS[kind]}
                </span>
                <div className="production-stepper">
                  <button
                    type="button"
                    aria-label={`Less ${RESOURCE_LABELS[kind]}`}
                    disabled={(flow.amounts[i] ?? 0) <= 0}
                    onClick={() => onNudgeRedistribute(i, -1)}
                  >
                    −
                  </button>
                  <span className="production-count">{flow.amounts[i] ?? 0}</span>
                  <button
                    type="button"
                    aria-label={`More ${RESOURCE_LABELS[kind]}`}
                    disabled={
                      flow.amounts.reduce((a, b) => a + b, 0) >= flow.total
                    }
                    onClick={() => onNudgeRedistribute(i, 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              disabled={flow.amounts.reduce((a, b) => a + b, 0) !== flow.total}
              onClick={onConfirmRedistribute}
            >
              Redistribute
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'redistributeTokens' &&
        (() => {
          // Budgeted ("53"+ Big Boom): up to `budget` unit changes from the
          // opening counts. Fixed-total ("50"-"52"): every token re-placed.
          const budget = flow.budget;
          const assigned = flow.entries.reduce((a, e) => a + e.tokens, 0);
          const used = flow.entries.reduce(
            (a, e) => a + Math.abs(e.tokens - e.start),
            0,
          );
          const exhausted = budget !== undefined && used >= budget;
          return (
            <div className="effects-confirm">
              <span className="build-picker-hint">
                {budget !== undefined
                  ? `Add or remove up to ${budget} token${budget === 1 ? '' : 's'} ` +
                    `across your other fortresses (${used}/${budget} used); each ` +
                    `stays within its cap. Costs 1 action, once per game.`
                  : `Move your ${flow.total} token${flow.total === 1 ? '' : 's'} between ` +
                    `your fortresses however you like (${assigned}/${flow.total} ` +
                    `placed); each stays within its cap. Costs 1 action, once per game.`}
              </span>
              <div className="production-rows">
                {flow.entries.map((entry, i) => (
                  <div className="production-row" key={`${entry.cardId}-${i}`}>
                    <span className="production-res">
                      {entry.name}
                      {Number.isFinite(entry.cap) ? ` (max ${entry.cap})` : ''}
                    </span>
                    <div className="production-stepper">
                      <button
                        type="button"
                        aria-label={`Fewer tokens on ${entry.name}`}
                        disabled={
                          entry.tokens <= 0 ||
                          (exhausted && entry.tokens <= entry.start)
                        }
                        onClick={() => onNudgeTokens(i, -1)}
                      >
                        −
                      </button>
                      <span className="production-count">{entry.tokens}</span>
                      <button
                        type="button"
                        aria-label={`More tokens on ${entry.name}`}
                        disabled={
                          entry.tokens >= entry.cap ||
                          (budget !== undefined
                            ? exhausted && entry.tokens >= entry.start
                            : assigned >= flow.total)
                        }
                        onClick={() => onNudgeTokens(i, 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="effects-confirm-actions">
                <button
                  type="button"
                  className="build-option"
                  disabled={
                    budget !== undefined ? used === 0 : assigned !== flow.total
                  }
                  onClick={onConfirmTokens}
                >
                  {budget !== undefined ? 'Adjust Tokens' : 'Redistribute'}
                </button>
              </div>
            </div>
          );
        })()}

      {flow.stage === 'pickIncome' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            Bonus income: {flow.remaining} resource
            {flow.remaining === 1 ? '' : 's'} to collect — pick one at a time,
            any kinds. Free of actions.
          </span>
          <div className="build-picker-options">
            {RESOURCE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className="build-option effect-use"
                disabled={flow.remaining <= 0}
                onClick={() => onIncomePick(kind)}
              >
                <ResourceIcon kind={kind} size={36} />
                {RESOURCE_LABELS[kind]}
              </button>
            ))}
          </div>
        </div>
      )}

      {flow.stage === 'dropResources' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            {flow.allowed.size > 0
              ? `Distribute ${flow.remaining} one-time resource${
                  flow.remaining === 1 ? '' : 's'
                }: pick a kind, then tap highlighted high-zone nodes to place ` +
                `1 each. Free of actions.`
              : 'No unoccupied nodes left in the highest energy zones.'}
          </span>
          <div className="build-picker-options">
            {RESOURCE_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className={`build-option effect-use${
                  flow.resource === kind ? ' is-selected' : ''
                }`}
                onClick={() => onSetDropResource(kind)}
              >
                <ResourceIcon kind={kind} size={36} />
                {RESOURCE_LABELS[kind]}
              </button>
            ))}
          </div>
        </div>
      )}

      {flow.stage === 'adjustToken' &&
        (() => {
          // "Any implemented fortress" is a catalogue "14"-"37" printing;
          // every other printing ("38"+ included) offers the holder's own
          // fortresses only.
          const sourceFx = cardsById.get(flow.cardId)?.effects;
          const anyFortress = !!sourceFx?.adjustAnyFortress;
          // Clobbering Bat / Tokenizer 9000 ("49"+): regular fortresses only.
          const regularOnly = !!sourceFx?.adjustRegularOnly;
          const holders = anyFortress ? allPlayers : [player];
          return (
            <div className="effects-list">
              <span className="build-picker-hint">
                Add or remove one token on{' '}
                {anyFortress
                  ? 'any implemented fortress'
                  : regularOnly
                    ? 'one of your regular fortresses'
                    : 'one of your fortresses'}
                {sourceFx?.tokenAdjustFromStored
                  ? `. Free — spends one of the ${
                      player.fortressCards.find((fc) => fc.cardId === flow.cardId)
                        ?.stored ?? 0
                    } token change(s) banked on this card.`
                  : '. Costs 1 action unless a held Tokenizer 9000 makes token actions free.'}
              </span>
              {holders.map((holder) =>
                holder.fortressCards
                  .filter(
                    (fc) =>
                      !regularOnly || cardsById.get(fc.cardId)?.kind === 'REGULAR',
                  )
                  .map((fc, i) => {
                  const cap = instanceTokenCap(cardsById.get(fc.cardId)?.effects, fc);
                  const tokens = fc.tokens;
                  const name = cardsById.get(fc.cardId)?.name ?? fc.cardId;
                  return (
                    <div
                      className="effect-row"
                      key={`${holder.id}-${fc.cardId}-${i}`}
                    >
                      <div className="effect-row-head">
                        <span className="effect-row-name">{name}</span>
                        {anyFortress && (
                          <span className="effect-chip">{holder.name}</span>
                        )}
                        <span className="effect-chip">
                          ● {tokens}
                          {Number.isFinite(cap) ? `/${cap}` : ''}
                        </span>
                      </div>
                      <div className="effect-row-actions">
                        <button
                          type="button"
                          className="build-option effect-use"
                          disabled={tokens >= cap}
                          onClick={() => onAdjustToken(holder.id, fc.cardId, 1)}
                          title={
                            tokens >= cap ? 'At its token cap.' : 'Add a token'
                          }
                        >
                          +1
                        </button>
                        <button
                          type="button"
                          className="build-option effect-use"
                          disabled={tokens <= 0}
                          onClick={() => onAdjustToken(holder.id, fc.cardId, -1)}
                          title={
                            tokens <= 0 ? 'No tokens to remove.' : 'Remove a token'
                          }
                        >
                          −1
                        </button>
                      </div>
                    </div>
                  );
                }),
              )}
            </div>
          );
        })()}

      {flow.stage === 'copyEffect' && (
        <div className="effects-list">
          <span className="build-picker-hint">
            Copy the effect of another implemented fortress — any player's,
            your own included. Only one copy at a time; copying again
            replaces it. Costs 1 action and a token.
          </span>
          {allPlayers.map((holder) => {
            // "Another implemented fortress": the copier itself is not a
            // target — hide ONE instance of the granting card from its own
            // holder's list (a duplicate CurdleCat remains offered).
            let hidCopier = holder.id !== player.id;
            return holder.fortressCards.map((fc, i) => {
              if (!hidCopier && fc.cardId === flow.cardId) {
                hidCopier = true;
                return null;
              }
              const name = cardsById.get(fc.cardId)?.name ?? fc.cardId;
              return (
                <div className="effect-row" key={`${holder.id}-${fc.cardId}-${i}`}>
                  <div className="effect-row-head">
                    <span
                      className="effect-row-name"
                      title={cardsById.get(fc.cardId)?.description}
                    >
                      {name}
                      {fc.copied ? ' (a CurdleCat copy)' : ''}
                    </span>
                    <span className="effect-chip">{holder.name}</span>
                  </div>
                  <div className="effect-row-actions">
                    <button
                      type="button"
                      className="build-option effect-use"
                      onClick={() => onCopyEffect(holder.id, fc.cardId)}
                      title={cardsById.get(fc.cardId)?.description}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              );
            });
          })}
        </div>
      )}

      {flow.stage === 'markFortresses' &&
        flow.markRegular !== undefined &&
        (cardsById.get(flow.cardId)?.effects?.markShufflePerCard ? (
          // "28"+ printing: each mark is settled on its own — shuffle back 0,
          // 1 or both. Each row toggles between the two fates; Confirm sends
          // the pair as they stand (both start on the printed reshuffle).
          <div className="effects-list">
            <span className="build-picker-hint">
              Choose what happens to each marked fortress: shuffle it back into
              its deck, or leave it face up in the market where anyone can
              still implement it.
            </span>
            {(
              [
                ['win', flow.markWin ?? '', flow.shuffleWin !== false],
                ['regular', flow.markRegular, flow.shuffleRegular !== false],
              ] as const
            ).map(([which, markedId, shuffling]) => (
              <div className="effect-row" key={which}>
                <div className="effect-row-head">
                  <span
                    className="effect-row-name"
                    title={cardsById.get(markedId)?.description}
                  >
                    {cardsById.get(markedId)?.name ?? markedId}
                  </span>
                  <span className={`effect-chip${shuffling ? '' : ' is-off'}`}>
                    {shuffling ? 'Shuffle back' : 'Face up'}
                  </span>
                </div>
                <div className="effect-row-actions">
                  <button
                    type="button"
                    className="build-option effect-use"
                    onClick={() => onToggleMarkShuffle(which)}
                    title={
                      shuffling
                        ? 'Leave this fortress face up in the market instead, still buildable.'
                        : 'Shuffle this fortress back into its deck instead; its row slot refills.'
                    }
                  >
                    {shuffling ? 'Leave Face Up' : 'Shuffle Back'}
                  </button>
                </div>
              </div>
            ))}
            <div className="effect-row-actions">
              <button
                type="button"
                className="build-option effect-use"
                onClick={() => onMarkShuffle()}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : (
          // "24"–"27" printing: one answer covers both marks.
          <div className="effects-confirm">
            <span className="build-picker-hint">
              {`Marked ${cardsById.get(flow.markWin ?? '')?.name ?? flow.markWin} and ` +
                `${cardsById.get(flow.markRegular)?.name ?? flow.markRegular}. ` +
                `Shuffle both back into their decks, or leave them face up in ` +
                `the market where anyone can still implement them?`}
            </span>
            <div className="effect-row-actions">
              <button
                type="button"
                className="build-option effect-use"
                onClick={() => onMarkShuffle(true)}
                title="Both marked fortresses return to their decks; each row slot refills."
              >
                Shuffle Back
              </button>
              <button
                type="button"
                className="build-option effect-use"
                onClick={() => onMarkShuffle(false)}
                title="Both marked fortresses stay face up in the market, still buildable."
              >
                Keep Showing
              </button>
            </div>
          </div>
        ))}

      {flow.stage === 'markFortresses' && flow.markRegular === undefined && (
        <div className="effects-list">
          <span className="build-picker-hint">
            {flow.markWin === undefined
              ? cardsById.get(flow.cardId)?.effects?.markShufflePerCard
                ? 'Mark a WIN fortress from the active row (1 of 2). You choose afterwards which of the marked cards — neither, one, or both — are shuffled back into their decks.'
                : cardsById.get(flow.cardId)?.effects?.markShuffleOptional
                  ? 'Mark a WIN fortress from the active row (1 of 2). You choose afterwards whether the marked cards are shuffled back into their decks.'
                  : 'Mark a WIN fortress from the active row (1 of 2). The marked cards are then shuffled back into their decks.'
              : 'Now mark a REGULAR fortress from the active row (2 of 2).'}
          </span>
          {(flow.markWin === undefined ? markets.win : markets.regular).cards.map(
            (id, i) => (
              <div className="effect-row" key={`${id}-${i}`}>
                <div className="effect-row-head">
                  <span
                    className="effect-row-name"
                    title={cardsById.get(id)?.description}
                  >
                    {cardsById.get(id)?.name ?? id}
                  </span>
                </div>
                <div className="effect-row-actions">
                  <button
                    type="button"
                    className="build-option effect-use"
                    onClick={() => onMarkPick(id)}
                    title={cardsById.get(id)?.description}
                  >
                    Mark
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {flow.stage === 'reorderMarket' && (
        <div className="effects-confirm">
          <span className="build-picker-hint">
            {cardsById.get(flow.cardId)?.effects?.reorderPeek
              ? `Choose a pile to glance at. You will privately arrange its top ` +
                `${cardsById.get(flow.cardId)?.effects?.reorderGlanceCount ?? 10} ` +
                `cards — and may send any of them to the bottom of the deck` +
                (cardsById.get(flow.cardId)?.effects?.reorderBottomOnly
                  ? '.'
                  : ` or the discard pile.`) +
                (cardsById.get(flow.cardId)?.effects?.reorderCycleCount
                  ? ` You may then cycle up to ` +
                    `${cardsById.get(flow.cardId)?.effects?.reorderCycleCount} ` +
                    `card out of that row for free.`
                  : '')
              : `Choose the row to combine and shuffle. You will privately ` +
                `arrange its top ` +
                `${cardsById.get(flow.cardId)?.effects?.reorderGlanceCount ?? 10} ` +
                `cards.`}
          </span>
          <div className="effects-confirm-actions">
            <button
              type="button"
              className="build-option"
              onClick={() => onStartReorder('REGULAR')}
            >
              Regular
            </button>
            <button
              type="button"
              className="build-option"
              onClick={() => onStartReorder('win')}
            >
              Win
            </button>
          </div>
        </div>
      )}

      {flow.stage === 'reorderDeck' &&
        (() => {
          const peek = !!cardsById.get(flow.cardId)?.effects?.reorderPeek;
          // Curious Climb "54"+: the bottom of the deck is the only other spot.
          const bottomOnly =
            !!cardsById.get(flow.cardId)?.effects?.reorderBottomOnly;
          const cycleCap =
            cardsById.get(flow.cardId)?.effects?.reorderCycleCount ?? 0;
          const cyclePile =
            flow.market === 'win' ? markets.win : markets.regular;
          // Peek printing: position numbers count only the cards staying on
          // top; routed cards show their destination instead.
          let topIndex = 0;
          return (
            <div className="effects-list">
              <span className="build-picker-hint">
                {peek
                  ? 'Arrange the top card first — or send a card to the ' +
                    (bottomOnly
                      ? 'bottom of the deck instead. '
                      : 'bottom of the deck or the discard pile instead. ') +
                    'This private prompt must be completed before another action.'
                  : 'Arrange the top card first. This private prompt must be ' +
                    'completed before another action.'}
              </span>
              {flow.order.map((id, index) => {
                const dest = peek ? flow.route[id] : undefined;
                const chip =
                  dest === 'bottom'
                    ? 'Bottom'
                    : dest === 'discard'
                      ? 'Discard'
                      : `${(topIndex += 1)}`;
                return (
                  <div className="effect-row" key={id}>
                    <div className="effect-row-head">
                      <span
                        className={`effect-chip${dest ? ' is-off' : ''}`}
                      >
                        {chip}
                      </span>
                      <button
                        type="button"
                        className="effect-row-name"
                        onMouseEnter={(e) =>
                          previewRowCard(cardsById.get(id), e.currentTarget)
                        }
                        onMouseLeave={() => endRowPreview(id)}
                        onFocus={(e) =>
                          previewRowCard(cardsById.get(id), e.currentTarget)
                        }
                        onBlur={() => endRowPreview(id)}
                        onClick={(e) =>
                          pinRowCard(cardsById.get(id), e.currentTarget)
                        }
                        title="Show this card"
                      >
                        {cardsById.get(id)?.name ?? id}
                      </button>
                    </div>
                    <div className="effect-row-actions">
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={flow.submitted || index === 0}
                        onClick={() => onMoveReorder(index, -1)}
                        aria-label="Move up"
                      >
                        {'↑'}
                      </button>
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={flow.submitted || index === flow.order.length - 1}
                        onClick={() => onMoveReorder(index, 1)}
                        aria-label="Move down"
                      >
                        {'↓'}
                      </button>
                      {peek && (
                        <>
                          <button
                            type="button"
                            className="build-option effect-use"
                            disabled={flow.submitted}
                            onClick={() => onSetReorderRoute(id, 'bottom')}
                            title={
                              dest === 'bottom'
                                ? 'Return to the top'
                                : 'Place on the bottom of the deck'
                            }
                          >
                            {dest === 'bottom' ? '✓ Btm' : 'Btm'}
                          </button>
                          {!bottomOnly && (
                            <button
                              type="button"
                              className="build-option effect-use"
                              disabled={flow.submitted}
                              onClick={() => onSetReorderRoute(id, 'discard')}
                              title={
                                dest === 'discard'
                                  ? 'Return to the top'
                                  : 'Place on the discard pile'
                              }
                            >
                              {dest === 'discard' ? '✓ Dis' : 'Dis'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {cycleCap > 0 && (
                <>
                  <span className="build-picker-hint">
                    {`You may also cycle up to ${cycleCap} face-up card${
                      cycleCap === 1 ? '' : 's'
                    } out of this row — free of cost and action. The ` +
                      'replacement comes off the deck you just arranged.'}
                  </span>
                  {cyclePile.cards.map((id, index) => {
                    const isLocked = cyclePile.locked.includes(id);
                    const selected = flow.cycle.includes(index);
                    const full = !selected && flow.cycle.length >= cycleCap;
                    return (
                      <div className="effect-row" key={`cycle:${id}`}>
                        <div className="effect-row-head">
                          <button
                            type="button"
                            className="effect-row-name"
                            onMouseEnter={(e) =>
                              previewRowCard(cardsById.get(id), e.currentTarget)
                            }
                            onMouseLeave={() => endRowPreview(id)}
                            onFocus={(e) =>
                              previewRowCard(cardsById.get(id), e.currentTarget)
                            }
                            onBlur={() => endRowPreview(id)}
                            onClick={(e) =>
                              pinRowCard(cardsById.get(id), e.currentTarget)
                            }
                            title="Show this card"
                          >
                            {cardsById.get(id)?.name ?? id}
                          </button>
                          {isLocked && (
                            <span className="effect-chip is-off">Locked</span>
                          )}
                        </div>
                        <div className="effect-row-actions">
                          <button
                            type="button"
                            className={`build-option effect-use${
                              selected ? ' is-selected' : ''
                            }`}
                            disabled={flow.submitted || isLocked || full}
                            onClick={() => onToggleReorderCycle(index)}
                            title={
                              isLocked
                                ? 'Locked cards can’t be cycled.'
                                : full
                                  ? `At most ${cycleCap} card${
                                      cycleCap === 1 ? '' : 's'
                                    } per use.`
                                  : undefined
                            }
                          >
                            {selected ? 'Cycling' : 'Cycle'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              <button
                type="button"
                className="build-option"
                disabled={flow.submitted}
                onClick={onConfirmReorder}
              >
                {flow.submitted
                  ? 'Saving...'
                  : flow.cycle.length > 0
                    ? `Confirm order + cycle ${flow.cycle.length}`
                    : 'Confirm order'}
              </button>
              {rowDetail && (
                <CardDetailPopover
                  card={rowDetail.card}
                  companions={companionCardsOf(rowDetail.card, cardsById)}
                  anchorEl={rowDetail.anchorEl}
                  pinned={rowDetail.pinned}
                  onClose={() => setRowDetail(null)}
                />
              )}
            </div>
          );
        })()}

      {flow.stage === 'market' && (
        <div className="effects-list">
          <span className="build-picker-hint">
            {flow.kind === 'set_fortress_lock'
              ? `Lock a face-up card so it can't be cycled (max 2 locked; ${lockedTotal}/2 in use). Costs 1 action.`
              : flow.kind === 'market_reaction'
                ? `Spend one banked reaction (${context.pendingMarketActions} banked): cycle ONE face-up card for free, or lock / unlock one (max 2 locked; ${lockedTotal}/2 in use). Free of actions.`
                : 'Pick any face-up cards from one market to cycle out — up to the whole row, free of cost and action, as one use.'}
          </span>
          {marketRows.map(({ kind, label, pile }) =>
            pile.cards.map((id, index) => {
              const name = cardsById.get(id)?.name ?? id;
              const isLocked = pile.locked.includes(id);
              return (
                <div className="effect-row" key={`${kind}:${id}`}>
                  <div className="effect-row-head">
                    <span className="effect-row-name">{name}</span>
                    <span className="effect-chip">{label}</span>
                    {isLocked && <span className="effect-chip is-off">Locked</span>}
                  </div>
                  <div className="effect-row-actions">
                    {flow.kind === 'market_reaction' && (
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={isLocked}
                        onClick={() => onFreeCycle(kind, [index])}
                        title={isLocked ? 'Locked cards can’t be cycled.' : undefined}
                      >
                        Cycle
                      </button>
                    )}
                    {flow.kind !== 'free_cycle_market' ? (
                      <button
                        type="button"
                        className="build-option effect-use"
                        disabled={!isLocked && lockedTotal >= 2}
                        onClick={() => onLock(kind, id, !isLocked)}
                        title={
                          !isLocked && lockedTotal >= 2
                            ? 'At most 2 cards may be locked.'
                            : undefined
                        }
                      >
                        {isLocked ? 'Unlock' : 'Lock'}
                      </button>
                    ) : (
                      (() => {
                        const selected =
                          freeSel?.kind === kind && freeSel.indices.has(index);
                        const otherRow =
                          freeSel !== null && freeSel.kind !== kind;
                        // The row is the ceiling; picks are confined to one
                        // row, so its length is the cap (rows are 6 wide, and
                        // shorter when a deck has run dry).
                        const full =
                          !selected &&
                          (freeSel?.indices.size ?? 0) >= pile.cards.length;
                        return (
                          <button
                            type="button"
                            className={`build-option effect-use${
                              selected ? ' is-selected' : ''
                            }`}
                            disabled={isLocked || otherRow || full}
                            onClick={() =>
                              setFreeSel((prev) => {
                                if (!prev || prev.kind !== kind) {
                                  return { kind, indices: new Set([index]) };
                                }
                                const indices = new Set(prev.indices);
                                if (indices.has(index)) indices.delete(index);
                                else indices.add(index);
                                return indices.size === 0
                                  ? null
                                  : { kind, indices };
                              })
                            }
                            title={
                              isLocked
                                ? 'Locked cards can’t be cycled.'
                                : otherRow
                                  ? 'All picks must come from the same market.'
                                  : full
                                    ? 'That’s the whole row already.'
                                    : undefined
                            }
                          >
                            {selected ? 'Selected' : 'Select'}
                          </button>
                        );
                      })()
                    )}
                  </div>
                </div>
              );
            }),
          )}
          {flow.kind === 'free_cycle_market' && (
            <button
              type="button"
              className="build-option"
              disabled={!freeSel}
              onClick={() => {
                if (freeSel) onFreeCycle(freeSel.kind, [...freeSel.indices]);
              }}
            >
              {freeSel
                ? `Cycle ${freeSel.indices.size} card${
                    freeSel.indices.size === 1 ? '' : 's'
                  } for free`
                : 'Select cards to cycle'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A player's setup-phase status — what they're doing in the starting phase. */
function setupStatus(
  p: LobbyPlayer,
  currentPlayerId: string,
  allHomesPlaced: boolean,
): { label: string; cls: string } {
  if (p.productionSet) return { label: 'Ready', cls: 'is-ready' };
  if (p.homePlaced)
    return allHomesPlaced
      ? { label: 'Assigning production', cls: 'is-active' }
      : { label: 'Home base placed', cls: 'is-waiting' };
  if (p.id === currentPlayerId)
    return { label: 'Placing home base', cls: 'is-active' };
  return { label: 'Waiting', cls: 'is-waiting' };
}

/** The current player's turn countdown, shown beside their turn flag when the
 * match has a per-turn limit. `mm:ss`, flagged `is-low` under 10s. Display only —
 * the server is authoritative on when the turn actually auto-ends. */
function TurnTimer({ remainingMs }: { remainingMs: number }) {
  const totalSec = Math.ceil(remainingMs / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return (
    <span
      className={`turn-timer${totalSec <= 10 ? ' is-low' : ''}`}
      title="Time left for this move"
    >
      ⏱ {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

/** Cumulative time a player has spent on turns this match. Shows `h:mm:ss`,
 * dropping leading zero segments (`m:ss` under an hour, bare seconds under a
 * minute). While the clock is armed, includes live elapsed since `turnStartedMs`. */
function CumulativeTurnTimer({
  turnTimeMs,
  turnStartedMs,
  now,
}: {
  turnTimeMs: number;
  turnStartedMs: number;
  now: number;
}) {
  const live =
    turnStartedMs > 0 ? Math.max(0, now - turnStartedMs) : 0;
  const totalSec = Math.floor((turnTimeMs + live) / 1000);
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  let label: string;
  if (hours > 0) label = `${hours}:${pad(mins)}:${pad(secs)}`;
  else if (mins > 0) label = `${mins}:${pad(secs)}`;
  else label = `${secs}`;
  return (
    <span
      className={`turn-timer turn-timer-cumulative${turnStartedMs > 0 ? ' is-live' : ''}`}
      title="Total time on turns this match"
    >
      {label}
    </span>
  );
}

/** Multi-user badge in the left sidebar's expanded header, just left of the
 * collapse arrow: shows how many people are watching the match. Hovering opens
 * a popup listing them by name; clicking pins it (click again — or anywhere
 * outside — to close). The popup renders `position: fixed` from the badge's
 * screen rect so the sidebar's `overflow: hidden` can't clip it. */
function SpectatorBadge({ spectators }: { spectators: SpectatorView[] }) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hovered || pinned;

  // A pinned popup closes on any press outside the badge and the popup.
  useEffect(() => {
    if (!pinned) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popupRef.current?.contains(t)) return;
      setPinned(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [pinned]);

  const rect = open ? btnRef.current?.getBoundingClientRect() : undefined;
  const count = spectators.length;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`sidebar-spectators${pinned ? ' is-active' : ''}`}
        title={`${count} spectator${count === 1 ? '' : 's'}`}
        aria-label={`${count} spectator${count === 1 ? '' : 's'}`}
        aria-expanded={open}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setPinned((p) => !p)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span className="sidebar-spectators-count">{count}</span>
      </button>
      {open &&
        rect &&
        // Portalled to <body> like the detail popovers: the sidebar's
        // backdrop-filter makes it the containing block for fixed-position
        // descendants, so an in-tree popup would be clipped by its overflow.
        createPortal(
          <div
            ref={popupRef}
            className="spectator-popup"
            style={{ left: rect.left, top: rect.bottom + 8 }}
            role="dialog"
            aria-label="Spectators"
          >
            <div className="spectator-popup-title">Spectators</div>
            {count === 0 ? (
              <p className="spectator-popup-empty">No one is watching.</p>
            ) : (
              <ul className="spectator-popup-list">
                {spectators.map((s) => (
                  <li key={s.id}>{s.name}</li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

interface PlayerSidebarProps {
  players: LobbyPlayer[];
  round: number | null;
  turn: number | null;
  /** Current match phase — drives the setup-status display vs. round/turn. */
  phase: string;
  currentPlayerId: string;
  /** Per-turn time limit (ms) from the match config, or undefined when untimed.
   * When set, the current player's row shows a live countdown. */
  timePerTurnMs?: number;
  selfId: string;
  handlers: SelectionHandlers;
  /** Registers each player row's live element (by id) so an action-notice popup
   * can anchor beside it; called with `null` on unmount to drop the entry. */
  registerRow: (id: string, el: HTMLElement | null) => void;
  /** Full move history (oldest first); shown when `historyMode` is on. */
  history: HistoryEntryView[];
  /** Boundaries the match can be rolled back to (oldest first) — drawn in the
   * Turn History between the rows they precede. */
  restorePoints: RestorePointView[];
  /** Whether THIS client may act on them (the match creator, mid-match). When
   * false the points are not drawn at all. */
  canRestore: boolean;
  /** Roll the match back to a restore point. */
  onRestore: (restoreId: number) => void;
  /** When true, the sidebar lists the Turn History instead of the player roster. */
  historyMode: boolean;
  /** Match-pinned fortress catalogue (id + name indexes). */
  cardsById: ReadonlyMap<string, FortressCard>;
  cardsByName: ReadonlyMap<string, FortressCard>;
  /** Toggle between the player roster and the Turn History list. */
  onToggleHistory: () => void;
  /** Pan/zoom the board to a history entry's node (when it has one). */
  onFocusNode: (nodeId: number) => void;
  collapsed: boolean;
  /** PHONES: the open panel is a bottom sheet — the toggle closes it (chevron
   * points down) and a dimmer behind it does too. */
  sheet?: boolean;
  onToggle: () => void;
  /** Everyone watching this match — shown behind the header's multi-user
   * badge (hover/click lists them). */
  spectators: SpectatorView[];
}

/** Short local wall-clock time (HH:MM) shown on each chat message. */
function formatChatTime(at: number): string {
  return new Date(at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Bottom-left chat panel: a compact, collapsible message log + composer, shown
 * through setup / play / post-game. Any occupant (player or spectator) can post;
 * the server snapshots the sender and syncs the last 1000 messages to everyone,
 * so this renders straight off synced state (no optimistic echo). Emoji are
 * welcome — the input takes them like any text. Slides right to clear the left
 * player sidebar when it's expanded. Starts collapsed on join so the board is
 * clear; keyed by room id so a fresh join remounts it closed.
 */
function ChatPanel({
  messages,
  selfId,
  sidebarExpanded,
  barUp,
  open,
  onToggleOpen,
  onSend,
}: {
  messages: ChatMessageView[];
  selfId: string;
  /** The left player sidebar is expanded — shift right to clear it. */
  sidebarExpanded: boolean;
  /** A bottom action bar is up — on phones/tablets it goes full-width over
   * the bottom-left corner, so the collapsed trigger hides under it (the
   * docked corner cluster's chat button opens the panel instead). */
  barUp: boolean;
  /** Lifted open state — also driven by the docked chat button. */
  open: boolean;
  onToggleOpen: () => void;
  onSend: (text: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const [unread, setUnread] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Stay pinned to the newest message unless the reader scrolled up to read
   * back — then don't yank them to the bottom when a message arrives. */
  const pinnedRef = useRef(true);
  /** Highest message id the player has seen while chat was open. */
  const lastSeenIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Seed / advance "seen" while open; flag unread when a foreign message
  // arrives while the panel is collapsed.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    if (open) {
      lastSeenIdRef.current = last.id;
      setUnread(false);
      return;
    }
    if (lastSeenIdRef.current === null) {
      lastSeenIdRef.current = last.id;
      return;
    }
    if (last.id > lastSeenIdRef.current && last.senderId !== selfId) {
      setUnread(true);
    }
  }, [messages, open, selfId]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
    pinnedRef.current = true;
  };

  // Opening marks everything seen via the effect above.
  const toggleOpen = onToggleOpen;

  // Bottom-sheet mode, over everything: phones always; up to tablet width
  // whenever the full-width action bar owns the corner the panel would
  // otherwise open into.
  const isSmallScreen = useMediaQuery(SMALL_SCREEN_QUERY);
  const isMediumScreen = useMediaQuery(MEDIUM_SCREEN_QUERY);
  const sheet = open && (isSmallScreen || (barUp && isMediumScreen));

  return (
    <>
      {/* Dimmer behind the open chat sheet — tapping it closes the sheet. */}
      {sheet && (
        <div
          className="chat-sheet-backdrop"
          onClick={toggleOpen}
          aria-hidden
        />
      )}
      <div
        className={`chat-panel${sidebarExpanded ? ' is-pushed' : ''}${
          open ? '' : ' is-closed'
        }${unread && !open ? ' has-unread' : ''}${
          barUp ? ' is-behind-bar' : ''
        }${sheet ? ' is-sheet' : ''}`}
      >
      <button
        type="button"
        className="chat-header"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-label={
          open
            ? 'Hide chat'
            : unread
              ? 'Show chat (new messages)'
              : 'Show chat'
        }
        title={open ? 'Hide chat' : unread ? 'New messages' : 'Show chat'}
      >
        <span className="chat-title">
          <ChromeGlyph name="chat" size={18} />
          <span className="chat-title-label">Chat</span>
          {unread && !open && (
            <span className="chat-unread" aria-hidden />
          )}
        </span>
        <span className="chat-toggle" aria-hidden>
          <ChromeGlyph
            name={open ? 'chevron-down' : 'chevron-right'}
            size={16}
            strokeWidth={2.4}
          />
        </span>
      </button>
      {open && (
        <>
          <div
            className="chat-messages"
            ref={listRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              pinnedRef.current =
                el.scrollHeight - el.scrollTop - el.clientHeight < 24;
            }}
          >
            {messages.length === 0 ? (
              <p className="chat-empty">No messages yet. Say hi 👋</p>
            ) : (
              messages.map((m) => {
                const color =
                  m.seat === SPECTATOR_SEAT ? '#9a948c' : seatColor(m.seat);
                return (
                  <div
                    className={`chat-msg${
                      m.senderId === selfId ? ' is-mine' : ''
                    }`}
                    key={m.id}
                  >
                    <span className="chat-msg-head">
                      <span className="chat-sender" style={{ color }}>
                        {m.senderName || '—'}
                      </span>
                      <span
                        className="chat-time"
                        title={new Date(m.at).toLocaleString()}
                      >
                        {formatChatTime(m.at)}
                      </span>
                    </span>
                    <span className="chat-text">{m.text}</span>
                  </div>
                );
              })
            )}
          </div>
          <div className="chat-composer">
            <input
              className="chat-input"
              type="text"
              value={draft}
              maxLength={500}
              placeholder="Message…"
              aria-label="Chat message"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Keep game/global key handlers (Escape, etc.) from firing while
                // typing, and send on plain Enter.
                e.stopPropagation();
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              type="button"
              className="chat-send"
              onClick={send}
              disabled={draft.trim().length === 0}
              aria-label="Send message"
            >
              <ChromeGlyph name="send" size={17} />
            </button>
          </div>
        </>
      )}
      </div>
    </>
  );
}

/** Phone-layout breakpoint — keep in sync with the styles.css media queries. */
const SMALL_SCREEN_QUERY = '(max-width: 640px)';

/** Up to here the action bar goes full-width with the corner buttons docked
 * above it (the desktop centred bar is too cramped once the sidebars' reserved
 * side space eats the width) — keep in sync with styles.css. */
const MEDIUM_SCREEN_QUERY = '(max-width: 1024px)';

/** Callback ref for the bottom `.turn-action-stack`: publishes its live height
 * as `--dock-h` on <html> while it's mounted, so on phones/tablets (where the
 * stack docks full-width) the side panels can end above it and scroll instead
 * of running underneath (styles.css). */
function useDockHeight() {
  const observer = useRef<ResizeObserver | null>(null);
  return useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    const root = document.documentElement;
    if (!el) {
      root.style.removeProperty('--dock-h');
      return;
    }
    const ro = new ResizeObserver(() => {
      root.style.setProperty(
        '--dock-h',
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    });
    ro.observe(el);
    observer.current = ro;
  }, []);
}

/** Live media-query match. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * Phone-only: a touch/pen flick that STARTS on the bare left/right screen edge
 * opens that side's sidebar (closing is covered by the sidebars' own swipe
 * handler below — an open sidebar IS that side of the screen). Window-level so
 * the gesture also works over the board canvas; the narrow start zone keeps
 * ordinary board pans from triggering it.
 */
function useEdgeSwipe(
  enabled: boolean,
  onOpenLeft: () => void,
  onOpenRight: () => void,
) {
  const onOpenLeftRef = useRef(onOpenLeft);
  const onOpenRightRef = useRef(onOpenRight);
  onOpenLeftRef.current = onOpenLeft;
  onOpenRightRef.current = onOpenRight;

  useEffect(() => {
    if (!enabled) return;

    const EDGE = 40;
    let startX = 0;
    let startY = 0;
    let pointerId: number | null = null;
    let fromEdge: 'left' | 'right' | null = null;

    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      fromEdge =
        e.clientX <= EDGE
          ? 'left'
          : e.clientX >= window.innerWidth - EDGE
            ? 'right'
            : null;
      if (!fromEdge) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
    };

    const onUp = (e: PointerEvent) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const edge = fromEdge;
      pointerId = null;
      fromEdge = null;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Same flick threshold as useSidebarSwipe.
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
      if (edge === 'left' && dx > 0) onOpenLeftRef.current();
      else if (edge === 'right' && dx < 0) onOpenRightRef.current();
    };

    const onCancel = (e: PointerEvent) => {
      if (pointerId !== null && e.pointerId === pointerId) {
        pointerId = null;
        fromEdge = null;
      }
    };

    window.addEventListener('pointerdown', onDown);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [enabled]);
}

/**
 * Touch/pen swipe on a sidebar toggles it open/closed on mobile. Horizontal
 * intent must dominate vertical scrolling so the roster/market lists still
 * scroll normally. Left: swipe right to open, left to close. Right: opposite.
 *
 * As a phone bottom sheet (`sheet`), a downward flick that STARTS on the
 * header strip also closes it. Only the header pulls: a drag that begins on
 * the body below stays a native scroll of the list, so the roster/market still
 * scroll up and down (the header's `touch-action: none` keeps the browser from
 * claiming the pull as a page pan, which would cancel our pointer).
 */
function useSidebarSwipe(
  side: 'left' | 'right',
  collapsed: boolean,
  onToggle: () => void,
  sheet = false,
) {
  const ref = useRef<HTMLElement | null>(null);
  const collapsedRef = useRef(collapsed);
  const onToggleRef = useRef(onToggle);
  const sheetRef = useRef(sheet);
  collapsedRef.current = collapsed;
  onToggleRef.current = onToggle;
  sheetRef.current = sheet;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let pointerId: number | null = null;
    /** The touch began on the open sheet's header strip (may pull it closed). */
    let fromHead = false;

    const onDown = (e: PointerEvent) => {
      // Mouse keeps the click-to-toggle chrome; swipes are for touch/pen.
      if (e.pointerType === 'mouse') return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      const head = el.querySelector(':scope > .sidebar-header');
      fromHead =
        sheetRef.current &&
        !collapsedRef.current &&
        !!head &&
        startY <= head.getBoundingClientRect().bottom;
    };

    const onUp = (e: PointerEvent) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      pointerId = null;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Sheet: a clear downward flick from the header pulls it closed.
      if (fromHead && dy >= 48 && dy > Math.abs(dx) * 1.35) {
        onToggleRef.current();
        return;
      }
      // Require a clear horizontal flick so vertical list scrolls aren't
      // mistaken for a collapse gesture.
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
      const isCollapsed = collapsedRef.current;
      if (side === 'left') {
        if ((isCollapsed && dx > 0) || (!isCollapsed && dx < 0)) {
          onToggleRef.current();
        }
      } else if ((isCollapsed && dx < 0) || (!isCollapsed && dx > 0)) {
        onToggleRef.current();
      }
    };

    const onCancel = (e: PointerEvent) => {
      if (pointerId !== null && e.pointerId === pointerId) pointerId = null;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
    };
  }, [side]);

  return ref;
}

/** Left sidebar: round/turn (or setup) header + every player. Shown through both
 * the starting phase and live play. Collapsible to a thin edge handle. */
function PlayerSidebar({
  players,
  round,
  turn,
  phase,
  currentPlayerId,
  timePerTurnMs,
  selfId,
  handlers,
  registerRow,
  history,
  historyMode,
  restorePoints,
  canRestore,
  onRestore,
  cardsById,
  cardsByName,
  onToggleHistory,
  onFocusNode,
  collapsed,
  sheet,
  onToggle,
  spectators,
}: PlayerSidebarProps) {
  const isSetup = phase === 'setup';
  const allHomesPlaced = players.every((p) => p.homePlaced);
  // Tick every second during play so cumulative turn timers (and timed
  // countdowns) update live. Setup ticks only for its timed countdowns.
  const timed =
    !!timePerTurnMs && (phase === 'setup' || phase === 'playing');
  const clockLive = phase === 'playing' || phase === 'ended' || timed;
  // Server-clock time: the turn stamps come from the server's clock.
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!clockLive) return;
    const id = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(id);
  }, [clockLive]);
  const swipeRef = useSidebarSwipe('left', collapsed, onToggle, sheet);
  return (
    <aside
      ref={swipeRef}
      className={`sidebar sidebar-left${collapsed ? ' is-collapsed' : ''}`}
    >
      <div className="sidebar-header">
        <button
          type="button"
          className={`sidebar-action${historyMode ? ' is-active' : ''}`}
          onClick={onToggleHistory}
          title={
            historyMode
              ? 'Show the player roster'
              : 'Show every action taken this match'
          }
          aria-pressed={historyMode}
        >
          <ChromeGlyph name={historyMode ? 'players' : 'clock'} size={15} />
          <span className="sidebar-action-label">
            {historyMode ? 'All Players' : 'Turn History'}
          </span>
        </button>
        <div className="sidebar-header-right">
          {!collapsed && <SpectatorBadge spectators={spectators} />}
          <button
            type="button"
            className="sidebar-toggle"
            onClick={onToggle}
            title={collapsed ? 'Show players' : 'Hide players'}
            aria-label={
              collapsed ? 'Expand players panel' : 'Collapse players panel'
            }
          >
            <ChromeGlyph
              name={
                collapsed ? 'chevron-right' : sheet ? 'chevron-down' : 'chevron-left'
              }
              size={18}
              strokeWidth={2.2}
            />
          </button>
        </div>
      </div>
      {collapsed ? (
        <div className="sidebar-rail">
          {!isSetup && (
            <div
              className="rail-round"
              title={`Round ${round ?? '—'}, Turn ${turn ?? '—'}`}
            >
              <span>R{round ?? '—'}</span>
              <span>T{turn ?? '—'}</span>
            </div>
          )}
          {players.map((p) => (
            <div
              key={p.id}
              ref={(el) => registerRow(p.id, el)}
              className="rail-player"
              data-cursor-target={`player:${p.id}`}
              title={p.name}
              aria-label={`${p.name}${p.id === selfId ? ' (you)' : ''}`}
            >
              <PlayerIcon
                name={p.name}
                seat={p.seat}
                connected={p.connected}
                size={34}
                current={p.id === currentPlayerId}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="sidebar-body">
        {historyMode ? (
          <TurnHistoryList
            history={history}
            onFocusNode={onFocusNode}
            handlers={handlers}
            cardsByName={cardsByName}
            restorePoints={canRestore ? restorePoints : []}
            onRestore={onRestore}
          />
        ) : (
          <>
            <div className="round-header">
              {isSetup ? (
                <span className="round-num">Starting phase</span>
              ) : (
                <>
                  <span className="round-num">Round {round ?? '—'}</span>
                  <span className="turn-num">Turn {turn ?? '—'}</span>
                </>
              )}
            </div>
            <ul className="player-list">
              {players.map((p) => {
                const isCurrent = p.id === currentPlayerId;
                const status = isSetup
                  ? setupStatus(p, currentPlayerId, allHomesPlaced)
                  : null;
                // Every running clock is a move the match is waiting on from
                // this player (turn, setup step, round-start cycle, window
                // answer), out of time at its start + the limit.
                const countdown =
                  timed && p.turnStartedMs > 0 ? (
                    <TurnTimer
                      remainingMs={Math.max(
                        0,
                        timePerTurnMs! - (now - p.turnStartedMs),
                      )}
                    />
                  ) : null;
                return (
                  <li
                    key={p.id}
                    ref={(el) => registerRow(p.id, el)}
                    className={`player-row${isCurrent ? ' is-current' : ''}`}
                    data-cursor-target={`player:${p.id}`}
                  >
                    <div className="player-top">
                      <PlayerIcon
                        name={p.name}
                        seat={p.seat}
                        connected={p.connected}
                        size={24}
                      />
                      <div className="player-top-main">
                        <span className="player-name">
                          {p.name}
                          {p.id === selfId ? ' (you)' : ''}
                        </span>
                        {p.passed && !status && (
                          <span className="passed-flag">passed</span>
                        )}
                      </div>
                      {status ? (
                        <div className="player-top-right">
                          {countdown}
                          <span className={`setup-status ${status.cls}`}>
                            {status.label}
                          </span>
                        </div>
                      ) : (
                        (phase === 'playing' || phase === 'ended') && (
                          <div className="player-top-right">
                            {countdown}
                            <CumulativeTurnTimer
                              turnTimeMs={p.turnTimeMs}
                              turnStartedMs={p.turnStartedMs}
                              now={now}
                            />
                            {isCurrent && phase === 'playing' && (
                              <span className="turn-flag">YOUR TURN</span>
                            )}
                          </div>
                        )
                      )}
                    </div>
                    <div className="player-stats">
                      <ResourceRow
                        label="Bank"
                        resources={p.resources}
                        oneTime
                        highlight
                      />
                      <ResourceRow
                        label="Production"
                        resources={p.production}
                      />
                    </div>
                    <FortressCards
                      faceUp={p.fortressCards}
                      faceDown={p.hiddenCards}
                      handlers={handlers}
                      playerId={p.id}
                      cardsById={cardsById}
                    />
                  </li>
                );
              })}
              {players.length === 0 && (
                <li className="player-row empty">No players</li>
              )}
            </ul>
          </>
        )}
        </div>
      )}
    </aside>
  );
}

/** Whether a queued move's payload is the Pass pseudo-move. */
function isQueuedPass(payload: string): boolean {
  try {
    return (JSON.parse(payload) as { type?: unknown }).type === 'pass';
  } catch {
    return false;
  }
}

const PASS_QUEUED_HERE =
  'A Pass is queued for this turn and must stand alone — cancel it below to queue moves here.';

/** The one empty upcoming-turn slot offered for queue-planning: the earliest
 * without moves — later empty slots stay hidden until every turn before them
 * is queued. A queued Pass is alone in its turn slot and nothing would run
 * after it, so once one is queued no slot is offered. */
function offeredQueueSlot(
  queuedMoves: LobbyPlayer['queuedMoves'],
): number | undefined {
  if (queuedMoves.some((m) => isQueuedPass(m.payload))) return undefined;
  return Array.from({ length: MAX_QUEUE_TURNS }, (_, i) => i + 1).find(
    (slot) => !queuedMoves.some((m) => m.slot === slot),
  );
}

/** One upcoming turn's queued moves. Clicking a row SELECTS it (revealing its
 * board ghost / market badge), clicking again deselects; ✕ cancels it. Shared
 * by the sidebar queue panel and the planning action bar. */
function QueuedMoveList({
  slot,
  moves,
  queuedSel,
  onSelectQueued,
  onUnqueueMove,
}: {
  slot: number;
  moves: LobbyPlayer['queuedMoves'];
  queuedSel: { slot: number; index: number } | null;
  onSelectQueued: (sel: { slot: number; index: number } | null) => void;
  onUnqueueMove: (slot: number, index: number) => void;
}) {
  return (
    <ul className="queue-move-list">
      {moves.map((move, index) => {
        const isSel = queuedSel?.slot === slot && queuedSel.index === index;
        return (
          <li
            key={`${slot}:${index}`}
            className={`queue-move is-selectable${isSel ? ' is-selected' : ''}`}
            onClick={() => onSelectQueued(isSel ? null : { slot, index })}
            title={
              isSel
                ? 'Hide this move’s preview'
                : 'Preview this move on the board / market'
            }
          >
            <span className="queue-move-label">{move.label}</span>
            <button
              type="button"
              className="queue-move-remove"
              onClick={(e) => {
                e.stopPropagation();
                onUnqueueMove(slot, index);
              }}
              title="Cancel this queued move"
              aria-label={`Cancel queued move: ${move.label}`}
            >
              ✕
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Everything you have queued, at the foot of the bottom action bar (opened
 * off-turn by the Queue turn button, so planning happens there): your queued
 * moves grouped by upcoming turn — the turn being planned is outlined — then
 * the round-start cycle, then the cards' standing answers (Statue auto-pay,
 * Swole Turtle deposit, BAM Exploder auto-destroy, Telescopic Arm capture).
 * Clicking a queued move SELECTS it, revealing its preview markers (board
 * ghost / market badge); clicking again deselects. Only you (and the server)
 * can see any of this; opponents' clients never receive the queue. Renders
 * nothing when there's nothing to show. */
function QueuePanel({
  self,
  planSlot,
  onUnqueueMove,
  queuedSel,
  onSelectQueued,
  cardsById,
  expectedFirst,
  rsQueueActive,
  rsMaxPerRow,
  onToggleRsQueue,
  onClearQueuedRoundStart,
  statue,
  onVigorAutoPay,
  turtle,
  onVigorDepositFloor,
  exploder,
  onArtifactAutoDestroy,
  captureBot,
  onCaptureSettings,
}: {
  self: LobbyPlayer;
  planSlot: number | null;
  onUnqueueMove: (slot: number, index: number) => void;
  queuedSel: { slot: number; index: number } | null;
  onSelectQueued: (sel: { slot: number; index: number } | null) => void;
  cardsById: ReadonlyMap<string, FortressCard>;
  expectedFirst: boolean;
  rsQueueActive: boolean;
  rsMaxPerRow: number;
  onToggleRsQueue: () => void;
  onClearQueuedRoundStart: () => void;
  statue: { cardId: string; name: string; stored: number; target: number } | null;
  onVigorAutoPay: (enabled: boolean, floor: number) => void;
  turtle: { cardId: string; name: string; tokens: number; collected: boolean } | null;
  onVigorDepositFloor: (floor: number) => void;
  exploder: { cardId: string; name: string; tokens: number; target: number } | null;
  onArtifactAutoDestroy: (enabled: boolean) => void;
  captureBot: { cardId: string; name: string; payment: number } | null;
  onCaptureSettings: (enabled: boolean, resource: number) => void;
}) {
  const slots = Array.from({ length: MAX_QUEUE_TURNS }, (_, i) => i + 1);
  const movesBySlot = new Map(
    slots.map((slot) => [
      slot,
      self.queuedMoves.filter((m) => m.slot === slot),
    ]),
  );
  const slotName = (slot: number) =>
    slot === 1 ? 'Next turn' : `In ${slot} turns`;
  const showRoundStart = expectedFirst || self.queuedRoundStartCycle !== '';
  if (
    self.queuedMoves.length === 0 &&
    !showRoundStart &&
    !statue &&
    !turtle &&
    !exploder &&
    !captureBot
  ) {
    return null;
  }
  return (
    <div className="turn-action-queued" aria-label="Queued moves">
      {slots.map((slot) => {
        const moves = movesBySlot.get(slot) ?? [];
        if (moves.length === 0) return null;
        return (
          <div
            key={slot}
            className={`queue-slot${planSlot === slot ? ' is-active' : ''}`}
          >
            <div className="queue-slot-head">
              <span className="queue-slot-name">{slotName(slot)}</span>
            </div>
            <QueuedMoveList
              slot={slot}
              moves={moves}
              queuedSel={queuedSel}
              onSelectQueued={onSelectQueued}
              onUnqueueMove={onUnqueueMove}
            />
          </div>
        );
      })}
      {/* The round-start cycle slot: offered only after taking the First-Turn
          marker by passing first this round (you're EXPECTED to open the next
          round) — not while merely leading the current round. The queued choice
          executes at round start only if you actually go first. */}
      {showRoundStart && (
        <div className={`queue-slot${rsQueueActive ? ' is-active' : ''}`}>
          <div className="queue-slot-head">
            <span className="queue-slot-name">Round start</span>
            {self.queuedRoundStartCycle === '' && (
              <button
                type="button"
                className={`queue-slot-plan${rsQueueActive ? ' is-active' : ''}`}
                onClick={onToggleRsQueue}
                title={
                  rsQueueActive
                    ? 'Close the round-start picker.'
                    : `You hold the First-Turn marker: pre-pick up to ${rsMaxPerRow} card${
                        rsMaxPerRow === 1 ? '' : 's'
                      } per market to cycle out (free) when the next round starts — or queue "cycle nothing". Skipped if someone else ends up going first.`
                }
              >
                {rsQueueActive ? 'Done' : '+ Queue'}
              </button>
            )}
          </div>
          {self.queuedRoundStartCycle !== '' && (
            <ul className="queue-move-list">
              <li className="queue-move">
                <span className="queue-move-label">
                  {roundStartCycleLabel(self.queuedRoundStartCycle, cardsById)}
                </span>
                <button
                  type="button"
                  className="queue-move-remove"
                  onClick={onClearQueuedRoundStart}
                  title="Cancel this queued round-start cycle"
                  aria-label="Cancel queued round-start cycle"
                >
                  ✕
                </button>
              </li>
            </ul>
          )}
        </div>
      )}
      {/* Zapped Bapped Statue auto-pay — QUEUED volleys only. When actions
          fire from players' queues, the real-time pay window would flash by
          unanswerable, so while this is on the server matches those penalties
          automatically, always keeping at least the chosen Energy reserve in
          the bank. Live opponents' actions still show the pay banner instead. */}
      {statue && (
        <div className="queue-slot vigor-autopay">
          <div className="queue-slot-head">
            <span className="queue-slot-name">
              Statue auto-pay
              <span className="vigor-autopay-progress">
                {statue.stored}/{Number.isFinite(statue.target) ? statue.target : '—'}{' '}
                stored
              </span>
            </span>
            <SwitchToggle
              className="vigor-autopay-toggle"
              label="Statue auto-pay"
              checked={self.vigorAutoPay}
              onChange={(on) => onVigorAutoPay(on, self.vigorAutoPayFloor)}
              title={
                self.vigorAutoPay
                  ? 'Auto-pay is on: while queued actions fire, opponents’ energy penalties are matched onto the Statue automatically, keeping at least the reserve below in your bank. Live actions still show the pay banner.'
                  : 'Turn on to match opponents’ energy penalties onto the Statue automatically while queued actions fire (the pay banner can’t be answered mid-volley). Live actions still show the banner.'
              }
            />
          </div>
          {self.vigorAutoPay && (
            <div className="vigor-autopay-row">
              <span>keep</span>
              <input
                type="number"
                min={0}
                max={255}
                value={self.vigorAutoPayFloor}
                onChange={(e) => {
                  const raw = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(raw)) return;
                  onVigorAutoPay(true, Math.max(0, Math.min(255, raw)));
                }}
                aria-label="Keep at least this much Energy when auto-paying"
              />
              <span>
                <ResourceIcon kind="vigor" size={12} /> in reserve
              </span>
            </div>
          )}
        </div>
      )}
      {/* Swole Turtle round-end deposit — "pay as many energy resources onto
          this card as you want". The choice is a standing reserve: at every
          round end, banked Energy above it moves onto the card as tokens
          (collect them ×2 with the card's action). Default 0 = deposit all.
          Collecting ×2 forfeits THIS round's deposit ("If you do, you cannot
          deposit energy at the end of that round") — the reserve input gives
          way to a note until the card re-arms at round start. */}
      {turtle && (
        <div className="queue-slot vigor-autopay">
          <div className="queue-slot-head">
            <span className="queue-slot-name">
              {turtle.name} deposit
              <span className="vigor-autopay-progress">
                {turtle.tokens} stored
              </span>
            </span>
          </div>
          {turtle.collected ? (
            <div
              className="vigor-autopay-row"
              title="You took back the card's energy ×2 this round, so no energy can be deposited at this round's end. The deposit resumes next round."
            >
              <span>collected ×2 — no deposit this round</span>
            </div>
          ) : (
            <div
              className="vigor-autopay-row"
              title="At every round end, Energy above this reserve is deposited onto the card. Take it all back ×2 with the card's Collect action — doing so skips that round's deposit."
            >
              <span>keep</span>
              <input
                type="number"
                min={0}
                max={255}
                value={self.vigorDepositFloor}
                onChange={(e) => {
                  const raw = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(raw)) return;
                  onVigorDepositFloor(Math.max(0, Math.min(255, raw)));
                }}
                aria-label="Keep this much Energy at round end; the rest is deposited onto the card"
              />
              <span>
                <ResourceIcon kind="vigor" size={12} /> at round end
              </span>
            </div>
          )}
        </div>
      )}
      {/* BAM Exploder round-end auto-destroy — the standing answer to the
          card's "you may destroy it": while on (the default), every fortress
          within your attack range of your Artifacts of Courage — from
          catalogue "12" your own included, each Artifact itself among them —
          is destroyed at round end, +1 token on the Exploder each. */}
      {exploder && (
        <div className="queue-slot vigor-autopay">
          <div className="queue-slot-head">
            <span className="queue-slot-name">
              Auto-destroy
              <span className="vigor-autopay-progress">
                {exploder.tokens}/
                {Number.isFinite(exploder.target) ? exploder.target : '—'}{' '}
                tokens
              </span>
            </span>
            <SwitchToggle
              className="vigor-autopay-toggle"
              label="Round-end auto-destroy"
              checked={self.artifactAutoDestroy}
              onChange={onArtifactAutoDestroy}
              title="Automatically destroy all fortresses within your attack range to your Artifact of Courages at the end of the round"
            />
          </div>
        </div>
      )}
      {/* Telescopic Arm capture — the standing answer to the card's
          "you may take control of the building instead of destroying it":
          while on, your successful Barracks attacks capture the building,
          paying the victim the card's price (2 on "54"+) in the picked kind
          (an unbanked kind falls back to your largest stash). Capturing skips
          the plunder a destruction would award. */}
      {captureBot && (
        <div className="queue-slot vigor-autopay">
          <div className="queue-slot-head">
            <span className="queue-slot-name">Capture</span>
            <SwitchToggle
              className="vigor-autopay-toggle"
              label="Capture instead of destroying"
              checked={self.captureAuto}
              onChange={(on) => onCaptureSettings(on, self.captureGiveResource)}
              title="Take control of buildings your successful Barracks attacks would destroy, instead of razing them (skips the plunder)"
            />
          </div>
          {self.captureAuto && (
            <div
              className="vigor-autopay-row"
              title={`Which resource each capture hands the victim — ${captureBot.payment} of it (your largest stash if you don't bank this kind; can't pay, no capture)`}
            >
              <span>give {captureBot.payment}</span>
              <select
                aria-label="Resource paid to the victim per capture"
                value={self.captureGiveResource}
                onChange={(e) =>
                  onCaptureSettings(true, Number(e.target.value) || 0)
                }
              >
                {RESOURCE_KINDS.map((kind, i) => (
                  <option key={kind} value={i}>
                    {RESOURCE_LABELS[kind]}
                  </option>
                ))}
              </select>
              <span>per capture</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The Turn History view: every action taken in the match, newest first. Each
 * row is a seat-coloured action card (the same title + labelled groups the
 * action-notice popup shows), tagged with the round/turn it happened on. Backed
 * by the synced `GameState.moveHistory`, so it's identical for every player and
 * survives reconnects.
 *
 * For the match creator it is also the rollback control: the turn and round
 * boundaries still inside the restore window are drawn between the rows they
 * precede, and everything ABOVE such a divider is what restoring there would
 * undo. */
function TurnHistoryList({
  history,
  onFocusNode,
  handlers,
  cardsByName,
  restorePoints,
  onRestore,
}: {
  history: HistoryEntryView[];
  onFocusNode: (nodeId: number) => void;
  handlers: SelectionHandlers;
  cardsByName: ReadonlyMap<string, FortressCard>;
  /** Boundaries the match can be rolled back to — EMPTY for everyone but the
   * creator, so the rest of the table never sees a control they can't use. */
  restorePoints: RestorePointView[];
  onRestore: (restoreId: number) => void;
}) {
  // Which point is one click from being used. A rollback discards other
  // people's turns, so it asks first — inline, in the divider's own slot,
  // rather than as a modal over the board it is about to change.
  const [confirming, setConfirming] = useState<number | null>(null);
  if (history.length === 0) {
    return (
      <div className="turn-history">
        <div className="round-header">
          <span className="round-num">Turn History</span>
        </div>
        <p className="turn-history-empty">No actions taken yet.</p>
      </div>
    );
  }
  // Stored oldest-first; show newest at the top.
  const ordered = [...history].reverse();
  // A point sits between the entries at or below its `afterHistoryId` and
  // those above it — so in this newest-first list it is drawn immediately
  // before the first entry that did NOT happen after it.
  const points = [...restorePoints].sort(
    (a, b) => b.afterHistoryId - a.afterHistoryId,
  );
  const rows: (
    | { row: 'entry'; entry: HistoryEntryView }
    | { row: 'point'; point: RestorePointView }
  )[] = [];
  let next = 0;
  for (const entry of ordered) {
    while (next < points.length && points[next]!.afterHistoryId >= entry.id) {
      rows.push({ row: 'point', point: points[next++]! });
    }
    rows.push({ row: 'entry', entry });
  }
  while (next < points.length) {
    rows.push({ row: 'point', point: points[next++]! });
  }

  return (
    <div className="turn-history">
      <div className="round-header">
        <span className="round-num">Turn History</span>
        <span className="turn-num">{history.length} actions</span>
      </div>
      <ul className="history-list">
        {rows.map((item) => {
          if (item.row === 'point') {
            const { point } = item;
            const label =
              point.kind === 'round'
                ? `Start of Round ${point.round}`
                : `Round ${point.round} · Turn ${point.turn}${
                    point.playerName ? ` — ${point.playerName}` : ''
                  }`;
            const undoes = history.filter(
              (e) => e.id > point.afterHistoryId,
            ).length;
            if (confirming === point.id) {
              return (
                <li className="restore-point is-confirming" key={`p${point.id}`}>
                  <span className="restore-confirm-text">
                    Restore to {label}?{' '}
                    {undoes > 0
                      ? `This undoes ${undoes} action${undoes === 1 ? '' : 's'} for everyone.`
                      : 'Nothing has happened since.'}
                  </span>
                  <span className="restore-confirm-actions">
                    <button
                      type="button"
                      className="restore-btn is-danger"
                      onClick={() => {
                        setConfirming(null);
                        onRestore(point.id);
                      }}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="restore-btn"
                      onClick={() => setConfirming(null)}
                    >
                      Cancel
                    </button>
                  </span>
                </li>
              );
            }
            return (
              <li className="restore-point" key={`p${point.id}`}>
                <button
                  type="button"
                  className="restore-btn"
                  title={`Roll the whole match back to ${label} — every player's board, cards and resources go back to that moment.`}
                  onClick={() => setConfirming(point.id)}
                >
                  ⟲ Restore to {label}
                </button>
              </li>
            );
          }
          const e = item.entry;
          const focusable = e.focusNodeId >= 0;
          return (
            <li
              className={`history-row${focusable ? ' is-focusable' : ''}`}
              key={e.id}
              data-cursor-target={`history:${e.id}`}
              onClick={focusable ? () => onFocusNode(e.focusNodeId) : undefined}
              title={focusable ? 'Show this on the board' : undefined}
            >
              <div className="history-row-head">
                <span
                  className="seat-dot"
                  style={{ background: seatColor(e.seat) }}
                  aria-hidden
                />
                <span className="history-actor">{e.playerName || '—'}</span>
                <span
                  className="history-when"
                  title={`Round ${e.round}, turn ${e.turn}`}
                >
                  R{e.round}·T{e.turn}
                </span>
              </div>
              <div className="history-title">{e.title}</div>
              {e.groups.map((g, i) => (
                <div className="history-group" key={`${g.label}-${i}`}>
                  <span className="history-group-label">{g.label}</span>
                  <div className="history-group-items">
                    {g.items.length === 0 ? (
                      <span className="history-group-item">—</span>
                    ) : (
                      g.items.map((item, j) => {
                        // Resolve card-name items (e.g. a Cycle's removed/added
                        // cards) to a hover/click detail popover; other items
                        // (building names, etc.) stay as plain text. One per line.
                        const card = cardsByName.get(item);
                        const target: SelectionTarget | null = card
                          ? { kind: 'historyCard', card }
                          : null;
                        return (
                          <span className="history-group-item" key={j}>
                            {target ? (
                              <button
                                type="button"
                                className="history-card-link"
                                onMouseEnter={(ev) =>
                                  handlers.onPreview(target, ev.currentTarget)
                                }
                                onMouseLeave={() =>
                                  handlers.onPreviewEnd(target)
                                }
                                onClick={(ev) => {
                                  // Keep the row's focus-node click from firing.
                                  ev.stopPropagation();
                                  handlers.onPin(target, ev.currentTarget);
                                }}
                              >
                                {item}
                              </button>
                            ) : (
                              item
                            )}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A player's Fortress cards: implemented (face-up) as a horizontal row of
 * pattern thumbnails, plus the owner's face-down hand. Each thumbnail is a
 * hover/click tile that opens its detail popover (same behaviour as the market
 * rows) — which highlights it on the board and offers "View on board". Renders
 * nothing when the player holds no cards. */
function FortressCards({
  faceUp,
  faceDown,
  handlers,
  playerId,
  cardsById,
}: {
  faceUp: FortressCardView[];
  faceDown: string[];
  handlers: SelectionHandlers;
  playerId: string;
  cardsById: ReadonlyMap<string, FortressCard>;
}) {
  if (faceUp.length === 0 && faceDown.length === 0) return null;

  // Resolve to catalogue cards, keeping the original index for a stable
  // per-instance key (a player can hold two copies of the same card).
  const resolved = faceUp.map((fc, i) => ({
    fc,
    i,
    card: cardsById.get(fc.cardId),
  }));

  return (
    <div className="player-cards">
      {resolved.length > 0 && (
        <ul className="fortress-tile-list">
          {resolved.map(({ fc, i, card }) => {
            const name = `${card?.name ?? fc.cardId}${
              fc.copied ? ' (copied by CurdleCat)' : ''
            }`;
            const target: SelectionTarget | null = card
              ? {
                  kind: 'fortressCard',
                  card,
                  nodeIds: fc.nodeIds,
                  tokens: fc.tokens,
                  stored: fc.stored,
                  disabled: fc.disabled,
                  choice: fc.choice,
                  instanceKey: `${playerId}:${i}`,
                }
              : null;
            return (
              <li
                key={`${fc.cardId}-${i}`}
                className={`fortress-tile${fc.disabled ? ' is-disabled' : ''}${
                  card?.kind === 'win' ? ' is-win' : ''
                }${fc.copied ? ' is-copied' : ''}`}
                title={name}
                onMouseEnter={
                  target
                    ? (e) => handlers.onPreview(target, e.currentTarget)
                    : undefined
                }
                onMouseLeave={
                  target ? () => handlers.onPreviewEnd(target) : undefined
                }
                onClick={
                  target
                    ? (e) => {
                        e.stopPropagation();
                        handlers.onPin(target, e.currentTarget);
                      }
                    : undefined
                }
              >
                {card && (
                  <span
                    className={`fortress-tile-pattern${
                      card.kind === 'win' ? ' is-win' : ''
                    }`}
                    aria-hidden
                  >
                    <PatternDiagram
                      pattern={card.pattern}
                      maxW={32}
                      maxH={28}
                      pad={3}
                      iconScale={0.8}
                      dark={card.kind === 'win'}
                    />
                  </span>
                )}
                {card && fc.stored > 0 && (
                  card.effects?.tokenAdjustFromStored ? (
                    <span
                      className="fortress-tile-stored"
                      title={`${fc.stored} token change${
                        fc.stored === 1 ? '' : 's'
                      } banked on this fortress`}
                    >
                      ±●{fc.stored}
                    </span>
                  ) : (
                    <span
                      className="fortress-tile-stored"
                      title={`${fc.stored} resource${
                        fc.stored === 1 ? '' : 's'
                      } stored on this fortress`}
                    >
                      <ResourceIcon kind="vigor" size={11} />
                      {fc.stored}
                    </span>
                  )
                )}
                {fc.copied && (
                  <span
                    className="fortress-tile-copied"
                    title="A CurdleCat copy — the effect without the board fortress"
                  >
                    ⧉
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {faceDown.length > 0 && (
        <div className="fc-section">
          <span className="fc-label">Hand</span>
          <span className="fc-chips">
            {faceDown.map((id, i) => {
              // Face-down cards sync only to their owner while the match is
              // live; at game end the server reveals every hand to everyone.
              // Each chip opens the plain card-face popover (`historyCard` —
              // hand cards aren't in a market row, so never pruned).
              const card = cardsById.get(id);
              const name = card?.name ?? id;
              const target: SelectionTarget | null = card
                ? { kind: 'historyCard', card }
                : null;
              return (
                <span
                  key={`${id}-${i}`}
                  className="fc-chip is-hidden"
                  title={name}
                  onMouseEnter={
                    target
                      ? (e) => handlers.onPreview(target, e.currentTarget)
                      : undefined
                  }
                  onMouseLeave={
                    target ? () => handlers.onPreviewEnd(target) : undefined
                  }
                  onClick={
                    target
                      ? (e) => {
                          e.stopPropagation();
                          handlers.onPin(target, e.currentTarget);
                        }
                      : undefined
                  }
                >
                  {name}
                </span>
              );
            })}
          </span>
        </div>
      )}
    </div>
  );
}

/** Market marker for the SELECTED queued move: badge the card a queued
 * Implement will buy (`cardId`) or the row slots a queued Cycle will discard
 * (`indices`), within row `row`. */
interface QueuedMarketMark {
  row: FortressKind;
  mode: 'implement' | 'cycle';
  cardId?: string;
  indices?: ReadonlySet<number>;
}

/** Everything the market sidebar needs to drive the round-start cycle picker —
 * the first player's free "cycle up to `maxPerRow` cards per market" step,
 * either answering the live window ('live') or pre-queueing next round's
 * choice ('queue'). */
interface RoundStartCycleControls {
  mode: 'live' | 'queue';
  /** The picks per market row (card ids); empty = "keep that row". */
  sel: { regular: readonly string[]; win: readonly string[] };
  /** Config cap on picks per row (`fortressCards.roundStartCycleMaxPerRow`). */
  maxPerRow: number;
  onToggle: (kind: FortressKind, cardId: string | null) => void;
  /** Confirm the current picks. */
  onConfirm: () => void;
  /** Explicitly cycle no cards (both rows kept). */
  onSkip: () => void;
  /** Leave the picker without deciding — queue mode only (the live window
   * must be answered). */
  onCancel?: () => void;
}

/** Implement pick mode: the sidebar is the Implement-Fortress card picker while
 * the flow's pick stage is open. Candidate market tiles become click targets
 * (the rest dim), hovering one previews where it lands on the board, and
 * face-down hand candidates get their own rows in the banner. */
interface ImplementPickControls {
  /** Market-row candidates by `${kind}:${cardId}`. */
  candidates: ReadonlyMap<string, ImplementCandidate>;
  /** Face-down hand candidates (Dungeon Dice Martyr) — not market tiles. */
  handCards: ImplementCandidate[];
  /** Discard-pile candidates (Dungeon Dice Martyr "53"+) — not market tiles. */
  discardCards: ImplementCandidate[];
  /** Live play defers a card's resource choice to the placement Confirm;
   * planning (false) must ask before queueing. */
  deferChoice: boolean;
  onPick: (
    cardId: string,
    kind: FortressKind,
    choice?: ImplementChoice,
  ) => void;
  /** Preview a candidate's landing nodes on the board (null clears). */
  onHover: (nodeIds: number[] | null) => void;
  onCancel: () => void;
}

interface MarketSidebarProps {
  markets: MarketView;
  collapsed: boolean;
  /** PHONES: the open panel is a bottom sheet — the toggle closes it (chevron
   * points down) and a dimmer behind it does too. */
  sheet?: boolean;
  onToggle: () => void;
  handlers: SelectionHandlers;
  cycle: CycleControls;
  /** Implement-Fortress pick mode, or null when off. */
  implementPick: ImplementPickControls | null;
  /** Round-start cycle picker, or null when off. Takes precedence over the
   * paid Cycle-Market flow (the two are never live together). */
  roundStart: RoundStartCycleControls | null;
  /** Match-pinned fortress catalogue. */
  cardsById: ReadonlyMap<string, FortressCard>;
  /** cardId → outline: 'ready' (pattern on board + Obsidian in hand) or 'poor'
   * (pattern on board, can't afford). Cards absent from the map get no outline. */
  buildable: ReadonlyMap<string, 'ready' | 'poor'>;
  /** Selected queued move's market markers (at most one per row; a queued
   * Cycle can span both rows). Empty when no move is selected. */
  queuedMarks: readonly QueuedMarketMark[];
  /** Our private fortress watchlist (server state, view-gated) + the toggle
   * that round-trips a change. Null for spectators — hides the feature. */
  watch: {
    ids: readonly string[];
    onToggle: (cardId: string) => void;
  } | null;
  /** Cards marked by ANY player's Dog Den, pinned as short tiles so
   * everyone knows them — `done` marks a half whose condition is already
   * met (its card was implemented). */
  marked: readonly { id: string; done: boolean }[];
}

/** Right sidebar: the face-up Regular and Win Fortress market rows. Collapsible
 * to a thin rail via the inline header toggle; the header eye opens the
 * watchlist search. */
function MarketSidebar({
  markets,
  collapsed,
  sheet,
  onToggle,
  handlers,
  cycle,
  implementPick,
  roundStart,
  cardsById,
  buildable,
  queuedMarks,
  watch,
  marked,
}: MarketSidebarProps) {
  // Implement pick mode: a picked card whose implement effect consumes a
  // resource choice (planning only — live play defers to placement Confirm)
  // waits here for the pick; the banner shows the resource options.
  const [implementChoosing, setImplementChoosing] =
    useState<ImplementCandidate | null>(null);
  /** Watchlist: watched card ids live in OUR player's view-gated server state
   * (`watch.ids` — other players never see them); the header eye toggle swaps
   * the whole body for the fortress search panel. */
  const [watchMode, setWatchMode] = useState(false);
  const [watchQuery, setWatchQuery] = useState('');
  /** The viewer's own fortress discounts, so every price here shows what THEY
   * would pay (Inverse Molehill) beside the struck-through printed cost. */
  const obsidianCostOf = useObsidianCost();
  /** Deck tabs under the search box — both on by default; the last one on
   * can't be switched off (an empty filter would show nothing). */
  const [watchKinds, setWatchKinds] = useState<ReadonlySet<FortressKind>>(
    () => new Set<FortressKind>(['REGULAR', 'win']),
  );
  const toggleWatchKind = (kind: FortressKind) =>
    setWatchKinds((prev) => {
      const next = new Set(prev);
      if (!next.delete(kind)) next.add(kind);
      return next.size > 0 ? next : prev;
    });
  const watched = useMemo(() => new Set(watch?.ids ?? []), [watch?.ids]);
  const toggleWatch = (id: string) => watch?.onToggle(id);
  /** Search-panel results: the whole catalogue, deck- and name-filtered. */
  const watchResults = useMemo(() => {
    const q = watchQuery.trim().toLowerCase();
    return [...cardsById.values()]
      .filter((c) => !c.undealt) // the Spy is never in a market row
      .filter((c) => watchKinds.has(c.kind))
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cardsById, watchQuery, watchKinds]);
  /** Watched cards resolvable in this match's catalogue, for the pinned
   * Watchlist section (ids from another catalogue version just stay dormant). */
  const watchedCards = useMemo(
    () =>
      [...watched]
        .map((id) => cardsById.get(id))
        .filter((c): c is FortressCard => !!c)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [watched, cardsById],
  );
  /** Card ids currently face-up in either market row — these get a star on
   * their market tile and an Active badge in the watchlist rows. */
  const faceUpIds = useMemo(
    () => new Set([...markets.regular.cards, ...markets.win.cards]),
    [markets.regular.cards, markets.win.cards],
  );
  /** Card ids sitting in either discard pile — the search rows badge them. */
  const discardIds = useMemo(
    () => new Set([...markets.regular.discarded, ...markets.win.discarded]),
    [markets.regular.discarded, markets.win.discarded],
  );
  const implementActive = !!implementPick;
  useEffect(() => {
    if (!implementActive) setImplementChoosing(null);
  }, [implementActive]);

  /** Route an implement pick: ask for the card's resource choice first when it
   * needs one now, otherwise hand it straight to the flow. */
  const pickImplementCandidate = (c: ImplementCandidate) => {
    if (!implementPick) return;
    if (!implementPick.deferChoice && c.needsChoice === true) {
      setImplementChoosing(c);
      return;
    }
    implementPick.onPick(c.cardId, c.kind);
  };

  // Per-row candidate lookup for the market tiles while pick mode is on.
  const sectionImplement = (kind: FortressKind) =>
    implementPick
      ? {
          get: (id: string) => implementPick.candidates.get(`${kind}:${id}`),
          onPick: pickImplementCandidate,
          onHover: implementPick.onHover,
        }
      : undefined;
  // In cycle mode every row stays selectable — regular and win cards mix
  // freely in one cycle, each paying its own row's cost.
  const sectionCycle = (kind: FortressKind) =>
    cycle.active
      ? {
          selectable: true,
          selected:
            kind === 'win' ? cycle.selected.win : cycle.selected.regular,
          onToggle: (index: number) => cycle.onToggleCard(kind, index),
        }
      : undefined;

  // Round-start picking: both rows stay selectable, each holding at most
  // `maxPerRow` picks (by card id — mapped to the row indices the card tiles
  // select by).
  const sectionRoundStart = (kind: FortressKind) => {
    if (!roundStart) return undefined;
    const cards = (kind === 'win' ? markets.win : markets.regular).cards;
    const picks = kind === 'win' ? roundStart.sel.win : roundStart.sel.regular;
    const pickIndices = picks
      .map((id) => cards.indexOf(id))
      .filter((i) => i >= 0);
    return {
      selectable: true,
      selected:
        pickIndices.length > 0
          ? new Set(pickIndices)
          : (EMPTY_SELECTION as ReadonlySet<number>),
      onToggle: (index: number) =>
        roundStart.onToggle(kind, cards[index] ?? null),
    };
  };
  const sectionMode = (kind: FortressKind) =>
    sectionRoundStart(kind) ?? sectionCycle(kind);

  const selectedCount = cycle.selected.regular.size + cycle.selected.win.size;
  const rsPickNames = roundStart
    ? [...roundStart.sel.regular, ...roundStart.sel.win].map(
        (id) => cardsById.get(id)?.name ?? id,
      )
    : [];

  const swipeRef = useSidebarSwipe('right', collapsed, onToggle, sheet);

  return (
    <aside
      ref={swipeRef}
      className={`sidebar sidebar-right${collapsed ? ' is-collapsed' : ''}`}
    >
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? 'Show Fortress markets' : 'Hide Fortress markets'}
          aria-label={
            collapsed ? 'Expand market panel' : 'Collapse market panel'
          }
        >
          <ChromeGlyph
            name={
              collapsed ? 'chevron-left' : sheet ? 'chevron-down' : 'chevron-right'
            }
            size={18}
            strokeWidth={2.2}
          />
        </button>
        <div className="sidebar-header-right">
          {watch && (
            <button
              type="button"
              className={`sidebar-action watch-toggle${watchMode ? ' is-active' : ''}`}
              onClick={() => setWatchMode((w) => !w)}
              title={
                watchMode
                  ? 'Back to the markets'
                  : 'Watchlist — search Fortresses and mark them to be watched'
              }
              aria-label={watchMode ? 'Close watchlist search' : 'Open watchlist search'}
              aria-pressed={watchMode}
            >
              <EyeGlyph />
            </button>
          )}
        </div>
      </div>
      {collapsed ? (
        <div className="market-rail">
          {([
            ['Regular Fortresses', markets.regular, REGULAR_ACCENT, 'REGULAR' as const],
            ['Win Fortresses', markets.win, WIN_ACCENT, 'win' as const],
          ] as const).map(([label, pile, accent, kind]) => {
            const mode = sectionMode(kind);
            const rowQueued =
              queuedMarks.find((m) => m.row === kind) ?? null;
            return (
            <div
              key={label}
              className="market-rail-group"
              style={{ borderColor: accent }}
              role="group"
              aria-label={label}
            >
              {pile.cards.map((id, index) => {
                const card = cardsById.get(id);
                const target: SelectionTarget | null = card
                  ? { kind: 'card', card }
                  : null;
                const buildableTier = buildable.get(id);
                const buildableClass =
                  buildableTier === 'ready'
                    ? ' is-buildable'
                    : buildableTier === 'poor'
                      ? ' is-buildable-poor'
                      : '';
                const queued =
                  rowQueued
                    ? rowQueued.mode === 'implement'
                      ? rowQueued.cardId === id
                        ? 'implement'
                        : undefined
                      : rowQueued.indices?.has(index)
                        ? 'cycle'
                        : undefined
                    : undefined;
                const locked = pile.locked.includes(id);
                const cycleSelectable =
                  !!mode && mode.selectable && !locked;
                const cycleSelected = !!mode?.selected.has(index);
                const cycleClass = mode
                  ? ` is-cycle${cycleSelectable ? ' is-cyclable' : ' is-cycle-locked'}${
                      cycleSelected ? ' is-cycle-selected' : ''
                    }`
                  : '';
                // Implement pick mode reaches the rail too (the sidebar can be
                // re-collapsed mid-pick): candidates stay clickable, the rest dim.
                const impl = sectionImplement(kind);
                const implCand = impl?.get(id);
                const implClass = impl
                  ? implCand
                    ? implCand.affordable
                      ? ' is-implement-pick'
                      : ' is-implement-poor'
                    : ' is-implement-locked'
                  : '';
                return (
                  <button
                    key={`${id}-${index}`}
                    type="button"
                    className={`rail-market-card${
                      kind === 'win' ? ' is-win' : ''
                    }${buildableClass}${queued ? ' is-queued' : ''}${cycleClass}${implClass}`}
                    data-cursor-target={`card:${id}`}
                    title={card?.name ?? id}
                    aria-label={card?.name ?? id}
                    aria-pressed={mode ? cycleSelected : undefined}
                    onMouseEnter={
                      target || implCand
                        ? (e) => {
                            // No detail popovers while picking — they'd cover
                            // the click targets; hover previews the landing
                            // nodes on the board instead.
                            if (target && !impl)
                              handlers.onPreview(target, e.currentTarget);
                            if (impl && implCand)
                              impl.onHover(implCand.nodeIds);
                          }
                        : undefined
                    }
                    onMouseLeave={
                      target || impl
                        ? () => {
                            if (target && !impl) handlers.onPreviewEnd(target);
                            impl?.onHover(null);
                          }
                        : undefined
                    }
                    onClick={
                      impl
                        ? implCand?.affordable
                          ? () => impl.onPick(implCand)
                          : undefined
                        : mode
                          ? cycleSelectable
                            ? () => mode.onToggle(index)
                            : undefined
                          : target
                            ? (e) => handlers.onPin(target, e.currentTarget)
                            : undefined
                    }
                  >
                    {card ? (
                      <PatternDiagram
                        pattern={card.pattern}
                        maxW={28}
                        maxH={28}
                        pad={2}
                        iconScale={0.8}
                        dark={kind === 'win'}
                      />
                    ) : (
                      <span aria-hidden>?</span>
                    )}
                    {watched.has(id) && (
                      <span
                        className="rail-watch-star"
                        title="On your watchlist"
                      >
                        ★
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            );
          })}
        </div>
      ) : watchMode && watch ? (
        // Watchlist search: the whole body becomes a catalogue search — mark
        // fortresses to be watched; watched ones get starred when they show up.
        // The pattern chip is the detail trigger (hover / tap), so the rest of
        // the row stays a plain watch toggle.
        <div className="sidebar-body watch-panel">
          <input
            type="search"
            className="watch-search"
            placeholder="Search Fortresses…"
            value={watchQuery}
            onChange={(e) => setWatchQuery(e.target.value)}
            autoFocus
            aria-label="Search Fortresses to watch"
          />
          <div className="watch-tabs" role="group" aria-label="Filter by deck">
            {(
              [
                ['REGULAR', 'Regular'],
                ['win', 'Win'],
              ] as const
            ).map(([kind, label]) => {
              const on = watchKinds.has(kind);
              return (
                <button
                  key={kind}
                  type="button"
                  className={`watch-tab${kind === 'win' ? ' is-win' : ''}${
                    on ? ' is-on' : ''
                  }`}
                  onClick={() => toggleWatchKind(kind)}
                  aria-pressed={on}
                  title={
                    on ? `Hide ${label} Fortresses` : `Show ${label} Fortresses`
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
          <ul className="card-list watch-results">
            {watchResults.map((card) => {
              const isWatched = watched.has(card.id);
              const target: SelectionTarget = { kind: 'card', card };
              const faceUp = faceUpIds.has(card.id);
              const discarded = discardIds.has(card.id);
              return (
                <li key={card.id}>
                  <div
                    className={`watch-row${card.kind === 'win' ? ' is-win' : ''}${
                      isWatched ? ' is-watched' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className={`watch-row-pattern${card.kind === 'win' ? ' is-win' : ''}`}
                      data-cursor-target={`card:${card.id}`}
                      aria-label={`Show ${card.name} details`}
                      onMouseEnter={(e) =>
                        handlers.onPreview(target, e.currentTarget)
                      }
                      onMouseLeave={() => handlers.onPreviewEnd(target)}
                      onClick={(e) => handlers.onPin(target, e.currentTarget)}
                    >
                      <PatternDiagram
                        pattern={card.pattern}
                        maxW={36}
                        maxH={30}
                        pad={3}
                        iconScale={0.8}
                        dark={card.kind === 'win'}
                      />
                    </button>
                    <button
                      type="button"
                      className="watch-row-main"
                      onClick={() => toggleWatch(card.id)}
                      title={
                        isWatched
                          ? `Stop watching ${card.name}`
                          : `Watch ${card.name}`
                      }
                      aria-pressed={isWatched}
                    >
                      <span className="watch-row-text">
                        <span className="watch-row-name">{card.name}</span>
                        <span className="watch-row-meta">
                          <span className="watch-row-cost" title="Obsidian cost">
                            <ResourceCost
                              kind="obsidian"
                              printed={card.cost[3] ?? 0}
                              paid={obsidianCostOf(card)}
                              size={14}
                            />
                          </span>
                          {faceUp && (
                            <span
                              className="watch-row-badge is-active"
                              title="Face up in a market row right now"
                            >
                              ★ Active
                            </span>
                          )}
                          {discarded && (
                            <span
                              className="watch-row-badge is-discard"
                              title="In the discard pile"
                            >
                              ♻ Discard
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="watch-row-mark" aria-hidden>
                        {isWatched ? '★' : <EyeGlyph size={16} />}
                      </span>
                    </button>
                  </div>
                </li>
              );
            })}
            {watchResults.length === 0 && (
              <li className="card empty">No Fortresses match</li>
            )}
          </ul>
        </div>
      ) : (
        <div className="sidebar-body">
        {implementPick && (
          <div
            className="cycle-banner implement-banner"
            role="region"
            aria-label="Implement a fortress"
          >
            {implementChoosing ? (
              <>
                <div className="cycle-banner-row">
                  <span className="cycle-banner-title">
                    {implementChoosing.name} —{' '}
                    {implementChoosing.pieceOptions
                      ? 'place which piece on the card?'
                      : implementChoosing.bankOrProduction
                        ? 'bank it, or grow production?'
                        : implementChoosing.capTargets
                          ? "double or halve which fortress's token cap?"
                          : 'gain which resource?'}
                  </span>
                </div>
                <div className="implement-choice-options">
                  {implementChoosing.pieceOptions
                    ? implementChoosing.pieceOptions.map((piece) => (
                        <button
                          key={piece}
                          type="button"
                          className="implement-choice"
                          onClick={() =>
                            implementPick.onPick(
                              implementChoosing.cardId,
                              implementChoosing.kind,
                              { piece },
                            )
                          }
                          title={`Choose ${PIECE_LABELS[piece]}`}
                          aria-label={`Choose ${PIECE_LABELS[piece]}`}
                        >
                          <PieceIcon kind={piece} size={28} />
                        </button>
                      ))
                    : implementChoosing.bankOrProduction
                      ? bankOrProductionOptions(
                          implementChoosing.bankOrProduction,
                        ).map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            className="implement-choice"
                            onClick={() =>
                              implementPick.onPick(
                                implementChoosing.cardId,
                                implementChoosing.kind,
                                opt.choice,
                              )
                            }
                            title={opt.label}
                            aria-label={opt.label}
                          >
                            {opt.icon ? (
                              <ResourceIcon kind={opt.icon} size={28} />
                            ) : (
                              <span>{opt.label}</span>
                            )}
                          </button>
                        ))
                    : implementChoosing.capTargets
                      ? implementChoosing.capTargets.flatMap((t) =>
                          (['double', 'halve'] as const).map((mode) => (
                            <button
                              key={`${t.cardId}:${mode}`}
                              type="button"
                              className="implement-choice"
                              onClick={() =>
                                implementPick.onPick(
                                  implementChoosing.cardId,
                                  implementChoosing.kind,
                                  { mode, targetCardId: t.cardId },
                                )
                              }
                              title={`${mode === 'double' ? 'Double' : 'Halve'} ${t.name}'s token cap`}
                              aria-label={`${mode === 'double' ? 'Double' : 'Halve'} ${t.name}'s token cap`}
                            >
                              <span>
                                {mode === 'double' ? '×2' : '÷2'} {t.name}
                              </span>
                            </button>
                          )),
                        )
                    : RESOURCE_KINDS.map((rk) => (
                        <button
                          key={rk}
                          type="button"
                          className="implement-choice"
                          onClick={() =>
                            implementPick.onPick(
                              implementChoosing.cardId,
                              implementChoosing.kind,
                              { resource: rk },
                            )
                          }
                          title={`Gain 1 ${RESOURCE_LABELS[rk]}`}
                          aria-label={`Gain 1 ${RESOURCE_LABELS[rk]}`}
                        >
                          <ResourceIcon kind={rk} size={28} />
                        </button>
                      ))}
                </div>
                <div className="cycle-banner-actions">
                  <button
                    type="button"
                    className="cycle-cancel"
                    onClick={() => setImplementChoosing(null)}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="cycle-banner-row">
                  <span className="cycle-banner-title">
                    Implement Fortress — pick a card below
                  </span>
                </div>
                {implementPick.candidates.size === 0 &&
                  implementPick.handCards.length === 0 &&
                  implementPick.discardCards.length === 0 && (
                    <span className="cycle-warning">
                      No revealed fortress matches your buildings.
                    </span>
                  )}
                {implementPick.handCards.map((c) => (
                  <button
                    key={`hand-${c.cardId}`}
                    type="button"
                    className="implement-hand-option"
                    disabled={!c.affordable}
                    onClick={() => pickImplementCandidate(c)}
                    onMouseEnter={() => implementPick.onHover(c.nodeIds)}
                    onMouseLeave={() => implementPick.onHover(null)}
                    title={
                      c.affordable
                        ? `Implement ${c.name} from your face-down hand (1 Obsidian cheaper)`
                        : 'Not enough Obsidian'
                    }
                  >
                    <span className="build-option-name">{c.name}</span>
                    <span className="effect-chip">Hand</span>
                    <span className="implement-hand-cost">
                      {/* The candidate's cost already carries this player's
                          discounts AND the hand card's -1; the catalogue holds
                          the printed price the strikeout shows. */}
                      <ResourceCost
                        kind="obsidian"
                        printed={
                          cardsById.get(c.cardId)?.cost[3] ?? c.cost[3] ?? 0
                        }
                        paid={c.cost[3] ?? 0}
                        size={24}
                      />
                    </span>
                  </button>
                ))}
                {implementPick.discardCards.map((c) => (
                  <button
                    key={`discard-${c.kind}-${c.cardId}`}
                    type="button"
                    className="implement-hand-option"
                    disabled={!c.affordable}
                    onClick={() => pickImplementCandidate(c)}
                    onMouseEnter={() => implementPick.onHover(c.nodeIds)}
                    onMouseLeave={() => implementPick.onHover(null)}
                    title={
                      c.affordable
                        ? `Implement ${c.name} from the discard pile (spends a token on ${
                            cardsById.get(c.fromDiscard ?? '')?.name ?? 'the granting card'
                          })`
                        : 'Not enough Obsidian'
                    }
                  >
                    <span className="build-option-name">{c.name}</span>
                    <span className="effect-chip">Discard</span>
                    <span className="implement-hand-cost">
                      <ResourceCost
                        kind="obsidian"
                        printed={
                          cardsById.get(c.cardId)?.cost[3] ?? c.cost[3] ?? 0
                        }
                        paid={c.cost[3] ?? 0}
                        size={24}
                      />
                    </span>
                  </button>
                ))}
                <div className="cycle-banner-actions">
                  <button
                    type="button"
                    className="cycle-cancel"
                    onClick={implementPick.onCancel}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {roundStart && (
          <div
            className="cycle-banner"
            role="region"
            aria-label="Round-start cycle"
          >
            <div className="cycle-banner-row">
              <span className="cycle-banner-title">
                {roundStart.mode === 'live'
                  ? `Round start — cycle out up to ${roundStart.maxPerRow} card${roundStart.maxPerRow === 1 ? '' : 's'} per market, free`
                  : `Queue for round start — cycle out up to ${roundStart.maxPerRow} card${roundStart.maxPerRow === 1 ? '' : 's'} per market, free`}
              </span>
            </div>
            <div className="cycle-banner-actions">
              <button
                type="button"
                className="cycle-confirm"
                disabled={rsPickNames.length === 0}
                onClick={roundStart.onConfirm}
                title={
                  rsPickNames.length > 0
                    ? `Cycle out: ${rsPickNames.join(' + ')}`
                    : 'Pick a card in either market first'
                }
              >
                {roundStart.mode === 'queue' ? 'Queue: cycle' : 'Cycle'}
                {rsPickNames.length > 0
                  ? ` ${rsPickNames.length} card${rsPickNames.length === 1 ? '' : 's'}`
                  : ''}
              </button>
              <button
                type="button"
                className="cycle-cancel"
                onClick={roundStart.onSkip}
                title="Keep both markets as they are"
              >
                {roundStart.mode === 'queue'
                  ? 'Queue: cycle nothing'
                  : 'Cycle nothing'}
              </button>
              {roundStart.onCancel && (
                <button
                  type="button"
                  className="cycle-cancel"
                  onClick={roundStart.onCancel}
                  title="Close without queueing anything"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
        {!roundStart && cycle.active && (
          <div className="cycle-banner" role="region" aria-label="Cycle market">
            <div className="cycle-banner-row">
              <span className="cycle-banner-title">Choose cards to cycle</span>
              <span
                className="cycle-cost"
                title="Food paid to cycle the selected cards"
              >
                <ResourceIcon kind="food" size={36} /> {cycle.foodCost}
              </span>
            </div>
            <div className="cycle-banner-actions">
              <button
                type="button"
                className="cycle-confirm"
                disabled={selectedCount === 0 || !cycle.affordable}
                onClick={cycle.onConfirm}
              >
                {selectedCount > 0
                  ? `Cycle ${selectedCount} card${selectedCount === 1 ? '' : 's'}`
                  : 'Cycle'}
              </button>
              <button
                type="button"
                className="cycle-cancel"
                onClick={cycle.onCancel}
              >
                Cancel
              </button>
            </div>
            {selectedCount > 0 && !cycle.affordable && (
              <span className="cycle-warning">Not enough Food.</span>
            )}
          </div>
        )}
        {marked.length > 0 && (
          <section className="market-section watchlist-section marked-section">
            <h2 className="market-title watchlist-title">
              ⚑ Marked — win when implemented
            </h2>
            <ul className="card-list">
              {marked.map(({ id, done }, i) => {
                const card = cardsById.get(id);
                if (!card) return null;
                const target: SelectionTarget = { kind: 'card', card };
                return (
                  <li
                    key={`${id}-${i}`}
                    className={`watch-item is-marked${done ? ' is-mark-done' : ''}${
                      card.kind === 'win' ? ' is-win' : ''
                    }${faceUpIds.has(id) ? ' is-face-up' : ''}`}
                    title={
                      done
                        ? 'Condition met — a marked fortress of this deck has been implemented'
                        : "Marked by a Dog Den — implementing this (or the den's other mark of the same deck) fills half of its win"
                    }
                    onMouseEnter={(e) =>
                      handlers.onPreview(target, e.currentTarget)
                    }
                    onMouseLeave={() => handlers.onPreviewEnd(target)}
                    onClick={(e) => handlers.onPin(target, e.currentTarget)}
                  >
                    <span
                      className={`watch-row-pattern${card.kind === 'win' ? ' is-win' : ''}`}
                    >
                      <PatternDiagram
                        pattern={card.pattern}
                        maxW={36}
                        maxH={30}
                        pad={3}
                        iconScale={0.8}
                        dark={card.kind === 'win'}
                      />
                    </span>
                    <span className="watch-row-name">{card.name}</span>
                    <span
                      className="watch-item-star"
                      title={done ? 'Condition met' : 'Marked'}
                    >
                      {done ? '✓' : '⚑'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {watchedCards.length > 0 && (
          <section className="market-section watchlist-section">
            <h2 className="market-title watchlist-title">
              <EyeGlyph size={13} /> Watchlist
            </h2>
            <ul className="card-list">
              {watchedCards.map((card) => {
                const faceUp = faceUpIds.has(card.id);
                const target: SelectionTarget = { kind: 'card', card };
                return (
                  <li
                    key={card.id}
                    className={`watch-item${card.kind === 'win' ? ' is-win' : ''}${
                      faceUp ? ' is-face-up' : ''
                    }`}
                    data-cursor-target={`card:${card.id}`}
                    onMouseEnter={(e) =>
                      handlers.onPreview(target, e.currentTarget)
                    }
                    onMouseLeave={() => handlers.onPreviewEnd(target)}
                    onClick={(e) => handlers.onPin(target, e.currentTarget)}
                  >
                    <span
                      className={`watch-row-pattern${card.kind === 'win' ? ' is-win' : ''}`}
                    >
                      <PatternDiagram
                        pattern={card.pattern}
                        maxW={36}
                        maxH={30}
                        pad={3}
                        iconScale={0.8}
                        dark={card.kind === 'win'}
                      />
                    </span>
                    <span className="watch-row-text">
                      <span className="watch-row-name">{card.name}</span>
                      <span className="watch-row-meta">
                        <span className="watch-row-cost" title="Obsidian cost">
                          <ResourceCost
                            kind="obsidian"
                            printed={card.cost[3] ?? 0}
                            paid={obsidianCostOf(card)}
                            size={14}
                          />
                        </span>
                        {faceUp && (
                          <span
                            className="watch-row-badge is-active"
                            title="Face up in a market row right now"
                          >
                            ★ Active
                          </span>
                        )}
                        {discardIds.has(card.id) && (
                          <span
                            className="watch-row-badge is-discard"
                            title="In the discard pile"
                          >
                            ♻ Discard
                          </span>
                        )}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="watch-item-unwatch"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWatch(card.id);
                      }}
                      title={`Stop watching ${card.name}`}
                      aria-label={`Stop watching ${card.name}`}
                    >
                      <EyeGlyph off size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        <MarketSection
          title="Regular Fortresses"
          pile={markets.regular}
          accent={REGULAR_ACCENT}
          handlers={handlers}
          cardsById={cardsById}
          cycle={sectionMode('REGULAR')}
          implement={sectionImplement('REGULAR')}
          buildable={buildable}
          watched={watched}
          queuedMark={queuedMarks.find((m) => m.row === 'REGULAR') ?? null}
        />
        <MarketSection
          title="Win Fortresses"
          pile={markets.win}
          accent={WIN_ACCENT}
          handlers={handlers}
          cardsById={cardsById}
          cycle={sectionMode('win')}
          implement={sectionImplement('win')}
          buildable={buildable}
          watched={watched}
          queuedMark={queuedMarks.find((m) => m.row === 'win') ?? null}
        />
        </div>
      )}
    </aside>
  );
}

/** One face-up market card row in normal (non-cycle) mode. The slot is keyed by
 * position, so when a cycle replaces the card here the SAME instance sees a new
 * `id` and plays a swap: the old card slides out to the left while the new one
 * slides in from the right. Hover/click open the detail popover, exactly as the
 * static row did. Respects `prefers-reduced-motion` (instant swap, no overlay). */
function MarketCard({
  id,
  handlers,
  cycle,
  implement,
  buildable,
  watched,
  locked,
  queued,
  cardsById,
}: {
  id: string;
  handlers: SelectionHandlers;
  /** Present in cycle mode. The tile renders identically (so its hover detail
   * popover still works); only the click behaviour changes — a click toggles
   * this card's cycle selection instead of pinning the detail popover. */
  cycle?: {
    selectable: boolean;
    selected: boolean;
    onToggle: () => void;
  };
  /** Present in Implement pick mode: `candidate` when THIS card is pickable —
   * a click picks it (instead of pinning the popover) and hovering previews
   * its landing nodes on the board; non-candidates dim out. */
  implement?: {
    candidate: ImplementCandidate | undefined;
    onPick: (c: ImplementCandidate) => void;
    onHover: (nodeIds: number[] | null) => void;
  };
  /** Outline tier: the player has this card's pattern on the board — 'ready'
   * (can also afford it) or 'poor' (short on Obsidian). */
  buildable?: 'ready' | 'poor';
  /** On the player's watchlist — the tile gets a star. */
  watched?: boolean;
  /** Locked against cycling (Supreme Bondage); still purchasable. */
  locked?: boolean;
  /** Marked by the SELECTED queued move: this card is queued to be implemented
   * or cycled when the owner's turn comes. */
  queued?: 'implement' | 'cycle';
  cardsById: ReadonlyMap<string, FortressCard>;
}) {
  // `shown` is the card currently in flow; `exiting` is the previous card kept
  // briefly as an overlay so it can animate out. Both null-exit and the entry
  // animation are gated on a real swap, so the first mount doesn't animate.
  const prevId = useRef(id);
  const [shown, setShown] = useState(id);
  const [exiting, setExiting] = useState<string | null>(null);
  const obsidianCostOf = useObsidianCost();

  useEffect(() => {
    if (prevId.current === id) return;
    const reduce =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!reduce) setExiting(prevId.current); // overlay the outgoing card
    setShown(id);
    prevId.current = id;
  }, [id]);

  // Drop the outgoing overlay when its slide-out ends; a timeout backstops the
  // case where `animationend` never fires (e.g. reduced motion).
  useEffect(() => {
    if (exiting === null) return;
    const t = window.setTimeout(() => setExiting(null), 950);
    return () => window.clearTimeout(t);
  }, [exiting]);

  const card = cardsById.get(shown);
  const isWin = card?.kind === 'win';
  const face = (faceId: string) => {
    const fc = cardsById.get(faceId);
    const faceWin = fc?.kind === 'win';
    return (
      <>
        {fc && (
          <span
            className={`card-pattern${faceWin ? ' is-win' : ''}`}
            title="Required build pattern"
          >
            <PatternDiagram
              pattern={fc.pattern}
              maxW={52}
              maxH={44}
              pad={5}
              iconScale={0.8}
              dark={faceWin}
            />
          </span>
        )}
        <span className="card-id">{fc?.name ?? faceId}</span>
        {fc && (
          <span className="card-cost-badge" title="Obsidian cost">
            <ResourceCost
              kind="obsidian"
              printed={fc.cost[3] ?? 0}
              paid={obsidianCostOf(fc)}
              size={36}
            />
          </span>
        )}
      </>
    );
  };

  // Click priority: Implement pick mode picks the candidate, cycle mode toggles
  // selection (when this row is selectable), otherwise the click pins the
  // detail popover. Hover detail previews stay wired in cycle/normal mode; in
  // Implement pick mode they're suppressed — the tile is a click target and a
  // popover would sit in the way (hovering previews the landing nodes on the
  // board instead).
  const implCand = implement?.candidate;
  const implClass = implement
    ? implCand
      ? implCand.affordable
        ? ' is-implement-pick'
        : ' is-implement-poor'
      : ' is-implement-locked'
    : '';
  const cycleClass = cycle
    ? ` is-cycle${cycle.selectable ? ' is-cyclable' : ' is-cycle-locked'}${
        cycle.selected ? ' is-cycle-selected' : ''
      }`
    : card && !implement
      ? ' is-clickable'
      : '';
  const onClick = implement
    ? implCand?.affordable
      ? () => implement.onPick(implCand)
      : undefined
    : cycle
      ? cycle.selectable
        ? () => cycle.onToggle()
        : undefined
      : card
        ? (e: ReactMouseEvent<HTMLLIElement>) =>
            handlers.onPin({ kind: 'card', card }, e.currentTarget)
        : undefined;

  const buildableClass =
    buildable === 'ready'
      ? ' is-buildable'
      : buildable === 'poor'
        ? ' is-buildable-poor'
        : '';

  return (
    <li
      className={`card card-anim${isWin ? ' is-win' : ''}${cycleClass}${implClass}${buildableClass}${queued ? ' is-queued' : ''}`}
      title={
        card
          ? implement && implCand && !implCand.affordable
            ? 'Not enough Obsidian'
            : undefined
          : shown
      }
      data-cursor-target={`card:${id}`}
      aria-pressed={cycle ? cycle.selected : undefined}
      onMouseEnter={
        card || implCand
          ? (e) => {
              if (card && !implement)
                handlers.onPreview({ kind: 'card', card }, e.currentTarget);
              if (implement && implCand) implement.onHover(implCand.nodeIds);
            }
          : undefined
      }
      onMouseLeave={
        card || implement
          ? () => {
              if (card && !implement)
                handlers.onPreviewEnd({ kind: 'card', card });
              implement?.onHover(null);
            }
          : undefined
      }
      onClick={onClick}
    >
      {queued && (
        <span
          className={`card-queued-badge is-${queued}`}
          title={
            queued === 'implement'
              ? 'Queued: implement this Fortress when your turn starts'
              : 'Queued: cycle this card out when your turn starts'
          }
        >
          ⏳ {queued === 'implement' ? 'Queued' : 'Cycling'}
        </span>
      )}
      {cycle?.selectable && (
        <span className="cycle-check card-cycle-check" aria-hidden>
          {cycle.selected ? '✓' : ''}
        </span>
      )}
      {locked && (
        <span
          className="card-lock-badge"
          title="Locked — can't be cycled, still purchasable"
        >
          🔒
        </span>
      )}
      {watched && (
        <span className="card-watch-star" title="On your watchlist">
          ★
        </span>
      )}
      {exiting !== null && (
        <div
          key={`exit-${exiting}`}
          className="card-face card-exit"
          onAnimationEnd={() => setExiting(null)}
        >
          {face(exiting)}
        </div>
      )}
      <div
        key={`face-${shown}`}
        className={`card-face${exiting !== null ? ' card-enter' : ''}`}
      >
        {face(shown)}
      </div>
    </li>
  );
}

function MarketSection({
  title,
  pile,
  accent,
  handlers,
  cycle,
  implement,
  buildable,
  watched,
  cardsById,
  queuedMark,
}: {
  title: string;
  pile: MarketPile;
  accent: string;
  handlers: SelectionHandlers;
  /** Present in cycle mode: per-card selection for this row. `selectable` is
   * false for the locked-out row once the other row holds the selection. */
  cycle?: {
    selectable: boolean;
    selected: ReadonlySet<number>;
    onToggle: (index: number) => void;
  };
  /** Present in Implement pick mode: candidate lookup + pick/hover for this
   * row's tiles. */
  implement?: {
    get: (id: string) => ImplementCandidate | undefined;
    onPick: (c: ImplementCandidate) => void;
    onHover: (nodeIds: number[] | null) => void;
  };
  buildable: ReadonlyMap<string, 'ready' | 'poor'>;
  /** Watchlisted card ids — a watched card's tile gets a star. */
  watched: ReadonlySet<string>;
  cardsById: ReadonlyMap<string, FortressCard>;
  /** Selected queued move's marker when it targets THIS row, else null. */
  queuedMark: QueuedMarketMark | null;
}) {
  // Discard-pile browser: discards happen face-up, so the pile is public
  // knowledge — the count expands into a browsable list (most recent first)
  // whose rows drive the same hover-preview / pinned-detail popover as the
  // face-up tiles. Auto-collapses when the pile empties.
  const [discardOpen, setDiscardOpen] = useState(false);
  const showDiscards = discardOpen && pile.discarded.length > 0;
  return (
    <section className={`market-section${cycle ? ' is-cycling' : ''}`}>
      <h2 className="market-title" style={{ borderColor: accent }}>
        {title}
      </h2>
      <div className="market-counts">
        <span title="Cards remaining in the draw pile">
          Deck {pile.deckCount}
        </span>
        <button
          type="button"
          className="market-discard-toggle"
          disabled={pile.discarded.length === 0}
          aria-expanded={showDiscards}
          title={
            pile.discarded.length === 0
              ? 'The discard pile is empty'
              : showDiscards
                ? 'Hide the discard pile'
                : 'View the discard pile'
          }
          onClick={() => setDiscardOpen((open) => !open)}
        >
          Discard {pile.discardCount}
          {pile.discarded.length > 0 && (showDiscards ? ' ▴' : ' ▾')}
        </button>
      </div>
      {showDiscards && (
        <ul className="market-discard-list">
          {[...pile.discarded].reverse().map((id, i) => {
            const card = cardsById.get(id);
            return (
              <li key={`${id}-${i}`}>
                <button
                  type="button"
                  className="market-discard-row"
                  onMouseEnter={
                    card
                      ? (e) =>
                          handlers.onPreview(
                            { kind: 'card', card },
                            e.currentTarget,
                          )
                      : undefined
                  }
                  onMouseLeave={
                    card
                      ? () => handlers.onPreviewEnd({ kind: 'card', card })
                      : undefined
                  }
                  onClick={
                    card
                      ? (e) =>
                          handlers.onPin({ kind: 'card', card }, e.currentTarget)
                      : undefined
                  }
                >
                  {card?.name ?? id}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <ul className="card-list">
        {pile.cards.map((id, i) => (
          // A self-animating slot, keyed by position so it persists across
          // cycles (and entering/leaving cycle mode) — the tile renders the same
          // in both modes; in cycle mode the click toggles selection instead of
          // pinning, while hover detail stays available.
          <MarketCard
            key={`slot-${i}`}
            id={id}
            handlers={handlers}
            cardsById={cardsById}
            buildable={buildable.get(id)}
            watched={watched.has(id)}
            locked={pile.locked.includes(id)}
            queued={
              queuedMark
                ? queuedMark.mode === 'implement'
                  ? queuedMark.cardId === id
                    ? 'implement'
                    : undefined
                  : queuedMark.indices?.has(i)
                    ? 'cycle'
                    : undefined
                : undefined
            }
            cycle={
              cycle
                ? {
                    // A locked card can't be cycled — the server rejects the
                    // batch, so don't let it into the selection either.
                    selectable:
                      cycle.selectable && !pile.locked.includes(id),
                    selected: cycle.selected.has(i),
                    onToggle: () => cycle.onToggle(i),
                  }
                : undefined
            }
            implement={
              implement
                ? {
                    candidate: implement.get(id),
                    onPick: implement.onPick,
                    onHover: implement.onHover,
                  }
                : undefined
            }
          />
        ))}
        {pile.cards.length === 0 && (
          <li className="card empty">Market empty</li>
        )}
      </ul>
    </section>
  );
}

/** A detail popover anchored beside a sidebar tile. Both sidebars hug a screen
 * edge and clip their own overflow, so this is portalled to `<body>` and fixed-
 * positioned beside the tile — to its left, flipping to its right when it won't
 * fit, clamped to the viewport. Shown as a hover preview or pinned open by a
 * click; the wrappers below supply the content.
 *
 * It anchors to the live `anchorEl` (re-measured), not a one-shot rect, so it
 * follows the row as the roster reflows (a sibling grows, a player leaves, the
 * list scrolls). Reposition fires from: every render (catches React-driven
 * reflow), a `ResizeObserver` on both the popover and the anchor (size changes),
 * and page scroll / resize. Dismissal:
 *  - Escape closes either kind.
 *  - A pinned popover also closes on an outside click; the listener is attached
 *    only when pinned, so the click-to-pin mousedown can't close the popover it
 *    just opened (effects run after that click).
 *  - A hover preview is read-only (`pointer-events:none`) and dismissed by its
 *    tile's `mouseleave`; if its tile unmounts, the parent clears the selection. */
function AnchoredPopover({
  anchorEl,
  pinned,
  label,
  onClose,
  children,
  className = 'detail-popover',
  onHoverEnter,
  onHoverLeave,
}: {
  anchorEl: HTMLElement;
  pinned: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
  /** Positioned-shell class (must set `position: fixed`). Defaults to the detail
   * popover; the action notice passes its own toast variant. */
  className?: string;
  /** Hover-preview lifecycle hooks. When supplied (and not pinned) the preview
   * becomes interactive so the cursor can move onto it: entering keeps it open,
   * leaving lets the parent close it. Omitted for pinned/notice popovers, which
   * keep their original behaviour. */
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    left: number;
    top: number;
    overAnchor: boolean;
  } | null>(null);

  const reposition = useCallback(() => {
    const el = ref.current;
    // If the anchor tile has unmounted (card cycled out / player left) its rect
    // collapses to 0,0; hold the last position and let the parent close us.
    if (!el || !anchorEl.isConnected) return;
    const a = anchorEl.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    const gap = 10;
    const margin = 8;
    let left = a.left - gap - width;
    if (left < margin) {
      left = Math.min(a.right + gap, window.innerWidth - width - margin);
    }
    const top = Math.max(
      margin,
      Math.min(a.top, window.innerHeight - height - margin),
    );
    // On narrow viewports the flip+clamp above can land the popover ON TOP of
    // its own anchor tile. An interactive hover preview there would steal the
    // pointer from the tile it describes (its clicks would silently die), so
    // remember the overlap and let the render drop pointer events for it.
    const overAnchor =
      left < a.right && left + width > a.left && top < a.bottom && top + height > a.top;
    // Equality-guard so the every-render layout effect can't loop: a new {left,top}
    // object would always be !== the old, re-rendering forever; return the same
    // reference when nothing moved and React bails out.
    setPos((prev) =>
      prev &&
      prev.left === left &&
      prev.top === top &&
      prev.overAnchor === overAnchor
        ? prev
        : { left, top, overAnchor },
    );
  }, [anchorEl]);

  // Re-anchor after every render — the cheapest way to follow React-driven
  // reflow of the roster/market (a row grows, the list re-sorts, a peer leaves).
  useLayoutEffect(reposition);

  // Async size changes that don't go through React: the popover's own content
  // (e.g. the pattern SVG / font swap) and the anchor row resizing in place.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(reposition);
    ro.observe(el);
    ro.observe(anchorEl);
    return () => ro.disconnect();
  }, [anchorEl, reposition]);

  // Scroll (the sidebar list, capture-phase) and viewport resize move the anchor
  // without a React render — follow it. Ignore scrolls inside the popover's own
  // scrollable body, which don't move the anchor.
  useEffect(() => {
    const onScrollOrResize = (e: Event) => {
      if (e.type === 'scroll' && ref.current?.contains(e.target as Node))
        return;
      reposition();
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [reposition]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    if (!pinned) {
      return () => document.removeEventListener('keydown', onKey);
    }
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, pinned]);

  // A hover preview with lifecycle hooks is interactive so the cursor can reach
  // it; without hooks (the action notice) it stays read-only. A pinned popover
  // is always interactive. A preview that overlaps its own anchor goes read-only
  // so the tile underneath keeps its hover/click (see `reposition`).
  const interactive = pinned || (!!onHoverEnter && !pos?.overAnchor);
  return createPortal(
    <div
      ref={ref}
      className={className}
      role="dialog"
      aria-label={label}
      // Hover hooks apply only to the preview; a pinned popover keeps its
      // original click-driven lifecycle untouched.
      onMouseEnter={!pinned ? onHoverEnter : undefined}
      onMouseLeave={!pinned ? onHoverLeave : undefined}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** A transient, read-only popup describing an action another player just took,
 * anchored beside that player's roster row. Reuses `AnchoredPopover` (so it
 * follows the row as the sidebar reflows and is non-interactive while showing);
 * the parent auto-dismisses it after `NOTICE_TTL`, and Escape closes it early. */
function ActionNoticePopover({
  notice,
  anchorEl,
  onDismiss,
}: {
  notice: ActionNotice;
  anchorEl: HTMLElement;
  onDismiss: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={false}
      onClose={onDismiss}
      label={`${notice.title} — action notification`}
      className="action-notice-popover"
    >
      <div className="action-notice">
        <div className="action-notice-title">{notice.title}</div>
        {notice.groups?.map((g, i) => (
          <div className="action-notice-group" key={`${g.label}-${i}`}>
            <div className="action-notice-group-label">{g.label}</div>
            <ul className="action-notice-items">
              {g.items.map((item, j) => (
                <li key={`${item}-${j}`}>{item}</li>
              ))}
              {g.items.length === 0 && (
                <li className="action-notice-empty">—</li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </AnchoredPopover>
  );
}

/** Cards a card's effect references as buildable alongside it
 * (`alwaysBuildable` — BAM Exploder's Artifacts of Courage), resolved so the
 * detail popovers can show their faces beside the card itself. */
function companionCardsOf(
  card: FortressCard,
  cardsById: ReadonlyMap<string, FortressCard>,
): FortressCard[] {
  return (card.effects?.alwaysBuildable ?? [])
    .map((id) => cardsById.get(id))
    .filter((c): c is FortressCard => c !== undefined);
}

/** The popover's card face(s): the card itself, plus any companion cards its
 * effect references (slightly smaller, beside it). */
function CardFaceRow({
  card,
  companions,
  tokens = 0,
}: {
  card: FortressCard;
  companions: FortressCard[];
  /** Tokens on the card in play — fills its token tray. */
  tokens?: number;
}) {
  if (companions.length === 0) {
    return <FortressCardFace card={card} tokens={tokens} style={{ width: 300 }} />;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <FortressCardFace card={card} tokens={tokens} style={{ width: 300, flexShrink: 0 }} />
      {companions.map((c) => (
        <FortressCardFace
          key={c.id}
          card={c}
          style={{ width: 260, flexShrink: 0 }}
        />
      ))}
    </div>
  );
}

/** Full detail of a market card, anchored beside its summary tile. */
function CardDetailPopover({
  card,
  companions = [],
  anchorEl,
  pinned,
  onClose,
  onHoverEnter,
  onHoverLeave,
}: {
  card: FortressCard;
  /** Companion cards shown beside the face (see `companionCardsOf`). */
  companions?: FortressCard[];
  anchorEl: HTMLElement;
  pinned: boolean;
  onClose: () => void;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={pinned}
      onClose={onClose}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}
      label={`${card.name} card details`}
    >
      <CardFaceRow card={card} companions={companions} />
    </AnchoredPopover>
  );
}

/** Detail of one of a player's implemented fortress cards, anchored beside its
 * roster tile: the full card face plus its live token / exhausted state, and a
 * "View on board" action that pans + zooms to where it sits. While this is open,
 * the fortress's nodes are ring-highlighted on the board (driven from App). */
function FortressCardDetailPopover({
  card,
  companions = [],
  tokens,
  stored,
  disabled,
  choice = '',
  isCopy = false,
  hasNodes,
  onViewOnBoard,
  anchorEl,
  pinned,
  onClose,
  onHoverEnter,
  onHoverLeave,
}: {
  card: FortressCard;
  /** Companion cards shown beside the face (see `companionCardsOf`). */
  companions?: FortressCard[];
  tokens: number;
  /** The piece placed on the card to denote its implement-time pick
   * (Shifty Engineer), "" when none. */
  choice?: string;
  /** Resources stored on the card; 0 hides the row. Public to every
   * viewer, like tokens. */
  stored: number;
  disabled: boolean;
  /** True for a CurdleCat-awarded copy — a phantom of the card's effect. */
  isCopy?: boolean;
  hasNodes: boolean;
  /** Pan + zoom the board to this fortress. Omitted when the popover was
   * opened FROM the board (already panned there) — the button is hidden. */
  onViewOnBoard?: () => void;
  anchorEl: HTMLElement;
  pinned: boolean;
  onClose: () => void;
  onHoverEnter?: () => void;
  onHoverLeave?: () => void;
}) {
  // A card with a token tray shows its tokens there (filled slots); the
  // separate "● N tokens" chip is for cards without one.
  const hasTray = cardTokenTray(card) !== null;
  return (
    <AnchoredPopover
      anchorEl={anchorEl}
      pinned={pinned}
      onClose={onClose}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}
      label={`${card.name} fortress details`}
    >
      <div className="fortress-detail">
        <CardFaceRow card={card} companions={companions} tokens={tokens} />
        {((tokens > 0 && !hasTray) || stored > 0 || disabled || isCopy || choice !== '') && (
          <div className="fortress-detail-state">
            {isCopy && (
              <span
                className="fortress-detail-copied"
                title="A CurdleCat copy — the card's effect without its board fortress"
              >
                ⧉ Copied by CurdleCat
              </span>
            )}
            {tokens > 0 && !hasTray && (
              <span
                className="fortress-detail-tokens"
                title="Tokens on this card"
              >
                ● {tokens} token{tokens === 1 ? '' : 's'}
              </span>
            )}
            {stored > 0 &&
              (() => {
                // The pile's resource kind follows the card's storing hook
                // (Singularity Ball absorbs Food; the penalty piles hold
                // energy), and a win-threshold pile shows its target.
                const kind = card.effects?.hooks?.some(
                  (h) => h.kind === 'food_spent_stored',
                )
                  ? ('food' as const)
                  : ('vigor' as const);
                const target = card.effects?.winAtStored;
                return (
                  <span
                    className="fortress-detail-stored"
                    title="Resources stored on this card"
                  >
                    <ResourceIcon kind={kind} size={18} /> {stored}
                    {target !== undefined ? `/${target}` : ''} stored
                  </span>
                );
              })()}
            {choice !== '' && (
              <span
                className="fortress-detail-choice"
                title="The piece placed on this card denotes the option chosen on implement"
              >
                <PieceIcon kind={choice as ImplementPiece} size={16} />{' '}
                {pieceLabel(choice)} chosen
              </span>
            )}
            {disabled && (
              <span className="fortress-detail-disabled" title="Exhausted">
                ⊘ Exhausted
              </span>
            )}
          </div>
        )}
        {onViewOnBoard && (
          <button
            type="button"
            className="fortress-detail-view"
            onClick={onViewOnBoard}
            disabled={!hasNodes}
            title={
              hasNodes
                ? 'Pan and zoom the board to this fortress'
                : 'This fortress has no recorded board location'
            }
          >
            ⌖ View on board
          </button>
        )}
      </div>
    </AnchoredPopover>
  );
}

/**
 * Host-only ranked controls: pick a skill rating pool for this match, or leave
 * it unranked. The pool list is fetched once when the lobby opens; the server
 * re-checks host + lobby phase on every change.
 */
function RankedSettings({
  lobbyInfo,
  onChange,
  disabled,
}: {
  lobbyInfo: LobbyInfo;
  onChange: (pool: SkillPool | null) => void;
  disabled: boolean;
}) {
  const [pools, setPools] = useState<SkillPool[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listPools()
      .then((body) => {
        if (!live) return;
        setPools(body.pools);
        setEnabled(body.enabled);
      })
      .catch(() => {
        if (live) setError("Couldn't load skill rating pools.");
      });
    return () => {
      live = false;
    };
  }, []);

  if (!enabled) {
    return error ? <p className="auth-error">{error}</p> : null;
  }

  return (
    <div className="lobby-ranked">
      <label className="lobby-ranked-toggle">
        <input
          type="checkbox"
          checked={lobbyInfo.ranked}
          disabled={disabled || pools.length === 0}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? (pools.find((p) => p.poolId === lobbyInfo.poolId) ??
                    pools[0] ??
                    null)
                : null,
            )
          }
        />
        Ranked match
      </label>
      {pools.length === 0 ? (
        <p className="lobby-ranked-hint">
          Create a skill rating pool on the hub first.
        </p>
      ) : (
        <select
          className="auth-input"
          aria-label="Skill rating pool"
          value={lobbyInfo.poolId}
          disabled={disabled || !lobbyInfo.ranked}
          onChange={(e) =>
            onChange(pools.find((p) => p.poolId === e.target.value) ?? null)
          }
        >
          {pools.map((pool) => (
            <option key={pool.poolId} value={pool.poolId}>
              {pool.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Host-only max-minutes-per-move field (empty = no limit). Sent on blur /
 * Enter rather than per keystroke — each change re-sends the match config to
 * the whole table. The server re-checks host + lobby phase + range.
 */
function TurnTimeLimitSetting({
  minutes,
  onChange,
  disabled,
}: {
  minutes: number;
  onChange: (minutes: number) => void;
  disabled: boolean;
}) {
  const shown = minutes > 0 ? String(minutes) : '';
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);

  const commit = () => {
    const next = draft.trim() === '' ? 0 : Number(draft);
    if (
      !Number.isInteger(next) ||
      next < 0 ||
      next > MAX_TURN_TIME_LIMIT_MINUTES
    ) {
      setDraft(shown);
      return;
    }
    if (next !== minutes) onChange(next);
  };

  return (
    <div className="lobby-ranked">
      <label className="lobby-ranked-toggle">
        Max minutes per move
        <input
          className="auth-input"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_TURN_TIME_LIMIT_MINUTES}
          step={1}
          placeholder="No limit"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </label>
    </div>
  );
}

/**
 * A player's standing in the lobby's rating pool, shown on their roster row
 * while the match is ranked.
 *
 * Someone with no ranked matches in this pool — never rated, or rated but yet
 * to finish one — reads as "No matches" beside the rating they'd debut on,
 * rather than a fake 0-0 record.
 */
function PlayerRating({ player }: { player: LobbyPlayer }) {
  const rating = Math.round(player.rating);
  if (player.ratingMatches === 0) {
    return (
      <span className="lobby-rating">
        {/* The pool's starting rating — omitted rather than shown as 0 when
            the rating server didn't report one (an older deployment). */}
        {rating > 0 && <span className="lobby-rating-mmr">{rating}</span>}
        <span className="lobby-rating-record is-unrated">No matches</span>
      </span>
    );
  }
  const { ratingWins: wins, ratingLosses: losses, ratingStreak: streak } = player;
  return (
    <span className="lobby-rating">
      <span className="lobby-rating-mmr">{rating}</span>
      <span className="lobby-rating-record">
        {wins}W–{losses}L
      </span>
      {streak !== 0 && (
        <span
          className={`lobby-rating-streak ${streak > 0 ? 'is-win' : 'is-loss'}`}
          title={
            streak > 0
              ? `${streak} win${streak === 1 ? '' : 's'} in a row`
              : `${-streak} loss${streak === -1 ? '' : 'es'} in a row`
          }
        >
          {streak > 0 ? `W${streak}` : `L${-streak}`}
        </span>
      )}
    </span>
  );
}

interface LobbyProps {
  players: LobbyPlayer[];
  self: LobbyPlayer | undefined;
  lobbyInfo: LobbyInfo;
  /** This client hosts the lobby — only they can change the ranked settings. */
  isHost: boolean;
  /** Host only: make the match ranked in `pool`, or unranked with `null`. */
  onRankedChange: (pool: SkillPool | null) => void;
  /** The match's max time per move (from its config); undefined = none. */
  timePerTurnMs?: number;
  /** Host only: set the max minutes per move, 0 for no limit. */
  onTurnTimeLimitChange: (minutes: number) => void;
  /** Everyone watching this room. */
  spectators: SpectatorView[];
  /** This client joined watch-only — no seat, no ready control. */
  isSpectator: boolean;
  onToggleReady: () => void;
  /** Creator/host only: close the lobby for everyone (server re-checks). */
  onKillLobby: () => void;
  disabled: boolean;
  /** Non-prod only: when true, show the dev Auto-Initialize button. */
  devMode: boolean;
  /** Dev-only: force-start the match, auto-setting up every player. */
  onAutoInit: () => void;
  onLeave: () => void;
}

function Lobby({
  players,
  self,
  lobbyInfo,
  isHost,
  onRankedChange,
  timePerTurnMs,
  onTurnTimeLimitChange,
  spectators,
  isSpectator,
  onToggleReady,
  onKillLobby,
  disabled,
  devMode,
  onAutoInit,
  onLeave,
}: LobbyProps) {
  const timeLimitMinutes = Math.round((timePerTurnMs ?? 0) / 60_000);
  const readyCount = players.filter((p) => p.ready).length;
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!lobbyInfo.autoStartAtMs) {
      setCountdown(null);
      return;
    }
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((lobbyInfo.autoStartAtMs - serverNow()) / 1000),
      );
      setCountdown(left);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [lobbyInfo.autoStartAtMs]);

  async function copyCode() {
    const code = lobbyInfo.inviteCode.toUpperCase();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="lobby">
      <div className="lobby-card">
        <header className="lobby-card-head">
          <FortressMark className="lobby-card-mark" />
          <div>
            <h1>Lobby</h1>
            <p className="lobby-sub">
              {readyCount} / {players.length} ready · {players.length}/
              {lobbyInfo.maxPlayers} seats
              {lobbyInfo.visibility === 'private' ? ' · Private' : ' · Public'}
              {spectators.length > 0 &&
                ` · ${spectators.length} watching`}
            </p>
          </div>
        </header>
        <div className="lobby-card-body">
        <p className="lobby-async-hint">
          Ready up and you can close the game: your seat stays held, and the
          match starts once everyone is ready. Leaving without readying up
          gives up your seat.
        </p>

        <div className="lobby-code-row">
          <span className="lobby-code-label">Code</span>
          <code className="lobby-code">{lobbyInfo.inviteCode || '————'}</code>
          <button
            type="button"
            className="lobby-copy-btn"
            onClick={() => void copyCode()}
            disabled={!lobbyInfo.inviteCode}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        {countdown !== null && (
          <p className="lobby-countdown" role="status">
            Starting in {countdown}s…
          </p>
        )}

        {isHost ? (
          <RankedSettings
            lobbyInfo={lobbyInfo}
            onChange={onRankedChange}
            disabled={disabled}
          />
        ) : (
          lobbyInfo.ranked && (
            <p className="lobby-ranked-hint" role="status">
              🏆 Ranked · {lobbyInfo.poolName || 'skill rating pool'}
            </p>
          )
        )}

        {isHost ? (
          <TurnTimeLimitSetting
            minutes={timeLimitMinutes}
            onChange={onTurnTimeLimitChange}
            disabled={disabled}
          />
        ) : (
          timeLimitMinutes > 0 && (
            <p className="lobby-ranked-hint" role="status">
              ⏱ Max {timeLimitMinutes} min per move
            </p>
          )
        )}

        <ul className="lobby-roster">
          {players.map((p) => (
            <li
              key={p.id}
              className={`${p.ready ? 'is-ready' : ''}${p.connected ? '' : ' is-away'}`}
              data-cursor-target={`player:${p.id}`}
            >
              <PlayerIcon
                name={p.name}
                seat={p.seat}
                connected={p.connected}
                size={28}
              />
              <span className="name">
                {p.name}
                {p.id === self?.id && <span className="you"> (you)</span>}
              </span>
              {lobbyInfo.ranked && p.ratingLoaded && <PlayerRating player={p} />}
              <span className="state">
                {p.ready && <ChromeGlyph name="check" size={15} strokeWidth={2.6} />}
                {p.ready
                  ? p.connected
                    ? 'Ready'
                    : 'Ready · away'
                  : p.connected
                    ? 'Not ready'
                    : 'Not ready · away'}
              </span>
            </li>
          ))}
          {players.length === 0 && (
            <li className="empty">Waiting for players…</li>
          )}
          {players.length > 0 &&
            Array.from(
              { length: Math.max(0, lobbyInfo.maxPlayers - players.length) },
              (_, i) => (
                <li key={`open-${i}`} className="empty is-open-seat">
                  Open seat
                </li>
              ),
            )}
        </ul>
        {isSpectator ? (
          <p className="lobby-spectating" role="status">
            👁 Spectating — you&apos;ll watch the match when it starts.
          </p>
        ) : (
          <button
            className={`ready-btn ${self?.ready ? 'on' : ''}`}
            data-cursor-target="lobby:ready"
            onClick={onToggleReady}
            disabled={disabled}
          >
            {self?.ready ? 'Cancel ready' : 'Ready up'}
          </button>
        )}
        <div className="lobby-leave-row">
          <button type="button" className="auth-link lobby-leave" onClick={onLeave}>
            {self?.ready
              ? 'Exit (your seat stays ready)'
              : isHost
                ? 'Exit (your seat is held)'
                : 'Leave lobby'}
          </button>
          {isHost && !isSpectator && (
            <button
              type="button"
              className="auth-link lobby-leave lobby-kill"
              onClick={onKillLobby}
              title="Close this lobby for everyone."
            >
              Close lobby
            </button>
          )}
        </div>
        {devMode && !isSpectator && (
          <button
            type="button"
            className="auto-init-btn"
            onClick={onAutoInit}
            disabled={disabled}
            title="Dev only: force-start now, auto-assigning every player's home base and production."
          >
            ⚡ Auto Initialize (dev)
          </button>
        )}
        </div>
      </div>
    </div>
  );
}
